import {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { StudioErrorBoundary } from "@/components/StudioErrorBoundary";
import {
  Plus,
  Lightning,
  Plus as PlusIcon,
  Minus,
  Trash,
  Star,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Ruler,
  GridFour,
  Magnet,
  ArrowsOut,
  ArrowsOutCardinal,
  ArrowsIn,
  ArrowsClockwise,
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowUUpLeft,
  ArrowUUpRight,
  Copy,
  LinkSimple,
  LinkSimpleBreak,
  FlipHorizontal,
  FlipVertical,
  Polygon,
  LineSegments,
  CircleDashed,
  SquaresFour,
  Check,
  ClipboardText,
  SlidersHorizontal,
  PencilRuler,
  PencilSimple,
  Shapes,
  Scissors,
  Wrench,
  TextT,
  Warning,
  X,
  FloppyDisk,
  Question,
  CaretRight,
  CaretDown,
  ArrowRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { EdgePanel, EdgeShape } from "@/components/EdgePanel";
import { LayoutPresets, useHiddenLayouts } from "@/components/LayoutPresets";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DesignerTour } from "@/components/DesignerTour";
import { CutoutTemplatePicker } from "@/components/CutoutTemplatePicker";
import { Input } from "@/components/ui/input";
import { MeasureInput } from "@/components/MeasureInput";
import { API } from "@/lib/api";
import { formatInches, parseInches } from "@/lib/measure";
import { money } from "@/lib/format";
import { SketchMode } from "@/components/sketch/SketchMode";
import { StoneExDrawing } from "@/components/sketch/StoneExDrawing";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const uid = () => Math.random().toString(36).slice(2, 10);

export const SHAPES = {
  Rectangle: (l = 96, d = 25.5) => [
    [0, 0],
    [l, 0],
    [l, d],
    [0, d],
  ],
  "L-shape": (l = 96, d = 25.5) => [
    [0, 0],
    [l, 0],
    [l, d],
    [d, d],
    [d, l * 0.7],
    [0, l * 0.7],
  ],
  // A uniform-depth L: both legs are `d` wide. Type the overall width and length; the strip stays.
  "Standard L": (w = 108, h = 108, d = 25.5) => [
    [0, 0],
    [w, 0],
    [w, d],
    [d, d],
    [d, h],
    [0, h],
  ],
  "U-shape": (l = 108, d = 25.5) => [
    [0, 0],
    [l, 0],
    [l, 80],
    [l - d, 80],
    [l - d, d],
    [d, d],
    [d, 80],
    [0, 80],
  ],
  // A uniform-depth U (galley / island run): both returns and the base are `d` wide. Type W × H.
  "Standard U": (w = 132, h = 108, d = 25.5) => [
    [0, 0],
    [w, 0],
    [w, h],
    [w - d, h],
    [w - d, d],
    [d, d],
    [d, h],
    [0, h],
  ],
  Island: (l = 84, d = 42) => [
    [0, 0],
    [l, 0],
    [l, d],
    [0, d],
  ],
  "L-shape right": (l = 96, d = 25.5) => [
    [0, 0],
    [l, 0],
    [l, l * 0.7],
    [l - d, l * 0.7],
    [l - d, d],
    [0, d],
  ],
  Peninsula: (l = 60, d = 42) => [
    [0, 0],
    [l, 0],
    [l, d],
    [l - 26, d],
    [l - 26, 25.5],
    [0, 25.5],
  ],
  "Angled corner L": (w = 68.875, h = 100.25, d = 25.5, s = 18) => [
    [0, 0],
    [w, 0],
    [w, h],
    [w - 25.5, h],
    [w - 25.5, d + s],
    [w - 25.5 - s, d],
    [0, d],
  ],
  // A long L (run along the top) whose outer corner is clipped at 45° and whose return
  // steps in — the common "angled corner with a step" template. Drop it, then type your
  // own run / depth / return / step / diagonal on each side.
  "Angled corner L stepped": (l = 105, d = 25.5, rw = 25.5, step = 19, clip = 35) => {
    const k = Math.round((clip / Math.SQRT2) * 100) / 100;
    return [
      [0, 0],
      [l, 0],
      [l, d + k + step],
      [l - rw, d + k + step],
      [l - rw, d + k],
      [l - rw - k, d],
      [0, d],
    ];
  },
  // A circle, approximated as a 64-sided polygon so it rides the existing poly pipeline.
  // Edges stay under ~6" so per-side dimension labels auto-hide; size is set by diameter.
  Circle: (d = 36) => {
    const n = 64;
    const r = d / 2;
    const out = [];
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n;
      out.push([
        Math.round((r + r * Math.cos(a)) * 100) / 100,
        Math.round((r + r * Math.sin(a)) * 100) / 100,
      ]);
    }
    return out;
  },
  // An ellipse (64-gon) sized by separate length x width — for oval tops and tables.
  Oval: (l = 48, w = 30) => {
    const n = 64;
    const rx = l / 2;
    const ry = w / 2;
    const out = [];
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n;
      out.push([
        Math.round((rx + rx * Math.cos(a)) * 100) / 100,
        Math.round((ry + ry * Math.sin(a)) * 100) / 100,
      ]);
    }
    return out;
  },
};

const SNAP_STEPS = [
  ['1/8"', 0.125],
  ['1/4"', 0.25],
  ['1/2"', 0.5],
  ['1"', 1],
  ['2"', 2],
];

export const LAYOUTS = [
  { key: "Rectangle", label: "Rectangle", note: '96" x 25.5"' },
  { key: "Circle", label: "Circle", note: '36" round · set the size' },
  { key: "Oval", label: "Oval", note: '48" x 30" · set each' },
  { key: "Standard L", label: "Standard L", note: '25.5" depth · type W × H' },
  { key: "L-shape", label: "L-Shape Left", note: "L run + return" },
  { key: "L-shape right", label: "L-Shape Right", note: "L run + return" },
  { key: "U-shape", label: "U-Shape", note: '108" horseshoe' },
  { key: "Standard U", label: "Standard U", note: '25.5" galley · type W × H' },
  { key: "Island", label: "Island", note: '84" x 42"' },
  { key: "Peninsula", label: "Peninsula", note: '60" x 42"' },
  {
    key: "Angled corner L",
    label: "Angled Corner L",
    note: '68⅞" x 100¼" · 45° corner',
  },
  {
    key: "Angled corner L stepped",
    label: "Angled Corner L (Stepped)",
    note: '105" run · 45° clip · 19" step',
  },
];

export const CUTOUT_SIZE = {
  "Undermount sink": [30, 18],
  "Drop-in sink": [33, 22],
  "Farmhouse sink": [33, 20],
  Cooktop: [30, 21],
  "Faucet hole": [3, 3],
  "Soap dispenser": [2, 2],
  Outlet: [4, 2],
  "Single gang": [2.25, 4.13],
  "Double gang": [4.13, 4.13],
  "Triple gang": [6, 4.13],
  "Radius corner": [4, 4],
  "Custom Cut-out Square": [12, 12],
  Oval: [18, 12],
  Circle: [12, 12],
  "Fireplace Cut-Out": [42, 20],
  Fireplace: [42, 20],
  Tub: [60, 30],
};

export const CUSTOM_CUTOUT_PRESETS = ["Fireplace", "Tub"];

export const SHAPE_CUTOUTS = ["Custom Cut-out Square", "Oval", "Circle", "Fireplace Cut-Out"];

// Merge the configured priced cut-out kinds with the built-in shape cut-outs.
export const withShapeCutouts = (list) => {
  const base = list && list.length ? list : Object.keys(CUTOUT_SIZE);
  return [...new Set([...base, ...SHAPE_CUTOUTS])];
};

// When a cut-out kind changes to a shape cut-out, also set its drawn shape.
export const kindPatch = (kind) => {
  const k = (kind || "").toLowerCase();
  if (k === "oval") return { kind, shape: "Oval" };
  if (k === "circle") return { kind, shape: "Round" };
  if (k === "custom cut-out square" || k.includes("fireplace"))
    return { kind, shape: "Rectangle" };
  return { kind };
};

// A sink's inner / cut-out size ("28.5\" x 16.5\"") → [length, width] in decimal inches.
export const parseCutoutSize = (raw) => {
  if (!raw) return null;
  const parts = String(raw).split(/\s*[x×*]\s*/i);
  if (parts.length < 2) return null;
  const w = parseInches(parts[0]);
  const h = parseInches(parts[1]);
  return w && h && w > 0 && h > 0 ? [w, h] : null;
};

// Cut-outs grouped so the palette reads at a glance
export const CUTOUT_GROUPS = [
  {
    key: "sinks",
    label: "Sinks",
    items: ["Undermount sink", "Drop-in sink", "Farmhouse sink"],
  },
  { key: "cooktop", label: "Cooktop", items: ["Cooktop"] },
  {
    key: "electrical",
    label: "Outlets & switches",
    items: ["Single gang", "Double gang", "Triple gang"],
  },
  {
    key: "holes",
    label: "Small holes & fittings",
    items: ["Faucet hole", "Soap dispenser", "Outlet", "Radius corner"],
  },
  {
    key: "shapes",
    label: "Custom shapes",
    items: ["Custom Cut-out Square", "Oval", "Circle", "Fireplace Cut-Out"],
  },
];

// One-click cut-out kits — parts placed relative to the piece centre (dx right+, dy front+)
export const DEFAULT_KITS = [
  {
    id: "sink-kit",
    name: "Sink kit",
    parts: [
      { kind: "Undermount sink", w: 30, h: 18, dx: 0, dy: 0 },
      { kind: "Faucet hole", w: 3, h: 3, dx: 0, dy: -11.5 },
      { kind: "Soap dispenser", w: 2, h: 2, dx: -4, dy: -11.5 },
    ],
  },
  {
    id: "vanity-kit",
    name: "Vanity kit",
    parts: [
      { kind: "Undermount sink", w: 19, h: 15, dx: 0, dy: 0 },
      { kind: "Faucet hole", w: 3, h: 3, dx: 0, dy: -10 },
    ],
  },
];
const SNAP_TOL = 4; // inches — pieces snap flush within this distance
const EDGE_HIT_W = 6; // inches — forgiving click/hover band so tapping near an edge still selects it

// Appliance footprint drawn on the 2D plans (print + interactive) so the size is
// obvious before opening 3D. Colors match the edge markers.
const APP_COLOR = {
  fridge: "#22d3ee",
  range: "#f97316",
  dishwasher: "#a78bfa",
  hood: "#f472b6",
};
const APP_LABEL = {
  fridge: "Fridge",
  range: "Stove",
  dishwasher: "D/W",
  hood: "Hood",
};
const appDims = (e, len) => {
  const wv = Number(e.appliance_width) || 0;
  if (e.appliance === "fridge") {
    const d =
      e.appliance_height === "tall"
        ? 25
        : e.appliance_height === "counter"
          ? 26
          : 30;
    return [wv || Math.min(42, Math.max(30, len)), d];
  }
  if (e.appliance === "range") return [wv || Math.min(36, Math.max(24, len)), 26];
  if (e.appliance === "dishwasher") return [wv || 24, 24];
  if (e.appliance === "hood") return [wv || 30, 20];
  return null;
};
const applianceFootprintGeom = (pts, i, e) => {
  const dims = appDims(e, round2(edgeLen(pts, i)));
  if (!dims) return null;
  const [W, D] = dims;
  const n = pts.length;
  const A = pts[i];
  const B = pts[(i + 1) % n];
  const L = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
  const ex = (B[0] - A[0]) / L;
  const ey = (B[1] - A[1]) / L;
  let nx = -ey;
  let ny = ex;
  const gx = pts.reduce((s, p) => s + p[0], 0) / n;
  const gy = pts.reduce((s, p) => s + p[1], 0) / n;
  const mx = (A[0] + B[0]) / 2;
  const my = (A[1] + B[1]) / 2;
  if ((mx - gx) * nx + (my - gy) * ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  let ux = ex;
  let uy = ey;
  let vx = nx;
  let vy = ny;
  const f = e.appliance_facing || "out";
  if (f === "in") {
    ux = -ex; uy = -ey; vx = -nx; vy = -ny;
  } else if (f === "left") {
    ux = nx; uy = ny; vx = -ex; vy = -ey;
  } else if (f === "right") {
    ux = -nx; uy = -ny; vx = ex; vy = ey;
  }
  const hw = W / 2;
  const corner = (sx, dz) => [mx + ux * sx + vx * dz, my + uy * sx + vy * dz];
  const c1 = corner(-hw, 0);
  const c2 = corner(hw, 0);
  const c3 = corner(hw, D);
  const c4 = corner(-hw, D);
  return {
    poly: [c1, c2, c3, c4].map(([x, y]) => `${round2(x)},${round2(y)}`).join(" "),
    cx: (c1[0] + c3[0]) / 2,
    cy: (c1[1] + c3[1]) / 2,
  };
};

const segIntersect = (p1, p2, p3, p4) => {
  const den =
    (p2[0] - p1[0]) * (p4[1] - p3[1]) - (p2[1] - p1[1]) * (p4[0] - p3[0]);
  if (Math.abs(den) < 1e-6) return null;
  const t =
    ((p3[0] - p1[0]) * (p4[1] - p3[1]) - (p3[1] - p1[1]) * (p4[0] - p3[0])) /
    den;
  return [p1[0] + t * (p2[0] - p1[0]), p1[1] + t * (p2[1] - p1[1])];
};

// Offset a polyline by `d` to the right of travel (or one depth per leg), mitring the corners.
const offsetChain = (pts, d) => {
  const segs = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[i + 1];
    const len = Math.hypot(x2 - x1, y2 - y1) || 1;
    const di = Array.isArray(d) ? d[i] : d;
    const nx = (-(y2 - y1) / len) * di;
    const ny = ((x2 - x1) / len) * di;
    segs.push([
      [x1 + nx, y1 + ny],
      [x2 + nx, y2 + ny],
    ]);
  }
  if (!segs.length) return [];
  const out = [segs[0][0]];
  for (let i = 0; i < segs.length - 1; i++) {
    const hit = segIntersect(
      segs[i][0],
      segs[i][1],
      segs[i + 1][0],
      segs[i + 1][1],
    );
    out.push(hit || segs[i][1]);
  }
  out.push(segs[segs.length - 1][1]);
  return out;
};

export const runPolygon = (pts, width) => [
  ...pts,
  ...offsetChain(pts, width).reverse(),
];

const polyArea = (pts) =>
  Math.abs(
    pts.reduce((s, [x, y], i) => {
      const [x2, y2] = pts[(i + 1) % pts.length];
      return s + x * y2 - x2 * y;
    }, 0) / 2,
  );

const edgeLen = (pts, i) => {
  const n = pts?.length || 0;
  if (!n) return 0;
  const a = pts[i];
  const b = pts[(i + 1) % n];
  if (!a || !b) return 0;
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
};

// Depth across an angled (non-axis) run: pairs of parallel, non-adjacent edges such as the two
// diagonal sides of a 45° middle piece. Returns [{ i, j, d, mid, nx, ny }] with j the shorter edge.
const diagDepths = (pts) => {
  const n = pts.length;
  const out = [];
  const seen = new Set();
  const isAxis = (dx, dy) => {
    const ang = Math.abs(Math.atan2(dy, dx)) % Math.PI;
    return ang < 0.05 || Math.abs(ang - Math.PI / 2) < 0.05 || Math.abs(ang - Math.PI) < 0.05;
  };
  // Only bent runs (at least one angled edge) get depth dimensions; L/U shapes are fully
  // described by their edge lengths already.
  const bent = pts.some((p, k) => {
    const q = pts[(k + 1) % n];
    return Math.hypot(q[0] - p[0], q[1] - p[1]) >= 2 && !isAxis(q[0] - p[0], q[1] - p[1]);
  });
  if (!bent) {
    // Square U-shapes: show the middle depth (outer base ↔ inner span, the edge whose both
    // corners are inside corners) so the closed run can be read and edited like a bent piece.
    const area2 = pts.reduce((s, p, k) => {
      const q = pts[(k + 1) % n];
      return s + p[0] * q[1] - q[0] * p[1];
    }, 0);
    const reflex = pts.map((p, k) => {
      const a = pts[(k - 1 + n) % n];
      const c = pts[(k + 1) % n];
      const cr = (p[0] - a[0]) * (c[1] - p[1]) - (p[1] - a[1]) * (c[0] - p[0]);
      return Math.sign(cr) !== Math.sign(area2) && Math.abs(cr) > 0.01;
    });
    for (let j = 0; j < n; j++) {
      if (!reflex[j] || !reflex[(j + 1) % n]) continue;
      const c = pts[j], e = pts[(j + 1) % n];
      const ex = e[0] - c[0], ey = e[1] - c[1];
      const l2 = Math.hypot(ex, ey);
      if (l2 < 2) continue;
      let best = null;
      for (let i = 0; i < n; i++) {
        if (i === j || i === (j + 1) % n || (i + 1) % n === j) continue;
        const a = pts[i], b = pts[(i + 1) % n];
        const dx = b[0] - a[0], dy = b[1] - a[1];
        const len = Math.hypot(dx, dy);
        if (len < l2 || Math.abs(dx * ey - dy * ex) / (len * l2) > 0.03) continue;
        const nx = -dy / len, ny = dx / len;
        const d = Math.abs((c[0] - a[0]) * nx + (c[1] - a[1]) * ny);
        if (d < 0.5) continue;
        const m1 = [(c[0] + e[0]) / 2, (c[1] + e[1]) / 2];
        const t = Math.max(0, Math.min(1, ((m1[0] - a[0]) * dx + (m1[1] - a[1]) * dy) / (len * len)));
        const foot = [a[0] + dx * t, a[1] + dy * t];
        const mid = [(foot[0] + m1[0]) / 2, (foot[1] + m1[1]) / 2];
        if (!insidePoly(mid, pts)) continue;
        if (!best || d < best.d) best = { i, j, d, from: foot, to: m1, mid, nx, ny };
      }
      if (best) out.push(best);
    }
    return out;
  }
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const len = Math.hypot(dx, dy);
    if (len < 2) continue;
    for (let j = 0; j < n; j++) {
      if (j === i || j === (i + 1) % n || (j + 1) % n === i || seen.has(`${j}-${i}`)) continue;
      const c = pts[j], e = pts[(j + 1) % n];
      const ex = e[0] - c[0], ey = e[1] - c[1];
      const l2 = Math.hypot(ex, ey);
      if (l2 < 2) continue;
      const cross = Math.abs(dx * ey - dy * ex) / (len * l2);
      if (cross > 0.03) continue;
      const nx = -dy / len, ny = dx / len;
      const d = Math.abs((c[0] - a[0]) * nx + (c[1] - a[1]) * ny);
      if (d < 0.5) continue;
      const t1 = ((c[0] - a[0]) * dx + (c[1] - a[1]) * dy) / (len * len);
      const t2 = ((e[0] - a[0]) * dx + (e[1] - a[1]) * dy) / (len * len);
      if (Math.max(t1, t2) < 0 || Math.min(t1, t2) > 1) continue;
      const [longI, shortJ] = len >= l2 ? [i, j] : [j, i];
      const m1 = [(c[0] + e[0]) / 2, (c[1] + e[1]) / 2];
      const t = Math.max(0, Math.min(1, ((m1[0] - a[0]) * dx + (m1[1] - a[1]) * dy) / (len * len)));
      const foot = [a[0] + dx * t, a[1] + dy * t];
      out.push({ i: longI, j: shortJ, d, from: foot, to: m1, mid: [(foot[0] + m1[0]) / 2, (foot[1] + m1[1]) / 2], nx, ny });
      seen.add(`${i}-${j}`);
    }
  }
  return out;
};

// Move edge j (both of its vertices) along the normal of edge i so the depth between them is `target`.
const setDepthPts = (pts, i, j, target) => {
  const n = pts.length;
  const a = pts[i], b = pts[(i + 1) % n];
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  const nx = -(b[1] - a[1]) / len, ny = (b[0] - a[0]) / len;
  const c = pts[j];
  const signed = (c[0] - a[0]) * nx + (c[1] - a[1]) * ny;
  const dir = signed >= 0 ? 1 : -1;
  const delta = (Math.max(1, target) - Math.abs(signed)) * dir;
  const next = pts.map((p) => [...p]);
  for (const k of [j, (j + 1) % n]) {
    next[k] = [round2(next[k][0] + nx * delta), round2(next[k][1] + ny * delta)];
  }
  return next;
};

// Shortest distance from a point to a line segment (inches, in SVG space).
const distToSeg = ([px, py], [ax, ay], [bx, by]) => {
  const dx = bx - ax;
  const dy = by - ay;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
};

// Absolute SVG-space polygon for a rendered piece — matches the canvas group
// transform translate(x,y) rotate(rot, length/2, width/2), so hover geometry
// lines up with what's drawn.
const pieceScreenPoints = (p) => {
  const cx = (p.length_in || 0) / 2;
  const cy = (p.width_in || 0) / 2;
  const rad = ((Number(p.rot) || 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const ox = Number(p.x) || 0;
  const oy = Number(p.y) || 0;
  return (p.points || []).map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    return [ox + cx + dx * cos - dy * sin, oy + cy + dx * sin + dy * cos];
  });
};

const rotPt = ([x, y], [cx, cy], deg) => {
  const r = (deg * Math.PI) / 180;
  const dx = x - cx;
  const dy = y - cy;
  return [
    cx + dx * Math.cos(r) - dy * Math.sin(r),
    cy + dx * Math.sin(r) + dy * Math.cos(r),
  ];
};

const SIDE_LETTER = (i) => String.fromCharCode(65 + (i % 26));

// ---- L-shape as one parametric shape -------------------------------------------------
// A 6-vertex rectilinear piece is fully described by its overall width / height and the
// notch (the missing bounding-box corner) width / height. The vertices stay the stored
// truth (quotes, PDFs and the slab layout all read them); these helpers derive the four
// parameters from them and rebuild all six vertices whenever one dimension is edited, so
// the shape can never tear and every label (edgeLen on the vertices) stays consistent.
const L_EPS = 0.05;
const lShapeOf = (pts) => {
  if (!pts || pts.length !== 6) return null;
  for (let i = 0; i < 6; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % 6];
    if (Math.abs(a[0] - b[0]) > L_EPS && Math.abs(a[1] - b[1]) > L_EPS) return null;
  }
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  const inner = pts.findIndex(
    (p) => p[0] > x0 + L_EPS && p[0] < x1 - L_EPS && p[1] > y0 + L_EPS && p[1] < y1 - L_EPS,
  );
  if (inner < 0) return null;
  const R = pts[inner];
  const missing = [
    [x0, y0],
    [x1, y0],
    [x0, y1],
    [x1, y1],
  ].filter((c) => !pts.some((p) => Math.abs(p[0] - c[0]) < L_EPS && Math.abs(p[1] - c[1]) < L_EPS));
  if (missing.length !== 1) return null;
  const [cx, cy] = missing[0];
  return {
    x0,
    y0,
    x1,
    y1,
    W: x1 - x0,
    H: y1 - y0,
    R,
    notchRight: Math.abs(cx - x1) < L_EPS,
    notchBottom: Math.abs(cy - y1) < L_EPS,
    nw: Math.abs(R[0] - cx),
    nh: Math.abs(R[1] - cy),
  };
};

// Rebuild the six vertices from parameters, keeping the original vertex order (so side
// letters A–F keep pointing at the same physical sides).
const lShapePoints = (pts, L, { W, H, nw, nh }) => {
  const rx = L.notchRight ? L.x0 + W - nw : L.x0 + nw;
  const ry = L.notchBottom ? L.y0 + H - nh : L.y0 + nh;
  const mapX = (x) =>
    Math.abs(x - L.x0) < L_EPS ? L.x0 : Math.abs(x - L.x1) < L_EPS ? L.x0 + W : rx;
  const mapY = (y) =>
    Math.abs(y - L.y0) < L_EPS ? L.y0 : Math.abs(y - L.y1) < L_EPS ? L.y0 + H : ry;
  return pts.map(([x, y]) => [round2(mapX(x)), round2(mapY(y))]);
};

// Which parameter does side i of an L drive?  W / H (overall), nw / nh (the notch's own
// sides) or their complements (the short outer sides = overall − notch).
const lSideKind = (pts, L, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % 6];
  const horizontal = Math.abs(a[1] - b[1]) < L_EPS;
  const len = edgeLen(pts, i);
  if (horizontal) {
    if (Math.abs(len - L.W) < L_EPS) return "W";
    return Math.abs(a[1] - L.R[1]) < L_EPS ? "nw" : "cw";
  }
  if (Math.abs(len - L.H) < L_EPS) return "H";
  return Math.abs(a[0] - L.R[0]) < L_EPS ? "nh" : "ch";
};

// The shared leg depth (strip width) of an L: both legs equal this when the shape is a
// uniform-depth L. Uses the smaller of the two outer caps for safety on legacy shapes.
const lStripWidthOf = (pts) => {
  const L = lShapeOf(pts);
  if (!L) return 0;
  return Math.min(L.W - L.nw, L.H - L.nh);
};

// ---- Run (L / multi-leg strip) as one parametric shape -----------------------------------
// A drawn run is [wall-side path..., offset side reversed] with one constant depth per leg, so
// the path + per-leg depths can be recovered from the vertices. That lets a corner be swung to
// a new angle (legs keep their lengths and depths) without tearing the shape.
export const BEND_ANGLES = [90, 67.5, 45, 22.5];
const RUN_EPS = 0.08;
export const runOf = (pts) => {
  const n = pts?.length || 0;
  if (n < 6 || n % 2) return null;
  const m = n / 2;
  let best = null;
  for (let k = 0; k < n; k++) {
    for (const dir of [1, -1]) {
      const order = Array.from({ length: n }, (_, j) => (((k + dir * j) % n) + n) % n);
      const q = order.map((i) => pts[i]);
      const path = q.slice(0, m);
      const offs = q.slice(m).reverse();
      const depths = [];
      let ok = true;
      for (let j = 0; j < m - 1 && ok; j++) {
        const [ax, ay] = path[j];
        const [bx, by] = path[j + 1];
        const len = Math.hypot(bx - ax, by - ay);
        if (len < 0.5) {
          ok = false;
          break;
        }
        const ex = (bx - ax) / len;
        const ey = (by - ay) / len;
        const da = (offs[j][0] - ax) * -ey + (offs[j][1] - ay) * ex;
        const db = (offs[j + 1][0] - bx) * -ey + (offs[j + 1][1] - by) * ex;
        const fwd = (offs[j + 1][0] - offs[j][0]) * ex + (offs[j + 1][1] - offs[j][1]) * ey;
        ok =
          Math.abs(da - db) < RUN_EPS &&
          Math.abs(da) >= 0.5 &&
          fwd > 0.2 &&
          (!depths.length || Math.sign(da) === Math.sign(depths[0])) &&
          (j > 0 || Math.abs((offs[0][0] - ax) * ex + (offs[0][1] - ay) * ey) < RUN_EPS) &&
          (j < m - 2 || Math.abs((offs[m - 1][0] - bx) * ex + (offs[m - 1][1] - by) * ey) < RUN_EPS);
        depths.push(da);
      }
      if (!ok) continue;
      // prefer the wall (outer) side as the path so leg lengths stay stable across bends
      const inner = m > 2 && Math.sign(depths[0]) !== Math.sign(runTurnDeg(path, 1));
      const score = depths.reduce((s, d) => s + Math.abs(d), 0) + (inner ? 0.001 : 0);
      if (!best || score < best.score - 1e-6) best = { order, path, depths, score };
    }
  }
  return best;
};

// Signed turn (degrees off straight) of the run at path corner c; 90 = square L.
export const runTurnDeg = (path, c) => {
  const a1 = Math.atan2(path[c][1] - path[c - 1][1], path[c][0] - path[c - 1][0]);
  const a2 = Math.atan2(path[c + 1][1] - path[c][1], path[c + 1][0] - path[c][0]);
  let d = ((a2 - a1) * 180) / Math.PI;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
};

// Which run corner (1..legs-1) does polygon vertex v sit on? -1 for an end-cap vertex.
export const runCornerOf = (run, v) => {
  const j = run.order.indexOf(v);
  const n = run.order.length;
  const c = j < n / 2 ? j : n - 1 - j;
  return c >= 1 && c <= n / 2 - 2 ? c : -1;
};

// Swing the shorter side of a run about corner c so its legs meet `deg` off straight.
// Returns the new vertices in the original order plus the pivot vertex, or null if the
// offset side would fold over (legs too short for the depth at that angle).
export const swingRunCorner = (pts, c, deg) => {
  const run = runOf(pts);
  if (!run || c < 1 || c > run.path.length - 2) return null;
  const { order, path, depths } = run;
  const cur = runTurnDeg(path, c);
  const want = (cur < 0 ? -1 : 1) * Math.max(5, Math.min(170, Number(deg) || 90));
  if (Math.abs(want - cur) < 0.01) return { same: true };
  const legLen = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const oldOffs = order.slice(path.length).map((i) => pts[i]).reverse();
  // centreline leg lengths, so "shorter leg" is the same whichever side runOf picked as the path
  const mid = (j) => (legLen(path[j], path[j + 1]) + legLen(oldOffs[j], oldOffs[j + 1])) / 2;
  const sum = (from, to) => path.slice(from, to).reduce((s, _, k) => s + mid(from + k), 0);
  const tail = sum(c, path.length - 1) <= sum(0, c);
  const phi = tail ? want - cur : cur - want;
  const moved = (j) => (tail ? j > c : j < c);
  const next = path.map((p, j) => (moved(j) ? rotPt(p, path[c], phi) : p));
  const poly = [...next, ...offsetChain(next, depths).reverse()];
  const offs = poly.slice(path.length).reverse();
  for (let j = 0; j < next.length - 1; j++) {
    const ex = next[j + 1][0] - next[j][0];
    const ey = next[j + 1][1] - next[j][1];
    if ((offs[j + 1][0] - offs[j][0]) * ex + (offs[j + 1][1] - offs[j][1]) * ey <= 0) return null;
  }
  const points = pts.map(() => null);
  order.forEach((i, j) => {
    points[i] = [round2(poly[j][0]), round2(poly[j][1])];
  });
  const movedZones = path
    .slice(0, -1)
    .map((_, j) => ((tail ? j >= c : j < c) ? [path[j], path[j + 1], oldOffs[j + 1], oldOffs[j]] : null))
    .filter(Boolean);
  return { points, pivot: order[c], pivotPt: path[c], phi, movedZones };
};

// Line-art icon of a run turning `deg` off straight (90 = square L).
const BendIcon = ({ deg, className = "h-4 w-4" }) => {
  const r = (deg * Math.PI) / 180;
  const x = (9 - 7.5 * Math.cos(r)).toFixed(2);
  const y = (8 + 7.5 * Math.sin(r)).toFixed(2);
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <path
        d={`M${x} ${y} L9 8 L19 8`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="8" r="1.1" fill="currentColor" />
    </svg>
  );
};

const AnglePicker = ({ value, onPick, idBase, className = "grid-cols-2" }) => (
  <div className={`grid gap-1.5 ${className}`} data-testid={`${idBase}-grid`}>
    {BEND_ANGLES.map((a) => {
      const on = Math.abs(Number(value) - a) < 0.26;
      return (
        <button
          key={a}
          type="button"
          data-testid={`${idBase}-${a}`}
          aria-pressed={on}
          onClick={() => onPick(a)}
          className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-bold transition-colors duration-150 ${
            on
              ? "border-indigo-400 bg-indigo-500 text-white"
              : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          }`}
        >
          <BendIcon deg={a} className={`h-4 w-4 shrink-0 ${on ? "text-white" : "text-indigo-300"}`} />
          {a}°
        </button>
      );
    })}
  </div>
);

// ---- U-shape (horseshoe) as one parametric shape -----------------------------------------
// 8 rectilinear vertices, all four bounding-box corners present, two reflex vertices that
// share one coordinate. Parameters: W (along the span), H (along the returns), d (depth of
// the closed run), rwA / rwB (the two return widths). `u` is the span axis, `v` the other.
const uShapeOf = (pts) => {
  if (!pts || pts.length !== 8) return null;
  for (let i = 0; i < 8; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % 8];
    if (Math.abs(a[0] - b[0]) > L_EPS && Math.abs(a[1] - b[1]) > L_EPS) return null;
  }
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  // the closed run's outer side is the one edge spanning the full bounding box
  let base = -1;
  let ui = -1;
  for (let i = 0; i < 8 && base < 0; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % 8];
    if (Math.abs(a[1] - b[1]) < L_EPS && Math.abs(Math.abs(a[0] - b[0]) - (x1 - x0)) < L_EPS) {
      base = i;
      ui = 0;
    } else if (Math.abs(a[0] - b[0]) < L_EPS && Math.abs(Math.abs(a[1] - b[1]) - (y1 - y0)) < L_EPS) {
      base = i;
      ui = 1;
    }
  }
  if (base < 0) return null;
  const vi = 1 - ui;
  const U0 = ui === 0 ? x0 : y0;
  const U1 = ui === 0 ? x1 : y1;
  const V0 = ui === 0 ? y0 : x0;
  const V1 = ui === 0 ? y1 : x1;
  const Vbase = pts[base][vi];
  const openAtV1 = Math.abs(Vbase - V0) < L_EPS;
  if (!openAtV1 && Math.abs(Vbase - V1) > L_EPS) return null;
  // the inner span: the other edge along u whose ends sit strictly inside the u-range
  let span = -1;
  for (let i = 0; i < 8; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % 8];
    if (i === base || Math.abs(a[vi] - b[vi]) > L_EPS) continue;
    if (a[ui] > U0 + L_EPS && a[ui] < U1 - L_EPS && b[ui] > U0 + L_EPS && b[ui] < U1 - L_EPS) span = i;
  }
  if (span < 0) return null;
  const s0 = pts[span];
  const s1 = pts[(span + 1) % 8];
  const [Ra, Rb] = s0[ui] < s1[ui] ? [s0, s1] : [s1, s0];
  const Rv = Ra[vi];
  const d = Math.abs(Rv - Vbase);
  if (d < 0.5) return null;
  // return lengths: from the base to the far end of each outer return side
  const endOf = (u) => {
    const vs = pts.filter((p) => Math.abs(p[ui] - u) < L_EPS).map((p) => p[vi]);
    return Math.max(...vs.map((v) => Math.abs(v - Vbase)));
  };
  const hA = endOf(U0);
  const hB = endOf(U1);
  if (hA <= d + 0.5 || hB <= d + 0.5) return null;
  // every vertex must sit on one of the four u-lines and one of the known v-lines
  const uOk = (p) => [U0, Ra[ui], Rb[ui], U1].some((u) => Math.abs(p[ui] - u) < L_EPS);
  if (!pts.every(uOk)) return null;
  return {
    ui,
    vi,
    U0,
    U1,
    V0,
    V1,
    W: U1 - U0,
    H: Math.max(hA, hB),
    hA,
    hB,
    Ra,
    Rb,
    Rv,
    Vbase,
    openAtV1,
    d,
    rwA: Ra[ui] - U0,
    rwB: U1 - Rb[ui],
  };
};

const uShapePoints = (pts, U, { W, H, d, rwA, rwB, hA, hB }) => {
  const sameH = Math.abs(H - U.H) < L_EPS;
  const la = hA ?? (sameH ? U.hA : H);
  const lb = hB ?? (sameH ? U.hB : H);
  const dir = U.openAtV1 ? 1 : -1;
  const mapU = (u) =>
    Math.abs(u - U.U0) < L_EPS
      ? U.U0
      : Math.abs(u - U.U1) < L_EPS
        ? U.U0 + W
        : Math.abs(u - U.Ra[U.ui]) < L_EPS
          ? U.U0 + rwA
          : U.U0 + W - rwB;
  const mapV = (p) => {
    const v = p[U.vi];
    if (Math.abs(v - U.Vbase) < L_EPS) return U.Vbase;
    if (Math.abs(v - U.Rv) < L_EPS) return U.Vbase + dir * d;
    const onA = p[U.ui] < (U.Ra[U.ui] + U.Rb[U.ui]) / 2;
    return U.Vbase + dir * (onA ? la : lb);
  };
  return pts.map((p) => {
    const out = [0, 0];
    out[U.ui] = round2(mapU(p[U.ui]));
    out[U.vi] = round2(mapV(p));
    return out;
  });
};

// W (overall span), H (overall return length), span (inner opening), rwA / rwB (return
// widths, on the open side), inner (a return's inside length = H − d).
const uSideKind = (pts, U, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % 8];
  const alongU = Math.abs(a[U.vi] - b[U.vi]) < L_EPS;
  const len = edgeLen(pts, i);
  if (alongU) {
    if (Math.abs(len - U.W) < L_EPS) return "W";
    if (Math.abs(a[U.vi] - U.Rv) < L_EPS) return "span";
    return Math.abs(Math.min(a[U.ui], b[U.ui]) - U.U0) < L_EPS ? "rwA" : "rwB";
  }
  const onA = Math.abs(a[U.ui] - U.U0) < L_EPS || Math.abs(a[U.ui] - U.Ra[U.ui]) < L_EPS;
  const outer = Math.abs(a[U.ui] - U.U0) < L_EPS || Math.abs(a[U.ui] - U.U1) < L_EPS;
  if (outer) return onA ? "hA" : "hB";
  return onA ? "innerA" : "innerB";
};

// ---- Angled "U-cap" band ------------------------------------------------------------------
// A non-rectilinear shape (e.g. a splayed / trapezoidal U) whose back run A and inner run E
// stay parallel while the connecting legs are angled. Lets a cap-length edit grow both caps
// evenly (every leg stays rigid) and exposes the perpendicular gap (height) between the caps.
const capMid = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
};
const capUnit = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  return [(b[0] - a[0]) / l, (b[1] - a[1]) / l];
};
// The parallel edge directly facing edge i across the band (largest projection overlap,
// smallest sideways offset), or -1 when none qualifies.
const uCapPartner = (pts, i) => {
  const n = pts.length;
  if (n < 6) return -1;
  const u = capUnit(pts, i);
  const mi = capMid(pts, i);
  const proj = (p) => p[0] * u[0] + p[1] * u[1];
  const ia = [proj(pts[i]), proj(pts[(i + 1) % n])].sort((a, b) => a - b);
  let best = -1;
  let bestScore = -Infinity;
  for (let j = 0; j < n; j++) {
    if (j === i || j === (i + 1) % n || j === (i - 1 + n) % n) continue;
    const v = capUnit(pts, j);
    if (Math.abs(u[0] * v[1] - u[1] * v[0]) > 0.03) continue; // not parallel
    const mj = capMid(pts, j);
    const dmx = mj[0] - mi[0];
    const dmy = mj[1] - mi[1];
    if (Math.abs(-dmx * u[1] + dmy * u[0]) < 0.5) continue; // the same line, not a facing cap
    const lateral = Math.abs(dmx * u[0] + dmy * u[1]);
    const ja = [proj(pts[j]), proj(pts[(j + 1) % n])].sort((a, b) => a - b);
    const overlap = Math.max(0, Math.min(ia[1], ja[1]) - Math.max(ia[0], ja[0]));
    const score = overlap - lateral * 0.5;
    if (overlap > 1 && score > bestScore) {
      bestScore = score;
      best = j;
    }
  }
  return best;
};
// Vertex indices on each side of the cap pair (edge i and its partner p).
const uCapChains = (n, i, p) => {
  const legX = [];
  const legY = [];
  for (let k = (i + 1) % n; ; k = (k + 1) % n) {
    legX.push(k);
    if (k === p) break;
  }
  for (let k = (p + 1) % n; ; k = (k + 1) % n) {
    legY.push(k);
    if (k === i) break;
  }
  return { legX, legY };
};
// Detect the cap pair: a mutual facing parallel edge pair whose two leg-chains each bend.
// Returns { a, b } edge indices (a = outer / longer cap, b = inner cap) or null.
const uCapOf = (pts) => {
  const n = pts?.length || 0;
  if (n < 8) return null;
  if (lShapeOf(pts) || uShapeOf(pts)) return null;
  for (let i = 0; i < n; i++) {
    const p = uCapPartner(pts, i);
    if (p < 0 || uCapPartner(pts, p) !== i) continue;
    const { legX, legY } = uCapChains(n, i, p);
    if (legX.length < 3 || legY.length < 3) continue; // each leg needs an intermediate bend
    const outer = edgeLen(pts, i) >= edgeLen(pts, p) ? i : p;
    const inner = outer === i ? p : i;
    return { a: outer, b: inner };
  }
  return null;
};
// Perpendicular gap (height) between the two caps, with a unit normal pointing outer-ward.
const uCapGap = (pts, cap) => {
  const u = capUnit(pts, cap.a);
  let nx = -u[1];
  let ny = u[0];
  const mo = capMid(pts, cap.a);
  const mi = capMid(pts, cap.b);
  if ((mo[0] - mi[0]) * nx + (mo[1] - mi[1]) * ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  return { gap: Math.abs((mo[0] - mi[0]) * nx + (mo[1] - mi[1]) * ny), nx, ny };
};
// The angled legs of a U-band: edges (not caps, not bottom returns) with exactly one endpoint
// on a cap. Four on a classic U (e.g. B, D, F, H).
const uCapLegs = (pts, cap) => {
  const n = pts.length;
  const corners = new Set([cap.a, (cap.a + 1) % n, cap.b, (cap.b + 1) % n]);
  const legs = [];
  for (let i = 0; i < n; i++) {
    if (i === cap.a || i === cap.b) continue;
    if (corners.has(i) !== corners.has((i + 1) % n)) legs.push(i);
  }
  return legs;
};
// For a leg edge i: its cap-corner vertex (the hinge), the far (bottom) vertex, and the
// intermediate vertices of that side's chain that move with the leg.
const uCapLegInfo = (pts, cap, i) => {
  const n = pts.length;
  const { legX, legY } = uCapChains(n, cap.a, cap.b);
  const inChain = (ch) => ch.includes(i) && ch.includes((i + 1) % n);
  const chain = inChain(legX) ? legX : inChain(legY) ? legY : null;
  if (!chain) return null;
  const ends = [chain[0], chain[chain.length - 1]];
  const e0 = i;
  const e1 = (i + 1) % n;
  const cv = ends.includes(e0) ? e0 : ends.includes(e1) ? e1 : -1;
  if (cv < 0) return null;
  const fv = cv === e0 ? e1 : e0;
  if (ends.includes(fv)) return null;
  return { cv, fv, inter: chain.slice(1, -1) };
};
// The other endpoint of whichever cap edge the hinge vertex cv sits on.
const uCapOther = (n, cap, cv) =>
  cv === cap.a
    ? (cap.a + 1) % n
    : cv === (cap.a + 1) % n
      ? cap.a
      : cv === cap.b
        ? (cap.b + 1) % n
        : cap.b;
// Current interior angle (degrees) at the cap corner between the cap edge and leg edge i.
const uCapLegAngle = (pts, cap, i) => {
  const info = uCapLegInfo(pts, cap, i);
  if (!info) return 0;
  const n = pts.length;
  const { cv, fv } = info;
  const p0 = pts[cv];
  const co = pts[uCapOther(n, cap, cv)];
  const cvec = [co[0] - p0[0], co[1] - p0[1]];
  const lvec = [pts[fv][0] - p0[0], pts[fv][1] - p0[1]];
  const cross = cvec[0] * lvec[1] - cvec[1] * lvec[0];
  const dot = cvec[0] * lvec[0] + cvec[1] * lvec[1];
  return Math.abs((Math.atan2(cross, dot) * 180) / Math.PI);
};
// The mirror leg on the opposite chain that attaches to the SAME cap (e.g. B<->H, D<->F), or -1.
const uCapLegMirror = (pts, cap, i) => {
  const n = pts.length;
  const info = uCapLegInfo(pts, cap, i);
  if (!info) return -1;
  const onCapA = info.cv === cap.a || info.cv === (cap.a + 1) % n;
  for (const j of uCapLegs(pts, cap)) {
    if (j === i) continue;
    const jf = uCapLegInfo(pts, cap, j);
    if (!jf) continue;
    const jOnCapA = jf.cv === cap.a || jf.cv === (cap.a + 1) % n;
    if (jOnCapA === onCapA) return j;
  }
  return -1;
};

// The shared strip depth of a U: all three runs equal this on a uniform-depth (galley) U.
const uStripWidthOf = (pts) => {
  const U = uShapeOf(pts);
  if (!U) return 0;
  return Math.min(U.rwA, U.rwB, U.d);
};

// The shared strip depth for whichever parametric shape this is (L legs, or U returns + base).
const stripWidthOf = (pts) =>
  lShapeOf(pts) ? lStripWidthOf(pts) : uShapeOf(pts) ? uStripWidthOf(pts) : 0;

// 25.5 -> 25 1/2" (nearest 1/16 or 1/8 per Drawing settings) so shop drawings read like a tape
const makeTag = (general) => (n) =>
  formatInches(n, {
    metric: (general?.measurement_system || "imperial") === "metric",
    fractional: general?.fractional_inches !== false,
    precision: Number(general?.fraction_precision) === 8 ? 8 : 16,
  });

// offset dimension line for a side, pushed outside the outline
const dimGeom = (pts, i, dist) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  const [nx, ny] = outwardNormal(pts, i);
  const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  return {
    a,
    b,
    mid,
    oa: [a[0] + nx * dist, a[1] + ny * dist],
    ob: [b[0] + nx * dist, b[1] + ny * dist],
    om: [mid[0] + nx * dist, mid[1] + ny * dist],
  };
};

// a w×h cut-out at (x,y) sits fully on the stone of this polygon
const cutFits = (pts, x, y, w, h) =>
  pts.length < 3 ||
  [[x, y], [x + w, y], [x, y + h], [x + w, y + h], [x + w / 2, y + h / 2]].every((q) =>
    insidePoly([q[0] - 0.05 * Math.sign(q[0] - x - w / 2), q[1] - 0.05 * Math.sign(q[1] - y - h / 2)], pts),
  );

// a cut-out's on-stone box once its angle is applied (it spins about its centre):
// offset from (x,y) and the rotated width/height
const cutFootprint = (c) => {
  const w = Number(c.w) || 0;
  const h = Number(c.h) || 0;
  const a = (((Number(c.rot) || 0) % 360) * Math.PI) / 180;
  const W = Math.abs(w * Math.cos(a)) + Math.abs(h * Math.sin(a));
  const H = Math.abs(w * Math.sin(a)) + Math.abs(h * Math.cos(a));
  return { dx: (w - W) / 2, dy: (h - H) / 2, W, H };
};

// Slide a cut-out toward the drag target, keeping it on the stone so it can travel
// anywhere on the piece — including around an L/U inner corner or across a notch.
// If the exact target doesn't fully fit, snap to the NEAREST fully-on-stone spot to
// the cursor (path-independent, so it never gets pinned at the run/leg junction).
const slideCut = (pts, fromX, fromY, toX, toY, w, h) => {
  if (cutFits(pts, toX, toY, w, h)) return [toX, toY];
  const R = Math.max(w, h) * 1.5 + 12; // search radius (inches)
  // coarse rings keep this to a few thousand checks so dragging stays smooth
  const stepI = Math.max(0.5, R / 40);
  for (let r = stepI; r <= R; r += stepI) {
    const n = Math.max(12, Math.round((2 * Math.PI * r) / stepI));
    let best = null;
    let bestD = Infinity;
    for (let a = 0; a < n; a++) {
      const ang = (a / n) * 2 * Math.PI;
      const px = Math.round((toX + Math.cos(ang) * r) * 8) / 8;
      const py = Math.round((toY + Math.sin(ang) * r) * 8) / 8;
      if (cutFits(pts, px, py, w, h)) {
        const d = (px - toX) ** 2 + (py - toY) ** 2;
        if (d < bestD) {
          bestD = d;
          best = [px, py];
        }
      }
    }
    if (best) return best; // nearest ring with a valid spot wins
  }
  return [fromX, fromY];
};

// true (rotated) rectangle corners of a cut-out whose on-stone AABB top-left is (fx,fy)
const cutCornersR = (fx, fy, w, h, rotDeg) => {
  const { W, H } = cutFootprint({ w, h, rot: rotDeg });
  const cx = fx + W / 2;
  const cy = fy + H / 2;
  const box = [
    [cx - w / 2, cy - h / 2],
    [cx + w / 2, cy - h / 2],
    [cx + w / 2, cy + h / 2],
    [cx - w / 2, cy + h / 2],
  ];
  return { cx, cy, corners: box.map((p) => rotPt(p, [cx, cy], rotDeg || 0)) };
};

// a (possibly rotated) cut-out sits fully on the stone — tests the real rotated
// rectangle, not its bounding box, so a spun sink can hug a 45°/angled edge.
const cutFitsR = (pts, fx, fy, w, h, rotDeg) => {
  if (!pts || pts.length < 3) return true;
  const { cx, cy, corners } = cutCornersR(fx, fy, w, h, rotDeg);
  const nudge = (q) => {
    const dx = cx - q[0];
    const dy = cy - q[1];
    const d = Math.hypot(dx, dy) || 1;
    return [q[0] + (dx / d) * 0.05, q[1] + (dy / d) * 0.05];
  };
  return [...corners, [cx, cy]].every((q) => insidePoly(nudge(q), pts));
};

// rotation-aware slide: keep a spun cut-out on the stone while it follows the cursor
const slideCutR = (pts, fromX, fromY, toX, toY, w, h, rotDeg) => {
  if (cutFitsR(pts, toX, toY, w, h, rotDeg)) return [toX, toY];
  const { W, H } = cutFootprint({ w, h, rot: rotDeg });
  const R = Math.max(W, H) * 1.5 + 12;
  const stepI = Math.max(0.5, R / 40);
  for (let r = stepI; r <= R; r += stepI) {
    const n = Math.max(12, Math.round((2 * Math.PI * r) / stepI));
    let best = null;
    let bestD = Infinity;
    for (let a = 0; a < n; a++) {
      const ang = (a / n) * 2 * Math.PI;
      const px = Math.round((toX + Math.cos(ang) * r) * 8) / 8;
      const py = Math.round((toY + Math.sin(ang) * r) * 8) / 8;
      if (cutFitsR(pts, px, py, w, h, rotDeg)) {
        const d = (px - toX) ** 2 + (py - toY) ** 2;
        if (d < bestD) {
          bestD = d;
          best = [px, py];
        }
      }
    }
    if (best) return best;
  }
  return [fromX, fromY];
};

// smallest gap (inches) from a cut-out to any piece edge or seam line — for the 3" clearance warning
const cutClearance = (piece, c, seamItems = []) => {
  const pts = piece.points || [];
  const corners = [[c.x, c.y], [c.x + c.w, c.y], [c.x + c.w, c.y + c.h], [c.x, c.y + c.h]];
  const mids = [[c.x + c.w / 2, c.y], [c.x + c.w, c.y + c.h / 2], [c.x + c.w / 2, c.y + c.h], [c.x, c.y + c.h / 2]];
  let best = Infinity;
  let what = "edge";
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const seam = !!(piece.edges || [])[i]?.seam;
    for (const q of [...corners, ...mids]) {
      const d = distToSeg(q, a, b);
      if (d < best) { best = d; what = seam ? "seam" : "edge"; }
    }
  }
  seamItems.forEach((sm) => {
    const d = sm.dir === "v" ? Math.min(Math.abs(sm.at - c.x), Math.abs(sm.at - (c.x + c.w))) : Math.min(Math.abs(sm.at - c.y), Math.abs(sm.at - (c.y + c.h)));
    const crosses = sm.dir === "v" ? sm.at > c.x && sm.at < c.x + c.w : sm.at > c.y && sm.at < c.y + c.h;
    const dd = crosses ? 0 : d;
    if (dd < best) { best = dd; what = "seam"; }
  });
  return { gap: round2(best), what };
};

const insidePoly = ([x, y], pts) => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
};

const outwardNormal = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  let nx = -(b[1] - a[1]) / len;
  let ny = (b[0] - a[0]) / len;
  const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  if (insidePoly([mid[0] + nx * 0.5, mid[1] + ny * 0.5], pts)) {
    nx = -nx;
    ny = -ny;
  }
  return [nx, ny];
};

// waterfall / mitred panels: a vertical panel that drops off the side
const DROP_DEFAULT = { waterfall: 34, mitre: 4 };

const dropKind = (edge, style) => {
  const shape = (style?.shape || "").toLowerCase();
  const name = (edge?.profile || "").toLowerCase();
  if (shape === "waterfall" || name.includes("waterfall")) return "waterfall";
  if (
    edge?.miter ||
    shape === "mitered" ||
    name.includes("miter") ||
    name.includes("mitre")
  )
    return "mitre";
  return null;
};

const miterLine = (pts, i, inset = 1.2) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  const [nx, ny] = outwardNormal(pts, i);
  return {
    x1: a[0] - nx * inset,
    y1: a[1] - ny * inset,
    x2: b[0] - nx * inset,
    y2: b[1] - ny * inset,
  };
};

const SEAM = "#27F54D";
const round2 = (n) => Math.round(n * 10000) / 10000; // keeps 1/16" (0.0625) exact

const rebuild = (piece, pts, edges) => {
  const minx = Math.min(...pts.map((p) => p[0]));
  const miny = Math.min(...pts.map((p) => p[1]));
  const shifted = pts.map(([x, y]) => [round2(x - minx), round2(y - miny)]);
  return {
    ...normalizePiece({ ...piece, points: shifted, edges }),
    edges: edges.map((e, n) => ({
      ...e,
      length_in: round2(edgeLen(shifted, n)),
    })),
  };
};
// True circular arc: chord w, rise d → (along, rise) at fraction t of the chord.
const arcPoint = (w, d, t) => {
  const half = w / 2;
  const R = (half * half) / (2 * d) + d / 2;
  const theta = d <= half ? Math.asin(Math.min(1, half / R)) : Math.PI - Math.asin(Math.min(1, half / R));
  const phi = -theta + 2 * theta * t;
  return [half + R * Math.sin(phi), R * Math.cos(phi) - (R - d)];
};



const CURVE_OPTIONS = [
  {
    kind: "bump_out",
    label: "Bump-Out",
    d: "M8,36 L8,20 L20,20 L24,10 L36,10 L40,20 L52,20 L52,36 Z",
    defaults: { d: 3, w: 30 },
  },
  {
    kind: "bump_in",
    label: "Bump-In",
    d: "M8,36 L8,12 L20,12 L24,22 L36,22 L40,12 L52,12 L52,36 Z",
    defaults: { d: 3, w: 30 },
  },
  {
    kind: "bump_out_curve",
    label: "Bump-Out Curve",
    d: "M8,36 L8,20 L20,20 Q30,4 40,20 L52,20 L52,36 Z",
    defaults: { d: 3, w: 15 },
  },
  {
    kind: "bump_in_curve",
    label: "Bump-In Curve",
    d: "M8,36 L8,12 L20,12 Q30,28 40,12 L52,12 L52,36 Z",
    defaults: { d: 3, w: 15 },
  },
  {
    kind: "full_curve",
    label: "Full Curve",
    d: "M8,36 L8,20 Q30,0 52,20 L52,36 Z",
    defaults: { d: 4, w: 30 },
  },
  { kind: "none", label: "None", d: "M8,36 L8,14 L52,14 L52,36 Z" },
];

// Best-guess linked service for a freshly placed bump ("Bump Out (Curve)", "Full Curve"...).
const matchBumpService = (kind, services) => {
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
  const label = norm(bumpTitle(kind));
  return (services || []).find((s) => norm(s.name) === label) || null;
};

export const edgeShape = (piece, i, kind, width, depth, offset) => {
  const pts = piece.points.map((p) => [...p]);
  const n = pts.length;
  const a = pts[i];
  const b = pts[(i + 1) % n];
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  const ux = (b[0] - a[0]) / len;
  const uy = (b[1] - a[1]) / len;

  if (kind === "none") {
    const onLine = (p) =>
      Math.abs((p[0] - a[0]) * uy - (p[1] - a[1]) * ux) < 0.2;
    const remove = new Set();
    let k = (i + 2) % n;
    let guard = 0;
    while (guard++ < n - 2 && !onLine(pts[k])) {
      remove.add(k);
      k = (k + 1) % n;
    }
    if (!remove.size) return null;
    const keepIdx = pts.map((_, idx) => idx).filter((idx) => !remove.has(idx));
    const rawPts = keepIdx.map((idx) => pts[idx]);
    const rawEdges = keepIdx.map(
      (idx) => piece.edges?.[idx] || { profile: "Eased", seam: false },
    );
    const nextPts = [];
    const edges = [];
    rawPts.forEach((p, idx) => {
      const prev = rawPts[(idx - 1 + rawPts.length) % rawPts.length];
      const nxt = rawPts[(idx + 1) % rawPts.length];
      const cross =
        (p[0] - prev[0]) * (nxt[1] - prev[1]) -
        (p[1] - prev[1]) * (nxt[0] - prev[0]);
      if (Math.abs(cross) > 0.01) {
        nextPts.push(p);
        edges.push(rawEdges[idx]);
      }
    });
    if (nextPts.length < 3) return null;
    return rebuild(piece, nextPts, edges);
  }

  let [nx, ny] = outwardNormal(pts, i);
  if (kind === "bump_in" || kind === "bump_in_curve") {
    nx = -nx;
    ny = -ny;
  }
  const d = Math.max(Number(depth) || 1, 1);
  const full = kind === "full_curve";
  const w = full ? len : Math.max(Math.min(Number(width) || 1, len - 1), 1);
  // distance from the first corner of this side; blank keeps it centred
  const wanted = Number(offset);
  const s = full
    ? 0
    : Number.isFinite(wanted) && String(offset).trim() !== ""
      ? Math.max(0, Math.min(wanted, len - w))
      : (len - w) / 2;
  const P = (dist, off) => [
    round2(a[0] + ux * dist + nx * off),
    round2(a[1] + uy * dist + ny * off),
  ];

  let insert = [];
  if (kind === "bump_out" || kind === "bump_in") {
    insert = [P(s, 0), P(s, d), P(s + w, d), P(s + w, 0)];
  } else {
    const steps = 10;
    if (!full) insert.push(P(s, 0));
    const from = full ? 1 : 0;
    const to = full ? steps - 1 : steps;
    for (let k = from; k <= to; k++) {
      const t = k / steps;
      const [ax, ay] = arcPoint(w, d, t);
      insert.push(P(s + ax, ay));
    }
    if (!full) insert.push(P(s + w, 0));
  }

  const nextPts = [...pts.slice(0, i + 1), ...insert, ...pts.slice(i + 1)];
  const e = piece.edges?.[i] || { profile: "Eased", seam: false };
  const edges = [
    ...(piece.edges || []).slice(0, i),
    ...Array.from({ length: insert.length + 1 }, () => ({ ...e })),
    ...(piece.edges || []).slice(i + 1),
  ];
  return rebuild(piece, nextPts, edges);
};

export const BUMP_ANGLES = [90, 75, 60, 45, 30];
export const BUMP_KINDS = ["bump_out", "bump_in", "bump_out_curve", "bump_in_curve", "full_curve"];
export const bumpTitle = (kind) =>
  CURVE_OPTIONS.find((o) => o.kind === kind)?.label || "Bump";

export const bumpEdgeIndexes = (piece, id) =>
  (piece.edges || [])
    .map((e, k) => (e.bump === id ? k : -1))
    .filter((k) => k >= 0);

export const bumpTopEdge = (piece, id) => {
  const idx = bumpEdgeIndexes(piece, id);
  return idx.length ? idx[Math.floor(idx.length / 2)] : null;
};

// For a CURVED bump the arc is drawn with many tessellation points. We keep the drawing
// smooth but hide the middle handles/labels — leaving just the two junctions + apex (3 dots),
// and a single edge label at the apex. Straight bumps are untouched.
export const arcHandleHidden = (piece) => {
  const edges = piece?.edges || [];
  const n = (piece?.points || []).length;
  const hide = new Set();
  const labelHide = new Set();
  const apex = new Map();
  const curveIds = new Set(
    (piece?.bumps || [])
      .filter((b) => String(b.kind || "").includes("curve"))
      .map((b) => b.id),
  );
  if (!curveIds.size) return { hide, labelHide, apex };
  let k = 0;
  while (k < n) {
    const id = edges[k]?.bump;
    if (!id || !curveIds.has(id)) {
      k++;
      continue;
    }
    const run = [];
    let j = k;
    while (j < n && edges[j]?.bump === id && curveIds.has(id)) {
      run.push(j);
      j++;
    }
    // interior arc vertices sit between consecutive bump edges: run[1..end]
    const interior = run.slice(1);
    const apexV = interior.length
      ? interior[Math.floor(interior.length / 2)]
      : null;
    if (apexV != null) apex.set(apexV, id);
    interior.forEach((vi) => {
      if (vi !== apexV) hide.add(vi);
    });
    const midEdge = run[Math.floor(run.length / 2)];
    run.forEach((ei) => {
      if (ei !== midEdge) labelHide.add(ei);
    });
    k = j;
  }
  return { hide, labelHide, apex };
};

// Curved bumps are stored as many short straight edges; on screen we draw them as one
// smooth curve (Catmull-Rom → cubic Bézier per edge) so no facet lines show.
const curveBumpIds = (piece) =>
  new Set(
    (piece?.bumps || [])
      .filter((b) => String(b.kind || "").includes("curve"))
      .map((b) => b.id),
  );
export const isArcEdge = (piece, i) => {
  const id = piece?.edges?.[i]?.bump;
  return !!id && curveBumpIds(piece).has(id);
};
// Cubic for edge i: returns [c1, c2] control points (piece-local).
export const arcControls = (piece, i) => {
  const pts = piece.points;
  const n = pts.length;
  const id = piece.edges[i].bump;
  const p1 = pts[i];
  const p2 = pts[(i + 1) % n];
  const prevSame = piece.edges[(i - 1 + n) % n]?.bump === id;
  const nextSame = piece.edges[(i + 1) % n]?.bump === id;
  const p0 = prevSame ? pts[(i - 1 + n) % n] : p1;
  const p3 = nextSame ? pts[(i + 2) % n] : p2;
  return [
    [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6],
    [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6],
  ];
};
export const edgePathD = (piece, i) => {
  const pts = piece.points;
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  if (!isArcEdge(piece, i)) return `M${a[0]},${a[1]} L${b[0]},${b[1]}`;
  const [c1, c2] = arcControls(piece, i);
  return `M${a[0]},${a[1]} C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${b[0]},${b[1]}`;
};
export const piecePathD = (piece) => {
  if (piece.circle || piece.oval) {
    const rx = (piece.length_in || 1) / 2;
    const ry = (piece.width_in || 1) / 2;
    return `M0,${ry} A${rx},${ry} 0 1 0 ${rx * 2},${ry} A${rx},${ry} 0 1 0 0,${ry} Z`;
  }
  const pts = piece.points;
  const n = pts.length;
  if (!n) return "";
  const arcs = curveBumpIds(piece);
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < n; i++) {
    const b = pts[(i + 1) % n];
    const id = piece.edges?.[i]?.bump;
    if (id && arcs.has(id)) {
      const [c1, c2] = arcControls(piece, i);
      d += ` C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${b[0]},${b[1]}`;
    } else d += ` L${b[0]},${b[1]}`;
  }
  return `${d} Z`;
};


// Puts a bump on side i and tags its edges (bump / bump_base) so it can be edited later.
export const applyBump = (piece, i, bump) => {
  const pts = piece.points.map((p) => [...p]);
  const n = pts.length;
  const a = pts[i];
  const b = pts[(i + 1) % n];
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  const ux = (b[0] - a[0]) / len;
  const uy = (b[1] - a[1]) / len;
  let [nx, ny] = outwardNormal(pts, i);
  if (bump.kind === "bump_in" || bump.kind === "bump_in_curve") {
    nx = -nx;
    ny = -ny;
  }
  const full = bump.kind === "full_curve";
  const d = Math.max(Number(bump.d) || 1, 0.5);
  const w = full ? len : Math.max(Math.min(Number(bump.w) || 1, len - 1), 1);
  const pos = full ? "center" : bump.pos || "center";
  const wanted = Number(bump.offset);
  const s = full
    ? 0
    : pos === "left"
      ? 0
      : pos === "right"
        ? len - w
        : pos === "custom" && Number.isFinite(wanted) && String(bump.offset).trim() !== ""
          ? Math.max(0, Math.min(wanted, len - w))
          : (len - w) / 2;
  const P = (dist, off) => [
    round2(a[0] + ux * dist + nx * off),
    round2(a[1] + uy * dist + ny * off),
  ];
  const insert = [];
  if (s > 0.01) insert.push(P(s, 0));
  const angleL = Number(bump.angle_l) || 90;
  const angleR = Number(bump.angle_r) || 90;
  if (bump.kind === "bump_out" || bump.kind === "bump_in") {
    const run = (ang) => {
      const t = Math.tan((ang * Math.PI) / 180);
      return Math.abs(t) < 1e-6 ? 0 : Math.max(0, d / t);
    };
    const maxRun = Math.max(0, (w - 1) / 2);
    const rl = Math.min(run(angleL), maxRun);
    const rr = Math.min(run(angleR), maxRun);
    insert.push(P(s + rl, d), P(s + w - rr, d));
  } else {
    const steps = 24;
    for (let k = 1; k < steps; k++) {
      const t = k / steps;
      const [ax, ay] = arcPoint(w, d, t);
      insert.push(P(s + ax, ay));
    }
  }
  if (s + w < len - 0.01) insert.push(P(s + w, 0));
  const nextPts = [...pts.slice(0, i + 1), ...insert, ...pts.slice(i + 1)];
  const base = { ...(piece.edges?.[i] || { profile: "Eased", seam: false }) };
  delete base.bump;
  delete base.bump_base;
  const onLine = (p) => Math.abs((p[0] - a[0]) * uy - (p[1] - a[1]) * ux) < 0.05;
  const made = Array.from({ length: insert.length + 1 }, (_, k) => {
    const p1 = nextPts[i + k];
    const p2 = nextPts[(i + k + 1) % nextPts.length];
    return onLine(p1) && onLine(p2)
      ? { ...base, bump_base: bump.id }
      : { ...base, bump: bump.id };
  });
  const edges = [
    ...(piece.edges || []).slice(0, i),
    ...made,
    ...(piece.edges || []).slice(i + 1),
  ];
  const record = { ...bump, w: round2(w), d, pos, angle_l: angleL, angle_r: angleR };
  const bumps = [...(piece.bumps || []).filter((x) => x.id !== bump.id), record];
  return rebuild({ ...piece, bumps }, nextPts, edges);
};

// Takes a bump off and gives back the straight side it sat on.
// Returns { piece, edge } where edge is the index of that restored side.
export const removeBump = (piece, id) => {
  const edges = piece.edges || [];
  const bumps = (piece.bumps || []).filter((x) => x.id !== id);
  const strip = (e) => {
    const c = { ...e };
    if (c.bump === id) delete c.bump;
    if (c.bump_base === id) delete c.bump_base;
    return c;
  };
  const idx = bumpEdgeIndexes(piece, id);
  if (!idx.length)
    return { piece: { ...piece, bumps, edges: edges.map(strip) }, edge: null };
  const pts = piece.points;
  const n = pts.length;
  const first = idx[0];
  const last = idx[idx.length - 1];
  const leftBase =
    edges[(first - 1 + n) % n]?.bump_base === id ? (first - 1 + n) % n : null;
  const rightBase =
    edges[(last + 1) % n]?.bump_base === id ? (last + 1) % n : null;
  if (leftBase == null && rightBase == null) {
    // full-side arc: no feet — drop the interior arc vertices and straighten the side
    const drop = new Set();
    for (let k = first + 1; k <= last; k++) drop.add(k % n);
    const keep = pts.map((_, k) => k).filter((k) => !drop.has(k));
    if (!drop.size || keep.length < 3)
      return { piece: { ...piece, bumps, edges: edges.map(strip) }, edge: null };
    const next = rebuild(
      { ...piece, bumps },
      keep.map((k) => pts[k]),
      keep.map((k) => strip(edges[k])),
    );
    return { piece: next, edge: keep.indexOf(first) };
  }
  // drop the bump's corners plus the two feet that sat on the original side
  const dropStart = leftBase != null ? first : first + 1;
  const dropEnd = rightBase != null ? last + 1 : last;
  const drop = new Set();
  for (let k = dropStart; k <= dropEnd; k++) drop.add(k % n);
  const startIdx = leftBase != null ? leftBase : first;
  const baseEdge = strip(edges[leftBase != null ? leftBase : rightBase]);
  const keep = pts.map((_, k) => k).filter((k) => !drop.has(k));
  if (keep.length < 3) return { piece: { ...piece, bumps }, edge: null };
  const next = rebuild(
    { ...piece, bumps },
    keep.map((k) => pts[k]),
    keep.map((k) => (k === startIdx ? baseEdge : strip(edges[k]))),
  );
  return { piece: next, edge: keep.indexOf(startIdx) };
};


export const bumpEdge = (piece, i, width, depth) => {
  const pts = piece.points.map((p) => [...p]);
  const n = pts.length;
  const a = pts[i];
  const b = pts[(i + 1) % n];
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  const w = Math.max(Math.min(width, len - 1), 1);
  const d = Math.max(depth, 1);
  const ux = (b[0] - a[0]) / len;
  const uy = (b[1] - a[1]) / len;
  let nx = -uy;
  let ny = ux;
  const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  if (insidePoly([mid[0] + nx * 0.5, mid[1] + ny * 0.5], pts)) {
    nx = -nx;
    ny = -ny;
  }
  const s = (len - w) / 2;
  const c1 = [a[0] + ux * s, a[1] + uy * s];
  const c2 = [c1[0] + nx * d, c1[1] + ny * d];
  const c3 = [c2[0] + ux * w, c2[1] + uy * w];
  const c4 = [c1[0] + ux * w, c1[1] + uy * w];
  const nextPts = [...pts.slice(0, i + 1), c1, c2, c3, c4, ...pts.slice(i + 1)];
  const e = piece.edges?.[i] || { profile: "Eased", seam: false };
  const edges = [
    ...(piece.edges || []).slice(0, i),
    ...Array.from({ length: 5 }, () => ({ ...e })),
    ...(piece.edges || []).slice(i + 1),
  ];
  return rebuild(piece, nextPts, edges);
};

const SINK_TYPES = [
  "Undermount",
  "Topmount",
  "Farmhouse",
  "Vessel",
  "Integrated",
];
const CUTOUT_SHAPES = [
  "Rectangle",
  "Oval",
  "Round",
  "D-shape",
  "Double",
  "60/40",
  "40/60",
  "70/30",
  "30/70",
];

// Split bowls: left bowl share of the overall width
const BOWL_SPLIT = {
  Double: 0.5,
  "60/40": 0.6,
  "40/60": 0.4,
  "70/30": 0.7,
  "30/70": 0.3,
};
const INSTALL_SERVICES = ["Installation", "Supply only", "Customer supplied"];

const DESIGN_STEPS = [
  {
    key: "dimensions",
    label: "Dimensions",
    icon: Ruler,
    hint: "Lay pieces down and size them",
  },
  {
    key: "shape",
    label: "Shape",
    icon: Polygon,
    hint: "Corners, bumps, flips and seams",
  },
  {
    key: "edges",
    label: "Edges",
    icon: LineSegments,
    hint: "Profiles, backsplash and colors",
  },
  {
    key: "cutouts",
    label: "Cutouts",
    icon: CircleDashed,
    hint: "Sinks, cooktops and holes",
  },
  {
    key: "layout",
    label: "Layout",
    icon: SquaresFour,
    hint: "Nest the pieces on slabs",
  },
];

const STEP_INDEX = (key) => DESIGN_STEPS.findIndex((s) => s.key === key);

// Free-mode tool switcher (same keys as the guided steps, minus layout).
const TOOLS = [
  ["dimensions", Ruler, "Size"],
  ["shape", Polygon, "Shape"],
  ["edges", LineSegments, "Edges"],
  ["cutouts", CircleDashed, "Cutouts"],
];

// One-tap starters that drop a whole common project, then let the user tweak it.
const STARTERS = [
  {
    key: "kitchen-l",
    label: "Kitchen — L + island",
    note: "2 pieces",
    build: () => [
      { label: "Perimeter", points: SHAPES["L-shape"](126, 25.5), x: 4, y: 4 },
      { label: "Island", points: SHAPES.Island(84, 42), x: 44, y: 118 },
    ],
  },
  {
    key: "kitchen-u",
    label: "Kitchen — U-shape",
    note: "1 piece",
    build: () => [{ label: "Perimeter", points: SHAPES["U-shape"](), x: 4, y: 4 }],
  },
  {
    key: "vanity",
    label: "Bathroom vanity",
    note: '61" x 22"',
    build: () => [{ label: "Vanity", points: SHAPES.Rectangle(61, 22), x: 4, y: 4 }],
  },
];

// Spotlight walkthrough shown once on first visit (and re-launchable from Help).
const TOUR_STEPS = [
  {
    selector: '[data-testid="designer-steps"]',
    title: "Five simple steps",
    body: "Work left to right — Dimensions, Shape, Edges, Cut-outs, then Layout. A tick shows what's already done.",
  },
  {
    selector: '[data-testid="designer-quickstart-section"]',
    title: "Start in one tap",
    body: "Drop a ready-made kitchen or vanity, or pick a single shape below — it lands straight on the sheet.",
  },
  {
    selector: '[data-testid="designer-canvas"]',
    title: "Size it your way",
    body: "Click a piece to select it, then drag a side or click it to type the exact length. Pull a corner straight out along a side to grow a new leg (L, then U). Hold Space to pan.",
  },
  {
    selector: '[data-testid="designer-step-next"]',
    title: "Move on when ready",
    body: "Each step lights up Next once it's ready. You can also jump around using the steps up top.",
  },
];

const SectionTitle = ({ icon: Icon, children, hint }) => (
  <div>
    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
      {Icon && <Icon size={13} weight="bold" className="text-indigo-300" />}
      {children}
    </div>
    {hint && <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">{hint}</p>}
  </div>
);

const GuideCard = ({ icon: Icon, title, lines, testId }) => (
  <div
    className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/50 p-4"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-xs font-semibold text-zinc-200">
      {Icon && <Icon size={15} weight="bold" className="text-indigo-300" />}
      {title}
    </div>
    <ul className="mt-3 space-y-2 text-[11px] leading-relaxed text-zinc-500">
      {lines.map((l) => (
        <li key={l} className="flex gap-2">
          <span className="mt-[6px] h-1 w-1 shrink-0 rounded-full bg-indigo-400/70" />
          {l}
        </li>
      ))}
    </ul>
  </div>
);

// Absolute polygon for a saved piece (points offset by x/y, rotated about its centroid).
const pieceAbsPoints = (p) => {
  const pts = p.points || [];
  if (pts.length < 3) return [];
  const rot = ((Number(p.rot) || 0) * Math.PI) / 180;
  const cx = pts.reduce((s, q) => s + q[0], 0) / pts.length;
  const cy = pts.reduce((s, q) => s + q[1], 0) / pts.length;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const ox = Number(p.x) || 0;
  const oy = Number(p.y) || 0;
  return pts.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    return [ox + cx + dx * cos - dy * sin, oy + cy + dx * sin + dy * cos];
  });
};

// Tiny at-a-glance preview of a saved layout's pieces.
const LayoutThumb = ({ pieces, className }) => {
  const polys = (pieces || []).map(pieceAbsPoints).filter((a) => a.length > 2);
  if (!polys.length) return null;
  const all = polys.flat();
  const xs = all.map((p) => p[0]);
  const ys = all.map((p) => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const w = Math.max(Math.max(...xs) - minX, 1);
  const h = Math.max(Math.max(...ys) - minY, 1);
  const pad = Math.max(w, h) * 0.08;
  const sw = Math.max(w, h) * 0.03;
  return (
    <svg
      viewBox={`${minX - pad} ${minY - pad} ${w + pad * 2} ${h + pad * 2}`}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      data-testid="designer-layout-thumb"
    >
      {polys.map((a, i) => (
        <polygon
          key={i}
          points={a.map((p) => p.join(",")).join(" ")}
          fill="rgba(99,102,241,0.28)"
          stroke="#a5b4fc"
          strokeWidth={sw}
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};

const clipHalf = (pts, axis, at, keepLow) => {
  const inside = (p) => (keepLow ? p[axis] <= at + 1e-6 : p[axis] >= at - 1e-6);
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const ai = inside(a);
    const bi = inside(b);
    if (ai) out.push(a);
    if (ai !== bi) {
      const t = (at - a[axis]) / (b[axis] - a[axis]);
      const cross = [
        round2(a[0] + (b[0] - a[0]) * t),
        round2(a[1] + (b[1] - a[1]) * t),
      ];
      out.push(cross);
    }
  }
  const dedup = out.filter(
    (p, i) =>
      i === 0 ||
      Math.abs(p[0] - out[i - 1][0]) > 0.01 ||
      Math.abs(p[1] - out[i - 1][1]) > 0.01,
  );
  return dedup;
};

export const splitPiece = (piece, axis, at) => {
  const seamId = uid();
  const halves = [true, false].map((keepLow) => {
    const pts = clipHalf(piece.points, axis, at, keepLow);
    if (pts.length < 3) return null;
    const edges = pts.map((p, i) => {
      const b = pts[(i + 1) % pts.length];
      const onLine =
        Math.abs(p[axis] - at) < 0.05 && Math.abs(b[axis] - at) < 0.05;
      const src = piece.edges?.[
        Math.min(i, (piece.edges?.length || 1) - 1)
      ] || {
        profile: "Eased",
      };
      return onLine
        ? { profile: src.profile || "Eased", seam: true, seam_id: seamId }
        : { ...src, seam: !!src.seam };
    });
    return rebuild(piece, pts, edges);
  });
  if (!halves[0] || !halves[1]) return null;
  const [a, b] = halves;
  const lowPts = clipHalf(piece.points, axis, at, true);
  const highPts = clipHalf(piece.points, axis, at, false);
  const minLow = [Math.min(...lowPts.map((p) => p[0])), Math.min(...lowPts.map((p) => p[1]))];
  const minHigh = [Math.min(...highPts.map((p) => p[0])), Math.min(...highPts.map((p) => p[1]))];
  // clip each cut-out to the halves it overlaps and re-base it to that half's own
  // origin on BOTH axes (a non-rectangular half can start above/left of the source).
  const cutA = [];
  const cutB = [];
  (piece.cutouts || []).forEach((c) => {
    const cx0 = Number(c.x) || 0;
    const cy0 = Number(c.y) || 0;
    const lo = axis === 0 ? cx0 : cy0;
    const size = axis === 0 ? Number(c.w) || 0 : Number(c.h) || 0;
    const hi = lo + size;
    const aLen = round2(Math.min(hi, at) - lo);
    const bLen = round2(hi - Math.max(lo, at));
    if (aLen > 0) {
      const base = { ...c, x: round2(cx0 - minLow[0]), y: round2(cy0 - minLow[1]) };
      if (axis === 0) base.w = aLen;
      else base.h = aLen;
      if (bLen > 0) base.seam_side = axis === 0 ? "right" : "bottom";
      cutA.push(base);
    }
    if (bLen > 0) {
      const startCut = round2(Math.max(lo, at) - minHigh[axis]);
      const base = {
        ...c,
        x: axis === 0 ? startCut : round2(cx0 - minHigh[0]),
        y: axis === 1 ? startCut : round2(cy0 - minHigh[1]),
      };
      if (axis === 0) base.w = bLen;
      else base.h = bLen;
      // the second copy is only the rest of the same cut, not another one to price
      if (aLen > 0) {
        base.id = uid();
        base.seam_part = true;
        base.part_of = c.id;
        base.seam_side = axis === 0 ? "left" : "top";
      }
      cutB.push(base);
    }
  });
  return [
    {
      ...a,
      id: uid(),
      label: `${piece.label} A`,
      cutouts: cutA,
    },
    {
      ...b,
      id: uid(),
      label: `${piece.label} B`,
      cutouts: cutB,
      x: axis === 0 ? piece.x + at : piece.x,
      y: axis === 1 ? piece.y + at : piece.y,
    },
  ];
};

// Where a seam line crosses the piece outline, so the guide matches the counter depth
const seamSpans = (pts, axis, at) => {
  const other = axis === 0 ? 1 : 0;
  const hits = [];
  for (let i = 0; i < (pts || []).length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    if ((a[axis] <= at && b[axis] > at) || (b[axis] <= at && a[axis] > at)) {
      const t = (at - a[axis]) / (b[axis] - a[axis]);
      hits.push(a[other] + t * (b[other] - a[other]));
    }
  }
  hits.sort((m, n) => m - n);
  const spans = [];
  for (let i = 0; i + 1 < hits.length; i += 2)
    spans.push([hits[i], hits[i + 1]]);
  return spans;
};

// Cutout boxes (in piece p's local coords) from p and any unrotated neighbours
const cutoutBoxes = (p, all) => {
  const boxes = [];
  for (const q of all || [p]) {
    const same = q.id === p.id;
    if (!same && ((Number(q.rot) || 0) !== 0 || (Number(p.rot) || 0) !== 0)) continue;
    const dx = same ? 0 : (q.x || 0) - (p.x || 0);
    const dy = same ? 0 : (q.y || 0) - (p.y || 0);
    for (const c of q.cutouts || []) {
      const rot90 = Math.abs((Number(c.rot) || 0) % 180) === 90;
      const w = rot90 ? c.h : c.w;
      const h = rot90 ? c.w : c.h;
      const cx = c.x + c.w / 2 + dx;
      const cy = c.y + c.h / 2 + dy;
      boxes.push([cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2]);
    }
  }
  return boxes;
};

// Split an axis-aligned segment so it skips over cutouts (seam stops at the sink)
const segmentMinusBoxes = (a, b, boxes) => {
  const vert = Math.abs(a[0] - b[0]) < 1e-6;
  const horiz = Math.abs(a[1] - b[1]) < 1e-6;
  if ((!vert && !horiz) || !boxes.length) return [[a, b]];
  const axis = vert ? 1 : 0;
  const other = vert ? 0 : 1;
  const fixed = a[other];
  let spans = [[Math.min(a[axis], b[axis]), Math.max(a[axis], b[axis])]];
  for (const bx of boxes) {
    if (fixed <= bx[other] || fixed >= bx[other + 2]) continue;
    const cLo = bx[axis];
    const cHi = bx[axis + 2];
    spans = spans.flatMap(([s, e]) => {
      if (cHi <= s || cLo >= e) return [[s, e]];
      const out = [];
      if (cLo > s) out.push([s, cLo]);
      if (cHi < e) out.push([cHi, e]);
      return out;
    });
  }
  return spans.map(([s, e]) =>
    vert ? [[fixed, s], [fixed, e]] : [[s, fixed], [e, fixed]],
  );
};

const dropCollinear = (pts) =>
  pts.filter((p, i, arr) => {
    const a = arr[(i - 1 + arr.length) % arr.length];
    const b = arr[(i + 1) % arr.length];
    const cross = (p[0] - a[0]) * (b[1] - a[1]) - (p[1] - a[1]) * (b[0] - a[0]);
    return Math.abs(cross) > 0.01;
  });

const absPts = (p) =>
  p.points.map(([x, y]) => [round2(x + p.x), round2(y + p.y)]);
const nearPt = (a, b) =>
  Math.abs(a[0] - b[0]) < 0.2 && Math.abs(a[1] - b[1]) < 0.2;

export const mergeAtSeam = (pieceA, i, others) => {
  if (pieceA.rot % 360 !== 0) return null;
  const A = absPts(pieceA);
  const a1 = A[i];
  const a2 = A[(i + 1) % A.length];
  for (const B0 of others) {
    if (B0.rot % 360 !== 0) continue;
    const B = absPts(B0);
    for (let j = 0; j < B.length; j++) {
      const b1 = B[j];
      const b2 = B[(j + 1) % B.length];
      if (!(
        (nearPt(a1, b2) && nearPt(a2, b1)) ||
        (nearPt(a1, b1) && nearPt(a2, b2))
      ))
        continue;
      const ringA = Array.from(
        { length: A.length },
        (_, k) => A[(i + 1 + k) % A.length],
      );
      const ringB = Array.from(
        { length: B.length },
        (_, k) => B[(j + 1 + k) % B.length],
      );
      let merged = [...ringA, ...ringB].filter(
        (p, idx, arr) => idx === 0 || !nearPt(p, arr[idx - 1]),
      );
      if (merged.length > 2 && nearPt(merged[0], merged[merged.length - 1]))
        merged.pop();
      merged = dropCollinear(merged);
      if (merged.length < 3) return null;
      const minx = Math.min(...merged.map((p) => p[0]));
      const miny = Math.min(...merged.map((p) => p[1]));
      // keep each side's own edge finish (and any other seams) — look up the source
      // edge that the merged side lies on; the joined seam itself disappears
      const profile = pieceA.edges?.[0]?.profile || "Eased";
      const onSeg = (q, s1, s2) => {
        const L = Math.hypot(s2[0] - s1[0], s2[1] - s1[1]) || 1;
        const cross = Math.abs((q[0] - s1[0]) * (s2[1] - s1[1]) - (q[1] - s1[1]) * (s2[0] - s1[0])) / L;
        const t = ((q[0] - s1[0]) * (s2[0] - s1[0]) + (q[1] - s1[1]) * (s2[1] - s1[1])) / (L * L);
        return cross < 0.05 && t > -0.001 && t < 1.001;
      };
      const srcEdge = (q) => {
        for (const [P, pts, skip] of [[pieceA, A, i], [B0, B, j]]) {
          for (let k = 0; k < pts.length; k++) {
            if (k === skip) continue;
            if (onSeg(q, pts[k], pts[(k + 1) % pts.length])) return P.edges?.[k];
          }
        }
        return null;
      };
      const edges = merged.map((p, k) => {
        const q = merged[(k + 1) % merged.length];
        const e = srcEdge([(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]);
        return e ? { ...e, profile: e.profile || profile, seam: !!e.seam } : { profile, seam: false };
      });
      let cutouts = [
        ...(pieceA.cutouts || []).map((c) => ({
          ...c,
          x: round2(c.x + pieceA.x - minx),
          y: round2(c.y + pieceA.y - miny),
        })),
        ...(B0.cutouts || []).map((c) => ({
          ...c,
          x: round2(c.x + B0.x - minx),
          y: round2(c.y + B0.y - miny),
        })),
      ];
      // a sink clipped by this seam comes back whole: fold the leftover part into its parent
      const parts = cutouts.filter(
        (o) => o.seam_part && cutouts.some((c) => c.id === o.part_of && !c.seam_part),
      );
      if (parts.length) {
        cutouts = cutouts
          .filter((o) => !parts.includes(o))
          .map((c) => {
            const mine = parts.filter((o) => o.part_of === c.id);
            if (!mine.length) return c;
            const all = [c, ...mine];
            const x0 = Math.min(...all.map((o) => o.x));
            const y0 = Math.min(...all.map((o) => o.y));
            const x1 = Math.max(...all.map((o) => o.x + (Number(o.w) || 0)));
            const y1 = Math.max(...all.map((o) => o.y + (Number(o.h) || 0)));
            const { seam_side: _s, ...rest } = c;
            return { ...rest, x: round2(x0), y: round2(y0), w: round2(x1 - x0), h: round2(y1 - y0) };
          });
      }
      const out = rebuild(
        { ...pieceA, label: pieceA.label.replace(/ [AB]$/, "") },
        merged,
        edges,
      );
      return { piece: { ...out, x: minx, y: miny, cutouts }, removeId: B0.id };
    }
  }
  return null;
};

export const flipPiece = (piece, axis) => {
  const maxX = Math.max(...piece.points.map((p) => p[0]));
  const maxY = Math.max(...piece.points.map((p) => p[1]));
  const pts = piece.points.map(([x, y]) =>
    axis === "h" ? [round2(maxX - x), y] : [x, round2(maxY - y)],
  );
  const cutouts = (piece.cutouts || []).map((c) =>
    axis === "h"
      ? { ...c, x: round2(Math.max(0, maxX - c.x - c.w)) }
      : { ...c, y: round2(Math.max(0, maxY - c.y - c.h)) },
  );
  return { ...rebuild(piece, pts, piece.edges), cutouts };
};

export const cornerCut = (piece, v, size, mode, meta = {}) => {
  const pts = piece.points.map((p) => [...p]);
  const n = pts.length;
  const cur = pts[v];
  const prev = pts[(v - 1 + n) % n];
  const next = pts[(v + 1) % n];
  const l1 = Math.hypot(prev[0] - cur[0], prev[1] - cur[1]) || 1;
  const l2 = Math.hypot(next[0] - cur[0], next[1] - cur[1]) || 1;
  const clipAngle =
    mode === "clip"
      ? Math.min(89, Math.max(1, Number(meta.angle) || 45))
      : 45;
  let cut1 = size;
  let cut2 = size;
  if (mode === "clip" && clipAngle !== 45) {
    const t = Math.tan((clipAngle * Math.PI) / 180);
    if (clipAngle < 45) {
      cut2 = size;
      cut1 = size * t;
    } else {
      cut1 = size;
      cut2 = size / t;
    }
  }
  const s1 = Math.min(cut1, l1 - 1);
  const s2 = Math.min(cut2, l2 - 1);
  const p1 = [
    cur[0] + ((prev[0] - cur[0]) / l1) * s1,
    cur[1] + ((prev[1] - cur[1]) / l1) * s1,
  ];
  const p2 = [
    cur[0] + ((next[0] - cur[0]) / l2) * s2,
    cur[1] + ((next[1] - cur[1]) / l2) * s2,
  ];
  const inner = [p1[0] + (p2[0] - cur[0]), p1[1] + (p2[1] - cur[1])];
  let insertPts;
  if (mode === "notch") insertPts = [p1, inner, p2];
  else if (mode === "radius") {
    const a0 = Math.atan2(p1[1] - inner[1], p1[0] - inner[0]);
    let a1 = Math.atan2(p2[1] - inner[1], p2[0] - inner[0]);
    while (a1 - a0 > Math.PI) a1 -= 2 * Math.PI;
    while (a0 - a1 > Math.PI) a1 += 2 * Math.PI;
    const r = Math.hypot(p1[0] - inner[0], p1[1] - inner[1]);
    const steps = 6;
    insertPts = Array.from({ length: steps + 1 }, (_, k) => {
      const t = a0 + ((a1 - a0) * k) / steps;
      return [
        round2(inner[0] + Math.cos(t) * r),
        round2(inner[1] + Math.sin(t) * r),
      ];
    });
  } else insertPts = [p1, p2];
  const nextPts = [...pts.slice(0, v), ...insertPts, ...pts.slice(v + 1)];
  const ePrev = (piece.edges || [])[(v - 1 + n) % n] || {
    profile: "Eased",
    seam: false,
  };
  const extra =
    mode === "notch" ? 2 : mode === "radius" ? insertPts.length - 1 : 1;
  const id = meta.id || uid();
  const kind = mode === "clip" ? "chamfer" : mode;
  const tag = { id, kind, size: round2(size), angle: round2(clipAngle) };
  const edges = [
    ...(piece.edges || []).slice(0, v),
    ...Array.from({ length: extra }, () => ({ ...ePrev, corner: tag })),
    ...(piece.edges || []).slice(v),
  ];
  const record = {
    id,
    kind,
    size: round2(size),
    angle: round2(clipAngle),
    service_kind: meta.service_kind || "",
    service_id: meta.service_id || "",
    service_name: meta.service_name || "",
    service_price: Number(meta.service_price) || 0,
    service_unit: meta.service_unit || "EA",
  };
  const corners = [...(piece.corners || []).filter((c) => c.id !== id), record];
  return rebuild({ ...piece, corners }, nextPts, edges);
};

// Runs of edges that came from one corner cut: [{id, kind, size, edges: [i...], record}]
export const cornerFeatures = (piece) => {
  const edges = piece.edges || [];
  const n = edges.length;
  const keyOf = (e, i) =>
    e?.corner ? e.corner.id || `legacy-${e.corner.kind}-${e.corner.size}-${i}` : "";
  const runs = [];
  let i = 0;
  while (i < n) {
    const e = edges[i];
    if (!e?.corner) {
      i += 1;
      continue;
    }
    const idx = [i];
    let j = i + 1;
    while (
      j < n &&
      edges[j]?.corner &&
      (e.corner.id
        ? edges[j].corner.id === e.corner.id
        : edges[j].corner.kind === e.corner.kind &&
          edges[j].corner.size === e.corner.size)
    ) {
      idx.push(j);
      j += 1;
    }
    runs.push({ id: keyOf(e, i), kind: e.corner.kind, size: e.corner.size, edges: idx });
    i = j;
  }
  // a run can wrap past the last vertex back to the first
  if (runs.length > 1) {
    const first = runs[0];
    const last = runs[runs.length - 1];
    const fe = edges[first.edges[0]];
    const le = edges[last.edges[0]];
    const same = fe.corner.id
      ? fe.corner.id === le.corner.id
      : fe.corner.kind === le.corner.kind && fe.corner.size === le.corner.size;
    if (first.edges[0] === 0 && last.edges[last.edges.length - 1] === n - 1 && same) {
      last.edges = [...last.edges, ...first.edges];
      runs.shift();
    }
  }
  return runs.map((r) => ({
    ...r,
    record: (piece.corners || []).find((c) => c.id === r.id) || {
      id: r.id,
      kind: r.kind,
      size: r.size,
    },
  }));
};

// Takes a corner cut off and puts the sharp corner back. Returns { piece, vertex }.
export const removeCorner = (piece, featureId) => {
  const feat = cornerFeatures(piece).find((f) => f.id === featureId);
  const corners = (piece.corners || []).filter((c) => c.id !== featureId);
  if (!feat) return { piece: { ...piece, corners }, vertex: null };
  const pts = piece.points;
  const edges = piece.edges || [];
  const n = pts.length;
  // spin the ring so the feature sits in the middle and never wraps
  const shift = (feat.edges[0] - 1 + n) % n;
  const rp = pts.map((_, k) => pts[(k + shift) % n]);
  const re = edges.map((_, k) => edges[(k + shift) % n]);
  const k0 = 1;
  const k1 = feat.edges.length; // edges k0..k1 belong to the feature
  if (k1 + 2 > n) return { piece: { ...piece, corners }, vertex: null };
  const a1 = rp[k0 - 1];
  const a2 = rp[k0];
  const b1 = rp[k1 + 1];
  const b2 = rp[(k1 + 2) % n];
  const den = (a1[0] - a2[0]) * (b1[1] - b2[1]) - (a1[1] - a2[1]) * (b1[0] - b2[0]);
  if (Math.abs(den) < 1e-9) return { piece: { ...piece, corners }, vertex: null };
  const t =
    ((a1[0] - b1[0]) * (b1[1] - b2[1]) - (a1[1] - b1[1]) * (b1[0] - b2[0])) / den;
  const corner = [
    round2(a1[0] + t * (a2[0] - a1[0])),
    round2(a1[1] + t * (a2[1] - a1[1])),
  ];
  const newPts = [...rp.slice(0, k0), corner, ...rp.slice(k1 + 2)];
  const newEdges = [...re.slice(0, k0), ...re.slice(k1 + 1)];
  if (newPts.length < 3) return { piece: { ...piece, corners }, vertex: null };
  return { piece: rebuild({ ...piece, corners }, newPts, newEdges), vertex: k0 };
};

const BumpDiagram = ({ bump }) => {
  const inward = bump.kind === "bump_in" || bump.kind === "bump_in_curve";
  const curve = bump.kind.includes("curve");
  const w = Math.max(Number(bump.w) || 1, 1);
  const d = Math.max(Number(bump.d) || 1, 0.5);
  const base = 80;
  const pxW = 150;
  const pxD = Math.max(14, Math.min(46, (d / w) * pxW));
  const dir = inward ? 1 : -1;
  const top = base + dir * pxD;
  const run = (ang) => {
    const t = Math.tan(((Number(ang) || 90) * Math.PI) / 180);
    return Math.abs(t) < 1e-6 ? 0 : Math.min(pxD / t, (pxW - 10) / 2);
  };
  const rl = run(bump.angle_l);
  const rr = run(bump.angle_r);
  const x0 = 55;
  const x1 = x0 + pxW;
  if (bump.kind === "full_curve") {
    const fd = Math.max(24, Math.min(50, (d / 12) * 50));
    const topY = base - fd;
    const cx = (x0 + x1) / 2;
    return (
      <svg viewBox="0 0 260 150" className="h-32 w-full" data-testid="designer-bump-diagram">
        <path
          d={`M${x0},140 L${x0},${base} Q${cx},${topY - fd * 0.9} ${x1},${base} L${x1},140`}
          fill="none"
          stroke="#e4e4e7"
          strokeWidth="2"
        />
        <line x1={x0} y1={base} x2={x1} y2={base} stroke="#a1a1aa" strokeWidth="1.5" strokeDasharray="4 3" />
        <line x1={cx} y1={base} x2={cx} y2={topY} stroke="#22d3ee" strokeWidth="2" />
        <line x1={cx - 5} y1={base} x2={cx + 5} y2={base} stroke="#22d3ee" strokeWidth="2" />
        <line x1={cx - 5} y1={topY} x2={cx + 5} y2={topY} stroke="#22d3ee" strokeWidth="2" />
        <text x={cx + 10} y={(base + topY) / 2 + 4} fill="#e4e4e7" fontSize="11" fontWeight="600">
          Depth {formatInches(d)}
        </text>
      </svg>
    );
  }
  const path = curve
    ? `M10,${base} L${x0},${base} Q${(x0 + x1) / 2},${top + dir * pxD * 0.9} ${x1},${base} L250,${base}`
    : `M10,${base} L${x0},${base} L${x0 + rl},${top} L${x1 - rr},${top} L${x1},${base} L250,${base}`;
  const depthX = curve ? (x0 + x1) / 2 : x0 + rl + 12;
  return (
    <svg
      viewBox="0 0 260 150"
      className="h-32 w-full"
      data-testid="designer-bump-diagram"
    >
      <path d={path} fill="none" stroke="#e4e4e7" strokeWidth="2" />
      <line
        x1={depthX}
        y1={base}
        x2={depthX}
        y2={top}
        stroke="#22d3ee"
        strokeWidth="2"
      />
      <line x1={depthX - 5} y1={base} x2={depthX + 5} y2={base} stroke="#22d3ee" strokeWidth="2" />
      <line x1={depthX - 5} y1={top} x2={depthX + 5} y2={top} stroke="#22d3ee" strokeWidth="2" />
      <text
        x={depthX + 10}
        y={(base + top) / 2 + 4}
        fill="#e4e4e7"
        fontSize="11"
        fontWeight="600"
      >
        Depth {formatInches(d)}
      </text>
      <line
        x1={x0}
        y1={inward ? 40 : 118}
        x2={x1}
        y2={inward ? 40 : 118}
        stroke="#22d3ee"
        strokeWidth="2"
      />
      <line x1={x0} y1={inward ? 34 : 112} x2={x0} y2={inward ? 46 : 124} stroke="#22d3ee" strokeWidth="2" />
      <line x1={x1} y1={inward ? 34 : 112} x2={x1} y2={inward ? 46 : 124} stroke="#22d3ee" strokeWidth="2" />
      <text
        x={(x0 + x1) / 2}
        y={inward ? 30 : 138}
        fill="#e4e4e7"
        fontSize="11"
        fontWeight="600"
        textAnchor="middle"
      >
        Width {formatInches(w)}
      </text>
    </svg>
  );
};

const BumpParams = ({
  bump,
  services,
  sideLength,
  onChange,
  onDuplicate,
  onRemove,
  onBack,
  onSavePreset,
}) => {
  const straight = bump.kind === "bump_out" || bump.kind === "bump_in";
  const full = bump.kind === "full_curve";
  const sel =
    "h-10 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-100 outline-none focus:border-cyan-400";
  return (
    <div
      className="mt-3 w-full space-y-4 rounded-lg border border-zinc-800 bg-zinc-950/70 p-3"
      data-testid="designer-bump-params"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="designer-bump-back"
          onClick={onBack}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white"
          title="Back to the side"
        >
          <ArrowLeft size={14} weight="bold" />
        </button>
        <span className="text-sm font-bold text-zinc-100">Bump Parameters</span>
      </div>
      <div className="text-xs text-zinc-400">
        Bump Type:{" "}
        <span className="font-semibold text-cyan-300" data-testid="designer-bump-type">
          {bumpTitle(bump.kind)}
        </span>
      </div>
      <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-1">
        <BumpDiagram bump={bump} />
      </div>
      {full ? (
        <div>
          <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
            Full Radius Depth
          </span>
          <div className="relative mt-1">
            <MeasureInput
              data-testid="designer-bump-d"
              min={0.5}
              fallback={0.5}
              value={bump.d}
              onChange={(n) => onChange({ d: n })}
              className="h-10 border-zinc-800 bg-zinc-950 pr-7"
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500">
              in
            </span>
          </div>
          <span className="font-mono-data mt-1 block text-[10px] text-zinc-500">
            spans the whole {sideLength}" side
          </span>
        </div>
      ) : (
      <div>
        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
          Size (D×W)
        </span>
        <div className="mt-1 flex items-center gap-2">
          <div className="relative flex-1">
            <MeasureInput
              data-testid="designer-bump-d"
              min={0.5}
              fallback={0.5}
              value={bump.d}
              onChange={(n) => onChange({ d: n })}
              className="h-10 border-zinc-800 bg-zinc-950 pr-7"
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500">
              in
            </span>
          </div>
          <span className="text-xs text-zinc-500">×</span>
          <div className="relative flex-1">
            <MeasureInput
              data-testid="designer-bump-w"
              min={1}
              fallback={1}
              value={bump.w}
              onChange={(n) => onChange({ w: n })}
              className="h-10 border-zinc-800 bg-zinc-950 pr-7"
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500">
              in
            </span>
          </div>
        </div>
        <span className="font-mono-data mt-1 block text-[10px] text-zinc-500">
          side {sideLength}" long
        </span>
      </div>
      )}
      {straight && (
        <div>
          <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
            Angles (Left–Right)
          </span>
          <div className="mt-1 flex items-center gap-2">
            <select
              data-testid="designer-bump-angle-l"
              value={bump.angle_l || 90}
              onChange={(e) => onChange({ angle_l: Number(e.target.value) })}
              className={sel}
            >
              {BUMP_ANGLES.map((a) => (
                <option key={a} value={a}>
                  {a}°
                </option>
              ))}
            </select>
            <span className="text-xs text-zinc-500">–</span>
            <select
              data-testid="designer-bump-angle-r"
              value={bump.angle_r || 90}
              onChange={(e) => onChange({ angle_r: Number(e.target.value) })}
              className={sel}
            >
              {BUMP_ANGLES.map((a) => (
                <option key={a} value={a}>
                  {a}°
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {!full && (
      <div>
        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
          Positioning
        </span>
        <div className="mt-1 grid grid-cols-3 overflow-hidden rounded-md border border-zinc-800">
          {[
            ["left", "Left"],
            ["center", "Center"],
            ["right", "Right"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              data-testid={`designer-bump-pos-${v}`}
              onClick={() => onChange({ pos: v, offset: "" })}
              className={`h-10 text-xs font-semibold transition-colors duration-150 ${
                (bump.pos || "center") === v
                  ? "bg-indigo-500/25 text-indigo-100 ring-1 ring-inset ring-indigo-400"
                  : "bg-zinc-950 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            From corner
          </span>
          <MeasureInput
            data-testid="designer-bump-custom-offset"
            allowEmpty
            min={0}
            placeholder="—"
            value={bump.pos === "custom" ? bump.offset ?? "" : ""}
            onChange={(n) =>
              n === ""
                ? onChange({ pos: "center", offset: "" })
                : onChange({ pos: "custom", offset: n })
            }
            className="h-8 w-24 border-zinc-800 bg-zinc-950 text-xs"
          />
          <span className="text-[10px] text-zinc-500">in</span>
        </div>
      </div>
      )}
      <div>
        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
          Linked service
        </span>
        <select
          data-testid="designer-bump-service"
          value={bump.service_id || ""}
          onChange={(e) => {
            const s = services.find((x) => x.id === e.target.value);
            onChange(
              s
                ? {
                    service_id: s.id,
                    service_name: s.name,
                    service_price: Number(s.cost_estimate) || 0,
                    service_unit: s.unit || "EA",
                  }
                : { service_id: "", service_name: "", service_price: 0, service_unit: "EA" },
            );
          }}
          className={`${sel} mt-1`}
        >
          <option value="">None</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.cost_estimate ? ` · $${Number(s.cost_estimate).toFixed(2)}/${s.unit || "EA"}` : ""}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        data-testid="designer-bump-save-preset"
        onClick={onSavePreset}
        className="flex h-9 w-full items-center justify-center gap-2 rounded-md border border-amber-400/40 bg-amber-500/10 text-xs font-semibold text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
      >
        <Star size={14} weight="bold" /> Save as preset
      </button>
      <div className={`grid gap-2 pt-1 ${full ? "grid-cols-1" : "grid-cols-2"}`}>
        {!full && (
        <button
          type="button"
          data-testid="designer-bump-duplicate"
          onClick={onDuplicate}
          className="flex h-10 items-center justify-center gap-2 rounded-md border border-zinc-700 text-xs font-semibold text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 hover:text-white"
        >
          <Copy size={14} weight="bold" /> Duplicate
        </button>
        )}
        <button
          type="button"
          data-testid="designer-bump-remove"
          onClick={onRemove}
          className="flex h-10 items-center justify-center gap-2 rounded-md border border-red-500/40 text-xs font-semibold text-red-300 transition-colors duration-150 hover:bg-red-500/10"
        >
          <Trash size={14} weight="bold" /> Remove
        </button>
      </div>
    </div>
  );
};


export const normalizePiece = (p) => {
  const isCircle = !!p.circle;
  const isOval = !!p.oval;
  const circleDia = round2(Number(p.diameter) || 36);
  const pts = isCircle
    ? SHAPES.Circle(circleDia)
    : isOval
      ? SHAPES.Oval(round2(Number(p.length_in) || 48), round2(Number(p.width_in) || 30))
      : p.points && p.points.length > 2
        ? p.points
        : SHAPES.Rectangle(p.length_in || 96, p.width_in || 25.5);
  const edges = pts.map((_, i) => ({
    ...(p.edges?.[i] || {}),
    profile: p.edges?.[i]?.profile || p.edges?.[0]?.profile || "Eased",
    seam: !!p.edges?.[i]?.seam,
    length_in: Math.round(edgeLen(pts, i) * 100) / 100,
  }));
  const xs = pts.map((a) => a[0]);
  const ys = pts.map((a) => a[1]);
  return {
    ...p,
    shape: "poly",
    circle: isCircle,
    oval: isOval,
    diameter: isCircle ? circleDia : undefined,
    points: pts,
    edges,
    x: p.x ?? 4,
    y: p.y ?? 4,
    rot: p.rot ?? 0,
    cutouts: p.cutouts || [],
    orientation: p.orientation === "vertical" ? "vertical" : "horizontal",
    stand: p.stand === "waterfall" ? "waterfall" : "backsplash",
    length_in: round2(Math.max(...xs)),
    width_in: round2(Math.max(...ys)),
  };
};

/** Plan-label suffix for a piece that stands up in the room. */
export const uprightTag = (p) =>
  p.orientation === "vertical"
    ? ` · upright ${p.stand === "waterfall" ? "waterfall" : "backsplash"}`
    : "";

const CORNER_TITLES = { radius: "Radius", chamfer: "Chamfer", notch: "Notch" };
const CORNER_LETTER = { radius: "R", chamfer: "C", notch: "N" };
// Line-art corner icons drawn as a rectangle with one treated corner (top-right).
const CORNER_ICON_PATH = {
  radius: "M5 3 L14 3 A5 5 0 0 1 19 8 L19 21 L5 21 Z",
  clip: "M5 3 L14 3 L19 8 L19 21 L5 21 Z",
  notch: "M5 3 L14 3 L14 8 L19 8 L19 21 L5 21 Z",
  none: "M5 3 L19 3 L19 21 L5 21 Z",
};

const CornerDiagram = ({ kind }) => {
  const cut =
    kind === "radius"
      ? "M60,110 A60,60 0 0 1 120,50"
      : kind === "chamfer"
        ? "M60,110 L120,50"
        : "M60,110 L60,50 L120,50";
  return (
    <svg
      viewBox="0 0 260 150"
      className="h-32 w-full"
      data-testid="designer-corner-diagram"
    >
      <path d="M10,110 L60,110" fill="none" stroke="#e4e4e7" strokeWidth="2" />
      <path d="M120,50 L120,10" fill="none" stroke="#e4e4e7" strokeWidth="2" />
      <path d={cut} fill="none" stroke="#e4e4e7" strokeWidth="2" />
      <path d={cut} fill="none" stroke="#22d3ee" strokeWidth="3" strokeLinecap="round" />
      {kind === "radius" && (
        <>
          <line x1="60" y1="50" x2="60" y2="110" stroke="#52525b" strokeWidth="1" strokeDasharray="3 3" />
          <line x1="60" y1="50" x2="120" y2="50" stroke="#52525b" strokeWidth="1" strokeDasharray="3 3" />
          <line x1="60" y1="50" x2="102" y2="92" stroke="#22d3ee" strokeWidth="1.5" />
        </>
      )}
      <text x="150" y={kind === "radius" ? 85 : 80} fill="#e4e4e7" fontSize="12" fontWeight="600">
        {CORNER_TITLES[kind] || "Corner"}
      </text>
    </svg>
  );
};

const CornerParams = ({
  feature,
  services,
  cutoutKinds,
  onChange,
  onRemove,
  onBack,
}) => {
  const rec = feature.record;
  const linked =
    rec.service_kind === "cutout"
      ? `cutout|${rec.service_name}`
      : rec.service_kind === "service"
        ? `service|${rec.service_id}`
        : "";
  return (
    <div
      className="mt-3 w-full space-y-4 rounded-lg border border-zinc-800 bg-zinc-950/70 p-3"
      data-testid="designer-corner-params"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="designer-corner-back"
          onClick={onBack}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white"
          title="Back to the piece"
        >
          <ArrowLeft size={14} weight="bold" />
        </button>
        <span className="text-sm font-bold text-zinc-100">Corner Parameters</span>
      </div>
      <div className="text-xs text-zinc-400">
        Corner Type:{" "}
        <span className="font-semibold text-cyan-300" data-testid="designer-corner-type">
          {CORNER_TITLES[feature.kind] || feature.kind}
        </span>
      </div>
      <div
        className="grid grid-cols-3 gap-2"
        data-testid="designer-corner-type-gallery"
      >
        {[
          ["radius", "radius", "Radius"],
          ["chamfer", "clip", "Clip"],
          ["notch", "notch", "Notch"],
        ].map(([kind, icon, label]) => {
          const on = feature.kind === kind;
          return (
            <button
              key={kind}
              type="button"
              data-testid={`designer-corner-type-${kind}`}
              onClick={() => !on && onChange({ kind })}
              className={`group flex flex-col items-center gap-1 rounded-lg border p-2 transition-colors duration-150 ${
                on
                  ? "border-cyan-400/70 bg-cyan-500/10"
                  : "border-zinc-700 bg-zinc-950 hover:border-indigo-400 hover:bg-indigo-500/10"
              }`}
            >
              <svg viewBox="0 0 24 24" className="h-9 w-9">
                <path
                  d={CORNER_ICON_PATH[icon]}
                  fill={on ? "rgba(34,211,238,0.15)" : "rgba(99,102,241,0.12)"}
                  stroke={on ? "#67e8f9" : "#a5b4fc"}
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className={`text-[10px] font-bold uppercase tracking-widest ${
                  on ? "text-cyan-200" : "text-zinc-300 group-hover:text-white"
                }`}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
      <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-1">
        <CornerDiagram kind={feature.kind} />
      </div>
      <div>
        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
          {feature.kind === "radius" ? "Radius" : "Size"}
        </span>
        <div className="relative mt-1">
          <MeasureInput
            data-testid="designer-corner-radius"
            min={0.5}
            fallback={0.5}
            value={rec.size ?? feature.size}
            onChange={(n) => onChange({ size: n })}
            className="h-10 border-zinc-800 bg-zinc-950 pr-8"
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500">
            in
          </span>
        </div>
      </div>
      <div>
        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
          Linked service
        </span>
        <select
          data-testid="designer-corner-service"
          value={linked}
          onChange={(e) => {
            const [kind, ref] = e.target.value.split("|");
            if (kind === "cutout")
              onChange({
                service_kind: "cutout",
                service_id: "",
                service_name: ref,
                service_price: 0,
                service_unit: "EA",
              });
            else if (kind === "service") {
              const s = services.find((x) => x.id === ref);
              onChange(
                s
                  ? {
                      service_kind: "service",
                      service_id: s.id,
                      service_name: s.name,
                      service_price: Number(s.cost_estimate) || 0,
                      service_unit: s.unit || "EA",
                    }
                  : { service_kind: "", service_id: "", service_name: "" },
              );
            } else
              onChange({
                service_kind: "",
                service_id: "",
                service_name: "",
                service_price: 0,
              });
          }}
          className="mt-1 h-10 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-100 outline-none focus:border-cyan-400"
        >
          <option value="">None</option>
          {cutoutKinds.length > 0 && (
            <optgroup label="Shop rates">
              {cutoutKinds.map((k) => (
                <option key={k} value={`cutout|${k}`}>
                  {k}
                </option>
              ))}
            </optgroup>
          )}
          {services.length > 0 && (
            <optgroup label="Services">
              {services.map((s) => (
                <option key={s.id} value={`service|${s.id}`}>
                  {s.name}
                  {s.cost_estimate
                    ? ` · $${Number(s.cost_estimate).toFixed(2)}/${s.unit || "EA"}`
                    : ""}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
      <button
        type="button"
        data-testid="designer-corner-remove"
        onClick={onRemove}
        className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-red-500/40 text-xs font-semibold text-red-300 transition-colors duration-150 hover:bg-red-500/10"
      >
        <Trash size={14} weight="bold" /> Remove · square the corner
      </button>
    </div>
  );
};

const dShapePath = (c) =>
  `M ${c.x} ${c.y} L ${c.x + c.w} ${c.y} L ${c.x + c.w} ${c.y + c.h * 0.4} ` +
  `A ${c.w / 2} ${c.h * 0.6} 0 0 1 ${c.x} ${c.y + c.h * 0.4} Z`;

const EDGE_LETTER = (i) => String.fromCharCode(65 + (i % 26));
// sheet margin around the drawing's 0,0 in the SVG view box (inches)
const VIEW_PAD = { x: -20, y: -12 };

// Panel sides in the on-sheet ghost, in rectangle-edge order used by withPanelPieces
// (0 top/exposed, 1 right end, 2 bottom/on the counter, 3 left end).
export const PANEL_SIDE_NAME = ["Top (exposed)", "Right end", "Bottom (on counter)", "Left end"];
// ghost polygon side s -> panel rectangle edge k
export const PANEL_GHOST_EDGE = [2, 1, 0, 3];

/** Backsplash and waterfall/mitre panels become their own pieces so they nest and print. */
export const withPanelPieces = (list) => {
  const mains = (list || []).filter((p) => !p.panel_kind);
  const existing = new Map(
    (list || []).filter((p) => p.panel_kind).map((p) => [p.id, p]),
  );
  const specsOut = [];
  mains.forEach((parent) => {
    (parent.edges || []).forEach((e, i) => {
      const length = Number(e.length_in) || 0;
      const splash = Number(e.backsplash_height_in) || 0;
      const drop = Number(e.drop_in) || 0;
      if (length <= 0) return;
      if (splash > 0)
        specsOut.push([parent, i, e, "backsplash", splash, "BS", "Backsplash"]);
      if (drop > 0)
        specsOut.push([
          parent,
          i,
          e,
          e.miter ? "mitre" : "waterfall",
          drop,
          e.miter ? "MT" : "WF",
          e.miter ? "Mitre panel" : "Waterfall",
        ]);
    });
  });

  // tidy row under everything already drawn, so panels never sit on a piece
  const bottom =
    Math.max(0, ...mains.map((p) => (p.y || 0) + (p.width_in || 0))) + 8;
  let cx = 4;
  let cy = bottom;
  let rowH = 0;

  const panels = specsOut.map(([parent, i, e, kind, height, abbr, word], n) => {
    const L = round2(Number(e.length_in) || 0);
    const H = round2(height);
    if (cx > 4 && cx + L > 136) {
      cx = 4;
      cy = round2(cy + rowH + 5);
      rowH = 0;
    }
    const x = cx;
    const y = cy;
    cx = round2(cx + L + 5);
    rowH = Math.max(rowH, H);
    const id = `${parent.id}-${kind}-${i}`;
    const prev = existing.get(id);
    return normalizePiece({
      ...(prev || {}),
      id,
      label: `${abbr}-${H}"`,
      panel_kind: kind,
      panel_of: parent.id,
      panel_edge: i,
      panel_no: n + 1,
      panel_note: `${word} · ${formatInches(H)} x ${formatInches(L)} · ${parent.label || "Run"} side ${EDGE_LETTER(i)}`,
      points: SHAPES.Rectangle(L, H),
      x,
      y,
      rot: 0,
      cutouts: [],
      backsplash_sqft: 0,
      // each side of the panel can carry its own profile (stored on the parent edge)
      edges: SHAPES.Rectangle(L, H).map((_, k) => ({
        profile: e.panel_edges?.[kind]?.[k] || e.profile || "Eased",
        seam: false,
      })),
    });
  });
  return [...mains, ...panels];
};

// Size + edge offsets shown while a cutout is selected in the Studio
const CutoutDims = ({ c, piece, col = "#fcd34d", fs = 1.7 }) => {
  const cx = c.x + c.w / 2;
  const cy = c.y + c.h / 2;
  const L = piece.length_in;
  const W = piece.width_in;
  const dim = (x1, y1, x2, y2, label, lx, ly, key) => (
    <g key={key}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={col} strokeWidth="0.18" strokeDasharray="1 0.8" />
      <text x={lx} y={ly} fill={col} fontSize={fs} textAnchor="middle" dominantBaseline="middle" className="select-none">
        {label}
      </text>
    </g>
  );
  return (
    <g className="pointer-events-none" data-testid={`designer-cutout-dims-${c.id}`}>
      <text x={cx} y={c.y + c.h + 2.4} fill={col} fontSize={fs} textAnchor="middle" className="select-none">
        {`${formatInches(c.w)} × ${formatInches(c.h)}`}
      </text>
      {dim(0, cy, c.x, cy, formatInches(c.x), c.x / 2, cy - 1.2, "l")}
      {dim(c.x + c.w, cy, L, cy, formatInches(L - c.x - c.w), (c.x + c.w + L) / 2, cy - 1.2, "r")}
      {dim(cx, 0, cx, c.y, formatInches(c.y), cx + 3.2, c.y / 2, "t")}
      {dim(cx, c.y + c.h, cx, W, formatInches(W - c.y - c.h), cx + 3.2, (c.y + c.h + W) / 2, "b")}
    </g>
  );
};

export const CutoutShape = ({
  c,
  piece,
  stroke = "#71717a",
  fill = "none",
  strokeWidth = 0.4,
  fontSize = 3,
  textFill,
}) => {
  const cx = c.x + c.w / 2;
  const cy = c.y + c.h / 2;
  const shape = c.shape || "Rectangle";
  const cornerR = Math.max(0, Number(c.radius ?? 1.5) || 0);
  const pen = { fill, stroke, strokeWidth };
  const txt = textFill || stroke;
  const holes = Math.min(Number(c.faucet_holes) || 0, 6);
  const holeGap = Number(c.faucet_spacing) > 0 ? Number(c.faucet_spacing) : 4;
  const holeY = c.y - 2.4 >= 1 ? c.y - 2.4 : c.y + 2.4;
  const isCooktop = /cook|range|hob|burner/i.test(`${c.kind || ""} ${c.name || ""}`);
  const burnerN = Math.min(Math.max(Number(c.burners) || 4, 1), 6);
  const burnerR = Math.min(c.w, c.h) * 0.14;
  const gx0 = c.x + c.w * 0.14;
  const gx1 = c.x + c.w * 0.86;
  const gxc = c.x + c.w * 0.5;
  const gyT = c.y + c.h * 0.22;
  const gyM = c.y + c.h * 0.5;
  const gyB = c.y + c.h * 0.72;
  const BURNERS = {
    1: [[gxc, gyM]],
    2: [[gx0, gyM], [gx1, gyM]],
    3: [[gx0, gyT], [gx1, gyT], [gxc, gyB]],
    4: [[gx0, gyT], [gx1, gyT], [gx0, gyB], [gx1, gyB]],
    5: [[gx0, gyT], [gx1, gyT], [gxc, gyM], [gx0, gyB], [gx1, gyB]],
    6: [[gx0, gyT], [gxc, gyT], [gx1, gyT], [gx0, gyB], [gxc, gyB], [gx1, gyB]],
  };
  const burnerCenters = isCooktop ? BURNERS[burnerN] || BURNERS[4] : [];
  const knobR = Math.max(burnerR * 0.16, 0.35);
  const knobStep = knobR * 2.6;
  const knobCx = gxc;
  const knobCy = c.y + c.h * 0.68;
  const knobCenters =
    isCooktop && ![3, 5].includes(burnerN)
      ? [[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy]) => [
          knobCx + (sx * knobStep) / 2,
          knobCy + (sy * knobStep) / 2,
        ])
      : [];
  const guides = [];
  if (piece && ["Left", "Right"].includes(c.center_h)) {
    const right = c.center_h === "Right";
    const from = right ? cx : 0;
    const to = right ? piece.length_in : cx;
    guides.push({
      key: "h",
      x1: from,
      y1: cy,
      x2: to,
      y2: cy,
      lx: (from + to) / 2,
      ly: cy - 1,
      label: formatInches(Math.abs(to - from)),
    });
  }
  if (piece && ["Top", "Bottom"].includes(c.center_v)) {
    const bottom = c.center_v === "Bottom";
    const from = bottom ? cy : 0;
    const to = bottom ? piece.width_in : cy;
    guides.push({
      key: "v",
      x1: cx,
      y1: from,
      x2: cx,
      y2: to,
      lx: cx + fontSize,
      ly: (from + to) / 2,
      label: formatInches(Math.abs(to - from)),
    });
  }
  return (
    <>
      {guides.map((g) => (
        <g key={g.key} className="pointer-events-none">
          <line
            x1={g.x1}
            y1={g.y1}
            x2={g.x2}
            y2={g.y2}
            stroke={stroke}
            strokeWidth={strokeWidth * 0.7}
            strokeDasharray="4 2 1 2"
          />
          <text
            x={g.lx}
            y={g.ly}
            fill={txt}
            fontSize={fontSize * 0.8}
            textAnchor="middle"
            className="select-none"
          >
            {g.label}
          </text>
        </g>
      ))}
      <g transform={`rotate(${Number(c.rot) || 0} ${cx} ${cy})`}>
        {shape === "Oval" ? (
          <ellipse cx={cx} cy={cy} rx={c.w / 2} ry={c.h / 2} {...pen} />
        ) : shape === "Round" ? (
          <circle cx={cx} cy={cy} r={Math.min(c.w, c.h) / 2} {...pen} />
        ) : shape === "D-shape" ? (
          <path d={dShapePath(c)} {...pen} />
        ) : BOWL_SPLIT[shape] ? (
          (() => {
            const gap = Math.min(1.5, c.w * 0.05);
            const left = (c.w - gap) * BOWL_SPLIT[shape];
            const right = c.w - gap - left;
            const rr = Math.min(cornerR, left / 2, right / 2, c.h / 2);
            return (
              <>
                <rect
                  x={c.x}
                  y={c.y}
                  width={left}
                  height={c.h}
                  rx={rr}
                  {...pen}
                />
                <rect
                  x={c.x + left + gap}
                  y={c.y}
                  width={right}
                  height={c.h}
                  rx={rr}
                  {...pen}
                />
              </>
            );
          })()
        ) : (
          <rect x={c.x} y={c.y} width={c.w} height={c.h} rx={Math.min(cornerR, c.w / 2, c.h / 2)} {...pen} />
        )}
        {isCooktop ? (
          <>
            {burnerCenters.map((bc, k) => (
              <g key={`b${k}`}>
                <circle
                  cx={bc[0]}
                  cy={bc[1]}
                  r={burnerR}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={strokeWidth * 1.2}
                />
                <circle
                  cx={bc[0]}
                  cy={bc[1]}
                  r={burnerR * 0.45}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                />
              </g>
            ))}
            {knobCenters.map((kc, k) => (
              <circle key={`k${k}`} cx={kc[0]} cy={kc[1]} r={knobR} fill={stroke} />
            ))}
          </>
        ) : (
          Array.from({ length: holes }).map((_, i) => (
            <circle
              key={i}
              cx={cx + (i - (holes - 1) / 2) * holeGap}
              cy={holeY}
              r="0.7"
              fill="none"
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
          ))
        )}
        <text
          x={cx}
          y={cy + fontSize * 0.4}
          fill={txt}
          fontSize={fontSize}
          textAnchor="middle"
          className="pointer-events-none select-none"
        >
          {c.kind}
        </text>
      </g>
    </>
  );
};

const SHAPE_PATH = {
  eased: "M2 3 H22 V11 H2 Z",
  bevel: "M2 3 H18 L22 7 V11 H2 Z",
  "half-bullnose": "M2 3 H18 Q22 3 22 7 V11 H2 Z",
  bullnose: "M2 3 H16 Q22 3 22 7 Q22 11 16 11 H2 Z",
  ogee: "M2 3 H16 Q20 3 18 7 Q16 11 22 11 H2 Z",
  "round-over": "M2 3 H19 Q22 3 22 6 V11 H2 Z",
  "double-round-over": "M2 3 H19 Q22 3 22 6 Q22 9 19 11 H2 Z",
  mitered: "M2 3 H22 V11 H18 L18 5 H2 Z",
  chisel: "M2 3 H18 L22 5 L18 7 L22 9 L18 11 H2 Z",
  pencil: "M2 3 H20 Q22 5 22 7 Q22 9 20 11 H2 Z",
  cove: "M2 3 H22 Q18 7 22 11 H2 Z",
  waterfall: "M2 3 H22 V11 H14 V14 H10 V11 H2 Z",
  unfinished: "M2 3 H22 V11 H2 Z",
};

export const DesignerPlan = ({
  pieces,
  light = false,
  edgeStyles = [],
  legend = true,
  title = null,
  hideCutoutDims = false,
}) => {
  // panel pieces (backsplash / waterfall / mitre) are drawn beside the side they belong to
  const list = pieces.filter((p) => !p.panel_kind).map(normalizePiece);
  const panels = pieces.filter((p) => p.panel_kind);
  const styleFor = (name) => (edgeStyles || []).find((s) => s.name === name);
  const mid = useMemo(() => Math.random().toString(36).slice(2, 7), []);
  if (!list.length) return null;
  const w = Math.max(24, ...list.map((p) => p.x + p.length_in));
  const h = Math.max(16, ...list.map((p) => p.y + p.width_in));
  const PANEL_GAP = 9;
  // every panel drawn beside its parent edge, in the parent's local frame
  const panelGhosts = panels
    .map((pn) => {
      const parent = list.find((p) => p.id === pn.panel_of);
      const i = Number(pn.panel_edge) || 0;
      const pts = parent?.points || [];
      const H = Number(pn.width_in) || 0;
      if (!parent || pts.length < 3 || i >= pts.length || H <= 0) return null;
      const A = pts[i];
      const B = pts[(i + 1) % pts.length];
      const [nx, ny] = outwardNormal(pts, i);
      const poly = [
        [A[0] + nx * PANEL_GAP, A[1] + ny * PANEL_GAP],
        [B[0] + nx * PANEL_GAP, B[1] + ny * PANEL_GAP],
        [B[0] + nx * (PANEL_GAP + H), B[1] + ny * (PANEL_GAP + H)],
        [A[0] + nx * (PANEL_GAP + H), A[1] + ny * (PANEL_GAP + H)],
      ];
      return { ...pn, parent, poly, reach: PANEL_GAP + H };
    })
    .filter(Boolean);
  const panelReach = Math.max(0, ...panelGhosts.map((g) => g.reach));
  const planTag = (p, i) => {
    const e = p.edges[i] || {};
    const len = round2(edgeLen(p.points, i));
    const extra = [
      Number(e.backsplash_height_in) > 0
        ? `+${formatInches(e.backsplash_height_in)} bs`
        : "",
      Number(e.drop_in) > 0 ? `${formatInches(e.drop_in)} drop` : e.miter ? "mitre" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const text = `${SIDE_LETTER(i)}: ${formatInches(len)}${extra ? ` · ${extra}` : ""}`;
    return { len, text, tw: text.length * 1.18 + 2.6 };
  };
  // keep the side tags inside the picture, however long the labels are
  let MX = 13;
  let MY = 13;
  list.forEach((p) =>
    p.points.forEach((a, i) => {
      const b = p.points[(i + 1) % p.points.length];
      const { len, tw } = planTag(p, i);
      if (len <= 6) return;
      if (Math.abs(b[1] - a[1]) > Math.abs(b[0] - a[0]))
        MX = Math.max(MX, tw + 7);
    }),
  );
  // leave room for appliance footprints drawn outside the counter outline
  if (list.some((p) => (p.edges || []).some((e) => e.appliance))) {
    MX = Math.max(MX, 34);
    MY = Math.max(MY, 34);
  }
  if (panelReach > 0) {
    MX = Math.max(MX, panelReach + 6);
    MY = Math.max(MY, panelReach + 6);
  }
  const C = light
    ? {
        fill: "rgba(37,99,235,0.10)",
        stroke: "#1d4ed8",
        text: "#334155",
        seamText: "#b45309",
        cut: "#475569",
        label: "#0f172a",
        dim: "#94a3b8",
        tagFill: "#ffffff",
        tagStroke: "#94a3b8",
        tagText: "#0f172a",
      }
    : {
        fill: "rgba(99,102,241,0.14)",
        stroke: "#818cf8",
        text: "#52525b",
        seamText: "#b45309",
        cut: "#71717a",
        label: "#a1a1aa",
        dim: "#8b8b93",
        tagFill: "#0c0c0f",
        tagStroke: "#71717a",
        tagText: "#e4e4e7",
      };
  // key printed under the drawing: only what this drawing actually uses
  const legendItems = [];
  const seenProfile = new Set();
  list.forEach((p) =>
    (p.edges || []).forEach((e) => {
      if (e.seam) return;
      const name = e.profile || "Unfinished";
      if (seenProfile.has(name)) return;
      seenProfile.add(name);
      const st = styleFor(name);
      legendItems.push({
        edge: true,
        abbr: st?.alias || st?.abbr || "",
        name,
        shape: st?.shape || "eased",
        color: e.color || st?.color || C.stroke,
      });
    }),
  );
  const flag = (test, label, color, dash) => {
    if (list.some(test)) legendItems.push({ label, color, dash });
  };
  flag((p) => (p.edges || []).some((e) => e.seam), "Seam", SEAM, "3 2");
  flag((p) => (p.edges || []).some((e) => e.miter), "Mitre", "#0891b2");
  if (panelGhosts.length)
    legendItems.push({ label: "Backsplash / waterfall panel", color: "#60a5fa" });
  flag((p) => (p.cutouts || []).length > 0, "Cut-out", C.cut, "3 2");
  flag(
    (p) => (p.edges || []).some((e) => e.appliance === "fridge"),
    "Fridge",
    "#22d3ee",
  );
  flag(
    (p) => (p.edges || []).some((e) => e.appliance === "range"),
    "Stove",
    "#f97316",
  );
  flag(
    (p) => (p.edges || []).some((e) => e.appliance === "dishwasher"),
    "Dishwasher",
    "#a78bfa",
  );
  flag(
    (p) => (p.edges || []).some((e) => e.appliance === "hood"),
    "Hood",
    "#f472b6",
  );
  const legendColor = light ? "#475569" : "#a1a1aa";
  const APP_COLOR = {
    fridge: "#22d3ee",
    range: "#f97316",
    dishwasher: "#a78bfa",
    hood: "#f472b6",
  };
  const APP_LABEL = {
    fridge: "Fridge",
    range: "Stove",
    dishwasher: "D/W",
    hood: "Hood",
  };
  const appDims = (e, len) => {
    const wv = Number(e.appliance_width) || 0;
    if (e.appliance === "fridge") {
      const d =
        e.appliance_height === "tall"
          ? 25
          : e.appliance_height === "counter"
            ? 26
            : 30;
      return [wv || Math.min(42, Math.max(30, len)), d];
    }
    if (e.appliance === "range") return [wv || Math.min(36, Math.max(24, len)), 26];
    if (e.appliance === "dishwasher") return [wv || 24, 24];
    if (e.appliance === "hood") return [wv || 30, 20];
    return null;
  };
  const applianceFootprint = (pts, i, e) => {
    const dims = appDims(e, round2(edgeLen(pts, i)));
    if (!dims) return null;
    const [W, D] = dims;
    const n = pts.length;
    const A = pts[i];
    const B = pts[(i + 1) % n];
    const L = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
    const ex = (B[0] - A[0]) / L;
    const ey = (B[1] - A[1]) / L;
    let nx = -ey;
    let ny = ex;
    const gx = pts.reduce((s, p) => s + p[0], 0) / n;
    const gy = pts.reduce((s, p) => s + p[1], 0) / n;
    const mx = (A[0] + B[0]) / 2;
    const my = (A[1] + B[1]) / 2;
    if ((mx - gx) * nx + (my - gy) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    let ux = ex;
    let uy = ey;
    let vx = nx;
    let vy = ny;
    const f = e.appliance_facing || "out";
    if (f === "in") {
      ux = -ex; uy = -ey; vx = -nx; vy = -ny;
    } else if (f === "left") {
      ux = nx; uy = ny; vx = -ex; vy = -ey;
    } else if (f === "right") {
      ux = -nx; uy = -ny; vx = ex; vy = ey;
    }
    const hw = W / 2;
    const corner = (sx, dz) => [mx + ux * sx + vx * dz, my + uy * sx + vy * dz];
    const c1 = corner(-hw, 0);
    const c2 = corner(hw, 0);
    const c3 = corner(hw, D);
    const c4 = corner(-hw, D);
    return {
      poly: [c1, c2, c3, c4]
        .map(([x, y]) => `${round2(x)},${round2(y)}`)
        .join(" "),
      cx: (c1[0] + c3[0]) / 2,
      cy: (c1[1] + c3[1]) / 2,
    };
  };

  return (
    <div className="w-full">
      <svg
        data-testid="designer-plan"
        viewBox={`${-MX} ${-MY} ${w + MX * 2} ${h + MY * 2}`}
        className="w-full"
        style={{ aspectRatio: `${w + MX * 2} / ${h + MY * 2}` }}
      >
        <defs>
          <marker
            id={`plan-dim-end-${mid}`}
            markerUnits="userSpaceOnUse"
            markerWidth="3"
            markerHeight="3"
            refX="2.7"
            refY="1.5"
            orient="auto"
          >
            <path d="M0,0 L3,1.5 L0,3 z" fill={C.dim} />
          </marker>
          <marker
            id={`plan-dim-start-${mid}`}
            markerUnits="userSpaceOnUse"
            markerWidth="3"
            markerHeight="3"
            refX="0.3"
            refY="1.5"
            orient="auto"
          >
            <path d="M3,0 L0,1.5 L3,3 z" fill={C.dim} />
          </marker>
        </defs>
        {list.map((p) => (
          <g
            key={p.id}
            transform={`translate(${p.x} ${p.y}) rotate(${p.rot} ${p.length_in / 2} ${p.width_in / 2})`}
          >
            <path
              d={piecePathD(p)}
              fill={C.fill}
              stroke={C.dim}
              strokeWidth="0.25"
              strokeLinejoin="round"
            />
            {p.points.map((a, i) => {
              const b = p.points[(i + 1) % p.points.length];
              const e = p.edges[i] || {};
              const len = round2(edgeLen(p.points, i));
              if (len <= 6 && !isArcEdge(p, i)) return null;
              return (
                <g key={i}>
                  {!e.seam && (
                    <path
                      d={edgePathD(p, i)}
                      fill="none"
                      strokeLinecap="round"
                      stroke={
                        e.appliance
                          ? APP_COLOR[e.appliance] || "#22d3ee"
                          : e.color || styleFor(e.profile)?.color || C.stroke
                      }
                      strokeWidth={e.appliance ? "0.9" : "0.6"}
                    />
                  )}
                  {e.appliance &&
                    (() => {
                      const fp = applianceFootprint(p.points, i, e);
                      if (!fp) return null;
                      const col = APP_COLOR[e.appliance] || "#22d3ee";
                      return (
                        <g data-testid={`plan-appliance-${p.id}-${i}`}>
                          <polygon
                            points={fp.poly}
                            fill={`${col}22`}
                            stroke={col}
                            strokeWidth="0.5"
                            strokeDasharray="2 1.5"
                          />
                          <text
                            x={fp.cx}
                            y={fp.cy}
                            fill={col}
                            fontSize="4"
                            fontWeight="700"
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            {APP_LABEL[e.appliance]}
                          </text>
                        </g>
                      );
                    })()}
                  {e.seam &&
                    segmentMinusBoxes(a, b, cutoutBoxes(p, list)).map(
                      ([sa, sb], si) => (
                        <line
                          key={si}
                          x1={sa[0]}
                          y1={sa[1]}
                          x2={sb[0]}
                          y2={sb[1]}
                          stroke={SEAM}
                          strokeWidth="0.6"
                        />
                      ),
                    )}
                  {e.miter && (
                    <line
                      {...miterLine(p.points, i)}
                      stroke="#0891b2"
                      strokeWidth="0.5"
                    />
                  )}
                  {(() => {
                    const g = dimGeom(p.points, i, 5.5);
                    const { text, tw } = planTag(p, i);
                    const vertical =
                      Math.abs(b[1] - a[1]) > Math.abs(b[0] - a[0]);
                    const [nx, ny] = outwardNormal(p.points, i);
                    const push = 2.6;
                    const lx = g.om[0] + nx * push;
                    const ly = g.om[1] + ny * push;
                    // side dimensions run along the edge so the pill sits between counter and panel
                    const rot = vertical ? `rotate(-90 ${lx} ${ly})` : undefined;
                    return (
                      <g data-testid={`plan-dim-${p.id}-${i}`}>
                        <line
                          x1={g.a[0]}
                          y1={g.a[1]}
                          x2={g.oa[0]}
                          y2={g.oa[1]}
                          stroke={C.dim}
                          strokeWidth="0.15"
                        />
                        <line
                          x1={g.b[0]}
                          y1={g.b[1]}
                          x2={g.ob[0]}
                          y2={g.ob[1]}
                          stroke={C.dim}
                          strokeWidth="0.15"
                        />
                        <line
                          x1={g.oa[0]}
                          y1={g.oa[1]}
                          x2={g.ob[0]}
                          y2={g.ob[1]}
                          stroke={C.dim}
                          strokeWidth="0.2"
                          markerStart={`url(#plan-dim-start-${mid})`}
                          markerEnd={`url(#plan-dim-end-${mid})`}
                        />
                        <g transform={rot}>
                        <text
                          x={lx}
                          y={ly + 0.85}
                          fill={e.seam ? C.seamText : C.tagText}
                          fontSize="2.4"
                          fontWeight="600"
                          letterSpacing="0.15"
                          textAnchor="middle"
                        >
                          {text}
                        </text>
                        </g>
                      </g>
                    );
                  })()}
                </g>
              );
            })}
            {(p.cutouts || []).map((c) => (
              <g key={c.id}>
                <CutoutShape c={c} piece={hideCutoutDims ? null : p} stroke={C.cut} fontSize={3} />
                {!hideCutoutDims && <CutoutDims c={c} piece={p} col={C.cut} fs={2.2} />}
              </g>
            ))}
            {panelGhosts
              .filter((g) => g.parent.id === p.id)
              .map((g) => {
                const cx = (g.poly[0][0] + g.poly[2][0]) / 2;
                const cy = (g.poly[0][1] + g.poly[2][1]) / 2;
                const [ax, ay] = g.poly[0];
                const [bx, by] = g.poly[1];
                let ang = (Math.atan2(by - ay, bx - ax) * 180) / Math.PI;
                if (ang > 90 || ang <= -90) ang += 180;
                return (
                  <g key={g.id} data-testid={`plan-panel-${g.id}`}>
                    <polygon
                      points={g.poly.map(([x, y]) => `${x},${y}`).join(" ")}
                      fill={light ? "rgba(96,165,250,0.12)" : "rgba(96,165,250,0.10)"}
                      stroke="#60a5fa"
                      strokeWidth="0.45"
                    />
                    {g.poly.map((a, s) => {
                      const b = g.poly[(s + 1) % 4];
                      const prof = g.edges?.[PANEL_GHOST_EDGE[s]]?.profile;
                      const col = styleFor(prof)?.color;
                      return col ? (
                        <line key={s} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={col} strokeWidth="0.6" />
                      ) : null;
                    })}
                    <text
                      x={cx}
                      y={cy}
                      fill={light ? "#1e40af" : "#93c5fd"}
                      fontSize={Math.min(3.2, Math.max(2, (Number(g.width_in) || 4) * 0.55))}
                      fontWeight="700"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      transform={`rotate(${(Number(g.width_in) || 0) < 8 ? ang : 0} ${cx} ${cy})`}
                    >
                      {`${g.label} | #${g.panel_no}`}
                    </text>
                  </g>
                );
              })}
            <text x="2" y="6" fill={C.label} fontSize="4">
              {p.label}
              {uprightTag(p)}
            </text>
          </g>
        ))}
        {title && (title.project || title.customer) && (() => {
          const rows = [
            title.project ? ["Project", title.project] : null,
            title.customer ? ["Customer", title.customer] : null,
            title.area ? ["Area", title.area] : null,
            ["Scale", `${title.scale || "NTS"}${title.date ? `  ·  ${title.date}` : ""}`],
          ].filter(Boolean);
          const bw = 54;
          const lh = 4.6;
          const bh = 3 + rows.length * lh;
          const bx = w + MX - bw - 1;
          const by = h + MY - bh - 1;
          return (
            <g data-testid="plan-title-block">
              <rect
                x={bx}
                y={by}
                width={bw}
                height={bh}
                fill={light ? "#ffffff" : "#0c0c0f"}
                stroke={C.stroke}
                strokeWidth="0.35"
              />
              {rows.map((r, i) => {
                const ry = by + 3.4 + i * lh;
                return (
                  <g key={i}>
                    {i > 0 && (
                      <line
                        x1={bx}
                        y1={ry - lh + 1.6}
                        x2={bx + bw}
                        y2={ry - lh + 1.6}
                        stroke={C.dim}
                        strokeWidth="0.12"
                      />
                    )}
                    <text x={bx + 1.6} y={ry} fontSize="2.2" fill={C.dim}>
                      {r[0]}
                    </text>
                    <text
                      x={bx + bw - 1.6}
                      y={ry}
                      fontSize="2.7"
                      fontWeight="600"
                      textAnchor="end"
                      fill={C.label}
                    >
                      {String(r[1]).slice(0, 24)}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })()}
      </svg>
      {legend && legendItems.length > 0 && (
        <div
          data-testid="plan-legend"
          className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t pt-2 text-[10px]"
          style={{ color: legendColor, borderColor: light ? "#e2e8f0" : "#27272a" }}
        >
          <span className="mr-1 text-[9px] font-bold uppercase tracking-[0.2em] opacity-60">Key</span>
          {legendItems.map((it, idx) =>
            it.edge ? (
              <span
                key={`edge-${idx}`}
                data-testid={`plan-legend-edge-${idx}`}
                className="inline-flex items-center gap-1.5"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: it.color }}
                />
                {it.abbr && <span className="font-bold">{it.abbr}</span>}
                <span>{it.name}</span>
                <svg
                  width="22"
                  height="13"
                  viewBox="0 0 24 14"
                  className="shrink-0"
                  aria-hidden="true"
                >
                  <rect
                    width="24"
                    height="14"
                    rx="2"
                    fill={light ? "#ffffff" : "#09090b"}
                    stroke={light ? "#cbd5e1" : "#3f3f46"}
                    strokeWidth="0.75"
                  />
                  <path
                    d={SHAPE_PATH[it.shape] || SHAPE_PATH.eased}
                    fill={it.shape === "unfinished" ? "none" : `${it.color}33`}
                    stroke={it.color}
                    strokeWidth="1"
                    strokeDasharray={it.shape === "unfinished" ? "2 1.5" : undefined}
                  />
                </svg>
              </span>
            ) : (
              <span
                key={`flag-${idx}`}
                className="inline-flex items-center gap-1.5"
              >
                <svg width="18" height="4" viewBox="0 0 18 4" aria-hidden="true">
                  <line
                    x1="0"
                    y1="2"
                    x2="18"
                    y2="2"
                    stroke={it.color}
                    strokeWidth="2"
                    strokeDasharray={it.dash}
                  />
                </svg>
                {it.label}
              </span>
            ),
          )}
        </div>
      )}
      {legend && panels.length > 0 && (
        <div
          data-testid="plan-panel-schedule"
          className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px]"
          style={{ color: legendColor }}
        >
          <span className="font-bold uppercase tracking-widest">Panels</span>
          {panels.map((p) => (
            <span key={p.id} data-testid={`plan-panel-${p.id}`}>
              {p.panel_note || p.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const CountertopDesignerInner = ({
  pieces,
  edgeProfiles = [],
  edgeStyles = [],
  onChange,
  layoutBoard = null,
  areaName = "",
  onAreaName,
  areas = [],
  currentAreaId = "",
  onMovePiece,
  products = [],
  services = [],
  cutoutKinds = [],
  cutoutColors = {},
  cutoutSizes = {},
  lineWeight = 0.12,
  cutoutKits = [],
  cutoutTemplates = [],
  onSaveCutoutTemplate,
  onDeleteCutoutTemplate,
  onRenameCutoutTemplate,
  onSetCutoutTemplatePhoto,
  customLayouts = [],
  onSaveCustomLayout,
  onDeleteCustomLayout,
  onReview,
  walls = [],
  notes = [],
  onNotesChange,
  canvasOnly = false,
  onCanvasOnly,
  onWallsChange,
  general = {},
  canvasHeight = "58vh",
  trace = null,
  onTraceChange,
  onUploadTrace,
  hiddenSteps = [],
  savedLayouts = [],
  onSaveLayout,
  onDeleteLayout,
  onRenameLayout,
  onGoToLayout,
  liveTotal,
  expectedRate,
  billedSqft,
  rateBand,
  stoneGroup = "",
  materialMissing = false,
  onPickMaterial,
  onOpenQuote,
  onSendToQuote,
}) => {
  const [sel, setSel] = useState(null); // {pieceId, edge}
  const [sketch, setSketch] = useState(() => {
    try {
      return localStorage.getItem("bgw_designer_mode") === "sketch";
    } catch (err) {
      return false;
    }
  });
  const [free, setFree] = useState(() => {
    try {
      return localStorage.getItem("bgw_designer_mode") !== "guided";
    } catch (err) {
      return true;
    }
  });
  const [focus, setFocus] = useState(false);
  const [hoverPiece, setHoverPiece] = useState(null);
  const [hoverEdge, setHoverEdge] = useState(null); // {pieceId, edge} — highlight edge on hover
  const [step, setStep] = useState("dimensions");
  const [polySnap, setPolySnap] = useState(90); // 0 = any angle, else degrees
  const [freeAngle, setFreeAngle] = useState(false); // Alt held = draw at any angle
  const [traceMove, setTraceMove] = useState(false);
  const traceRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState(null); // {pieceId, left, top} in %
  const [ctxStep, setCtxStep] = useState(1); // nudge step (inches) for the piece menu
  const clearanceMin = (() => {
    const quartz = /quartz|engineered|porcelain|sintered/i.test(stoneGroup || "");
    const v = quartz ? general?.cutout_clearance_quartz_in : general?.cutout_clearance_stone_in;
    return v == null || v === "" ? 3 : Math.max(0, Number(v) || 0);
  })();
  // nearest spot (from the cut-out's own position) that clears every edge & seam by clearanceMin
  const nudgeClear = (piece, c, seamItems) => {
    if (clearanceMin <= 0) return null;
    let best = null;
    for (let y = 0; y <= piece.width_in - c.h; y += 0.5)
      for (let x = 0; x <= piece.length_in - c.w; x += 0.5) {
        const d = Math.hypot(x - c.x, y - c.y);
        if (best && d >= best.d) continue;
        if (!cutFits(piece.points, x, y, c.w, c.h)) continue;
        if (cutClearance(piece, { ...c, x, y }, seamItems).gap < clearanceMin) continue;
        best = { x: round2(x), y: round2(y), d };
      }
    return best;
  };
  const [cornerMenu, setCornerMenu] = useState(null); // {pieceId, vertex, left, top}
  const [ctxAngle, setCtxAngle] = useState(22.5);
  const [spaceDown, setSpaceDown] = useState(false);
  const spaceRef = useRef(false);
  const panRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [legChip, setLegChip] = useState(null); // {L, x, y} — live leg length while pulling a corner
  const [snap, setSnap] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [showDims, setShowDims] = useState(true);
  const [lStripLock, setLStripLock] = useState(true); // L-shape: keep each leg's depth when a length is typed (off = each side edits freely)
  const [ucapMirror, setUcapMirror] = useState(true); // angled U-band: mirror left/right legs when editing one
  const [gridStyle, setGridStyleState] = useState(
    () => localStorage.getItem("bgw_designer_grid") || "dots",
  );
  const setGridStyle = (v) => {
    setGridStyleState(v);
    try {
      localStorage.setItem("bgw_designer_grid", v);
    } catch {
      /* ignore */
    }
  };
  const [snapPieces, setSnapPieces] = useState(true);
  const [showTools, setShowTools] = useState(true);
  const [showPanel, setShowPanel] = useState(true);
  const [showDraw, setShowDraw] = useState(true);
  const [savingLayout, setSavingLayout] = useState(false);
  const [layoutName, setLayoutName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [renamingTplId, setRenamingTplId] = useState(null);
  const [renameTplName, setRenameTplName] = useState("");
  const confirmTplRename = () => {
    const name = (renameTplName || "").trim();
    if (!name || !renamingTplId) return;
    onRenameCutoutTemplate?.(renamingTplId, name);
    setRenamingTplId(null);
  };
  const [showHelp, setShowHelp] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const coachRef = useRef(false);
  const styleFor = (profile) =>
    (edgeStyles || []).find((s) => s.name === profile);
  const [labelEdit, setLabelEdit] = useState(null);
  // Back arrow in Bump Parameters: show the picker for this bump (same side) instead of deselecting.
  const [pickerFor, setPickerFor] = useState(null);
  useEffect(() => setPickerFor(null), [sel?.pieceId, sel?.edge]);
  const [bumpPresets, setBumpPresets] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bgw_bump_presets") || "[]");
    } catch (err) {
      return [];
    }
  });
  const savePresets = (next) => {
    setBumpPresets(next);
    try {
      localStorage.setItem("bgw_bump_presets", JSON.stringify(next));
    } catch (err) {
      /* ignore */
    }
  };
  const usedBumpKinds = useMemo(() => {
    const seen = [];
    (pieces || []).forEach((p) =>
      (p.bumps || []).forEach((b) => {
        if (b.kind && !seen.includes(b.kind)) seen.push(b.kind);
      }),
    );
    return seen;
  }, [pieces]);
  const [cornerSize, setCornerSize] = useState(4);
  const [cornerAngle, setCornerAngle] = useState(45);
  const [setback, setSetback] = useState(3);
  const [tplName, setTplName] = useState(null);
  const [tplPicker, setTplPicker] = useState(null); // null | {cutoutId?} — cutout template popup
  const hiddenLayouts = useHiddenLayouts(useState);
  const [customName, setCustomName] = useState(null);
  const [customCut, setCustomCut] = useState(null); // {name, w, h} — custom cut-out form
  const addCustomCutout = () => {
    const name = (customCut?.name || "").trim();
    if (!name) return;
    addCutout(name, [Math.max(0.25, Number(customCut.w) || 24), Math.max(0.25, Number(customCut.h) || 18)]);
    setCustomCut(null);
  };
  const [draw, setDraw] = useState(null); // {kind:"run"|"wall"|"poly"|"line", pts, cursor}
  const [runWidth, setRunWidth] = useState(25.5);
  const [runAngle, setRunAngle] = useState(90); // corner angle for the next turn of a drawn run
  const [lineLen, setLineLen] = useState(25);
  const [lineAngle, setLineAngle] = useState(0);
  const [dynPx, setDynPx] = useState(null); // screen px of the cursor while drawing by length
  const lnScale = Math.max(0.15, Math.min(4, (Number(lineWeight) || 0.5) / 0.5)); // line-weight setting as a scale (0.5 = normal)
  const lnW = (base) => Math.round(base * lnScale * 1000) / 1000;
  const dimLineW = lnW(0.12);
  const [lineSnap, setLineSnap] = useState(true);
  const [wallSide, setWallSide] = useState("right");
  // amber seam guides for the selected piece — as many as you like, each moved on its own
  const [seamsFor, setSeamsFor] = useState({ pieceId: null, items: [] });
  const [activeSeam, setActiveSeam] = useState(null);
  const [rejoinFly, setRejoinFly] = useState(null);
  useEffect(() => {
    if (!rejoinFly || rejoinFly.on) return undefined;
    const raf = requestAnimationFrame(() =>
      setRejoinFly((f) => (f && !f.on ? { ...f, on: true } : f)),
    );
    return () => cancelAnimationFrame(raf);
  }, [rejoinFly]);
  const seams =
    seamsFor.pieceId && seamsFor.pieceId === sel?.pieceId ? seamsFor.items : [];
  const putSeams = (pieceId, items) => setSeamsFor({ pieceId, items });
  const editSeams = (fn) =>
    setSeamsFor((cur) => ({ ...cur, items: fn(cur.items) }));
  const [showSeamGuide, setShowSeamGuide] = useState(false);
  const [seamSnap, setSeamSnap] = useState(null);
  const [cutoutGuide, setCutoutGuide] = useState(null);
  const [snapGuide, setSnapGuide] = useState(null); // {x?, y?, bx, by} — green flush guide while dragging a piece
  const [sheetOpen, setSheetOpen] = useState(false); // phones: tool panel as a bottom sheet
  const slabL = Math.max(1, Number(general?.slab_length_in) || 126);
  const slabW = Math.max(1, Number(general?.slab_width_in) || 63);
  const fitsSlabDims = (L, W) => {
    const bigS = Math.max(slabL, slabW);
    const smallS = Math.min(slabL, slabW);
    const bigP = Math.max(L, W);
    const smallP = Math.min(L, W);
    return bigP <= bigS + 0.01 && smallP <= smallS + 0.01;
  };
  const maxSegment = (span, ats) => {
    const m = [0, ...ats.filter((a) => a > 0 && a < span), span].sort(
      (a, b) => a - b,
    );
    let mx = 0;
    for (let i = 0; i < m.length - 1; i++) mx = Math.max(mx, m[i + 1] - m[i]);
    return mx || span;
  };
  const pieceNeedsSeam = (p) => {
    const isSel = p.id === sel?.pieceId;
    const vAts = isSel
      ? seams.filter((s) => s.dir === "v").map((s) => Number(s.at))
      : [];
    const hAts = isSel
      ? seams.filter((s) => s.dir === "h").map((s) => Number(s.at))
      : [];
    return !fitsSlabDims(
      maxSegment(p.length_in, vAts),
      maxSegment(p.width_in, hAts),
    );
  };
  const isSinkKind = (k) => /sink/i.test(k || "");
  const isAccessoryKind = (k) => /faucet|soap/i.test(k || "");
  const seamCharge = Math.max(0, Number(general?.seam_charge) || 0);
  const kits = cutoutKits && cutoutKits.length ? cutoutKits : DEFAULT_KITS;
  const [histLen, setHistLen] = useState([0, 0]);
  const past = useRef([]);
  const future = useRef([]);
  const movedRef = useRef(false);
  const svgRef = useRef(null);
  const viewRef = useRef(null);

  // backsplash / waterfall / mitre panels are generated behind the scenes for the slab
  // layout and the quote — they are never edited on the sheet. With "Show physical
  // pieces" on, each one is drawn beside the side it belongs to (display only).
  const list = useMemo(
    () => pieces.filter((p) => !p.panel_kind).map(normalizePiece),
    [pieces],
  );
  const panelPieces = useMemo(() => {
    // Ghosts are display-only normally, but in the Shape, Edges and Cut-outs steps we show
    // them (even without "physical pieces" on) so a backsplash / waterfall can be seen beside
    // its side, and tapped to detach and edit — curves & bumps and cut-outs then apply to it.
    const editable = step === "shape" || step === "edges" || step === "cutouts";
    if (general?.attached_pieces !== "physical" && !editable) return [];
    const byId = Object.fromEntries(list.map((p) => [p.id, p]));
    const gap = 7.5;
    return withPanelPieces(list)
      .filter((p) => p.panel_kind)
      .map((pn) => {
        const parent = byId[pn.panel_of];
        const pts = parent?.points || [];
        const i = Number(pn.panel_edge) || 0;
        if (!parent || pts.length < 3 || i >= pts.length) return null;
        if (parent.edges?.[i]?.hide_panel) return null;
        const A = pts[i];
        const B = pts[(i + 1) % pts.length];
        const [nx, ny] = outwardNormal(pts, i);
        const H = Number(pn.width_in) || 0;
        const poly = [
          [A[0] + nx * gap, A[1] + ny * gap],
          [B[0] + nx * gap, B[1] + ny * gap],
          [B[0] + nx * (gap + H), B[1] + ny * (gap + H)],
          [A[0] + nx * (gap + H), A[1] + ny * (gap + H)],
        ];
        const cx = (poly[0][0] + poly[2][0]) / 2;
        const cy = (poly[0][1] + poly[2][1]) / 2;
        let ang = (Math.atan2(B[1] - A[1], B[0] - A[0]) * 180) / Math.PI;
        if (ang > 90 || ang <= -90) ang += 180;
        return {
          ...pn,
          parent,
          poly,
          cx,
          cy,
          textRot: H < 8 ? ang : -(Number(parent.rot) || 0),
        };
      })
      .filter(Boolean);
  }, [list, general, step]);
  const tag = useMemo(() => makeTag(general), [general]);
  const dimMode = general?.measurement_display || "segments";

  // Which side labels the drawing shows, per the Drawing settings display mode
  const showSide = (p, i) => {
    if (dimMode === "segments" || dimMode === "combined") return true;
    const len = edgeLen(p.points, i);
    const longest = Math.max(
      ...p.points.map((_, n) => edgeLen(p.points, n)),
      0.01,
    );
    const isBump = len < longest / 3;
    return dimMode === "main_bumps" ? true : !isBump;
  };
  // short sides (bumps, notches, clipped corners) are secondary: drawn smaller and faded
  const isShortSide = (p, i) => {
    const longest = Math.max(...p.points.map((_, n) => edgeLen(p.points, n)), 0.01);
    return edgeLen(p.points, i) < longest / 3;
  };
  // full dimensions only in the Dimensions step; elsewhere only the hovered/selected piece
  const dimVisible = (p, i, selected) => {
    if (!showDims || edgeLen(p.points, i) <= 6) return false;
    if (step !== "dimensions" && !selected && hoverPiece !== p.id) return false;
    return showSide(p, i);
  };
  const bounds = useMemo(() => {
    const w = Math.max(140, ...list.map((p) => p.x + p.length_in + 8));
    const h = Math.max(90, ...list.map((p) => p.y + p.width_in + 8));
    return { w, h };
  }, [list]);

  // the sheet always fits the canvas: pad the view to the box aspect, then zoom/pan it
  const view = useMemo(() => {
    const base = { x: VIEW_PAD.x, y: VIEW_PAD.y, w: bounds.w + 40, h: bounds.h + 24 };
    const asp = box.w && box.h ? box.w / box.h : base.w / base.h;
    let w = base.w;
    let h = base.h;
    if (w / h < asp) w = h * asp;
    else h = w / asp;
    const zw = w / zoom;
    const zh = h / zoom;
    return { x: base.x / zoom + pan.x, y: base.y / zoom + pan.y, w: zw, h: zh };
  }, [bounds.w, bounds.h, box.w, box.h, zoom, pan.x, pan.y]);
  const viewStateRef = useRef({ view, zoom });
  viewStateRef.current = { view, zoom };

  // wheel = zoom about the cursor (page scroll is swallowed); React's onWheel is passive,
  // so bind natively
  useEffect(() => {
    const el = viewRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const f = e.deltaY > 0 ? 1 / 1.12 : 1.12;
      const { view: v, zoom: z } = viewStateRef.current;
      const z2 = Math.min(6, Math.max(0.2, Math.round(z * f * 1000) / 1000));
      if (z2 === z) return;
      const rect = svgRef.current?.getBoundingClientRect();
      const fx = rect?.width ? (e.clientX - rect.left) / rect.width : 0.5;
      const fy = rect?.height ? (e.clientY - rect.top) / rect.height : 0.5;
      // the drawing point under the pointer stays put while the sheet scales around it
      const px = v.x + fx * v.w;
      const py = v.y + fy * v.h;
      const W = v.w * z;
      const H = v.h * z;
      setZoom(z2);
      setPan({
        x: px - (fx * W + VIEW_PAD.x) / z2,
        y: py - (fy * H + VIEW_PAD.y) / z2,
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const ticks = useMemo(() => {
    const step = bounds.w > 240 ? 24 : 12;
    return Array.from(
      { length: Math.floor(bounds.w / step) + 1 },
      (_, i) => i * step,
    );
  }, [bounds.w]);
  const vTicks = useMemo(() => {
    const step = bounds.h > 180 ? 24 : 12;
    return Array.from(
      { length: Math.floor(bounds.h / step) + 1 },
      (_, i) => i * step,
    );
  }, [bounds.h]);

  const sync = () => setHistLen([past.current.length, future.current.length]);

  const snapshot = () => {
    past.current = [...past.current.slice(-49), pieces];
    future.current = [];
    sync();
  };

  const push = (next) => {
    snapshot();
    onChange(withPanelPieces(next));
  };

  const update = (pieceId, patch, opts = {}) => {
    const next = list.map((p) => (p.id === pieceId ? { ...p, ...patch } : p));
    if (opts.silent) onChange(withPanelPieces(next));
    else push(next);
  };

  const replacePiece = (pieceId, nextPiece) =>
    push(list.map((p) => (p.id === pieceId ? nextPiece : p)));

  const undo = () => {
    if (!past.current.length) return;
    const prev = past.current.pop();
    future.current.push(pieces);
    sync();
    setSel(null);
    setLabelEdit(null);
    onChange(prev);
  };

  const redo = () => {
    if (!future.current.length) return;
    const nxt = future.current.pop();
    past.current.push(pieces);
    sync();
    setSel(null);
    setLabelEdit(null);
    onChange(nxt);
  };

  const fitView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Link tops: seam every pair of abutting, collinear edges between pieces in the same area so a
  // mitred / multi-piece run reads as one continuous top (shared seam_id ties them for vein match).
  const _segLink = (a1, a2, b1, b2) => {
    const ax = a2[0] - a1[0];
    const ay = a2[1] - a1[1];
    const la = Math.hypot(ax, ay);
    if (la < 1) return false;
    const ux = ax / la;
    const uy = ay / la;
    const bx = b2[0] - b1[0];
    const by = b2[1] - b1[1];
    const lb = Math.hypot(bx, by);
    if (lb < 1) return false;
    if (Math.abs(ux * by - uy * bx) / lb > 0.08) return false; // ~4.5° parallel tol
    if (Math.abs((b1[0] - a1[0]) * uy - (b1[1] - a1[1]) * ux) > 0.75) return false; // on same line
    const pb1 = (b1[0] - a1[0]) * ux + (b1[1] - a1[1]) * uy;
    const pb2 = (b2[0] - a1[0]) * ux + (b2[1] - a1[1]) * uy;
    const lo = Math.max(0, Math.min(pb1, pb2));
    const hi = Math.min(la, Math.max(pb1, pb2));
    return hi - lo > 2; // >2" shared run
  };
  const linkTops = () => {
    const flats = list.filter((p) => !p.panel_kind && (p.points || []).length > 2);
    if (flats.length < 2) {
      toast.info("Draw at least two pieces that touch, then link them");
      return;
    }
    const worlds = new Map(flats.map((p) => [p.id, pieceAbsPoints(p)]));
    const seamIds = {}; // `${pieceId}:${edgeIdx}` -> shared id
    let pairs = 0;
    for (let i = 0; i < flats.length; i++) {
      for (let j = i + 1; j < flats.length; j++) {
        const A = flats[i];
        const B = flats[j];
        if ((A.area_id || "") !== (B.area_id || "")) continue;
        const wa = worlds.get(A.id);
        const wb = worlds.get(B.id);
        for (let ei = 0; ei < wa.length; ei++) {
          for (let ej = 0; ej < wb.length; ej++) {
            if (
              _segLink(
                wa[ei],
                wa[(ei + 1) % wa.length],
                wb[ej],
                wb[(ej + 1) % wb.length],
              )
            ) {
              const id = `lnk${Date.now()}-${pairs++}`;
              seamIds[`${A.id}:${ei}`] = id;
              seamIds[`${B.id}:${ej}`] = id;
            }
          }
        }
      }
    }
    if (!pairs) {
      toast.info("No touching edges found — move the pieces so they meet");
      return;
    }
    push(
      list.map((p) => {
        let hit = false;
        const edges = (p.edges || []).map((e, idx) => {
          const id = seamIds[`${p.id}:${idx}`];
          if (!id) return e;
          hit = true;
          return { ...(e || { profile: "Eased" }), seam: true, seam_id: id };
        });
        return hit ? { ...p, edges } : p;
      }),
    );
    toast.success(`Linked ${pairs} shared edge${pairs === 1 ? "" : "s"} into seams`);
  };
  // Unlink: clear seams created by Link tops (seam_id starts with "lnk") so joined tops split apart.
  const unlinkTops = () => {
    let n = 0;
    const next = list.map((p) => {
      let hit = false;
      const edges = (p.edges || []).map((e) => {
        if (e?.seam && typeof e.seam_id === "string" && e.seam_id.startsWith("lnk")) {
          hit = true;
          n++;
          const { seam_id, ...rest } = e;
          return { ...rest, seam: false };
        }
        return e;
      });
      return hit ? { ...p, edges } : p;
    });
    if (!n) {
      toast.info("No linked seams to clear");
      return;
    }
    push(next);
    toast.success(`Unlinked ${n} seam${n === 1 ? "" : "s"}`);
  };

  // Fit to screen: frame the drawn pieces tightly in the viewport and recenter.
  const fitToContent = () => {
    const pts = list.flatMap(pieceAbsPoints);
    if (!pts.length) {
      fitView();
      return;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    pts.forEach(([x, y]) => {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    });
    const padIn = Math.max(6, (maxX - minX) * 0.08, (maxY - minY) * 0.08);
    minX -= padIn;
    minY -= padIn;
    maxX += padIn;
    maxY += padIn;
    const tw = Math.max(1, maxX - minX);
    const th = Math.max(1, maxY - minY);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    // zoom-1 view dimensions (same aspect-fit as the `view` memo)
    const base = { w: bounds.w + 40, h: bounds.h + 24 };
    const asp = box.w && box.h ? box.w / box.h : base.w / base.h;
    let W0 = base.w;
    let H0 = base.h;
    if (W0 / H0 < asp) W0 = H0 * asp;
    else H0 = W0 / asp;
    let z = Math.min(W0 / tw, H0 / th);
    z = Math.min(4, Math.max(0.2, Math.round(z * 100) / 100));
    setZoom(z);
    setPan({ x: cx - (VIEW_PAD.x + W0 / 2) / z, y: cy - (VIEW_PAD.y + H0 / 2) / z });
  };

  const svgPoint = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const pxPerIn = rect.width / view.w;
    return [
      (e.clientX - rect.left) / pxPerIn + view.x,
      (e.clientY - rect.top) / pxPerIn + view.y,
    ];
  };

  // Pull a point onto a traced wall (corner first, then anywhere along the wall).
  const snapToWalls = ([x, y]) => {
    let best = null;
    const consider = (p) => {
      const d = Math.hypot(p[0] - x, p[1] - y);
      if (d < 3 && (!best || d < best.d)) best = { p, d };
    };
    walls.forEach((w) => {
      w.forEach(consider);
      for (let i = 0; i < w.length - 1; i++) {
        const [ax, ay] = w[i];
        const [bx, by] = w[i + 1];
        const dx = bx - ax;
        const dy = by - ay;
        const l2 = dx * dx + dy * dy || 1;
        const t = Math.max(
          0,
          Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2),
        );
        consider([ax + dx * t, ay + dy * t]);
      }
    });
    return best ? [round2(best.p[0]), round2(best.p[1])] : null;
  };

  // Snap a free-draw point to the grid step and lock it orthogonal to the last corner.
  const snapRunPoint = (pts, [x, y]) => {
    const onWall = snapToWalls([x, y]);
    if (onWall) return onWall;
    let nx = Math.max(0, Math.round(x / snap) * snap);
    let ny = Math.max(0, Math.round(y / snap) * snap);
    const last = pts[pts.length - 1];
    const prev = pts[pts.length - 2];
    const turnAngle = Number(runAngle) || 90;
    if (last && prev && turnAngle !== 90) {
      // bend the run by the chosen angle off straight (or carry straight on) from the last leg
      const base = Math.atan2(last[1] - prev[1], last[0] - prev[0]);
      const turn = (turnAngle * Math.PI) / 180;
      const want = Math.atan2(ny - last[1], nx - last[0]);
      const diff = (a) => Math.abs(Math.atan2(Math.sin(want - a), Math.cos(want - a)));
      const ang = [base, base + turn, base - turn].reduce((b, a) => (diff(a) < diff(b) ? a : b));
      const d = Math.max(snap, Math.round(Math.hypot(nx - last[0], ny - last[1]) / snap) * snap);
      return [round2(last[0] + Math.cos(ang) * d), round2(last[1] + Math.sin(ang) * d)];
    }
    if (last) {
      if (Math.abs(nx - last[0]) < Math.abs(ny - last[1])) nx = last[0];
      else ny = last[1];
    }
    return [round2(nx), round2(ny)];
  };

  const runFromPath = (path, depth, label) => {
    const poly = runPolygon(path, depth);
    const minx = Math.min(...poly.map((p) => p[0]));
    const miny = Math.min(...poly.map((p) => p[1]));
    return normalizePiece({
      id: uid(),
      label,
      points: poly.map(([x, y]) => [round2(x - minx), round2(y - miny)]),
      x: round2(minx),
      y: round2(miny),
      rot: 0,
      backsplash_sqft: 0,
    });
  };

  // Freehand corners: grid snap, optional angle lock from the last corner, and a
  // magnet on the first corner so the shape closes cleanly.
  const snapPolyPoint = (pts, [x, y]) => {
    const first = pts[0];
    if (first && pts.length >= 3 && Math.hypot(first[0] - x, first[1] - y) < 3)
      return first;
    let nx = Math.max(0, Math.round(x / snap) * snap);
    let ny = Math.max(0, Math.round(y / snap) * snap);
    const last = pts[pts.length - 1];
    const stepDeg = freeAngle ? 0 : polySnap;
    if (last && stepDeg > 0) {
      const dx = nx - last[0];
      const dy = ny - last[1];
      const dist = Math.hypot(dx, dy);
      const stepRad = (stepDeg * Math.PI) / 180;
      const ang = Math.round(Math.atan2(dy, dx) / stepRad) * stepRad;
      const d = Math.max(snap, Math.round(dist / snap) * snap);
      nx = last[0] + Math.cos(ang) * d;
      ny = last[1] + Math.sin(ang) * d;
    }
    return [round2(nx), round2(ny)];
  };

  const drawPoint = (pts, kind, raw) =>
    kind === "poly" ? snapPolyPoint(pts, raw) : snapRunPoint(pts, raw);

  const finishPoly = (pts) => {
    let ring = pts;
    if (ring.length > 3 && nearPt(ring[0], ring[ring.length - 1]))
      ring = ring.slice(0, -1);
    ring = dropCollinear(ring);
    if (ring.length < 3) {
      toast.error("Click at least three corners to make a shape");
      return;
    }
    const minx = Math.min(...ring.map((p) => p[0]));
    const miny = Math.min(...ring.map((p) => p[1]));
    push([
      ...list,
      normalizePiece({
        id: uid(),
        label: `Shape ${list.length + 1}`,
        points: ring.map(([x, y]) => [round2(x - minx), round2(y - miny)]),
        x: round2(minx),
        y: round2(miny),
        rot: 0,
        backsplash_sqft: 0,
      }),
    ]);
    return true;
  };

  // Draw-by-length: extend from a point by a polar length + angle (0°=right, CCW+).
  const polarNext = (from, len, angleDeg) => {
    const r = ((Number(angleDeg) || 0) * Math.PI) / 180;
    const L = Math.max(0.1, Number(len) || 0);
    return [
      round2(from[0] + Math.cos(r) * L),
      round2(from[1] - Math.sin(r) * L),
    ];
  };

  const addLineSegment = () => {
    if (!draw || !["line", "poly"].includes(draw.kind) || !draw.pts.length) return;
    const last = draw.pts[draw.pts.length - 1];
    const next = polarNext(last, Number(lineLen) || 0, Number(lineAngle) || 0, lineSnap);
    const first = draw.pts[0];
    const closeThresh = Math.max(2, snap * 1.5);
    if (
      lineSnap &&
      draw.pts.length >= 3 &&
      Math.hypot(first[0] - next[0], first[1] - next[1]) < closeThresh
    ) {
      finishPoly(draw.pts);
      setDraw(null);
      return;
    }
    setDraw({ ...draw, pts: [...draw.pts, next], cursor: null });
  };

  // AutoCAD-style dynamic input keys: Enter commits the segment, Esc completes the shape.
  const onDynKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addLineSegment();
    } else if (e.key === "Escape") {
      e.preventDefault();
      finishDraw();
    }
  };

  const traceUrl = trace?.path
    ? `${API}/photos/${trace.path}?auth=${localStorage.getItem("sw_token")}`
    : "";
  const traceH = (Number(trace?.w_in) || 120) / (Number(trace?.aspect) || 1.5);

  const uploadTrace = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onUploadTrace) return;
    const aspect = await new Promise((res) => {
      const img = new Image();
      img.onload = () => res(img.naturalWidth / (img.naturalHeight || 1));
      img.onerror = () => res(1.5);
      img.src = URL.createObjectURL(file);
    });
    const path = await onUploadTrace(file);
    if (!path) return;
    onTraceChange?.({
      path,
      aspect: round2(aspect || 1.5),
      w_in: Math.max(60, Math.round(bounds.w * 0.8)),
      x: 0,
      y: 0,
      opacity: 0.55,
    });
    setTraceMove(true);
    toast.success("Sketch placed under the drawing — set its width to scale it");
  };

  const startTraceDrag = (e) => {
    if (e.button === 1 || spaceRef.current || !trace) return;
    e.stopPropagation();
    const rect = svgRef.current.getBoundingClientRect();
    const pxPerIn = rect.width / view.w;
    setDrag({
      traceMove: true,
      rect,
      pxPerIn,
      vx: view.x,
      vy: view.y,
      offX: (e.clientX - rect.left) / pxPerIn + view.x - (Number(trace.x) || 0),
      offY: (e.clientY - rect.top) / pxPerIn + view.y - (Number(trace.y) || 0),
    });
  };

  const finishDraw = () => {
    if (draw?.kind === "poly" || draw?.kind === "line") {
      if (draw.pts.length >= 3) finishPoly(draw.pts);
      setDraw(null);
      return;
    }
    if (!draw || draw.pts.length < 2) {
      setDraw(null);
      return;
    }
    if (draw.kind === "wall") {
      onWallsChange?.([...walls, draw.pts]);
      setDraw(null);
      return;
    }
    const w = Math.max(6, Number(runWidth) || 25.5);
    push([...list, runFromPath(draw.pts, w, `Run ${list.length + 1}`)]);
    setDraw(null);
  };

  const counterAlongWalls = () => {
    const runs = walls.filter((w) => w.length >= 2);
    if (!runs.length) return;
    const w = Math.max(6, Number(runWidth) || 25.5);
    const depth = wallSide === "right" ? w : -w;
    const next = [...list];
    runs.forEach((path) => {
      next.push(runFromPath(path, depth, `Run ${next.length + 1}`));
    });
    push(next);
  };

  const addShape = (kind) => {
    const custom = customLayouts.find((l) => l.key === kind);
    if (!custom && kind === "Circle") {
      push([
        ...list,
        normalizePiece({
          id: uid(),
          label: `Circle ${list.length + 1}`,
          circle: true,
          diameter: 36,
          x: 4,
          y: 4 + list.length * 8,
          rot: 0,
          backsplash_sqft: 0,
        }),
      ]);
      return;
    }
    if (!custom && kind === "Oval") {
      push([
        ...list,
        normalizePiece({
          id: uid(),
          label: `Oval ${list.length + 1}`,
          oval: true,
          length_in: 48,
          width_in: 30,
          x: 4,
          y: 4 + list.length * 8,
          rot: 0,
          backsplash_sqft: 0,
        }),
      ]);
      return;
    }
    const pts = custom ? custom.points.map((p) => [...p]) : SHAPES[kind]();
    push([
      ...list,
      normalizePiece({
        id: uid(),
        label: `${custom ? custom.label : kind} ${list.length + 1}`,
        points: pts,
        x: 4,
        y: 4 + list.length * 8,
        rot: 0,
        backsplash_sqft: 0,
      }),
    ]);
  };

  // Resize a circle/oval by dragging its pink handle. axis "x" sets diameter (circle)
  // or length (oval); axis "y" sets oval width. Self-contained via pointer capture.
  const onRoundResizeDown = (ev, p, axis) => {
    ev.stopPropagation();
    ev.preventDefault();
    if (general?.enable_resize === false) return;
    const el = ev.currentTarget;
    try {
      el.setPointerCapture(ev.pointerId);
    } catch (err) {
      /* ignore */
    }
    const svg = el.ownerSVGElement;
    const inv = el.getScreenCTM().inverse();
    const toLocal = (clientX, clientY) => {
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      return pt.matrixTransform(inv);
    };
    snapshot();
    const apply = (loc, commit) => {
      const base = list.find((q) => q.id === p.id) || p;
      let patch;
      if (base.circle) patch = { circle: true, diameter: Math.max(2, round2(loc.x)) };
      else if (axis === "y") patch = { oval: true, width_in: Math.max(2, round2(loc.y)) };
      else patch = { oval: true, length_in: Math.max(2, round2(loc.x)) };
      update(p.id, normalizePiece({ ...base, ...patch }), { silent: !commit });
    };
    const move = (e) => apply(toLocal(e.clientX, e.clientY), false);
    const up = (e) => {
      apply(toLocal(e.clientX, e.clientY), true);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
  };

  const addStarter = (starter) => {
    const built = starter.build();
    push([
      ...list,
      ...built.map((b, idx) =>
        normalizePiece({
          id: uid(),
          label: b.label,
          points: b.points,
          x: b.x ?? 4,
          y: b.y ?? 4 + (list.length + idx) * 8,
          rot: 0,
          backsplash_sqft: 0,
        }),
      ),
    ]);
  };

  const addSavedLayout = (sl) => {
    const items = sl.pieces || [];
    const offY = list.length ? bounds.h + 8 : 0;
    push([
      ...list,
      ...items.map((p) =>
        normalizePiece({ ...p, id: uid(), y: (Number(p.y) || 4) + offY }),
      ),
    ]);
  };

  const saveCurrentLayout = () => {
    const name = (layoutName || "").trim();
    if (!name || !list.length) return;
    const pieces = list.map((p) => ({
      label: p.label,
      points: p.points,
      x: p.x,
      y: p.y,
      rot: p.rot,
      edges: p.edges,
      cutouts: p.cutouts,
      backsplash_sqft: p.backsplash_sqft,
    }));
    onSaveLayout?.({ id: uid(), name, pieces });
    setLayoutName("");
    setSavingLayout(false);
  };

  const confirmRename = () => {
    const name = (renameName || "").trim();
    if (!name || !renamingId) return;
    onRenameLayout?.(renamingId, name);
    setRenamingId(null);
    setRenameName("");
  };

  const duplicateLayout = (sl) => {
    const copy = {
      id: uid(),
      name: `${sl.name || "Layout"} copy`,
      pieces: (sl.pieces || []).map((p) => ({ ...p })),
    };
    onSaveLayout?.(copy);
    setRenamingId(copy.id);
    setRenameName(copy.name);
  };

  const selPiece = list.find((p) => p.id === sel?.pieceId);
  const selEdge =
    (selPiece && sel?.edge != null && selPiece.edges?.[sel.edge]) || null;
  const selNote =
    (sel?.noteId && (notes || []).find((n) => n.id === sel.noteId)) || null;

  // Free mode: the left panel offers "add pieces" whenever nothing is selected.
  const lstep = free && !selPiece ? "dimensions" : step;
  const setMode = (f) => {
    setFree(f);
    setSketch(false);
    try {
      localStorage.setItem("bgw_designer_mode", f ? "free" : "guided");
    } catch (err) {
      /* ignore */
    }
  };
  const enterSketch = () => {
    setSketch(true);
    try {
      localStorage.setItem("bgw_designer_mode", "sketch");
    } catch (err) {
      /* ignore */
    }
  };
  const setTool = (key) => {
    setStep(key);
    if (sel?.pieceId) setSel({ pieceId: sel.pieceId, edge: null });
  };
  const missing = [];
  if (list.length === 0)
    missing.push({ key: "pieces", text: "No pieces yet", go: () => setTool("dimensions") });
  if (materialMissing)
    missing.push({ key: "material", text: "No material picked", go: () => onPickMaterial?.() });
  list.forEach((p) => {
    const sides = (p.edges || []).filter((e) => !e.seam);
    if (sides.length && sides.every((e) => !e.profile || e.profile === "Unfinished"))
      missing.push({
        key: `edges-${p.id}`,
        text: `${p.label}: no edge profile`,
        go: () => {
          setStep("edges");
          setSel({ pieceId: p.id, edge: 0 });
        },
      });
    if (/island|vanity|sink|kitchen|perimeter|bar/i.test(p.label || "") && !(p.cutouts || []).length)
      missing.push({
        key: `cut-${p.id}`,
        text: `${p.label}: no cut-out`,
        go: () => {
          setStep("cutouts");
          setSel({ pieceId: p.id, edge: null });
        },
      });
  });

  const visSteps = DESIGN_STEPS.filter((s) => !hiddenSteps.includes(s.key));
  const curStepIdx = visSteps.findIndex((s) => s.key === step);
  const nextStep = visSteps[curStepIdx + 1] || null;
  const prevStep = curStepIdx > 0 ? visSteps[curStepIdx - 1] : null;
  const nextBlocked = step === "dimensions" && list.length === 0;
  const drawOpen = showDraw || !!draw;

  const stepAction = () => {
    if (lstep === "dimensions")
      return list.length === 0
        ? "Start here: tap a quick-start or a shape below to drop it on the sheet."
        : free
          ? "Drag a side to resize, or click a side to type its exact length. Click a piece for its tools."
          : "Drag a side to resize, or click a side to type its exact length. Add more pieces, then hit Next.";
    if (lstep === "shape")
      return selPiece
        ? "Click a side to add a bump or curve; click a corner dot to clip or notch it."
        : "Click a piece on the sheet to reshape its sides and corners. This step is optional.";
    if (lstep === "edges")
      return selPiece
        ? "Click each side to set its finished edge profile, color and backsplash."
        : "Click a piece, then choose how each side is finished.";
    if (lstep === "cutouts")
      return selPiece
        ? "Add a sink, cooktop or hole, then drag it into place on the piece."
        : "Click a piece to add sinks, cooktops and holes.";
    return "Nest your pieces onto slabs to see the cut plan.";
  };

  const startTour = () => {
    setShowHelp(false);
    setMode(false);
    setFocus(false);
    setStep("dimensions");
    setTourOpen(true);
  };
  const closeTour = () => {
    setTourOpen(false);
    try {
      localStorage.setItem("bgw_studio_tour_v1", "1");
    } catch (err) {
      /* ignore */
    }
  };

  useEffect(() => {
    try {
      if (!localStorage.getItem("bgw_studio_tour_v1")) {
        const t = setTimeout(() => setTourOpen(true), 500);
        return () => clearTimeout(t);
      }
    } catch (err) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (list.length > 0 && !coachRef.current) {
      coachRef.current = true;
      try {
        if (!localStorage.getItem("bgw_studio_coach_v1")) {
          toast("Piece added", {
            description:
              "Click any side to type its exact length. Right-click a piece for quick tools.",
          });
          localStorage.setItem("bgw_studio_coach_v1", "1");
        }
      } catch (err) {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.length]);

  const addNote = () => {
    if (!onNotesChange) return;
    const id = uid();
    const n = {
      id,
      x: round2(view.x + view.w * 0.5),
      y: round2(view.y + view.h * 0.5),
      text: "Note",
      size: 3,
      color: "#fde68a",
    };
    onNotesChange([...(notes || []), n]);
    setSel({ noteId: id });
    setCtxMenu(null);
    setCornerMenu(null);
  };

  const patchNote = (id, patch) =>
    onNotesChange?.((notes || []).map((n) => (n.id === id ? { ...n, ...patch } : n)));

  const deleteNote = (id) => {
    onNotesChange?.((notes || []).filter((n) => n.id !== id));
    setSel(null);
  };

  const startNoteDrag = (e, n) => {
    if (e.button === 1 || spaceRef.current) return;
    e.stopPropagation();
    const rect = svgRef.current.getBoundingClientRect();
    const pxPerIn = rect.width / view.w;
    setSel({ noteId: n.id });
    setDrag({
      note: n.id,
      rect,
      pxPerIn,
      vx: view.x,
      vy: view.y,
      offX: (e.clientX - rect.left) / pxPerIn + view.x - n.x,
      offY: (e.clientY - rect.top) / pxPerIn + view.y - n.y,
    });
  };

  const selDrop = selEdge ? dropKind(selEdge, styleFor(selEdge.profile)) : null;
  const selBump =
    (selEdge?.bump &&
      (selPiece?.bumps || []).find((b) => b.id === selEdge.bump)) ||
    null;
  const selCorner =
    (sel?.corner &&
      selPiece &&
      cornerFeatures(selPiece).find((f) => f.id === sel.corner)) ||
    null;

  // a new radius corner links to the shop's "Radius corner" rate when one exists
  const defaultCornerService = (mode) => {
    if (mode !== "radius") return {};
    const k = (cutoutKinds || []).find((c) => /radius/i.test(String(c)));
    return k ? { service_kind: "cutout", service_name: k } : {};
  };

  const cutCorner = (piece, vertex, size, mode, meta = {}) => {
    const id = meta.id || uid();
    const next = cornerCut(piece, vertex, size, mode, {
      ...defaultCornerService(mode),
      ...meta,
      id,
    });
    replacePiece(piece.id, next);
    setSel({ pieceId: piece.id, edge: null, corner: id });
    setCornerMenu(null);
  };

  const updateCorner = (patch) => {
    if (!selPiece || !selCorner) return;
    const rec = { ...selCorner.record, ...patch };
    const { piece: cleared, vertex } = removeCorner(selPiece, selCorner.id);
    if (vertex == null) return;
    const mode = rec.kind === "chamfer" ? "clip" : rec.kind;
    cutCorner(cleared, vertex, Number(rec.size) || 1, mode, rec);
  };

  const deleteCorner = () => {
    if (!selPiece || !selCorner) return;
    const { piece: cleared, vertex } = removeCorner(selPiece, selCorner.id);
    replacePiece(selPiece.id, cleared);
    setSel(vertex != null ? { pieceId: selPiece.id, edge: null, vertex } : null);
    toast.success("Corner squared off");
  };

  const bumpSpec = (kind, extra = {}) => {
    const def = CURVE_OPTIONS.find((o) => o.kind === kind)?.defaults || { d: 3, w: 30 };
    const svc = matchBumpService(kind, services);
    return {
      kind,
      w: def.w,
      d: def.d,
      angle_l: 90,
      angle_r: 90,
      pos: "center",
      offset: "",
      service_id: svc?.id || "",
      service_name: svc?.name || "",
      service_price: svc ? Number(svc.cost_estimate) || 0 : 0,
      service_unit: svc?.unit || "EA",
      ...extra,
    };
  };

  const addBumpOnEdge = (kind, extra = {}) => {
    if (!selPiece || sel?.edge == null) return;
    setPickerFor(null);
    if (selBump) {
      // picker opened from an existing bump: switch its kind / apply preset in place
      updateBump(bumpSpec(kind, extra));
      return;
    }
    const id = uid();
    const next = applyBump(selPiece, sel.edge, { id, ...bumpSpec(kind, extra) });
    replacePiece(selPiece.id, next);
    setSel({ pieceId: selPiece.id, edge: bumpTopEdge(next, id) });
  };

  const saveBumpPreset = () => {
    if (!selBump) return;
    const { kind, d, w, angle_l, angle_r, service_id, service_name, service_price, service_unit } = selBump;
    const label = `${bumpTitle(kind)} ${kind === "full_curve" ? `D${formatInches(d)}` : `${formatInches(d)}×${formatInches(w)}`}`;
    if (bumpPresets.some((p) => p.label === label && p.service_id === (service_id || ""))) {
      toast.message("That preset is already saved");
      return;
    }
    savePresets([
      ...bumpPresets,
      { id: uid(), label, kind, d, w, angle_l, angle_r, service_id: service_id || "", service_name, service_price, service_unit },
    ]);
    toast.success("Preset saved — find it under Used");
  };

  const applyBumpPreset = (p) => {
    const { id, label, ...spec } = p;
    addBumpOnEdge(spec.kind, spec);
  };

  const updateBump = (patch) => {
    if (!selPiece || !selBump) return;
    const rec = { ...selBump, ...patch, id: selBump.id };
    const { piece: cleared, edge } = removeBump(selPiece, selBump.id);
    if (edge == null) return;
    const next = applyBump(cleared, edge, rec);
    replacePiece(selPiece.id, next);
    setSel({ pieceId: selPiece.id, edge: bumpTopEdge(next, rec.id) });
  };

  const deleteBump = () => {
    if (!selPiece || !selBump) return;
    setPickerFor(null);
    const { piece: cleared, edge } = removeBump(selPiece, selBump.id);
    replacePiece(selPiece.id, cleared);
    setSel(edge != null ? { pieceId: selPiece.id, edge } : null);
    toast.success("Bump removed");
  };

  const duplicateBump = () => {
    if (!selPiece || !selBump) return;
    const idx = bumpEdgeIndexes(selPiece, selBump.id);
    const n = selPiece.edges.length;
    const room = (k) =>
      selPiece.edges[k]?.bump_base === selBump.id &&
      (selPiece.edges[k].length_in || 0) >= (Number(selBump.w) || 1) + 2;
    const spot = [(idx[idx.length - 1] + 1) % n, (idx[0] - 1 + n) % n].find(room);
    if (spot == null) {
      toast.error("No room beside this bump — make it narrower first");
      return;
    }
    const id = uid();
    const next = applyBump(selPiece, spot, {
      ...selBump,
      id,
      pos: "center",
      offset: "",
    });
    replacePiece(selPiece.id, next);
    setSel({ pieceId: selPiece.id, edge: bumpTopEdge(next, id) });
    toast.success("Bump duplicated beside the first one");
  };

  useEffect(() => {
    const el = viewRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const r = entry.contentRect;
      setBox({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [step, showTools]);

  const setEdge = (patch) => {
    if (!selPiece || sel.edge == null) return;
    const edges = selPiece.edges.map((e, i) =>
      i === sel.edge ? { ...e, ...patch } : e,
    );
    const out = { edges };
    if ("backsplash_height_in" in patch)
      out.backsplash_sqft = round2(
        edges.reduce(
          (n, e) =>
            n +
            ((Number(e.backsplash_height_in) || 0) * (e.length_in || 0)) / 144,
          0,
        ),
      );
    if ("drop_in" in patch)
      out.drop_sqft = round2(
        edges.reduce(
          (n, e) => n + ((Number(e.drop_in) || 0) * (e.length_in || 0)) / 144,
          0,
        ),
      );
    update(selPiece.id, out);
  };

  // switch a side between finished / seam / waterfall / mitre, keeping its depth sane
  const setEdgeType = (kind) => {
    if (!selEdge) return;
    if (kind === "seam") return setEdge({ seam: true, appliance: undefined });
    if (kind === "fridge")
      return setEdge({ seam: false, miter: false, drop_in: 0, appliance: "fridge" });
    if (kind === "range" || kind === "stove")
      return setEdge({ seam: false, miter: false, drop_in: 0, appliance: "range" });
    if (kind === "dishwasher")
      return setEdge({ seam: false, miter: false, drop_in: 0, appliance: "dishwasher" });
    if (kind === "hood")
      return setEdge({ seam: false, miter: false, drop_in: 0, appliance: "hood" });
    const nameFor = (re, fallback) =>
      (edgeProfiles || []).find((p) => re.test(p)) || fallback;
    const keep = Number(selEdge.drop_in) || 0;
    if (kind === "waterfall")
      return setEdge({
        seam: false,
        miter: false,
        appliance: undefined,
        profile: nameFor(/waterfall/i, "Waterfall"),
        drop_in: keep || DROP_DEFAULT.waterfall,
      });
    if (kind === "mitre")
      return setEdge({
        seam: false,
        miter: true,
        appliance: undefined,
        profile: nameFor(/mit(er|re)/i, "Mitered 2cm"),
        drop_in: keep || DROP_DEFAULT.mitre,
      });
    setEdge({ seam: false, miter: false, drop_in: 0, appliance: undefined });
  };

  const setEdgeProfile = (name) => {
    const kind = dropKind(
      { profile: name, miter: selEdge?.miter },
      styleFor(name),
    );
    setEdge({
      profile: name,
      ...(kind
        ? { drop_in: Number(selEdge?.drop_in) || DROP_DEFAULT[kind] }
        : { drop_in: 0 }),
    });
  };

  // Keep one unmoved vertex pinned in world space so a resize doesn't make the piece jump
  const anchored = (piece, next, anchorIdx) => {
    const before = pieceScreenPoints(piece)[anchorIdx];
    const after = pieceScreenPoints(next)[anchorIdx];
    if (!before || !after) return next;
    return {
      ...next,
      x: round2(next.x + before[0] - after[0]),
      y: round2(next.y + before[1] - after[1]),
    };
  };

  // Back/wall edge = the piece's topmost edge. Pin its left corner so a typed length edit
  // keeps the wall line fixed and grows the piece outward (the free end moves), never drifting.
  const wallAnchorIdx = (pts) => {
    if (!pts || !pts.length) return 0;
    let best = 0;
    for (let k = 1; k < pts.length; k++) {
      const [x, y] = pts[k];
      const [bx, by] = pts[best];
      if (y < by - 0.01 || (Math.abs(y - by) < 0.01 && x < bx - 0.01)) best = k;
    }
    return best;
  };

  // How far side i may be pushed along normal n before a neighbouring side drops under 1"
  // or flips: returns [lo, hi] in inches.
  const sideRange = (pts, i, [nx, ny]) => {
    const n = pts.length;
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const prev = pts[(i - 1 + n) % n];
    const after = pts[(i + 2) % n];
    const un = (a[0] - prev[0]) * nx + (a[1] - prev[1]) * ny;
    const wn = (after[0] - b[0]) * nx + (after[1] - b[1]) * ny;
    let lo = -Infinity;
    let hi = Infinity;
    if (un > 0.001) lo = Math.max(lo, 1 - un);
    else if (un < -0.001) hi = Math.min(hi, -1 - un);
    if (wn > 0.001) hi = Math.min(hi, wn - 1);
    else if (wn < -0.001) lo = Math.max(lo, wn + 1);
    return [lo, hi];
  };

  // Typed length for an L-shape. With the strip lock ON (default) both legs (B & E) keep one
  // shared depth: editing the overall width/length only moves the far corner, and editing a
  // leg cap re-sizes both legs symmetrically. With the lock OFF the four sides are free.
  const setLSideLength = (piece, i, target) => {
    const pts = piece.points;
    const L = lShapeOf(pts);
    if (!L) return false;
    const kind = lSideKind(pts, L, i);
    const kinds = pts.map((_, k) => lSideKind(pts, L, k));
    const letterOf = (k) => SIDE_LETTER(kinds.indexOf(k));
    const me = SIDE_LETTER(i);
    let { W, H, nw, nh } = L;
    let v = target;
    let msg = null;
    const clampTo = (lo, hi, why) => {
      const c = Math.min(hi, Math.max(lo, v));
      if (Math.abs(c - v) > 0.001) msg = `${me} ${why} — set to ${tag(round2(c))}.`;
      v = c;
    };
    if (lStripLock) {
      // Each leg keeps its OWN depth: E (left leg = W−C) and B (top leg = F−D) are
      // independent. Typing a length holds that leg's width; typing a leg cap resizes only
      // that leg, so the perpendicular leg is never dragged along.
      const eDepth = W - nw; // left leg width  (cw / E)
      const bDepth = H - nh; // top  leg depth  (ch / B)
      if (kind === "W") {
        clampTo(eDepth + 1, Infinity, `can't be less than ${letterOf("cw")} + 1" (${tag(round2(eDepth + 1))})`);
        W = v;
        nw = W - eDepth; // keep leg E; C grows/shrinks
      } else if (kind === "H") {
        clampTo(bDepth + 1, Infinity, `can't be less than ${letterOf("ch")} + 1" (${tag(round2(bDepth + 1))})`);
        H = v;
        nh = H - bDepth; // keep leg B; D grows/shrinks
      } else if (kind === "nw") {
        clampTo(1, Infinity, `must be at least 1"`);
        nw = v;
        W = v + eDepth; // keep leg E; the overall width follows
      } else if (kind === "nh") {
        clampTo(1, Infinity, `must be at least 1"`);
        nh = v;
        H = v + bDepth; // keep leg B; the overall length follows
      } else if (kind === "cw") {
        // editing leg E's width: only the left leg changes, B is left untouched
        clampTo(1, W - 1, `must stay inside the overall ${letterOf("W")} (${tag(round2(W))})`);
        nw = W - v;
      } else {
        // "ch" editing leg B's depth: only the top leg changes, E is left untouched
        clampTo(1, H - 1, `must stay inside the overall ${letterOf("H")} (${tag(round2(H))})`);
        nh = H - v;
      }
    } else if (kind === "W") {
      clampTo(nw + 1, Infinity, `can't be shorter than ${letterOf("nw")} + 1" (${tag(round2(nw + 1))})`);
      W = v;
    } else if (kind === "H") {
      clampTo(nh + 1, Infinity, `can't be shorter than ${letterOf("nh")} + 1" (${tag(round2(nh + 1))})`);
      H = v;
    } else if (kind === "nw" || kind === "cw") {
      clampTo(1, W - 1, `must stay inside the overall ${letterOf("W")} (${tag(round2(W))})`);
      nw = kind === "nw" ? v : W - v;
    } else {
      clampTo(1, H - 1, `must stay inside the overall ${letterOf("H")} (${tag(round2(H))})`);
      nh = kind === "nh" ? v : H - v;
    }
    if (msg) toast.message(msg);
    const next = lShapePoints(pts, L, { W, H, nw, nh });
    if (next.every((p, k) => Math.abs(p[0] - pts[k][0]) < 0.001 && Math.abs(p[1] - pts[k][1]) < 0.001))
      return true;
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), wallAnchorIdx(pts)));
    return true;
  };

  // Set the shared strip depth of an L-shape: both legs stay square and equal, overall size
  // is unchanged. Clamped so a leg can never be wider than the overall shape.
  const setLStrip = (piece, value) => {
    const pts = piece.points;
    const L = lShapeOf(pts);
    if (!L) return;
    const strip = Math.max(1, Math.min(Number(value) || 0, Math.min(L.W, L.H) - 1));
    if (Math.abs(strip - value) > 0.001)
      toast.message(`Strip width set to ${tag(round2(strip))} — it must stay inside the overall shape.`);
    const next = lShapePoints(pts, L, { W: L.W, H: L.H, nw: L.W - strip, nh: L.H - strip });
    const same = (p, k) => Math.abs(p[0] - pts[k][0]) < 0.001 && Math.abs(p[1] - pts[k][1]) < 0.001;
    if (next.every(same)) return;
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), Math.max(next.findIndex(same), 0)));
  };

  // Set every run of a U-shape to one uniform depth (both returns + the base), clamped so the
  // returns still fit inside the overall span.
  const setUStrip = (piece, value) => {
    const pts = piece.points;
    const U = uShapeOf(pts);
    if (!U) return;
    const maxStrip = Math.max(1, Math.min((U.W - 1) / 2, U.H - 1));
    const strip = Math.max(1, Math.min(Number(value) || 0, maxStrip));
    if (Math.abs(strip - value) > 0.001)
      toast.message(`Strip width set to ${tag(round2(strip))} — it must fit inside the U.`);
    const next = uShapePoints(pts, U, { W: U.W, H: U.H, d: strip, rwA: strip, rwB: strip });
    const same = (p, k) => Math.abs(p[0] - pts[k][0]) < 0.001 && Math.abs(p[1] - pts[k][1]) < 0.001;
    if (next.every(same)) return;
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), Math.max(next.findIndex(same), 0)));
  };

  // Swing one leg of an L / run piece so its corner meets `deg` off straight (90 = square).
  // Cut-outs riding on the swung leg turn with it; edges keep their profiles and seams.
  const bendRunCorner = (piece, corner, deg) => {
    const res = swingRunCorner(piece.points, corner, deg);
    if (!res) {
      toast.error("That angle doesn't fit — the legs are too short for this depth.");
      return;
    }
    if (res.same) return;
    const minx = Math.min(...res.points.map((p) => p[0]));
    const miny = Math.min(...res.points.map((p) => p[1]));
    const cutouts = (piece.cutouts || []).map((c) => {
      const w = Number(c.w) || 0;
      const h = Number(c.h) || 0;
      const cx = (Number(c.x) || 0) + w / 2;
      const cy = (Number(c.y) || 0) + h / 2;
      const hit = res.movedZones.some((z) => insidePoly([cx, cy], z));
      const [nx, ny] = hit ? rotPt([cx, cy], res.pivotPt, res.phi) : [cx, cy];
      return {
        ...c,
        x: round2(nx - w / 2 - minx),
        y: round2(ny - h / 2 - miny),
        rot: hit ? round2((((Number(c.rot) || 0) + res.phi) % 360 + 360) % 360) : c.rot,
      };
    });
    replacePiece(
      piece.id,
      anchored(piece, rebuild({ ...piece, cutouts }, res.points, piece.edges), res.pivot),
    );
  };

  // U-shape: depth of the closed run only (outer A to the inner E), returns untouched.
  const setUParam = (piece, patch, why) => {
    const pts = piece.points;
    const U = uShapeOf(pts);
    if (!U) return;
    const p = { W: U.W, H: U.H, d: U.d, rwA: U.rwA, rwB: U.rwB, hA: U.hA, hB: U.hB, ...patch };
    const key = Object.keys(patch)[0];
    const want = p[key];
    if (key === "d") p.d = Math.max(1, Math.min(p.d, Math.min(U.hA, U.hB) - 1));
    if (key === "hA" || key === "hB") p[key] = Math.max(U.d + 1, p[key]);
    p.H = Math.max(p.hA, p.hB);
    if (key === "rwA") p.rwA = Math.max(1, Math.min(p.rwA, U.W - U.rwB - 1));
    if (key === "rwB") p.rwB = Math.max(1, Math.min(p.rwB, U.W - U.rwA - 1));
    if (Math.abs(p[key] - want) > 0.001)
      toast.message(`${why} set to ${tag(round2(p[key]))} — it must fit inside the U.`);
    const next = uShapePoints(pts, U, p);
    const same = (q, k) => Math.abs(q[0] - pts[k][0]) < 0.001 && Math.abs(q[1] - pts[k][1]) < 0.001;
    if (next.every(same)) return;
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), Math.max(next.findIndex(same), 0)));
  };
  const setUBaseDepth = (piece, value) => setUParam(piece, { d: Number(value) || 0 }, "Middle depth");

  // Route the strip-depth input to whichever parametric shape is selected.
  const setStrip = (piece, value) => {
    if (lShapeOf(piece.points)) setLStrip(piece, value);
    else if (uShapeOf(piece.points)) setUStrip(piece, value);
  };

  // Typed length for a U-shape: both returns stay aligned because every side edits one
  // parameter and all eight vertices are rebuilt. Return widths can't meet; the closed run
  // can't swallow the returns.
  const setUSideLength = (piece, i, target) => {
    const pts = piece.points;
    const U = uShapeOf(pts);
    if (!U) return false;
    const kind = uSideKind(pts, U, i);
    const kinds = pts.map((_, k) => uSideKind(pts, U, k));
    const letterOf = (k) => SIDE_LETTER(kinds.indexOf(k));
    const me = SIDE_LETTER(i);
    let { W, H, d, rwA, rwB, hA, hB } = U;
    let v = target;
    let msg = null;
    const clampTo = (lo, hi, why) => {
      const c = Math.min(hi, Math.max(lo, v));
      if (Math.abs(c - v) > 0.001) msg = `${me} ${why} — set to ${tag(round2(c))}.`;
      v = c;
    };
    if (kind === "W") {
      clampTo(rwA + rwB + 1, Infinity, `can't be shorter than both returns + 1" (${tag(round2(rwA + rwB + 1))})`);
      W = v;
    } else if (kind === "hA" || kind === "hB") {
      clampTo(d + 1, Infinity, `can't be shorter than the run depth + 1" (${tag(round2(d + 1))})`);
      if (kind === "hA") hA = v;
      else hB = v;
      H = Math.max(hA, hB);
    } else if (kind === "span") {
      clampTo(1, Infinity, `must be at least 1"`);
      W = rwA + rwB + v; // returns keep their width; the overall grows / shrinks
    } else if (kind === "rwA" || kind === "rwB") {
      // only THIS return's width changes; its outer (wall) edge stays, the inner edge moves
      const other = kind === "rwA" ? rwB : rwA;
      clampTo(1, W - other - 1, `must leave room for the other return inside ${letterOf("W")} (${tag(round2(W))})`);
      if (kind === "rwA") rwA = v;
      else rwB = v;
    } else {
      // inner return side: its length = return length - middle depth
      // inner return side: only THIS return grows / shrinks from its free end; the base run
      // depth and the other return are left alone (one leg at a time, wall edge fixed).
      clampTo(1, Infinity, `must be at least 1"`);
      if (kind === "innerA") hA = d + v;
      else hB = d + v;
      H = Math.max(hA, hB);
    }
    if (msg) toast.message(msg);
    const next = uShapePoints(pts, U, { W, H, d, rwA, rwB, hA, hB });
    const same = (p, k) => Math.abs(p[0] - pts[k][0]) < 0.001 && Math.abs(p[1] - pts[k][1]) < 0.001;
    if (next.every(same)) return true;
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), wallAnchorIdx(pts)));
    return true;
  };

  // Typed length on a cap of an angled U-band (back run A or inner run E). Both caps grow /
  // shrink by the same amount, centred, while every leg keeps its length and angle — so only
  // the two caps change and the shape never skews.
  const setUCapSideLength = (piece, i, target) => {
    const pts = piece.points;
    const cap = uCapOf(pts);
    if (!cap || (i !== cap.a && i !== cap.b)) return false;
    const n = pts.length;
    const p = i === cap.a ? cap.b : cap.a;
    const u = capUnit(pts, i);
    const cur = edgeLen(pts, i);
    const partnerCur = edgeLen(pts, p);
    const me = SIDE_LETTER(i);
    let delta = target - cur;
    const lo = Math.max(1 - cur, 1 - partnerCur);
    if (delta < lo) {
      toast.message(`${me} can't go below ${tag(round2(cur + lo))} — ${SIDE_LETTER(p)} would disappear.`);
      delta = lo;
    }
    if (Math.abs(delta) < 0.001) return true;
    const { legX } = uCapChains(n, i, p);
    const sx = new Set(legX);
    const t = delta / 2;
    const next = pts.map((pt, k) =>
      sx.has(k)
        ? [round2(pt[0] + u[0] * t), round2(pt[1] + u[1] * t)]
        : [round2(pt[0] - u[0] * t), round2(pt[1] - u[1] * t)],
    );
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), wallAnchorIdx(pts)));
    return true;
  };

  // Set the perpendicular height (gap) between the two caps of an angled U-band. The outer cap
  // slides straight away from / toward the inner cap; the two legs follow (lengthen / shorten).
  const setUCapHeight = (piece, value) => {
    const pts = piece.points;
    const cap = uCapOf(pts);
    if (!cap) return;
    const n = pts.length;
    const { gap, nx, ny } = uCapGap(pts, cap);
    const want = Math.max(1, Number(value) || 0);
    const delta = want - gap;
    if (Math.abs(delta) < 0.001) return;
    const vi = cap.a;
    const vj = (cap.a + 1) % n;
    const next = pts.map((pt, k) =>
      k === vi || k === vj ? [round2(pt[0] + nx * delta), round2(pt[1] + ny * delta)] : [...pt],
    );
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), wallAnchorIdx(pts)));
  };

  // Pure transform: slide a leg's bottom assembly along the leg's own direction to a new length.
  const ucapLegLenNext = (pts, cap, i, target) => {
    const info = uCapLegInfo(pts, cap, i);
    if (!info) return pts;
    const { cv, fv, inter } = info;
    const cur = edgeLen(pts, i) || 1;
    const dx = (pts[fv][0] - pts[cv][0]) / cur;
    const dy = (pts[fv][1] - pts[cv][1]) / cur;
    const delta = Math.max(1 - cur, (Number(target) || 0) - cur);
    if (Math.abs(delta) < 0.001) return pts;
    const move = new Set(inter);
    return pts.map((pt, k) =>
      move.has(k) ? [round2(pt[0] + dx * delta), round2(pt[1] + dy * delta)] : [...pt],
    );
  };
  // Pure transform: rotate a leg's bottom assembly about its cap corner to a new splay angle.
  const ucapLegAngNext = (pts, cap, i, deg) => {
    const info = uCapLegInfo(pts, cap, i);
    if (!info) return pts;
    const n = pts.length;
    const { cv, fv, inter } = info;
    const p0 = pts[cv];
    const co = pts[uCapOther(n, cap, cv)];
    const cvec = [co[0] - p0[0], co[1] - p0[1]];
    const lvec = [pts[fv][0] - p0[0], pts[fv][1] - p0[1]];
    const cur = Math.atan2(
      cvec[0] * lvec[1] - cvec[1] * lvec[0],
      cvec[0] * lvec[0] + cvec[1] * lvec[1],
    );
    const side = cur < 0 ? -1 : 1;
    const want = (side * Math.max(5, Math.min(175, Number(deg) || 90)) * Math.PI) / 180;
    const phi = want - cur;
    if (Math.abs(phi) < 0.001) return pts;
    const cos = Math.cos(phi);
    const sin = Math.sin(phi);
    const move = new Set(inter);
    return pts.map((pt, k) => {
      if (!move.has(k)) return [...pt];
      const ddx = pt[0] - p0[0];
      const ddy = pt[1] - p0[1];
      return [round2(p0[0] + ddx * cos - ddy * sin), round2(p0[1] + ddx * sin + ddy * cos)];
    });
  };

  // Typed length for one angled leg; with Mirror on, its opposite leg gets the same length too.
  const setUCapLegLength = (piece, i, target) => {
    const pts = piece.points;
    const cap = uCapOf(pts);
    if (!cap) return;
    let next = ucapLegLenNext(pts, cap, i, target);
    if (ucapMirror) {
      const m = uCapLegMirror(pts, cap, i);
      if (m >= 0) next = ucapLegLenNext(next, cap, m, target);
    }
    if (next === pts) return;
    if (next.some((_, k) => edgeLen(next, k) < 0.5)) {
      toast.message(`${SIDE_LETTER(i)} can't reach ${tag(round2(Number(target) || 0))} without collapsing a side.`);
      return;
    }
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), wallAnchorIdx(pts)));
  };

  // Set an angled leg's splay angle; with Mirror on, its opposite leg gets the same angle too.
  const setUCapLegAngle = (piece, i, deg) => {
    const pts = piece.points;
    const cap = uCapOf(pts);
    if (!cap) return;
    let next = ucapLegAngNext(pts, cap, i, deg);
    if (ucapMirror) {
      const m = uCapLegMirror(pts, cap, i);
      if (m >= 0) next = ucapLegAngNext(next, cap, m, deg);
    }
    if (next === pts) return;
    if (next.some((_, k) => edgeLen(next, k) < 0.5)) {
      toast.message(`${SIDE_LETTER(i)} can't swing to ${Math.round(Number(deg) || 0)}° without collapsing a side.`);
      return;
    }
    replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), wallAnchorIdx(pts)));
  };

  // Move the far end of edge i (vertex j and its neighbour k) along the edge to reach a new
  // length. L- and U-shapes go through the parametric path; anything else is clamped so the
  // next side keeps at least 1" and never flips.
  // Typed length on an end leg of a run (e.g. a swung 45° peninsula return): slide that leg's
  // end cap along the leg so the typed side reaches the target and the leg keeps its angle.
  const setRunSideLength = (piece, i, target) => {
    const pts = piece.points;
    const run = runOf(pts);
    if (!run) return false;
    const { order, path } = run;
    const m = path.length;
    const n = pts.length;
    const pathIdx = (j) => order[j];
    const offIdx = (j) => order[n - 1 - j];
    const j = (i + 1) % n;
    const legOf = (l) => [pathIdx(l), pathIdx(l + 1), offIdx(l), offIdx(l + 1)];
    for (const l of [0, m - 2]) {
      const leg = legOf(l);
      if (!leg.includes(i) || !leg.includes(j)) continue;
      const capEnd = l === 0 ? 0 : m - 1;
      const cap = [pathIdx(capEnd), offIdx(capEnd)];
      if (cap.includes(i) && cap.includes(j)) return false; // the end cap itself = depth, not length
      const a = path[l];
      const b = path[l + 1];
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
      const dir = l === 0 ? [(a[0] - b[0]) / len, (a[1] - b[1]) / len] : [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
      const cur = edgeLen(pts, i);
      const delta = Math.max(1 - cur, target - cur);
      if (Math.abs(delta) < 0.001) return true;
      const next = pts.map((p) => [...p]);
      for (const k of cap) next[k] = [round2(next[k][0] + dir[0] * delta), round2(next[k][1] + dir[1] * delta)];
      const pin = pathIdx(l === 0 ? 1 : m - 2);
      replacePiece(piece.id, anchored(piece, rebuild(piece, next, piece.edges), pin));
      return true;
    }
    return false;
  };

  const setEdgeLengthOn = (piece, i, target) => {
    if (!piece || i == null || !target) return;
    if (setLSideLength(piece, i, target)) return;
    if (setUSideLength(piece, i, target)) return;
    if (setRunSideLength(piece, i, target)) return;
    if (setUCapSideLength(piece, i, target)) return;
    const pts = piece.points.map((p) => [...p]);
    const n = pts.length;
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const cur = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    const dx = (b[0] - a[0]) / cur;
    const dy = (b[1] - a[1]) / cur;
    const j = (i + 1) % n;
    const k = (i + 2) % n;
    const after = pts[(i + 3) % n];
    let delta = target - cur;
    if (n > 3 && after) {
      const wd = (after[0] - pts[k][0]) * dx + (after[1] - pts[k][1]) * dy;
      const lo = wd < 0 ? wd + 1 : -Infinity;
      const hi = wd > 0 ? wd - 1 : Infinity;
      const clamped = Math.min(hi, Math.max(lo, delta));
      if (Math.abs(clamped - delta) > 0.001) {
        toast.message(
          `${SIDE_LETTER(i)} can only go to ${tag(round2(cur + clamped))} — the ${SIDE_LETTER(k)} side would disappear. Change ${SIDE_LETTER(k)} or ${SIDE_LETTER((i + 3) % n)} instead.`,
        );
        delta = clamped;
      }
    }
    delta = Math.max(delta, 1 - cur);
    if (Math.abs(delta) < 0.001) return;
    pts[j] = [round2(pts[j][0] + dx * delta), round2(pts[j][1] + dy * delta)];
    pts[k] = [round2(pts[k][0] + dx * delta), round2(pts[k][1] + dy * delta)];
    replacePiece(piece.id, anchored(piece, rebuild(piece, pts, piece.edges), wallAnchorIdx(piece.points)));
  };

  const localPointer = (e, rect, pxPerIn, piece) => {
    const world = [
      (e.clientX - rect.left) / pxPerIn + view.x - piece.x,
      (e.clientY - rect.top) / pxPerIn + view.y - piece.y,
    ];
    return rotPt(world, [piece.length_in / 2, piece.width_in / 2], -piece.rot);
  };

  // Drag a side handle: push / pull that side along its outward normal (both endpoints move,
  // neighbouring sides grow or shrink) — clamped so a neighbour never drops under 1".
  // Drag the depth handle of a middle piece: slides edge j along edge i's normal.
  const onDepthDown = (e, piece, dep) => {
    if (e.button === 1 || spaceRef.current) return;
    if (general?.enable_resize === false) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const pxPerIn = rect.width / view.w;
    const local = localPointer(e, rect, pxPerIn, piece);
    const pts = piece.points;
    const a = pts[dep.i];
    const signed = (pts[dep.j][0] - a[0]) * dep.nx + (pts[dep.j][1] - a[1]) * dep.ny;
    snapshot();
    setSel({ pieceId: piece.id, edge: dep.j });
    setDrag({
      pieceId: piece.id,
      depth: dep,
      rect,
      pxPerIn,
      vx: view.x,
      vy: view.y,
      base: piece,
      startPointer: local[0] * dep.nx + local[1] * dep.ny,
      startSigned: signed,
    });
  };

  const onSideDown = (e, piece, i) => {
    if (e.button === 1 || spaceRef.current) return;
    if (step !== "dimensions" || general?.enable_resize === false) return;
    e.stopPropagation();
    const rect = svgRef.current.getBoundingClientRect();
    const pxPerIn = rect.width / view.w;
    const pts = piece.points;
    const normal = outwardNormal(pts, i);
    const [lo, hi] = sideRange(pts, i, normal);
    const local = localPointer(e, rect, pxPerIn, piece);
    e.preventDefault();
    snapshot();
    setSel({ pieceId: piece.id, edge: i });
    setDrag({
      pieceId: piece.id,
      side: i,
      rect,
      pxPerIn,
      vx: view.x,
      vy: view.y,
      base: piece,
      normal,
      start: local[0] * normal[0] + local[1] * normal[1],
      lo,
      hi,
    });
  };

  // Drag a corner dot: pushes BOTH sides that meet there (each along its own normal, each
  // with the same 1" neighbour clamp) so two dimensions change at once and the outline
  // stays rectilinear.
  // Drag the single apex dot of a curved bump to push its curve deeper or shallower.
  const onBumpDepthDown = (e, piece, bumpId) => {
    if (e.button === 1 || spaceRef.current) return;
    if (step !== "dimensions" || general?.enable_resize === false) return;
    e.stopPropagation();
    e.preventDefault();
    const rec = (piece.bumps || []).find((x) => x.id === bumpId);
    if (!rec) return;
    const { piece: cleared, edge } = removeBump(piece, bumpId);
    if (edge == null) return;
    const a = cleared.points[edge];
    const b = cleared.points[(edge + 1) % cleared.points.length];
    const [nx, ny] = outwardNormal(cleared.points, edge);
    svgRef.current?.setPointerCapture?.(e.pointerId);
    movedRef.current = false;
    snapshot();
    setSel({ pieceId: piece.id, edge: null });
    setDrag({
      type: "bump-depth",
      pieceId: piece.id,
      bumpId,
      base: cleared,
      edge,
      ax: a[0],
      ay: a[1],
      bx: b[0],
      by: b[1],
      nx,
      ny,
      inward: String(rec.kind).includes("_in"),
      rec,
    });
  };

  const onCornerDown = (e, piece, i) => {
    if (e.button === 1 || spaceRef.current) return;
    if (step !== "dimensions" || general?.enable_resize === false) return;
    e.stopPropagation();
    const rect = svgRef.current.getBoundingClientRect();
    const pxPerIn = rect.width / view.w;
    const pts = piece.points;
    const n = pts.length;
    const e1 = (i - 1 + n) % n; // side ending at this corner
    const e2 = i; // side starting at this corner
    const n1 = outwardNormal(pts, e1);
    const n2 = outwardNormal(pts, e2);
    const det = n1[0] * n2[1] - n1[1] * n2[0];
    if (Math.abs(det) < 0.05) return; // parallel sides — nothing sensible to do
    const local = localPointer(e, rect, pxPerIn, piece);
    e.preventDefault();
    movedRef.current = false;
    snapshot();
    setSel({ pieceId: piece.id, edge: null });
    setDrag({
      pieceId: piece.id,
      corner: i,
      rect,
      pxPerIn,
      vx: view.x,
      vy: view.y,
      base: piece,
      startLocal: local,
      e1,
      e2,
      n1,
      n2,
      r1: sideRange(pts, e1, n1),
      r2: sideRange(pts, e2, n2),
    });
  };

  const setEdgeLength = (target) => {
    if (!selPiece || sel.edge == null) return;
    setEdgeLengthOn(selPiece, sel.edge, target);
  };

  const openLabel = (piece, i, ev) => {
    ev.stopPropagation();
    if (general?.enable_resize === false) return;
    const a = piece.points[i];
    const b = piece.points[(i + 1) % piece.points.length];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const r = rotPt(mid, [piece.length_in / 2, piece.width_in / 2], piece.rot);
    setSel({ pieceId: piece.id, edge: i });
    setLabelEdit({
      pieceId: piece.id,
      edge: i,
      value: String(round2(edgeLen(piece.points, i))),
      left: Math.min(
        82,
        Math.max(18, ((piece.x + r[0] - view.x) / view.w) * 100),
      ),
      top: Math.min(
        40,
        Math.max(2, ((piece.y + r[1] - view.y) / view.h) * 100),
      ),
    });
  };

  const commitLabel = (close = true) => {
    if (!labelEdit) return;
    const piece = list.find((p) => p.id === labelEdit.pieceId);
    const target = Number(labelEdit.value);
    if (close) setLabelEdit(null);
    if (!piece || !(target > 0)) return;
    if (labelEdit.ucapHeight) {
      setUCapHeight(piece, target);
      return;
    }
    if (labelEdit.depth) {
      applyDepth(piece, labelEdit.depth, target);
      return;
    }
    const cur = edgeLen(piece.points, labelEdit.edge);
    if (Math.abs(cur - target) < 0.02) return; // untouched: leave the shape alone
    setEdgeLengthOn(piece, labelEdit.edge, target);
  };

  const applyDepth = (piece, dep, target) => {
    const cur = diagDepths(piece.points).find((x) => x.i === dep.i && x.j === dep.j);
    if (!cur || Math.abs(cur.d - target) < 0.02) return;
    if (uShapeOf(piece.points)) {
      setUBaseDepth(piece, target);
      return;
    }
    replacePiece(piece.id, anchored(piece, rebuild(piece, setDepthPts(piece.points, dep.i, dep.j, target), piece.edges), dep.i));
  };

  const openDepth = (piece, dep, ev) => {
    ev.stopPropagation();
    if (general?.enable_resize === false) return;
    const r = rotPt(dep.mid, [piece.length_in / 2, piece.width_in / 2], piece.rot);
    setSel({ pieceId: piece.id, edge: dep.j });
    setLabelEdit({
      pieceId: piece.id,
      edge: dep.j,
      depth: { i: dep.i, j: dep.j },
      value: String(round2(dep.d)),
      left: Math.min(82, Math.max(18, ((piece.x + r[0] - view.x) / view.w) * 100)),
      top: Math.min(40, Math.max(2, ((piece.y + r[1] - view.y) / view.h) * 100)),
    });
  };

  // Open the inline editor for the straight gap (height) between the two caps of an angled U-band.
  const openUCapHeight = (piece, ev) => {
    ev.stopPropagation();
    if (general?.enable_resize === false) return;
    const cap = uCapOf(piece.points);
    if (!cap) return;
    const mA = capMid(piece.points, cap.a);
    const mE = capMid(piece.points, cap.b);
    const mid = [(mA[0] + mE[0]) / 2, (mA[1] + mE[1]) / 2];
    const r = rotPt(mid, [piece.length_in / 2, piece.width_in / 2], piece.rot);
    setSel({ pieceId: piece.id, edge: null });
    setLabelEdit({
      pieceId: piece.id,
      edge: null,
      ucapHeight: true,
      value: String(round2(uCapGap(piece.points, cap).gap)),
      left: Math.min(82, Math.max(18, ((piece.x + r[0] - view.x) / view.w) * 100)),
      top: Math.min(40, Math.max(2, ((piece.y + r[1] - view.y) / view.h) * 100)),
    });
  };

  const onSeamDown = (e, pieceId, seamId) => {
    if (e.button === 1 || spaceRef.current) return;
    e.stopPropagation();
    const rect = svgRef.current.getBoundingClientRect();
    setActiveSeam(seamId);
    setDrag({
      pieceId,
      seam: seamId,
      rect,
      vx: view.x,
      vy: view.y,
      pxPerIn: rect.width / view.w,
    });
  };

  const onRotDown = (e, pieceId) => {
    if (e.button === 1 || spaceRef.current) return;
    e.stopPropagation();
    const rect = svgRef.current.getBoundingClientRect();
    const p = list.find((x) => x.id === pieceId);
    snapshot();
    setDrag({
      pieceId,
      rotate: true,
      rect,
      vx: view.x,
      vy: view.y,
      pxPerIn: rect.width / view.w,
      cx: p.x + p.length_in / 2,
      cy: p.y + p.width_in / 2,
    });
    setSel({ pieceId, edge: null });
  };

  const onDown = (e, pieceId) => {
    if (e.button === 1 || spaceRef.current) return;
    e.stopPropagation();
    const rect = svgRef.current.getBoundingClientRect();
    const p = list.find((x) => x.id === pieceId);
    const pxPerIn = rect.width / view.w;
    snapshot();
    setSel((prev) =>
      prev?.pieceId === pieceId ? prev : { pieceId, edge: null },
    );
    setDrag({
      pieceId,
      rect,
      pxPerIn,
      vx: view.x,
      vy: view.y,
      offX: (e.clientX - rect.left) / pxPerIn + view.x - p.x,
      offY: (e.clientY - rect.top) / pxPerIn + view.y - p.y,
    });
    setSel({ pieceId, edge: null });
  };

  // Let the floating dimension / corner popouts be dragged anywhere on the sheet
  // by grabbing their header, so they never sit on top of the piece you're editing.
  const startPopoutDrag = (e, kind) => {
    if (e.button === 1) return;
    if (e.target.closest("button, input, select, [role='combobox']")) return;
    e.stopPropagation();
    const panel = e.currentTarget.closest("[data-designer-popout]");
    const parent = panel?.offsetParent;
    if (!panel || !parent) return;
    const prect = parent.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const base = kind === "corner" ? cornerMenu : labelEdit;
    const set = kind === "corner" ? setCornerMenu : setLabelEdit;
    if (!base) return;
    const move = (ev) => {
      const dx = ((ev.clientX - startX) / prect.width) * 100;
      const dy = ((ev.clientY - startY) / prect.height) * 100;
      set((cur) =>
        cur
          ? {
              ...cur,
              left: Math.min(97, Math.max(3, base.left + dx)),
              top: Math.min(94, Math.max(2, base.top + dy)),
            }
          : cur,
      );
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  useEffect(() => {
    const setSpace = (on) => (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.code !== "Space") return;
      e.preventDefault();
      spaceRef.current = on;
      setSpaceDown(on);
    };
    const down = setSpace(true);
    const up = setSpace(false);
    const altDown = (e) => e.key === "Alt" && setFreeAngle(true);
    const altUp = (e) => e.key === "Alt" && setFreeAngle(false);
    window.addEventListener("keydown", altDown);
    window.addEventListener("keyup", altUp);
    window.addEventListener("blur", altUp);
    const esc = (e) => {
      if (e.key !== "Escape") return;
      setCtxMenu(null);
      setCornerMenu(null);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("keydown", esc);
      window.removeEventListener("keydown", altDown);
      window.removeEventListener("keyup", altUp);
      window.removeEventListener("blur", altUp);
    };
  }, []);

  const ctxPos = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const halfPct = Math.min(45, (300 / rect.width) * 100);
    const raw = ((e.clientX - rect.left) / rect.width) * 100;
    return {
      left: Math.min(100 - halfPct, Math.max(halfPct, raw)),
      top: Math.min(
        90,
        Math.max(6, ((e.clientY - rect.top) / rect.height) * 100),
      ),
    };
  };

  const openCutCtx = (e, pieceId, cutId) => {
    if (draw) return;
    e.preventDefault();
    e.stopPropagation();
    setSel({ pieceId, edge: null, cutoutId: cutId });
    setCtxMenu({ pieceId, cutId, ...ctxPos(e) });
  };

  const patchCutout = (pieceId, cutId, patch) => {
    const p = list.find((x) => x.id === pieceId);
    if (!p) return;
    update(pieceId, {
      cutouts: (p.cutouts || []).map((c) =>
        c.id === cutId ? { ...c, ...patch } : c,
      ),
    });
  };

  // Rotate a cut-out by 90° (true rotation around its centre). Fine angles are
  // available in the panel's Angle control.
  const rotateCutout = (pieceId, cutId) => {
    const p = list.find((x) => x.id === pieceId);
    const c = p?.cutouts?.find((x) => x.id === cutId);
    if (!p || !c) return;
    patchCutout(pieceId, cutId, { rot: ((Number(c.rot) || 0) + 90) % 360 });
    toast.success("Cut-out rotated 90°");
  };

  const openCtx = (e, pieceId) => {
    if (draw || !["dimensions", "shape", "edges", "cutouts"].includes(step)) return;
    e.preventDefault();
    e.stopPropagation();
    setSel({ pieceId, edge: null });
    const piece = list.find((p) => p.id === pieceId);
    let at = null;
    if (piece && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const pxPerIn = rect.width / view.w;
      const gx = (e.clientX - rect.left) / pxPerIn + view.x - piece.x;
      const gy = (e.clientY - rect.top) / pxPerIn + view.y - piece.y;
      at = rotPt([gx, gy], [piece.length_in / 2, piece.width_in / 2], -(piece.rot || 0));
    }
    setCtxMenu({ pieceId, at, ...ctxPos(e) });
  };

  const duplicatePiece = (p) => {
    const copy = {
      ...p,
      id: uid(),
      label: `${p.label} copy`,
      x: round2(p.x + 4),
      y: round2(p.y + 4),
      cutouts: (p.cutouts || []).map((c) => ({ ...c, id: uid() })),
    };
    push([...list, copy]);
    setSel({ pieceId: copy.id, edge: null });
  };

  // drop another amber guide on the piece, spaced clear of the ones already there
  const cutSuggestion = (piece, sm) => {
    const axis = sm.dir === "h" ? 1 : 0;
    const at = round2(Number(sm.at) || 0);
    const halves = splitPiece(piece, axis, at);
    if (!halves) {
      toast.error("That seam falls outside the piece");
      return;
    }
    const parts = halves.map((h) => ({ ...h, seam_suggestions: [] }));
    push(list.flatMap((p) => (p.id === piece.id ? parts : [p])));
    setSel(null);
    setCtxMenu(null);
    toast.success("Seam cut — split into fabrication parts");
  };

  const addSeam = (piece, dir = "v") => {
    const span = dir === "v" ? piece.length_in : piece.width_in;
    const cur = seamsFor.pieceId === piece.id ? seamsFor.items : [];
    let at = round2(span / 2);
    while (
      cur.some((s) => s.dir === dir && Math.abs(Number(s.at) - at) < 2) &&
      at < span - 2
    )
      at = round2(at + 6);
    const seam = { id: uid(), dir, at };
    putSeams(piece.id, [...cur, seam]);
    setActiveSeam(seam.id);
    return seam;
  };

  // cut the selected piece on every amber guide, or take a seam back out
  const splitAtSeams = (piece) => {
    const guides = seams.length
      ? seams
      : [{ id: uid(), dir: "v", at: round2(piece.length_in / 2) }];
    const abs = guides
      .map((g) => ({
        axis: g.dir === "v" ? 0 : 1,
        at: (g.dir === "v" ? piece.x : piece.y) + (Number(g.at) || 0),
      }))
      .sort((a, b) => a.axis - b.axis || a.at - b.at);
    let parts = [piece];
    let cuts = 0;
    abs.forEach((g) => {
      let done = false;
      parts = parts.flatMap((sp) => {
        if (done) return [sp];
        const base = g.axis === 0 ? sp.x : sp.y;
        const span = g.axis === 0 ? sp.length_in : sp.width_in;
        const local = round2(g.at - base);
        if (!(local > 0.25 && local < span - 0.25)) return [sp];
        const halves = splitPiece(sp, g.axis, local);
        if (!halves) return [sp];
        done = true;
        cuts += 1;
        return halves;
      });
    });
    if (!cuts) {
      toast.error("Those seams fall outside the piece");
      return;
    }
    const base = piece.label.replace(/ [A-Z]$/, "");
    parts = parts.map((p, i) => ({
      ...p,
      label: `${base} ${String.fromCharCode(65 + i)}`,
    }));
    push(list.flatMap((p) => (p.id === piece.id ? parts : [p])));
    putSeams(null, []);
    setActiveSeam(null);
    setSel(null);
    setCtxMenu(null);
    toast.success(cuts === 1 ? "Seam cut" : `${cuts} seams cut`);
  };

  const rot0 = (p) => (Number(p.rot) || 0) % 360 === 0;
  const seamIdsOf = (p) =>
    (p.edges || []).filter((e) => e?.seam && e?.seam_id).map((e) => e.seam_id);

  // Pure: reconnect every part sharing seam ids with `anchor` within `src`.
  // Snaps each part back to where it was cut from, then merges all into one.
  // Returns { merged, removeIds, snaps } or null if there is nothing to join.
  const computeRejoin = (anchor, src) => {
    if (!seamIdsOf(anchor).length || !rot0(anchor)) return null;
    const byId = new Map(src.map((p) => [p.id, p]));
    const positioned = new Map([[anchor.id, { x: anchor.x, y: anchor.y }]]);
    const order = [anchor.id];
    const queue = [anchor.id];
    while (queue.length) {
      const curId = queue.shift();
      const cur = { ...byId.get(curId), ...positioned.get(curId) };
      for (const sid of seamIdsOf(cur)) {
        const nb = src.find(
          (p) =>
            p.id !== curId &&
            !positioned.has(p.id) &&
            !p.panel_kind &&
            rot0(p) &&
            seamIdsOf(p).includes(sid),
        );
        if (!nb) continue;
        const i = (cur.edges || []).findIndex((e) => e?.seam && e.seam_id === sid);
        const j = (nb.edges || []).findIndex((e) => e?.seam && e.seam_id === sid);
        if (i < 0 || j < 0) continue;
        const A = absPts(cur);
        const B = absPts(nb);
        const a1 = A[i];
        const a2 = A[(i + 1) % A.length];
        const b1 = B[j];
        const b2 = B[(j + 1) % B.length];
        let dx = round2(a2[0] - b1[0]);
        let dy = round2(a2[1] - b1[1]);
        if (!nearPt([b2[0] + dx, b2[1] + dy], a1)) {
          dx = round2(a1[0] - b1[0]);
          dy = round2(a1[1] - b1[1]);
        }
        positioned.set(nb.id, { x: round2(nb.x + dx), y: round2(nb.y + dy) });
        order.push(nb.id);
        queue.push(nb.id);
      }
    }
    if (order.length < 2) return null;
    const snappedById = new Map(
      order.map((id) => [id, { ...byId.get(id), ...positioned.get(id) }]),
    );
    let merged = snappedById.get(anchor.id);
    const removeIds = [];
    const rest = order.filter((id) => id !== anchor.id);
    let guard = 0;
    while (rest.length && guard++ < 60) {
      let did = false;
      for (let r = 0; r < rest.length; r++) {
        const cand = snappedById.get(rest[r]);
        let res = null;
        for (let i = 0; i < merged.points.length; i++) {
          res = mergeAtSeam(merged, i, [cand]);
          if (res) break;
        }
        if (res) {
          merged = res.piece;
          removeIds.push(rest[r]);
          rest.splice(r, 1);
          did = true;
          break;
        }
      }
      if (!did) break;
    }
    if (!removeIds.length) return null;
    const snaps = removeIds.map((id) => ({
      id,
      old: byId.get(id),
      snap: snappedById.get(id),
    }));
    return { merged, removeIds, snaps };
  };

  // Build fly-in ghost outlines for parts that actually moved on rejoin.
  const ghostFromSnaps = (snaps) =>
    snaps
      .map(({ old, snap }) => {
        const tx = round2(old.x - snap.x);
        const ty = round2(old.y - snap.y);
        if (Math.abs(tx) < 0.5 && Math.abs(ty) < 0.5) return null;
        const d =
          absPts(snap)
            .map((pt, k) => `${k ? "L" : "M"}${pt[0]} ${pt[1]}`)
            .join(" ") + " Z";
        return { d, tx, ty };
      })
      .filter(Boolean);

  // Rejoin every split run on the whole drawing in one tap.
  const rejoinAll = () => {
    let work = list.slice();
    const allRemove = new Set();
    const allSnaps = [];
    let mergedCount = 0;
    let guard = 0;
    while (guard++ < 80) {
      const anchor = work.find(
        (p) =>
          !allRemove.has(p.id) &&
          !p.panel_kind &&
          rot0(p) &&
          seamIdsOf(p).length,
      );
      if (!anchor) break;
      const r = computeRejoin(anchor, work);
      if (!r) {
        // orphan seam flag with no partner left — clear it so we don't loop forever
        work = work.map((p) =>
          p.id === anchor.id
            ? {
                ...p,
                edges: (p.edges || []).map((e) =>
                  e?.seam ? { ...e, seam: false, seam_id: undefined } : e,
                ),
              }
            : p,
        );
        continue;
      }
      work = work
        .filter((p) => r.removeIds.includes(p.id) === false)
        .map((p) => (p.id === anchor.id ? r.merged : p));
      r.removeIds.forEach((id) => allRemove.add(id));
      allSnaps.push(...r.snaps);
      mergedCount += r.removeIds.length;
    }
    if (!mergedCount) {
      toast("No split seams to rejoin");
      return;
    }
    const commit = () => {
      push(work);
      putSeams(null, []);
      setActiveSeam(null);
      setCtxMenu(null);
      toast.success(`Rejoined all seams — ${mergedCount + 1} parts merged`);
    };
    const ghosts = ghostFromSnaps(allSnaps);
    if (ghosts.length) {
      setRejoinFly({ ghosts, hideIds: new Set(allRemove), on: false });
      setTimeout(() => {
        commit();
        setRejoinFly(null);
      }, 340);
    } else {
      commit();
    }
  };

  const joinSeam = (piece) => {
    const straight = (p) =>
      (p.points || []).length === 4 && (Number(p.rot) || 0) % 90 === 0;
    const box = (p) => ({
      x0: p.x,
      y0: p.y,
      x1: p.x + p.length_in,
      y1: p.y + p.width_in,
    });

    // 1) Rejoin by the shared seam ids stamped when the piece was cut — reconnect
    // every part of the original piece no matter how far they've been dragged, and
    // for any shape (straight, L or angled).
    const r = computeRejoin(piece, list);
    if (r) {
      const n = r.removeIds.length + 1;
      const commit = () => {
        push(
          list
            .filter((p) => r.removeIds.includes(p.id) === false)
            .map((p) => (p.id === piece.id ? r.merged : p)),
        );
        putSeams(null, []);
        setActiveSeam(null);
        setSel({ pieceId: r.merged.id, edge: null });
        setCtxMenu(null);
        toast.success(
          n > 2
            ? `Joined ${n} pieces back together`
            : "Seam removed — pieces joined",
        );
      };
      const ghosts = ghostFromSnaps(r.snaps);
      if (ghosts.length) {
        setRejoinFly({ ghosts, hideIds: new Set(r.removeIds), on: false });
        setTimeout(() => {
          commit();
          setRejoinFly(null);
        }, 320);
      } else {
        commit();
      }
      return;
    }

    // 2) Fallback: straight pieces still butted together (legacy adjacency join).
    if (!straight(piece)) {
      toast.error(
        "Couldn't find the matching half — cut a seam on this piece first, then Join back",
      );
      return;
    }
    const a = box(piece);
    const near = (m, n) => Math.abs(m - n) < 0.4;
    let row = false;
    const mate = list.find((p) => {
      if (p.id === piece.id || p.panel_kind || !straight(p)) return false;
      const b = box(p);
      const sameRow = near(a.y0, b.y0) && near(a.y1, b.y1);
      const sameCol = near(a.x0, b.x0) && near(a.x1, b.x1);
      if (sameRow && (near(a.x1, b.x0) || near(b.x1, a.x0))) {
        row = true;
        return true;
      }
      if (sameCol && (near(a.y1, b.y0) || near(b.y1, a.y0))) {
        row = false;
        return true;
      }
      return false;
    });
    if (!mate) {
      toast.error("Nothing butted up against this piece to join");
      return;
    }
    const b = box(mate);
    const x0 = Math.min(a.x0, b.x0);
    const y0 = Math.min(a.y0, b.y0);
    const w = row
      ? round2(Math.max(a.x1, b.x1) - x0)
      : round2(Math.max(piece.length_in, mate.length_in));
    const h = row
      ? round2(Math.max(piece.width_in, mate.width_in))
      : round2(Math.max(a.y1, b.y1) - y0);
    const pts = SHAPES.Rectangle(w, h);
    const shift = (p) =>
      (p.cutouts || [])
        .filter((c) => !c.seam_part)
        .map((c) => {
          // the sink was clipped at the seam: restore its full size from the other half
          const rest = [...(piece.cutouts || []), ...(mate.cutouts || [])].find(
            (o) => o.seam_part && o.part_of === c.id,
          );
          return {
            ...c,
            x: round2((c.x || 0) + (p.x - x0)),
            y: round2((c.y || 0) + (p.y - y0)),
            w: rest && row ? round2((c.w || 0) + (rest.w || 0)) : c.w,
            h: rest && !row ? round2((c.h || 0) + (rest.h || 0)) : c.h,
          };
        });
    const joined = {
      ...piece,
      x: round2(x0),
      y: round2(y0),
      rot: 0,
      points: pts,
      length_in: w,
      width_in: h,
      edges: pts.map((_, n) => ({
        profile: piece.edges?.[n]?.profile || "Eased",
        seam: false,
      })),
      cutouts: [...shift(piece), ...shift(mate)],
    };
    push(
      list
        .filter((p) => p.id !== mate.id)
        .map((p) => (p.id === piece.id ? joined : p)),
    );
    setSel({ pieceId: joined.id, edge: null });
    toast.success(`Seam removed — joined with ${mate.label}`);
  };

  const startPan = (e) => {
    if (draw?.kind === "box" && e.button === 0 && !spaceRef.current) {
      // press-and-drag a rectangle: anchor on the grid, size follows the pointer
      const [x, y] = svgPoint(e);
      const a = [
        round2(Math.max(0, Math.round(x / snap) * snap)),
        round2(Math.max(0, Math.round(y / snap) * snap)),
      ];
      setDraw({ kind: "box", pts: [a], cursor: a });
      viewRef.current?.setPointerCapture?.(e.pointerId);
      return;
    }
    if (e.button !== 1 && !spaceRef.current) return;
    e.preventDefault();
    panRef.current = {
      x: e.clientX,
      y: e.clientY,
      px: pan.x,
      py: pan.y,
      pxPerIn: (svgRef.current?.getBoundingClientRect().width || 1) / view.w,
    };
    viewRef.current?.setPointerCapture?.(e.pointerId);
  };

  const onMove = (e) => {
    if (panRef.current) {
      const { x, y, px, py, pxPerIn } = panRef.current;
      setPan({
        x: px - (e.clientX - x) / pxPerIn,
        y: py - (e.clientY - y) / pxPerIn,
      });
      return;
    }
    if (draw) {
      if (draw.kind === "line") {
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) setDynPx({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        return;
      }
      if (draw.kind === "box") {
        if (!draw.pts.length) return;
        const [x, y] = svgPoint(e);
        const c = [
          round2(Math.max(0, Math.round(x / snap) * snap)),
          round2(Math.max(0, Math.round(y / snap) * snap)),
        ];
        setDraw((d) => ({ ...d, cursor: c }));
        return;
      }
      setDraw((d) => ({ ...d, cursor: drawPoint(d.pts, d.kind, svgPoint(e)) }));
      return;
    }
    if (!drag) {
      // Forgiving edge hover: in the Edges step, highlight the nearest edge to
      // the cursor within the hit band so it's obvious which side you'll select.
      if (step === "edges" && svgRef.current) {
        const pt = svgPoint(e);
        const thr = EDGE_HIT_W / 2;
        let best = null;
        for (const p of list) {
          const abs = pieceScreenPoints(p);
          for (let i = 0; i < abs.length; i++) {
            const a = abs[i];
            const b = abs[(i + 1) % abs.length];
            const d = distToSeg(pt, a, b);
            if (d <= thr && (!best || d < best.d))
              best = { pieceId: p.id, edge: i, d };
          }
        }
        setHoverEdge((h) => {
          const nextId = best ? best.pieceId : null;
          const nextEdge = best ? best.edge : null;
          if ((h?.pieceId ?? null) === nextId && (h?.edge ?? null) === nextEdge)
            return h;
          return best ? { pieceId: nextId, edge: nextEdge } : null;
        });
      } else if (hoverEdge) {
        setHoverEdge(null);
      }
      return;
    }
    if (drag.type === "bump-depth") {
      const rect = svgRef.current.getBoundingClientRect();
      const pxPerIn = rect.width / view.w;
      const [px, py] = localPointer(e, rect, pxPerIn, drag.base);
      let depth = (px - drag.ax) * drag.nx + (py - drag.ay) * drag.ny;
      if (drag.inward) depth = -depth;
      depth = Math.max(1, Math.min(round2(depth), 60));
      movedRef.current = true;
      const next = applyBump(drag.base, drag.edge, { ...drag.rec, d: depth });
      replacePiece(drag.pieceId, next);
      return;
    }
    if (drag.traceMove) {
      const x = (e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx - drag.offX;
      const y = (e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy - drag.offY;
      onTraceChange?.({
        ...trace,
        x: round2(Math.round(x / snap) * snap),
        y: round2(Math.round(y / snap) * snap),
      });
      return;
    }
    if (drag.note) {
      const x = (e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx - drag.offX;
      const y = (e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy - drag.offY;
      patchNote(drag.note, {
        x: round2(Math.round(x / snap) * snap),
        y: round2(Math.round(y / snap) * snap),
      });
      return;
    }
    movedRef.current = true;
    if (drag.corner != null) {
      const base = drag.base;
      const local = rotPt(
        [
          (e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx - base.x,
          (e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy - base.y,
        ],
        [base.length_in / 2, base.width_in / 2],
        -base.rot,
      );
      const mx = local[0] - drag.startLocal[0];
      const my = local[1] - drag.startLocal[1];
      // Pulling a corner straight out along one of its sides grows a new leg (rectangle -> L -> U)
      // instead of stretching the whole piece; a diagonal pull keeps the classic two-side resize.
      if (drag.mode !== "resize") {
        const bp = base.points;
        const bn = bp.length;
        const v = bp[drag.corner];
        const prev = bp[(drag.corner - 1 + bn) % bn];
        const nxt = bp[(drag.corner + 1) % bn];
        const unit = (a, b) => {
          const l = Math.hypot(a[0] - b[0], a[1] - b[1]) || 1;
          return [(a[0] - b[0]) / l, (a[1] - b[1]) / l, l];
        };
        const u1 = unit(v, prev);
        const u2 = unit(v, nxt);
        const dist = Math.hypot(mx, my);
        if (!drag.mode) {
          if (dist < 1.5) {
            movedRef.current = false;
            return;
          }
          const pick = (u, other) => {
            const along = mx * u[0] + my * u[1];
            const across = Math.abs(mx * u[1] - my * u[0]);
            return along > 0 && across < along * 0.47 && other[2] > 2 ? { u, w: Math.min(other[2] - 1, 25.5) } : null;
          };
          const ext = pick(u1, u2) ? { ...pick(u1, u2), axis: 1 } : pick(u2, u1) ? { ...pick(u2, u1), axis: 2 } : null;
          setDrag({ ...drag, mode: ext ? "extrude" : "resize", ext });
          if (!ext) return;
          drag.ext = ext;
        }
        if (drag.mode === "extrude" || drag.ext) {
          const { u, w, axis } = drag.ext;
          let L = Math.round((mx * u[0] + my * u[1]) / snap) * snap;
          if (L < 1) {
            setLegChip(null);
            onChange(withPanelPieces(list.map((p) => (p.id === base.id ? base : p))));
            return;
          }
          const back = axis === 1 ? u2 : u1;
          const P = (a, b) => [round2(v[0] + u[0] * a - back[0] * b), round2(v[1] + u[1] * a - back[1] * b)];
          const ins = axis === 1 ? [P(L, 0), P(L, w), P(0, w)] : [P(0, w), P(L, w), P(L, 0)];
          const pts = [...bp.slice(0, drag.corner), ...ins, ...bp.slice(drag.corner + 1)];
          const eIdx = axis === 1 ? drag.e1 : drag.e2;
          const src = { ...(base.edges?.[eIdx] || { profile: "Eased", seam: false }) };
          delete src.bump;
          delete src.bump_base;
          delete src.corner;
          const edges = [
            ...(base.edges || []).slice(0, drag.corner),
            { ...src },
            { ...src },
            ...(base.edges || []).slice(drag.corner),
          ];
          const next = anchored(base, rebuild(base, pts, edges), (drag.corner - 1 + bn) % bn);
          setLegChip({ L, x: e.clientX - drag.rect.left, y: e.clientY - drag.rect.top });
          onChange(withPanelPieces(list.map((p) => (p.id === base.id ? next : p))));
          return;
        }
      }
      // split the pointer move into a push along each side's normal: m = d1·n1 + d2·n2
      const [a1, b1] = drag.n1;
      const [a2, b2] = drag.n2;
      const det = a1 * b2 - b1 * a2;
      let d1 = (mx * b2 - my * a2) / det;
      let d2 = (a1 * my - b1 * mx) / det;
      d1 = Math.min(drag.r1[1], Math.max(drag.r1[0], Math.round(d1 / snap) * snap));
      d2 = Math.min(drag.r2[1], Math.max(drag.r2[0], Math.round(d2 / snap) * snap));
      const pts = base.points.map((p) => [...p]);
      const n = pts.length;
      const shift = (idx, [nx, ny], d) => {
        pts[idx] = [round2(pts[idx][0] + nx * d), round2(pts[idx][1] + ny * d)];
      };
      shift(drag.e1, drag.n1, d1);
      shift((drag.e1 + 1) % n, drag.n1, d1);
      shift(drag.e2, drag.n2, d2);
      shift((drag.e2 + 1) % n, drag.n2, d2);
      const next = anchored(base, rebuild(base, pts, base.edges), (drag.corner + 2) % n);
      onChange(withPanelPieces(list.map((p) => (p.id === base.id ? next : p))));
      return;
    }
    if (drag.depth) {
      const base = drag.base;
      const world = [
        (e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx - base.x,
        (e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy - base.y,
      ];
      const local = rotPt(world, [base.length_in / 2, base.width_in / 2], -base.rot);
      const { i, j, nx, ny } = drag.depth;
      let moved = local[0] * nx + local[1] * ny - drag.startPointer;
      moved = Math.round(moved / snap) * snap;
      const signed = drag.startSigned + moved;
      const target = Math.max(1, Math.abs(signed));
      if (Math.sign(signed) !== Math.sign(drag.startSigned)) return; // don't flip through the edge
      const next = anchored(base, rebuild(base, setDepthPts(base.points, i, j, target), base.edges), i);
      onChange(withPanelPieces(list.map((p) => (p.id === base.id ? next : p))));
      return;
    }
    if (drag.side != null) {
      const base = drag.base;
      const world = [
        (e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx - base.x,
        (e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy - base.y,
      ];
      const local = rotPt(world, [base.length_in / 2, base.width_in / 2], -base.rot);
      const [nx, ny] = drag.normal;
      let d = local[0] * nx + local[1] * ny - drag.start;
      d = Math.round(d / snap) * snap;
      d = Math.min(drag.hi, Math.max(drag.lo, d));
      const pts = base.points.map((p) => [...p]);
      const n = pts.length;
      const i = drag.side;
      const j = (i + 1) % n;
      // slide the endpoints along the neighbouring sides so their angles never change
      const oa = [pts[i][0] + nx * d, pts[i][1] + ny * d];
      const ob = [pts[j][0] + nx * d, pts[j][1] + ny * d];
      const prev = pts[(i - 1 + n) % n];
      const nxt = pts[(j + 1) % n];
      const hitA = n >= 4 ? segIntersect(prev, pts[i], oa, ob) : null;
      const hitB = n >= 4 ? segIntersect(pts[j], nxt, oa, ob) : null;
      const sane = (h, o) => h && Math.hypot(h[0] - o[0], h[1] - o[1]) <= Math.abs(d) * 3 + snap;
      pts[i] = (sane(hitA, oa) ? hitA : oa).map(round2);
      pts[j] = (sane(hitB, ob) ? hitB : ob).map(round2);
      const next = anchored(base, rebuild(base, pts, base.edges), (i + 2) % n);
      onChange(withPanelPieces(list.map((p) => (p.id === base.id ? next : p))));
      return;
    }
    if (drag.seam) {
      const piece = list.find((p) => p.id === drag.pieceId);
      const seam = seams.find((s) => s.id === drag.seam);
      if (!piece || !seam) return;
      const px =
        (e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx - piece.x;
      const py = (e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy - piece.y;
      const local = rotPt(
        [px, py],
        [piece.length_in / 2, piece.width_in / 2],
        -piece.rot,
      );
      const axis = seam.dir === "v" ? 0 : 1;
      const span = axis === 0 ? piece.length_in : piece.width_in;
      let at = Math.min(
        Math.max(Math.round(local[axis] / snap) * snap, 1),
        span - 1,
      );
      // snap to the centre, slab-fit marks and even spacing with the piece's other seams
      const otherAts = seams
        .filter((s) => s.id !== seam.id && s.dir === seam.dir)
        .map((s) => Number(s.at))
        .filter(Number.isFinite);
      const marks = [
        { at: span / 2, label: "Centre" },
        { at: slabL, label: "Slab fit" },
        { at: span - slabL, label: "Slab fit" },
      ];
      // sink centre: a seam through a cutout looks cleanest when centred on it
      (piece.cutouts || []).forEach((c) => {
        const cc = axis === 0 ? c.x + c.w / 2 : c.y + c.h / 2;
        marks.push({ at: cc, label: "Sink centre" });
      });
      // even spacing: replicate an existing gap beside any boundary so strips stay equal
      const bounds = [0, ...otherAts, span].sort((a, b) => a - b);
      for (let i = 0; i < bounds.length - 1; i++) {
        const g = bounds[i + 1] - bounds[i];
        bounds.forEach((b) => {
          marks.push({ at: b + g, label: "Even" });
          marks.push({ at: b - g, label: "Even" });
        });
      }
      otherAts.forEach((o) => marks.push({ at: span - o, label: "Even" }));
      const nearOther = (v) => otherAts.some((o) => Math.abs(o - v) < 0.5);
      const usable = marks.filter(
        (m) => m.at > 1 && m.at < span - 1 && !nearOther(m.at),
      );
      const seamTol = Math.max(1.5, snap * 1.5);
      let snapLabel = null;
      let bestD = seamTol;
      usable.forEach((m) => {
        const d = Math.abs(m.at - at);
        if (d <= bestD) {
          bestD = d;
          at = m.at;
          snapLabel = m.label;
        }
      });
      setSeamSnap(snapLabel);
      editSeams((items) =>
        items.map((s) => (s.id === seam.id ? { ...s, at: round2(at) } : s)),
      );
      return;
    }
    if (drag.rotate) {
      const px = (e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx;
      const py = (e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy;
      const ang = (Math.atan2(py - drag.cy, px - drag.cx) * 180) / Math.PI;
      const rot = (((Math.round(ang / 5) * 5) % 360) + 360) % 360;
      update(drag.pieceId, { rot }, { silent: true });
      return;
    }
    let x = Math.max(
      0,
      Math.round(
        ((e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx - drag.offX) /
          snap,
      ) * snap,
    );
    let y = Math.max(
      0,
      Math.round(
        ((e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy - drag.offY) /
          snap,
      ) * snap,
    );
    if (drag.cutoutId) {
      const gx = (e.clientX - drag.rect.left) / drag.pxPerIn + drag.vx;
      const gy = (e.clientY - drag.rect.top) / drag.pxPerIn + drag.vy;
      const piece = list.find((p) => p.id === drag.pieceId);
      const cut = piece?.cutouts.find((x) => x.id === drag.cutoutId);
      if (!piece || !cut) return;
      // Work with the cut-out's on-screen box (its angle applied) so a rotated sink is
      // clamped/fitted by what you actually see, not its upright size.
      const fp = cutFootprint(cut);
      const r8 = (v) => Math.round(v * 8) / 8;
      // place the cut-out's box at local (lx,ly) on `pc`; keepOn = stay fully on stone
      const place = (pc, lx, ly, keepOn) => {
        let fx = Math.min(Math.max(0, r8(lx + fp.dx)), Math.max(0, pc.length_in - fp.W));
        let fy = Math.min(Math.max(0, r8(ly + fp.dy)), Math.max(0, pc.width_in - fp.H));
        const direct = cutFitsR(pc.points, fx, fy, Number(cut.w) || 0, Number(cut.h) || 0, Number(cut.rot) || 0);
        if (keepOn && !direct) {
          const [nx, ny] = slideCutR(pc.points, fx, fy, fx, fy, Number(cut.w) || 0, Number(cut.h) || 0, Number(cut.rot) || 0);
          fx = r8(nx);
          fy = r8(ny);
        }
        return { x: round2(fx - fp.dx), y: round2(fy - fp.dy), direct };
      };
      const gl = rotPt(
        [gx - piece.x, gy - piece.y],
        [piece.length_in / 2, piece.width_in / 2],
        -(piece.rot || 0),
      );
      // cross-piece: hand the cut-out to an adjacent (unrotated) run only when the
      // cursor has left this piece and the cut-out sits cleanly on the other one
      const overSelf = insidePoly(gl, piece.points);
      const target = overSelf
        ? null
        : list.find(
            (p) =>
              p.id !== drag.pieceId &&
              !p.panel_kind &&
              (Number(p.rot) || 0) % 360 === 0 &&
              insidePoly([gx - p.x, gy - p.y], p.points),
          );
      const onTarget = target
        ? place(target, gx - target.x - drag.offX, gy - target.y - drag.offY, false)
        : null;
      if (target && onTarget.direct) {
        const moved = { ...cut, x: onTarget.x, y: onTarget.y };
        const next = list.map((p) => {
          if (p.id === drag.pieceId)
            return { ...p, cutouts: p.cutouts.filter((c) => c.id !== cut.id) };
          if (p.id === target.id)
            return { ...p, cutouts: [...(p.cutouts || []), moved] };
          return p;
        });
        onChange(withPanelPieces(next));
        setDrag((d) => ({ ...d, pieceId: target.id }));
        setSel({ pieceId: target.id, edge: null, cutoutId: cut.id });
        setCutoutGuide(null);
        return;
      }
      // same-piece move (free 1/8" steps): snaps to the nearest fully-on-stone spot
      // under the cursor (around L/U corners too). If the cut-out can't fit anywhere
      // nearby (bigger than the run, odd angle) it is never pinned — it simply
      // follows the cursor inside the piece outline.
      const at = place(piece, gl[0] - drag.offX, gl[1] - drag.offY, true);
      setCutoutGuide(null);
      update(
        drag.pieceId,
        {
          cutouts: piece.cutouts.map((c) =>
            c.id === drag.cutoutId ? { ...c, x: at.x, y: at.y } : c,
          ),
        },
        { silent: true },
      );
      return;
    }
    // snap flush to a neighbouring piece's edges — an upright panel snaps hard onto the
    // nearest flat counter so backsplash / waterfall pieces sit right against the run
    if (snapPieces) {
      const me = list.find((p) => p.id === drag.pieceId);
      const upright = me?.orientation === "vertical";
      const others = list.filter(
        (p) => p.id !== drag.pieceId && (!upright || p.orientation !== "vertical"),
      );
      const tol = upright ? SNAP_TOL * 3 : SNAP_TOL;
      const alongTol = upright ? SNAP_TOL * 4 : SNAP_TOL * 2;
      let snapped = false;
      let guide = null;
      others.forEach((o) => {
        const cand = [
          [o.x + o.length_in, y],
          [o.x - me.length_in, y],
          [x, o.y + o.width_in],
          [x, o.y - me.width_in],
        ];
        cand.forEach(([cx, cy], i) => {
          if (snapped) return;
          const near = i < 2 ? Math.abs(cx - x) : Math.abs(cy - y);
          if (near <= tol) {
            // land exactly flush (no whole-inch rounding) so there is never a hairline gap
            x = Math.max(0, round2(i < 2 ? cx : x));
            y = Math.max(0, round2(i < 2 ? y : cy));
            // align along the shared edge so the piece sits flush end-to-end
            if (i < 2 && Math.abs(o.y - y) <= alongTol) y = o.y;
            if (i >= 2 && Math.abs(o.x - x) <= alongTol) x = o.x;
            snapped = true;
            const lo = i < 2 ? Math.min(o.y, y) : Math.min(o.x, x);
            const hi = i < 2 ? Math.max(o.y + o.width_in, y + me.width_in) : Math.max(o.x + o.length_in, x + me.length_in);
            guide = i < 2
              ? { x: i === 0 ? cx : o.x, from: lo, to: hi, bx: i === 0 ? cx : o.x, by: (lo + hi) / 2 }
              : { y: i === 2 ? cy : o.y, from: lo, to: hi, bx: (lo + hi) / 2, by: i === 2 ? cy : o.y };
          }
        });
      });
      setSnapGuide(guide);
    }
    update(drag.pieceId, { x, y }, { silent: true });
  };

  // Snap an upright panel flush along a counter edge and auto-fit it to that edge's length.
  const [attachPick, setAttachPick] = useState(false);
  const attachPanelToEdge = (panel, parent, e, heightOverride) => {
    const pts = parent.points || [];
    const n = pts.length;
    if (n < 2) return;
    const center = [(parent.length_in || 0) / 2, (parent.width_in || 0) / 2];
    const W = (pt) => {
      const r = rotPt(pt, center, parent.rot || 0);
      return [r[0] + (parent.x || 0), r[1] + (parent.y || 0)];
    };
    const A = W(pts[e]);
    const B = W(pts[(e + 1) % n]);
    const len = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
    const ux = (B[0] - A[0]) / len;
    const uy = (B[1] - A[1]) / len;
    const cen = W([
      pts.reduce((s, q) => s + q[0], 0) / n,
      pts.reduce((s, q) => s + q[1], 0) / n,
    ]);
    let nx = -uy;
    let ny = ux;
    const mx = (A[0] + B[0]) / 2;
    const my = (A[1] + B[1]) / 2;
    if ((mx - cen[0]) * nx + (my - cen[1]) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    const stand = panel.stand === "waterfall" ? "waterfall" : "backsplash";
    const height =
      Number(heightOverride) > 0
        ? Number(heightOverride)
        : stand === "waterfall"
          ? 34.5
          : 4;
    const corners = [
      A,
      B,
      [B[0] + nx * height, B[1] + ny * height],
      [A[0] + nx * height, A[1] + ny * height],
    ];
    const minX = Math.min(...corners.map((c) => c[0]));
    const minY = Math.min(...corners.map((c) => c[1]));
    const rnd = round2;
    update(panel.id, {
      orientation: "vertical",
      stand,
      attach: { piece_id: parent.id, edge: e },
      points: corners.map((c) => [rnd(c[0] - minX), rnd(c[1] - minY)]),
      x: rnd(minX),
      y: rnd(minY),
      rot: 0,
      length_in: rnd(len),
      width_in: height,
    });
  };

  const setPanelHeight = (panel, h) => {
    const parent = list.find((p) => p.id === panel.attach?.piece_id);
    if (parent) attachPanelToEdge(panel, parent, panel.attach.edge, h);
    else update(panel.id, { width_in: Math.max(1, Number(h) || 1) });
  };

  // Turn an auto-generated backsplash / waterfall ghost into a standalone, editable
  // upright piece (curves & bumps, seams and cut-outs) and drop the auto panel off the
  // parent edge so it isn't drawn twice or double-priced. Waterfalls/mitres stay linked
  // to the counter edge so they keep matching its length; backsplashes become fixed.
  const detachPanel = (parent, edgeIdx, kind) => {
    const e = parent?.edges?.[edgeIdx];
    if (!e) return;
    const k = kind || "backsplash";
    const L = round2(edgeLen(parent.points, edgeIdx));
    const H = round2(
      k === "backsplash" ? Number(e.backsplash_height_in) || 0 : Number(e.drop_in) || 0,
    );
    if (!(L > 0 && H > 0)) {
      toast.error("Nothing to detach on this side yet");
      return;
    }
    const stand = k === "backsplash" ? "backsplash" : "waterfall";
    const linked = k !== "backsplash";
    const abbr = k === "backsplash" ? "BS" : k === "mitre" ? "MT" : "WF";
    const baseLabel = (parent.label || "Run").replace(/ [A-Z]$/, "");
    const geo = {
      points: SHAPES.Rectangle(L, H),
      x: round2(parent.x || 0),
      y: round2((parent.y || 0) + (parent.width_in || 0) + 8),
      rot: round2(parent.rot || 0),
    };
    const panel = normalizePiece({
      id: uid(),
      label: `${baseLabel} ${abbr}`,
      orientation: "vertical",
      stand,
      cutouts: [],
      ...(linked ? { attach: { piece_id: parent.id, edge: edgeIdx } } : {}),
      ...geo,
      edges: geo.points.map((_, i) => ({
        profile: e.panel_edges?.[k]?.[i] || e.profile || "Eased",
        seam: linked && i === 0,
      })),
    });
    const nextList = list.map((p) =>
      p.id === parent.id
        ? {
            ...p,
            edges: (p.edges || []).map((eg, i) =>
              i === edgeIdx
                ? k === "backsplash"
                  ? { ...eg, backsplash_height_in: 0, backsplash_sqft: 0 }
                  : { ...eg, drop_in: 0, miter: false, seam: true }
                : eg,
            ),
          }
        : p,
    );
    push([...nextList, panel]);
    setSel({ pieceId: panel.id, edge: null });
    toast.success(
      linked
        ? "Waterfall detached & linked to the counter — edit it like any piece"
        : "Backsplash detached — edit it like any piece",
    );
  };

  // Keep length-linked upright panels matched to their counter edge (rectangles only —
  // once a panel is shaped with bumps it keeps its own outline).
  useEffect(() => {
    list.forEach((p) => {
      if (p.orientation !== "vertical" || !p.attach?.piece_id) return;
      if ((p.bumps || []).length || (p.points || []).length !== 4) return;
      const par = list.find((x) => x.id === p.attach.piece_id);
      if (!par) return;
      const want = round2(edgeLen(par.points, p.attach.edge));
      const have = round2(edgeLen(p.points, 0));
      if (want > 0 && Math.abs(want - have) > 0.1)
        update(p.id, { points: SHAPES.Rectangle(want, p.width_in || 1) });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  // Drop a row of evenly-spaced single-gang outlets along an upright panel.
  const addOutletRow = () => {
    if (!selPiece) return;
    const [w, h] = CUTOUT_SIZE["Single gang"];
    const span = selPiece.length_in || 0;
    const count = Math.max(2, Math.min(6, Math.floor(span / 30)));
    const y = outletY(h);
    const slot = span / count;
    const items = Array.from({ length: count }, (_, i) => ({
      id: uid(),
      kind: "Single gang",
      w,
      h,
      x: round2(slot * (i + 0.5) - w / 2),
      y,
      rot: 0,
    }));
    update(selPiece.id, { cutouts: [...(selPiece.cutouts || []), ...items] });
    toast.success(`Added ${count} outlets`);
  };

  // new cut-outs sit a set-back off the front (bottom) edge, not the piece centre
  const frontY = (h, kind, piece = selPiece) => {
    const ck = /cooktop|range|hob/i.test(kind || "") ? Number(general?.cooktop_setback_in) || 0 : 0;
    const d = ck > 0 ? ck : Math.max(0, Number(setback) || 0);
    return Math.max(0, round2(piece.width_in - h - d));
  };

  // outlet / switch height on an upright panel: a set distance above the counter
  // (bottom edge) when configured in Drawing settings, otherwise centred on the panel.
  const outletY = (h, piece = selPiece) => {
    const oh = Number(general?.outlet_height_in) || 0;
    return oh > 0
      ? Math.max(1, round2(piece.width_in - oh - h))
      : Math.max(1, round2(piece.width_in / 2 - h / 2));
  };

  // nearest spot to `prefer` where a w×h cut-out sits fully on the stone (matters for L / U pieces)
  const placeInside = (piece, w, h, prefer) => {
    const pts = piece.points || [];
    const fits = (x, y) => cutFits(pts, x, y, w, h);
    const px = Math.max(0, Math.min(piece.length_in - w, prefer[0]));
    const py = Math.max(0, Math.min(piece.width_in - h, prefer[1]));
    if (pts.length < 3 || fits(px, py)) return { x: round2(px), y: round2(py) };
    let best = null;
    for (let y = 0; y <= piece.width_in - h; y += 1)
      for (let x = 0; x <= piece.length_in - w; x += 1) {
        const d = Math.hypot(x - px, y - py);
        if ((!best || d < best.d) && fits(x, y)) best = { x, y, d };
      }
    return best ? { x: round2(best.x), y: round2(best.y) } : { x: round2(px), y: round2(py) };
  };

  // Default drawn size for a cut-out kind: the shop's saved Drawing → Cut-outs default wins,
  // then the built-in preset, then a generic fallback.
  const cutDims = (kind) => {
    const s = cutoutSizes?.[kind];
    if (s && Number(s.w) > 0 && Number(s.h) > 0) return [Number(s.w), Number(s.h)];
    return CUTOUT_SIZE[kind] || [24, 18];
  };

  const addCutout = (kind, size, opts = {}) => {
    const piece = opts.piece || selPiece;
    if (!piece) return;
    const [w, h] = size || cutDims(kind);
    const isSink = kind.toLowerCase().includes("sink");
    const round = ["Faucet hole", "Soap dispenser", "Circle"].includes(kind);
    const isOval = kind === "Oval";
    // outlet / switch boxes sit at the outlet height (or centred) on the piece
    const electrical = /gang|outlet|switch/i.test(kind);
    const prefer = opts.at
      ? [opts.at[0] - w / 2, opts.at[1] - h / 2]
      : [Math.max(2, Math.round(piece.length_in / 2 - w / 2)), electrical ? outletY(h, piece) : frontY(h, kind, piece)];
    const spot = placeInside(piece, w, h, prefer);
    update(piece.id, {
      cutouts: [
        ...(piece.cutouts || []),
        {
          id: uid(),
          kind,
          w,
          h,
          x: spot.x,
          y: spot.y,
          rot: 0,
          shape: isOval ? "Oval" : round ? "Round" : "Rectangle",
          sink_type: kind === "Farmhouse sink" ? "Farmhouse" : "Undermount",
          faucet_holes: isSink ? 1 : 0,
        },
      ],
    });
  };

  const addKit = (kit) => {
    if (!selPiece || !kit) return;
    const L = selPiece.length_in;
    const W = selPiece.width_in;
    const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
    const parts = (kit.parts || []).map((pt) => {
      const w = Math.max(0.25, Number(pt.w) || 4);
      const h = Math.max(0.25, Number(pt.h) || 4);
      const isSink = /sink/i.test(pt.kind || "");
      const round = /faucet|soap|hole|dispenser|drain/i.test(pt.kind || "");
      const cxp = L / 2 + (Number(pt.dx) || 0);
      const cyp = W / 2 + (Number(pt.dy) || 0);
      return {
        id: uid(),
        kind: pt.kind || "Faucet hole",
        w,
        h,
        x: round2(clamp(cxp - w / 2, 0, Math.max(0, L - w))),
        y: round2(clamp(cyp - h / 2, 0, Math.max(0, W - h))),
        rot: 0,
        shape: round ? "Round" : "Rectangle",
        ...(isSink
          ? { sink_type: "Undermount", faucet_holes: 1 }
          : { faucet_holes: 0 }),
      };
    });
    if (!parts.length) return;
    if (parts.some((c) => c.w > L + 0.01 || c.h > W + 0.01)) {
      toast.error(`This piece is too small for the ${kit.name || "kit"}`);
      return;
    }
    update(selPiece.id, {
      cutouts: [...(selPiece.cutouts || []), ...parts],
    });
    toast.success(`${kit.name || "Kit"} added`);
  };

  const autoNestSeams = (piece) => {
    if (!piece) return;
    const L = piece.length_in;
    const W = piece.width_in;
    const items = [];
    const nV = Math.max(1, Math.ceil(L / slabL));
    for (let k = 1; k < nV; k++)
      items.push({ id: uid(), dir: "v", at: round2((L * k) / nV) });
    const nH = Math.max(1, Math.ceil(W / slabW));
    for (let k = 1; k < nH; k++)
      items.push({ id: uid(), dir: "h", at: round2((W * k) / nH) });
    if (!items.length) {
      toast("This piece already fits on a single slab");
      return;
    }
    setShowSeamGuide(true);
    putSeams(piece.id, items);
    setActiveSeam(items[0].id);
    toast.success(
      `Suggested ${items.length} seam${items.length > 1 ? "s" : ""} to nest on ${
        nV * nH
      } slab${nV * nH > 1 ? "s" : ""}`,
    );
  };

  const tplFields = (t) => ({
    kind: t.kind || "Undermount sink",
    w: Number(t.w) || 24,
    h: Number(t.h) || 18,
    shape: t.shape || "Rectangle",
    sink_type: t.sink_type || "Undermount",
    faucet_holes: Number(t.faucet_holes) || 0,
    faucet_spacing: Number(t.faucet_spacing) || 4,
    center_h: t.center_h || "None",
    center_v: t.center_v || "None",
    install_service: t.install_service || "Installation",
    product: t.product || "",
  });

  const useTemplateFromPicker = (t) => {
    if (!selPiece) return;
    if (tplPicker?.cutoutId) {
      update(selPiece.id, {
        cutouts: (selPiece.cutouts || []).map((x) =>
          x.id === tplPicker.cutoutId ? { ...x, ...tplFields(t) } : x,
        ),
      });
    } else applyTemplate(t);
    setTplPicker(null);
    toast.success(`Applied "${t.name}"`);
  };

  const saveCustomLayout = () => {
    const name = (customName || "").trim();
    if (!name || !selPiece) return;
    const pts = selPiece.points.map((p) => [round2(p[0]), round2(p[1])]);
    const w = Math.max(...pts.map((p) => p[0]));
    const h = Math.max(...pts.map((p) => p[1]));
    const id = uid();
    onSaveCustomLayout({
      id,
      key: `custom-${id}`,
      label: name,
      note: `${formatInches(w)} x ${formatInches(h)}`,
      points: pts,
      custom: true,
    });
    setCustomName(null);
  };

  const applyTemplate = (t) => {
    if (!selPiece) return;
    const w = Number(t.w) || 24;
    const h = Number(t.h) || 18;
    update(selPiece.id, {
      cutouts: [
        ...(selPiece.cutouts || []),
        {
          id: uid(),
          kind: t.kind || "Undermount sink",
          w,
          h,
          x: Math.max(2, Math.round(selPiece.length_in / 2 - w / 2)),
          y: frontY(h),
          rot: 0,
          shape: t.shape || "Rectangle",
          sink_type: t.sink_type || "Undermount",
          faucet_holes: Number(t.faucet_holes) || 0,
          faucet_spacing: Number(t.faucet_spacing) || 4,
          center_h: t.center_h || "None",
          center_v: t.center_v || "None",
          install_service: t.install_service || "Installation",
          product: t.product || "",
        },
      ],
    });
  };

  const snapCutout = (c, where) => {
    const d = Math.max(0, Number(setback) || 0);
    if (where === "left") return { x: d };
    if (where === "right")
      return { x: Math.max(0, round2(selPiece.length_in - c.w - d)) };
    if (where === "top") return { y: d };
    if (where === "bottom")
      return { y: Math.max(0, round2(selPiece.width_in - c.h - d)) };
    return { x: Math.max(0, round2(selPiece.length_in / 2 - c.w / 2)) };
  };

  const onCutoutDown = (e, pieceId, cutoutId) => {
    if (e.button === 1 || spaceRef.current || step !== "cutouts") return;
    e.stopPropagation();
    snapshot();
    const rect = svgRef.current.getBoundingClientRect();
    const pxPerIn = rect.width / view.w;
    const piece = list.find((p) => p.id === pieceId);
    const c = piece.cutouts.find((x) => x.id === cutoutId);
    setDrag({
      pieceId,
      cutoutId,
      rect,
      pxPerIn,
      vx: view.x,
      vy: view.y,
      // grab offset in piece-local inches (undo the piece rotation so drags track the mouse)
      ...(() => {
        const g = [(e.clientX - rect.left) / pxPerIn + view.x - piece.x, (e.clientY - rect.top) / pxPerIn + view.y - piece.y];
        const l = rotPt(g, [piece.length_in / 2, piece.width_in / 2], -(piece.rot || 0));
        return { offX: l[0] - c.x, offY: l[1] - c.y };
      })(),
    });
    setSel({ pieceId, edge: null, cutoutId });
  };

  const totalSqft = list.reduce((n, p) => n + polyArea(p.points) / 144, 0);
  const reviewSqft =
    typeof billedSqft === "number" && billedSqft > 0 ? billedSqft : totalSqft;
  const reviewPerSqft =
    reviewSqft > 0 && typeof liveTotal === "number" ? liveTotal / reviewSqft : null;
  const bandLow =
    rateBand && rateBand.low != null && Number(rateBand.low) > 0
      ? Number(rateBand.low)
      : typeof expectedRate === "number" && expectedRate > 0
        ? expectedRate
        : null;
  const bandHigh =
    rateBand && rateBand.high != null && Number(rateBand.high) > 0
      ? Number(rateBand.high)
      : typeof expectedRate === "number" && expectedRate > 0
        ? expectedRate * 4
        : null;
  const reviewRateOff =
    reviewPerSqft != null && bandLow != null && bandHigh != null
      ? reviewPerSqft < bandLow || reviewPerSqft > bandHigh
      : false;
  if (reviewRateOff)
    missing.push({
      key: "rate",
      text:
        reviewPerSqft < bandLow
          ? `Below ${money(bandLow)}/ft² floor`
          : `Above ${money(bandHigh)}/ft² ceiling`,
      go: () => onOpenQuote?.(),
    });

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "z") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieces]);

  useEffect(() => {
    if (!draw) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "Escape") setDraw(null);
      if (e.key === "Enter") {
        if (draw?.kind === "line" || draw?.kind === "poly") addLineSegment();
        else finishDraw();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draw, runWidth, pieces, lineLen, lineAngle, lineSnap]);

  // Cut-out parameters: rendered in the LEFT tool panel (see lstep === "cutouts").
  const cutoutPanel = selPiece && sel?.cutoutId != null ? (
              (() => {
                const c = (selPiece.cutouts || []).find(
                  (x) => x.id === sel.cutoutId,
                );
                if (!c) return null;
                const setCut = (patch) =>
                  update(selPiece.id, {
                    cutouts: selPiece.cutouts.map((x) =>
                      x.id === c.id ? { ...x, ...patch } : x,
                    ),
                  });
                return (
                  <div
                    className="mt-3 space-y-3"
                    data-testid="designer-cutout-panel"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Cut-out name
                        </span>
                        <Input
                          data-testid="designer-cutout-name"
                          value={c.name ?? c.kind ?? ""}
                          onChange={(e) => setCut({ name: e.target.value })}
                          placeholder={c.kind || "Cut-out"}
                          className="mt-1 h-9 border-zinc-800 bg-zinc-950 text-xs"
                        />
                      </div>
                      <button
                        type="button"
                        data-testid="designer-use-template"
                        onClick={() => setTplPicker({ cutoutId: c.id })}
                        className="mt-4 flex shrink-0 items-center gap-1 rounded-md border border-zinc-700 px-2 py-2 text-[9px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-zinc-800"
                      >
                        <ClipboardText size={11} weight="bold" /> Use template
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Sink type
                        </span>
                        <Select
                          value={c.sink_type || "Undermount"}
                          onValueChange={(v) => setCut({ sink_type: v })}
                        >
                          <SelectTrigger
                            data-testid="designer-cutout-sink-type"
                            className="h-10 border-zinc-800 bg-zinc-950 text-xs"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SINK_TYPES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Shape
                        </span>
                        <Select
                          value={c.shape || "Rectangle"}
                          onValueChange={(v) => setCut({ shape: v })}
                        >
                          <SelectTrigger
                            data-testid="designer-cutout-shape"
                            className="h-10 border-zinc-800 bg-zinc-950 text-xs"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CUTOUT_SHAPES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Size L (in)
                        </span>
                        <MeasureInput
                          data-testid="designer-cutout-width"
                          min={1}
                          fallback={1}
                          value={c.w}
                          onChange={(n) => setCut({ w: n })}
                          className="h-10 border-zinc-800 bg-zinc-950"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Size W (in)
                        </span>
                        <MeasureInput
                          data-testid="designer-cutout-height"
                          min={1}
                          fallback={1}
                          value={c.h}
                          onChange={(n) => setCut({ h: n })}
                          className="h-10 border-zinc-800 bg-zinc-950"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          From left (in)
                        </span>
                        <MeasureInput
                          data-testid="designer-cutout-x"
                          min={0}
                          value={c.x}
                          onChange={(n) => setCut({ x: n })}
                          className="h-10 border-zinc-800 bg-zinc-950"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          From front / bottom (in)
                        </span>
                        <MeasureInput
                          data-testid="designer-cutout-y"
                          min={0}
                          value={round2(Math.max(0, selPiece.width_in - c.y - c.h))}
                          onChange={(n) =>
                            setCut({ y: Math.max(0, round2(selPiece.width_in - c.h - (Number(n) || 0))) })
                          }
                          className="h-10 border-zinc-800 bg-zinc-950"
                        />
                      </div>
                    </div>

                    {!["Oval", "Round", "D-shape"].includes(c.shape) && (
                      <div className="space-y-1" data-testid="designer-cutout-radius">
                        <span className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
                          Corner radius (in)
                          {Number(c.radius) > 0 && (
                            <button
                              type="button"
                              data-testid="designer-cutout-radius-remove"
                              onClick={() => setCut({ radius: 0 })}
                              className="text-[10px] font-bold uppercase tracking-widest text-red-400 hover:text-red-300"
                            >
                              Remove
                            </button>
                          )}
                        </span>
                        <MeasureInput
                          data-testid="designer-cutout-radius-input"
                          min={0}
                          value={c.radius ?? 1.5}
                          onChange={(n) => setCut({ radius: Math.max(0, n) })}
                          className="h-10 border-zinc-800 bg-zinc-950"
                        />
                      </div>
                    )}

                    <div className="space-y-1" data-testid="designer-cutout-angle">
                      <span className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
                        Angle (°)
                        {((Number(c.rot) || 0) % 360) !== 0 && (
                          <button
                            type="button"
                            data-testid="designer-cutout-angle-reset"
                            onClick={() => setCut({ rot: 0 })}
                            className="text-[10px] font-bold uppercase tracking-widest text-red-400 hover:text-red-300"
                          >
                            Reset
                          </button>
                        )}
                      </span>
                      <div className="flex gap-1">
                        {[0, 45, 90, 135].map((a) => (
                          <button
                            key={a}
                            type="button"
                            data-testid={`designer-cutout-angle-${a}`}
                            onClick={() => setCut({ rot: a })}
                            className={`flex-1 rounded-md py-1.5 text-[10px] font-bold transition-colors duration-150 ${
                              ((Number(c.rot) || 0) % 360) === a
                                ? "bg-indigo-500 text-white"
                                : "border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                            }`}
                          >
                            {a}°
                          </button>
                        ))}
                        <input
                          type="number"
                          data-testid="designer-cutout-angle-input"
                          min={0}
                          max={359}
                          value={Math.round(Number(c.rot) || 0)}
                          onChange={(e) => {
                            let v = Math.round(Number(e.target.value) || 0) % 360;
                            if (v < 0) v += 360;
                            setCut({ rot: v });
                          }}
                          className="h-10 w-16 rounded-md border border-zinc-800 bg-zinc-950 px-2 text-center text-sm text-zinc-100"
                        />
                      </div>
                      <p className="text-[9px] text-zinc-600">
                        Spin the cut-out to any angle — handy for corner sinks.
                      </p>
                    </div>

                    {/cook|range|hob|burner/i.test(`${c.kind || ""} ${c.name || ""}`) ? (
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Number of burners
                        </span>
                        <div
                          className="flex gap-1"
                          data-testid="designer-cutout-burners"
                        >
                          {[2, 4, 5, 6].map((n) => (
                            <button
                              key={n}
                              type="button"
                              data-testid={`designer-cutout-burner-${n}`}
                              onClick={() => setCut({ burners: n })}
                              className={`flex-1 rounded-md py-1.5 text-[10px] font-bold transition-colors duration-150 ${
                                (Number(c.burners) || 4) === n
                                  ? "bg-indigo-500 text-white"
                                  : "border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                              }`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                    <div className="space-y-1">
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                        Faucet holes
                      </span>
                      <div
                        className="flex gap-1"
                        data-testid="designer-cutout-faucets"
                      >
                        {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                          <button
                            key={n}
                            type="button"
                            data-testid={`designer-cutout-faucet-${n}`}
                            onClick={() => setCut({ faucet_holes: n })}
                            className={`flex-1 rounded-md py-1.5 text-[10px] font-bold transition-colors duration-150 ${
                              (c.faucet_holes || 0) === n
                                ? "bg-indigo-500 text-white"
                                : "border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                            }`}
                          >
                            {n === 6 ? "6+" : n}
                          </button>
                        ))}
                      </div>
                      {(c.faucet_holes || 0) > 1 && (
                        <div
                          className="flex gap-1 pt-1"
                          data-testid="designer-cutout-faucet-spacing"
                        >
                          {[4, 6, 8].map((s) => (
                            <button
                              key={s}
                              type="button"
                              data-testid={`designer-cutout-faucet-spacing-${s}`}
                              onClick={() => setCut({ faucet_spacing: s })}
                              className={`flex-1 rounded-md py-1 text-[10px] font-bold transition-colors duration-150 ${
                                (Number(c.faucet_spacing) || 4) === s
                                  ? "bg-indigo-500 text-white"
                                  : "border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                              }`}
                            >
                              {s}" spread
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    )}

                    <div className="space-y-1">
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                        Cut-out service (priced)
                      </span>
                      <Select
                        value={c.kind}
                        onValueChange={(v) => setCut(kindPatch(v))}
                      >
                        <SelectTrigger
                          data-testid="designer-cutout-service"
                          className="h-10 border-zinc-800 bg-zinc-950 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {withShapeCutouts(cutoutKinds).map((k) => (
                            <SelectItem key={k} value={k}>
                              {k}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                        Price for this cut-out ($)
                      </span>
                      <Input
                        data-testid="designer-cutout-price"
                        type="number"
                        step="0.01"
                        value={c.price ?? ""}
                        placeholder="Shop rate"
                        onChange={(e) => setCut({ price: e.target.value })}
                        className="h-10 border-zinc-800 bg-zinc-950"
                      />
                      <span className="block text-[10px] text-zinc-500">
                        Leave it empty to charge your Pricing settings rate for
                        a {c.kind || "cut-out"}.
                      </span>
                    </div>

                    {products.length > 0 && (
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Product
                        </span>
                        <Select
                          value={c.product || "none"}
                          onValueChange={(v) => {
                            const prod = products.find((x) => x.label === v);
                            const dims = parseCutoutSize(prod?.cutout_size);
                            setCut({
                              product: v === "none" ? "" : v,
                              ...(prod?.sink_type
                                ? { sink_type: prod.sink_type }
                                : {}),
                              ...(dims ? { w: dims[0], h: dims[1] } : {}),
                            });
                            if (dims)
                              toast.success(
                                `Cut-out sized to ${prod.label} (${formatInches(dims[0])} × ${formatInches(dims[1])})`,
                              );
                          }}
                        >
                          <SelectTrigger
                            data-testid="designer-cutout-product"
                            className="h-10 border-zinc-800 bg-zinc-950 text-xs"
                          >
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No product</SelectItem>
                            {products.map((p) => (
                              <SelectItem
                                key={p.id}
                                value={p.label}
                              >
                                {p.label}
                                {p.qty != null
                                  ? p.qty > 0
                                    ? ` · ${p.qty} in stock`
                                    : " · out of stock"
                                  : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <button
                      type="button"
                      data-testid="designer-cutout-customer-provided"
                      aria-pressed={!!c.customer_provided}
                      onClick={() => setCut({ customer_provided: !c.customer_provided })}
                      className={`flex h-10 w-full items-center justify-between rounded-lg border px-3 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                        c.customer_provided
                          ? "border-amber-400 bg-amber-500/15 text-amber-200"
                          : "border-zinc-700 bg-zinc-950 text-zinc-400 hover:border-zinc-500"
                      }`}
                    >
                      Sink provided by customer
                      <span className="text-[10px]">{c.customer_provided ? "Yes" : "No"}</span>
                    </button>

                    <div className="space-y-1">
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                        Installation service
                      </span>
                      <Select
                        value={c.install_service || "Installation"}
                        onValueChange={(v) => setCut({ install_service: v })}
                      >
                        <SelectTrigger
                          data-testid="designer-cutout-install"
                          className="h-10 border-zinc-800 bg-zinc-950 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INSTALL_SERVICES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Angle (°)
                        </span>
                        <Input
                          data-testid="designer-cutout-rot"
                          type="number"
                          step="any"
                          value={c.rot || 0}
                          onChange={(e) =>
                            setCut({ rot: Number(e.target.value) || 0 })
                          }
                          className="h-10 border-zinc-800 bg-zinc-950"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Set back from front (in)
                        </span>
                        <MeasureInput
                          data-testid="designer-cutout-setback"
                          min={0}
                          value={round2(Math.max(0, selPiece.width_in - c.y - c.h))}
                          onChange={(n) => {
                            const d = Math.max(0, Number(n) || 0);
                            setSetback(d);
                            setCut({ y: Math.max(0, round2(selPiece.width_in - c.h - d)) });
                          }}
                          className="h-10 border-zinc-800 bg-zinc-950"
                        />
                      </div>
                    </div>

                    {onSaveCutoutTemplate &&
                      (tplName === null ? (
                        <Button
                          data-testid="designer-save-template"
                          variant="outline"
                          onClick={() => setTplName(`${c.kind} ${c.w}x${c.h}`)}
                          className="h-10 w-full border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest hover:bg-zinc-800"
                        >
                          Save cut-out as template
                        </Button>
                      ) : (
                        <div
                          className="flex gap-2"
                          data-testid="designer-save-template-row"
                        >
                          <Input
                            data-testid="designer-template-name"
                            value={tplName}
                            onChange={(e) => setTplName(e.target.value)}
                            className="h-10 border-zinc-800 bg-zinc-950"
                          />
                          <Button
                            data-testid="designer-template-confirm"
                            onClick={() => {
                              onSaveCutoutTemplate({
                                id: uid(),
                                name: tplName.trim() || c.kind,
                                kind: c.kind,
                                shape: c.shape || "Rectangle",
                                sink_type: c.sink_type || "Undermount",
                                w: c.w,
                                h: c.h,
                                faucet_holes: Number(c.faucet_holes) || 0,
                                faucet_spacing: Number(c.faucet_spacing) || 4,
                                center_h: c.center_h || "None",
                                center_v: c.center_v || "None",
                                install_service:
                                  c.install_service || "Installation",
                                product: c.product || "",
                              });
                              setTplName(null);
                            }}
                            className="h-10 bg-indigo-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                          >
                            Save
                          </Button>
                          <Button
                            data-testid="designer-template-cancel"
                            variant="outline"
                            onClick={() => setTplName(null)}
                            className="h-10 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest hover:bg-zinc-800"
                          >
                            Cancel
                          </Button>
                        </div>
                      ))}

                    <div className="flex w-full min-w-0 items-center gap-2">
                      <Button
                        data-testid="designer-cutout-duplicate"
                        variant="outline"
                        onClick={() => {
                          const copy = {
                            ...c,
                            id: uid(),
                            x: round2(c.x + 4),
                            y: round2(c.y + 4),
                          };
                          update(selPiece.id, {
                            cutouts: [...selPiece.cutouts, copy],
                          });
                          setSel({
                            pieceId: selPiece.id,
                            edge: null,
                            cutoutId: copy.id,
                          });
                        }}
                        className="h-10 flex-1 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest hover:bg-zinc-800"
                      >
                        Duplicate
                      </Button>
                      <Button
                        data-testid="designer-cutout-center"
                        variant="outline"
                        onClick={() =>
                          setCut({
                            x: Math.max(
                              0,
                              Math.round(selPiece.length_in / 2 - c.w / 2),
                            ),
                            y: Math.max(
                              0,
                              Math.round(selPiece.width_in / 2 - c.h / 2),
                            ),
                          })
                        }
                        className="h-10 flex-1 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest hover:bg-zinc-800"
                      >
                        Centre it
                      </Button>
                      <Button
                        data-testid="designer-cutout-delete"
                        variant="outline"
                        onClick={() => {
                          update(selPiece.id, {
                            cutouts: selPiece.cutouts.filter(
                              (x) => x.id !== c.id,
                            ),
                          });
                          setSel({ pieceId: selPiece.id, edge: null });
                        }}
                        title="Delete cut-out"
                        className="h-10 w-10 shrink-0 border-red-500/40 bg-transparent px-0 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash size={16} weight="bold" />
                      </Button>
                    </div>
                  </div>
                );
              })()
  ) : null;

  // "Draw your own" tools — lives in the right-hand panel (dimensions step).
  const drawSection = (
    <section data-testid="designer-draw-section">
    <button
      type="button"
      data-testid="designer-draw-collapse"
      onClick={() => setShowDraw((v) => !v)}
      className="flex w-full items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 transition-colors duration-150 hover:text-zinc-200"
    >
      <PencilRuler
        size={13}
        weight="bold"
        className="text-indigo-300"
      />
      Draw your own
      {drawOpen ? (
        <CaretDown
          size={13}
          weight="bold"
          className="ml-auto text-zinc-500"
        />
      ) : (
        <CaretRight
          size={13}
          weight="bold"
          className="ml-auto text-zinc-500"
        />
      )}
    </button>
    {!drawOpen && (
      <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
        Prefer to draw from scratch? Trace walls, sketch freehand
        or type exact lengths.
      </p>
    )}
    {drawOpen && (
    <div
      className="mt-3 space-y-2"
      data-testid="designer-freedraw"
    >
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[10px] uppercase tracking-widest text-zinc-500">
          Depth (in)
        </span>
        <MeasureInput
          data-testid="designer-run-width"
          min={1}
          value={runWidth}
          onChange={(n) => setRunWidth(n)}
          className="h-9 w-20 border-zinc-800 bg-zinc-950 text-xs"
        />
      </div>
      <div className="space-y-1.5" data-testid="designer-run-angle-panel">
        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
          Swing leg (bend run) · off straight
        </span>
        <AnglePicker
          value={runAngle}
          onPick={(a) => setRunAngle(a)}
          idBase="designer-run-angle-panel"
          className="grid-cols-4"
        />
      </div>
      <button
        type="button"
        data-testid="designer-draw-toggle"
        onClick={() =>
          setDraw(
            draw?.kind === "run"
              ? null
              : { kind: "run", pts: [], cursor: null },
          )
        }
        className={`w-full rounded-md py-2 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
          draw?.kind === "run"
            ? "bg-indigo-500 text-white"
            : "border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        }`}
      >
        <span className="block">
          {draw?.kind === "run" ? "Drawing… cancel" : "Draw a run"}
        </span>
        {draw?.kind !== "run" && (
          <span className="mt-0.5 block text-[9px] font-normal normal-case tracking-normal text-zinc-500">
            Straight counter · set depth
          </span>
        )}
      </button>
      <button
        type="button"
        data-testid="designer-wall-toggle"
        onClick={() =>
          setDraw(
            draw?.kind === "wall"
              ? null
              : { kind: "wall", pts: [], cursor: null },
          )
        }
        className={`w-full rounded-md py-2 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
          draw?.kind === "wall"
            ? "bg-amber-500 text-zinc-950"
            : "border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        }`}
      >
        <span className="block">
          {draw?.kind === "wall" ? "Tracing… cancel" : "Trace walls"}
        </span>
        {draw?.kind !== "wall" && (
          <span className="mt-0.5 block text-[9px] font-normal normal-case tracking-normal text-zinc-500">
            Follow the room walls
          </span>
        )}
      </button>
      <button
        type="button"
        data-testid="designer-poly-toggle"
        onClick={() =>
          setDraw(
            draw?.kind === "poly"
              ? null
              : { kind: "poly", pts: [], cursor: null },
          )
        }
        className={`w-full rounded-md py-2 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
          draw?.kind === "poly"
            ? "bg-emerald-500 text-zinc-950"
            : "border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        }`}
      >
        <span className="block">
          {draw?.kind === "poly" ? "Drawing… cancel" : "Freehand shape"}
        </span>
        {draw?.kind !== "poly" && (
          <span className="mt-0.5 block text-[9px] font-normal normal-case tracking-normal text-zinc-500">
            Click each corner
          </span>
        )}
      </button>
      <button
        type="button"
        data-testid="designer-box-toggle"
        onClick={() =>
          setDraw(
            draw?.kind === "box"
              ? null
              : { kind: "box", pts: [], cursor: null },
          )
        }
        className={`w-full rounded-md py-2 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
          draw?.kind === "box"
            ? "bg-sky-400 text-zinc-950"
            : "border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        }`}
      >
        <span className="block">
          {draw?.kind === "box" ? "Dragging… cancel" : "Drag a box"}
        </span>
        {draw?.kind !== "box" && (
          <span className="mt-0.5 block text-[9px] font-normal normal-case tracking-normal text-zinc-500">
            Press, drag and release for a rectangle
          </span>
        )}
      </button>
      <button
        type="button"
        data-testid="designer-line-toggle"
        onClick={() =>
          setDraw(
            draw?.kind === "line"
              ? null
              : { kind: "line", pts: [], cursor: null },
          )
        }
        className={`w-full rounded-md py-2 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
          draw?.kind === "line"
            ? "bg-fuchsia-500 text-white"
            : "border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        }`}
      >
        <span className="block">
          {draw?.kind === "line" ? "Drawing… cancel" : "Draw by length"}
        </span>
        {draw?.kind !== "line" && (
          <span className="mt-0.5 block text-[9px] font-normal normal-case tracking-normal text-zinc-500">
            Type length &amp; angle
          </span>
        )}
      </button>
      {(draw?.kind === "line" || draw?.kind === "poly") && (
        <div
          className="space-y-2 rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 p-2"
          data-testid="designer-line-panel"
        >
          {draw?.kind === "poly" && (
            <div className="text-[9px] font-normal normal-case tracking-normal text-fuchsia-200/70">
              Click the first corner, then type a length &amp; angle and press Add
              line (or Enter) to place each exact segment.
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="flex-1 text-[10px] uppercase tracking-widest text-zinc-400">
              Length (in)
            </span>
            <MeasureInput
              data-testid="designer-line-length"
              min={0.25}
              value={lineLen}
              onChange={(n) => setLineLen(n)}
              className="h-8 w-20 border-zinc-800 bg-zinc-950 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="flex-1 text-[10px] uppercase tracking-widest text-zinc-400">
              Angle (°)
            </span>
            <Input
              data-testid="designer-line-angle"
              type="number"
              step="any"
              value={lineAngle}
              onChange={(e) =>
                setLineAngle(Number(e.target.value) || 0)
              }
              className="h-8 w-20 border-zinc-800 bg-zinc-950 text-xs"
            />
          </div>
          <div
            className="flex gap-1"
            data-testid="designer-line-rotate"
          >
            {[5, 15, 25, 45, 90].map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`designer-line-rotate-${d}`}
                onClick={() =>
                  setLineAngle(
                    (a) =>
                      ((Math.round((Number(a) || 0) + d) % 360) +
                        360) %
                      360,
                  )
                }
                className="flex-1 rounded-md border border-zinc-800 py-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-fuchsia-500/15 hover:text-fuchsia-200"
              >
                +{d}°
              </button>
            ))}
          </div>
          <label className="flex cursor-pointer items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-zinc-400">
            <span>Snap &amp; join</span>
            <input
              type="checkbox"
              data-testid="designer-line-snap"
              checked={lineSnap}
              onChange={(e) => setLineSnap(e.target.checked)}
              className="h-4 w-4 accent-fuchsia-400"
            />
          </label>
          <button
            type="button"
            data-testid="designer-line-add"
            onClick={addLineSegment}
            disabled={!draw.pts.length}
            className="w-full rounded-md bg-fuchsia-500 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-fuchsia-400 disabled:opacity-40"
          >
            Add line
          </button>
        </div>
      )}
      {draw?.kind === "poly" && (
        <div className="flex gap-1" data-testid="designer-poly-snap">
          {[
            [90, "90°"],
            [45, "45°"],
            [15, "15°"],
            [0, "Any angle"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              data-testid={`designer-poly-snap-${v}`}
              onClick={() => setPolySnap(v)}
              className={`flex-1 rounded-md py-1.5 text-[9px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                polySnap === v
                  ? "bg-emerald-500/80 text-zinc-950"
                  : "border border-zinc-800 text-zinc-500 hover:bg-zinc-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {draw && (
        <>
          <p className="text-[10px] leading-relaxed text-zinc-500">
            {draw.kind === "wall"
              ? "Click each wall corner. Right-click, double-click or Finish closes the wall line, Esc cancels."
              : draw.kind === "poly"
                ? `Click each corner${polySnap ? ` — lines lock to ${polySnap}° (hold Alt for any angle)` : " at any angle"} — click the first corner again, double-click or Finish closes the shape, Esc cancels.`
                : draw.kind === "line"
                  ? "Click a start point, then set a length and angle and press Add line (or Enter). Each line joins the last; the endpoint snaps to the start to close. Finish or double-click to lay it down."
                  : draw.kind === "box"
                    ? `Press on the sheet, drag to size the rectangle and release — corners snap to ${snap}in. Keep dragging boxes; Esc stops.`
                    : `Move the mouse and click each corner — runs lock square, snap to ${snap}in and hug traced walls. Right-click, double-click or Finish lays the piece down, Esc cancels.`}
          </p>
          <div className="flex gap-1">
            <button
              type="button"
              data-testid="designer-draw-back"
              onClick={() =>
                setDraw((d) => ({
                  ...d,
                  pts: d.pts.slice(0, -1),
                }))
              }
              className="flex-1 rounded-md border border-zinc-700 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:bg-zinc-800"
            >
              Back
            </button>
            <button
              type="button"
              data-testid="designer-draw-finish"
              onClick={finishDraw}
              className="flex-1 rounded-md bg-indigo-500 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
            >
              Finish
            </button>
          </div>
        </>
      )}
      {walls.length > 0 && (
        <div
          className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950 p-2"
          data-testid="designer-walls-card"
        >
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
            {walls.length} wall line{walls.length > 1 ? "s" : ""}{" "}
            traced
          </div>
          <div className="flex gap-1">
            {["right", "left"].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`designer-wall-side-${s}`}
                onClick={() => setWallSide(s)}
                className={`flex-1 rounded-md py-1.5 text-[9px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  wallSide === s
                    ? "bg-zinc-700 text-white"
                    : "border border-zinc-800 text-zinc-500 hover:bg-zinc-800"
                }`}
              >
                {s} side
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="designer-counter-along-walls"
            onClick={counterAlongWalls}
            className="w-full rounded-md bg-amber-500 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-950 transition-colors duration-150 hover:bg-amber-400"
          >
            Counter along walls
          </button>
          <button
            type="button"
            data-testid="designer-walls-clear"
            onClick={() => onWallsChange?.([])}
            className="w-full rounded-md border border-zinc-800 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-red-400"
          >
            Clear walls
          </button>
        </div>
      )}
      {onTraceChange && (
        <div
          className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950 p-2"
          data-testid="designer-trace-card"
        >
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Sketch underlay
          </div>
          {!trace?.path ? (
            <button
              type="button"
              data-testid="designer-trace-upload"
              onClick={() => traceRef.current?.click()}
              className="w-full rounded-md border border-dashed border-zinc-700 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-emerald-400 hover:text-emerald-200"
            >
              Upload a sketch or photo
            </button>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-1">
                {[
                  ["w_in", "Width"],
                  ["x", "X"],
                  ["y", "Y"],
                ].map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="block text-[9px] uppercase tracking-widest text-zinc-600">
                      {label}
                    </span>
                    <MeasureInput
                      data-testid={`designer-trace-${k}`}
                      value={trace[k] ?? 0}
                      onChange={(n) =>
                        onTraceChange({
                          ...trace,
                          [k]: n,
                        })
                      }
                      className="h-8 border-zinc-800 bg-zinc-900 px-1.5 text-[11px]"
                    />
                  </label>
                ))}
              </div>
              <label className="block">
                <span className="block text-[9px] uppercase tracking-widest text-zinc-600">
                  Opacity
                </span>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  data-testid="designer-trace-opacity"
                  value={trace.opacity ?? 0.55}
                  onChange={(e) =>
                    onTraceChange({
                      ...trace,
                      opacity: Number(e.target.value),
                    })
                  }
                  className="h-1.5 w-full cursor-pointer accent-emerald-400"
                />
              </label>
              <div className="flex gap-1">
                <button
                  type="button"
                  data-testid="designer-trace-move"
                  onClick={() => setTraceMove((v) => !v)}
                  className={`flex-1 rounded-md py-1.5 text-[9px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    traceMove
                      ? "bg-emerald-500/80 text-zinc-950"
                      : "border border-zinc-800 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  {traceMove ? "Dragging on" : "Drag to move"}
                </button>
                <button
                  type="button"
                  data-testid="designer-trace-remove"
                  onClick={() => {
                    onTraceChange(null);
                    setTraceMove(false);
                  }}
                  className="flex-1 rounded-md border border-zinc-800 py-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-red-400"
                >
                  Remove
                </button>
              </div>
            </>
          )}
          <input
            ref={traceRef}
            data-testid="designer-trace-input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={uploadTrace}
          />
        </div>
      )}
    </div>
    )}
    </section>
  );

  if (sketch)
    return <StoneExDrawing onExit={() => setMode(free)} onSendToQuote={onSendToQuote} areaId={currentAreaId} fallbackRate={expectedRate || 0} />;
  return (
    <div
      data-testid="countertop-designer"
      className="flex h-full flex-col overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
    >
      <div className="shrink-0 border-b border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-900/60 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="designer-toggle-tools"
            title={showTools ? "Hide the tool panel" : "Show the tool panel"}
            onClick={() => setShowTools((v) => !v)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all duration-150 ${
              showTools
                ? "border-indigo-400/40 bg-indigo-500/10 text-indigo-200"
                : "border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
            }`}
          >
            <Wrench size={13} weight="bold" />
            {showTools ? "Tools" : "Tools"}
          </button>
          <button
            type="button"
            data-testid="designer-toggle-panel"
            title={showPanel ? "Hide the properties panel" : "Show the properties panel"}
            onClick={() => setShowPanel((v) => !v)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all duration-150 ${
              showPanel
                ? "border-indigo-400/40 bg-indigo-500/10 text-indigo-200"
                : "border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
            }`}
          >
            <SlidersHorizontal size={13} weight="bold" />
            Panel
          </button>
          <span
            className="font-mono-data flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950/60 px-3 py-1.5 text-[11px] text-zinc-400"
            data-testid="designer-totals"
          >
            <Shapes size={13} weight="bold" className="text-indigo-300" />
            {onAreaName ? (
              <>
                <input
                  data-testid="designer-area-name"
                  value={areaName || ""}
                  onChange={(e) => onAreaName(e.target.value)}
                  onBlur={(e) => !e.target.value.trim() && onAreaName("Area")}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  placeholder="Area name"
                  title="Rename this area"
                  aria-label="Area name"
                  className="w-24 rounded-sm border border-transparent bg-transparent px-1 -mx-1 font-sans text-[11px] font-semibold text-zinc-200 outline-none transition-colors duration-150 hover:border-zinc-700 focus:border-amber-400/60 focus:bg-zinc-900"
                />
                <span>·</span>
              </>
            ) : areaName ? (
              `${areaName} · `
            ) : (
              ""
            )}
            {list.length} pieces · {totalSqft.toFixed(2)} ft²
          </span>
          {free && (
            <div
              className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto"
              data-testid="designer-missing"
            >
              {missing.length === 0 ? (
                <span
                  data-testid="designer-missing-clear"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300"
                >
                  <Check size={11} weight="bold" /> Drawing complete
                </span>
              ) : (
                missing.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    data-testid={`designer-missing-${m.key}`}
                    onClick={m.go}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
                  >
                    <Warning size={11} weight="fill" /> {m.text}
                  </button>
                ))
              )}
            </div>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-1">
            {/* Cluster: Mode */}
            <div
              className="flex overflow-hidden rounded-lg border border-zinc-700"
              data-testid="designer-mode"
              title="Guided walks through the steps; Free shows every tool at once"
            >
              {[
                ["guided", "Guided", false],
                ["free", "Free", true],
              ].map(([k, label, val]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`designer-mode-${k}`}
                  onClick={() => setMode(val)}
                  className={`h-8 px-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    free === val
                      ? "bg-indigo-500 text-white"
                      : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="mx-0.5 h-6 w-px shrink-0 bg-zinc-700/70" aria-hidden="true" data-testid="designer-toolbar-div-mode" />
            {/* Cluster: Draw tools */}
            <button
              type="button"
              data-testid="designer-layout-picker-open"
              title="Start with a layout — Rectangle, L, U, island and more"
              onClick={() => setShowLayoutPicker(true)}
              className="flex h-8 items-center gap-1.5 rounded-lg border border-indigo-400/50 bg-indigo-500/10 px-2.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-all duration-150 hover:bg-indigo-500/20"
            >
              <Shapes size={14} weight="bold" />{" "}
              <span className="hidden sm:inline">Layout</span>
            </button>
            {onNotesChange && (
              <button
                type="button"
                data-testid="designer-add-note"
                title="Add a text note to the drawing"
                onClick={addNote}
                className="flex h-8 items-center gap-1.5 rounded-lg border border-zinc-700 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-all duration-150 hover:border-amber-400/60 hover:bg-amber-500/10 hover:text-amber-200"
              >
                <TextT size={15} weight="bold" /> Text
              </button>
            )}
            <button
              type="button"
              data-testid="designer-link-tops"
              title="Join touching countertops — seam every shared edge so the run reads as one top"
              onClick={linkTops}
              className="flex h-8 items-center gap-1.5 rounded-lg border border-zinc-700 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-all duration-150 hover:border-indigo-400/60 hover:bg-indigo-500/10 hover:text-indigo-200"
            >
              <LinkSimple size={14} weight="bold" />{" "}
              <span className="hidden sm:inline">Link tops</span>
            </button>
            <button
              type="button"
              data-testid="designer-unlink-tops"
              title="Split joined tops — clear the shared seams created by Link tops"
              onClick={unlinkTops}
              className="flex h-8 items-center gap-1.5 rounded-lg border border-zinc-700 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-all duration-150 hover:border-rose-400/60 hover:bg-rose-500/10 hover:text-rose-200"
            >
              <LinkSimpleBreak size={14} weight="bold" />{" "}
              <span className="hidden sm:inline">Unlink</span>
            </button>
            <span className="mx-0.5 h-6 w-px shrink-0 bg-zinc-700/70" aria-hidden="true" data-testid="designer-toolbar-div-tools" />
            {/* Cluster: History */}
            {[
              ["designer-undo", ArrowUUpLeft, "Undo", undo, !histLen[0], false],
              [
                "designer-redo",
                ArrowUUpRight,
                "Redo",
                redo,
                !histLen[1],
                false,
              ],
            ].map(([tid, Icon, label, fn, disabled, on]) => (
              <button
                key={tid}
                type="button"
                data-testid={tid}
                title={label}
                disabled={disabled}
                onClick={fn}
                className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-150 disabled:opacity-30 ${
                  on
                    ? "border-indigo-400/50 bg-indigo-500/15 text-indigo-200"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-100"
                }`}
              >
                <Icon size={15} weight="bold" />
              </button>
            ))}
            <span className="mx-0.5 h-6 w-px shrink-0 bg-zinc-700/70" aria-hidden="true" data-testid="designer-toolbar-div-view" />
            {/* Cluster: View */}
            <div className="relative">
              <button
                type="button"
                data-testid="designer-view-toggle"
                title="View options — dimensions, grid, snap, focus, full screen"
                onClick={() => setShowView((v) => !v)}
                className={`flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[10px] font-bold uppercase tracking-widest transition-all duration-150 ${
                  showView
                    ? "border-indigo-400/50 bg-indigo-500/15 text-indigo-200"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-100"
                }`}
              >
                <SlidersHorizontal size={14} weight="bold" /> View
                <CaretDown size={10} weight="bold" />
              </button>
              {showView && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowView(false)}
                  />
                  <div
                    className="absolute right-0 z-50 mt-2 w-56 rounded-xl border border-zinc-700 bg-zinc-900 p-1.5 shadow-2xl"
                    data-testid="designer-view-panel"
                  >
                    {[
                      ["designer-toggle-dims", Ruler, "Dimensions", () => setShowDims((v) => !v), showDims],
                      ["designer-toggle-snap", Magnet, "Snap pieces", () => setSnapPieces((v) => !v), snapPieces],
                      ["designer-focus", ArrowsOut, "Focus mode", () => setFocus((v) => !v), focus],
                    ].map(([tid, Icon, label, fn, on]) => (
                      <button
                        key={tid}
                        type="button"
                        data-testid={tid}
                        onClick={fn}
                        className="flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-[11px] font-semibold text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                      >
                        <span className="flex items-center gap-2">
                          <Icon size={14} weight="bold" /> {label}
                        </span>
                        <span
                          className={`flex h-4 w-7 rounded-full p-0.5 transition-colors duration-150 ${
                            on ? "bg-indigo-500" : "bg-zinc-700"
                          }`}
                        >
                          <span
                            className={`block h-3 w-3 rounded-full bg-white transition-transform duration-150 ${
                              on ? "translate-x-3" : ""
                            }`}
                          />
                        </span>
                      </button>
                    ))}
                    <div className="mt-1 border-t border-zinc-800 pt-1.5">
                      <div className="px-2.5 pb-1 text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                        Grid
                      </div>
                      <div className="grid grid-cols-3 gap-1 px-1.5 pb-1">
                        {[
                          ["dots", "Dots"],
                          ["lines", "Lines"],
                          ["off", "Off"],
                        ].map(([val, lab]) => (
                          <button
                            key={val}
                            type="button"
                            data-testid={`designer-grid-${val}`}
                            onClick={() => setGridStyle(val)}
                            className={`rounded-md px-1.5 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors duration-150 ${
                              gridStyle === val
                                ? "bg-indigo-500 text-white"
                                : "bg-zinc-800 text-zinc-400 hover:text-zinc-100"
                            }`}
                          >
                            {lab}
                          </button>
                        ))}
                      </div>
                    </div>
                    {onCanvasOnly && (
                      <button
                        type="button"
                        data-testid="designer-canvas-only"
                        onClick={() => {
                          onCanvasOnly(!canvasOnly);
                          setFocus(!canvasOnly);
                          setShowView(false);
                        }}
                        className="flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-[11px] font-semibold text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                      >
                        <span className="flex items-center gap-2">
                          {canvasOnly ? (
                            <ArrowsIn size={14} weight="bold" />
                          ) : (
                            <ArrowsOut size={14} weight="bold" />
                          )}{" "}
                          Full screen
                        </span>
                        <span
                          className={`flex h-4 w-7 rounded-full p-0.5 transition-colors duration-150 ${
                            canvasOnly ? "bg-amber-500" : "bg-zinc-700"
                          }`}
                        >
                          <span
                            className={`block h-3 w-3 rounded-full bg-white transition-transform duration-150 ${
                              canvasOnly ? "translate-x-3" : ""
                            }`}
                          />
                        </span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                data-testid="designer-help-toggle"
                title="Help & keyboard shortcuts"
                onClick={() => setShowHelp((v) => !v)}
                className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-150 ${
                  showHelp
                    ? "border-indigo-400/50 bg-indigo-500/15 text-indigo-200"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-100"
                }`}
              >
                <Question size={15} weight="bold" />
              </button>
              {showHelp && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowHelp(false)}
                  />
                  <div
                    className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-zinc-700 bg-zinc-900 p-3 shadow-2xl"
                    data-testid="designer-help-panel"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">
                      Getting started
                    </div>
                    <button
                      type="button"
                      data-testid="designer-help-tour"
                      onClick={startTour}
                      className="mt-2 flex w-full items-center gap-2 rounded-lg bg-indigo-500 px-3 py-2 text-[11px] font-bold text-white transition-colors duration-150 hover:bg-indigo-400"
                    >
                      <Question size={13} weight="bold" /> Take the 30-sec tour
                    </button>
                    <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Keyboard &amp; mouse
                    </div>
                    <ul className="mt-2 space-y-1.5 text-[11px] text-zinc-400">
                      {[
                        ["Undo / redo", "⌘/Ctrl+Z · ⇧Z"],
                        ["Pan the sheet", "Hold Space + drag"],
                        ["Quick tools", "Right-click a piece"],
                        ["Finish a drawing", "Double-click / Enter"],
                        ["Cancel a drawing", "Esc"],
                      ].map(([k, v]) => (
                        <li
                          key={k}
                          className="flex items-center justify-between gap-2"
                        >
                          <span>{k}</span>
                          <span className="font-mono-data text-[10px] text-zinc-500">
                            {v}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <Dialog open={showLayoutPicker} onOpenChange={setShowLayoutPicker}>
          <DialogContent
            className="max-w-2xl border-zinc-800 bg-zinc-900 text-zinc-100"
            data-testid="designer-layout-picker"
          >
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Shapes size={16} weight="bold" className="text-indigo-300" /> Start
              with a layout
            </DialogTitle>
            <p className="text-xs text-zinc-400">
              Pick a shape to drop on the sheet, then drag its sides to size.
            </p>
            <DialogDescription className="sr-only">
              Choose a starting countertop layout shape.
            </DialogDescription>
            <div className="mt-2 max-h-[60vh] overflow-y-auto pr-1">
              <LayoutPresets
                layouts={[...LAYOUTS, ...customLayouts]}
                shapes={SHAPES}
                size="lg"
                testPrefix="picker-shape"
                onPick={(k) => {
                  addShape(k);
                  setShowLayoutPicker(false);
                  toast.success("Layout added — drag its sides to size");
                }}
                {...hiddenLayouts}
                onDeleteCustom={onDeleteCustomLayout}
              />
            </div>
          </DialogContent>
        </Dialog>

        {!free && (
        <div
          className="-mx-1 mt-2.5 flex items-center gap-1 overflow-x-auto px-1 pb-0.5"
          data-testid="designer-steps"
        >
          {DESIGN_STEPS.filter((s) => !hiddenSteps.includes(s.key)).map((s, i) => {
            const active = step === s.key;
            const done = DESIGN_STEPS.findIndex((x) => x.key === step) > i;
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex shrink-0 items-center">
                {i > 0 && (
                  <span
                    className={`mx-1 h-px w-4 transition-colors duration-200 ${
                      done || active ? "bg-indigo-400/50" : "bg-zinc-800"
                    }`}
                  />
                )}
                <button
                  type="button"
                  data-testid={`designer-step-${s.key}`}
                  title={s.hint}
                  onClick={() => setStep(s.key)}
                  className={`group flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all duration-200 ${
                    active
                      ? "border-indigo-400/60 bg-indigo-500 text-white shadow-[0_0_18px_-4px_rgba(99,102,241,0.8)]"
                      : done
                        ? "border-indigo-400/30 bg-indigo-500/10 text-indigo-200 hover:bg-indigo-500/20"
                        : "border-zinc-800 bg-zinc-950/60 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] transition-colors duration-200 ${
                      active
                        ? "bg-white/25 text-white"
                        : done
                          ? "bg-indigo-500/30 text-indigo-100"
                          : "bg-zinc-800 text-zinc-500 group-hover:text-zinc-300"
                    }`}
                  >
                    {done ? <Check size={11} weight="bold" /> : i + 1}
                  </span>
                  <Icon size={14} weight="bold" />
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
              </div>
            );
          })}
          {onReview && (
            <div className="flex shrink-0 items-center">
              <span className="mx-1 h-px w-4 bg-zinc-800" />
              <button
                type="button"
                data-testid="designer-step-review"
                title="Save and open the quote review"
                onClick={onReview}
                className="group flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-200 transition-all duration-200 hover:bg-emerald-500/20"
              >
                <ClipboardText size={14} weight="bold" />
                <span className="hidden sm:inline">Review</span>
                <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          )}
          <span className="ml-3 hidden truncate text-[11px] text-zinc-500 xl:inline">
            {list.length === 0 ? "Pick a layout preset or draw a run to begin" : ""}
          </span>
        </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {step === "layout" && layoutBoard ? (
          <div className="min-w-0 flex-1" data-testid="designer-layout-step">
            {isValidElement(layoutBoard)
              ? cloneElement(layoutBoard, {
                  onBackToDrawing: () => setStep("dimensions"),
                })
              : layoutBoard}
          </div>
        ) : (
          showTools && !focus && (
            <aside
              data-testid="designer-tool-panel"
              data-sheet-open={sheetOpen ? "1" : "0"}
              className={`designer-tool-sheet shrink-0 border-zinc-800 bg-zinc-900/95 p-4 lg:h-full lg:w-64 lg:overflow-y-auto lg:border-r lg:bg-zinc-900/60 ${sheetOpen ? "is-open" : ""}`}
            >
              <button
                type="button"
                data-testid="designer-tool-sheet-handle"
                aria-expanded={sheetOpen}
                onClick={() => setSheetOpen((v) => !v)}
                className="designer-sheet-handle lg:hidden"
              >
                <span className="designer-sheet-grip" aria-hidden="true" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-300">
                  {sheetOpen ? "Hide tools" : `Tools · ${DESIGN_STEPS.find((s) => s.key === lstep)?.label || ""}`}
                </span>
              </button>
              <div className="mb-5 flex items-start gap-3" data-testid="designer-step-header">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 font-mono-data text-xs font-bold text-indigo-200 ring-1 ring-inset ring-indigo-400/30 shadow-[0_4px_14px_-8px_rgba(99,102,241,0.8)]">
                  {STEP_INDEX(lstep) + 1}
                </span>
                <div className="min-w-0 pt-0.5">
                  <div className="text-sm font-bold tracking-tight text-zinc-100">
                    {DESIGN_STEPS.find((s) => s.key === lstep)?.label}
                  </div>
                  <div className="mt-0.5 text-[11px] leading-snug text-zinc-500">
                    {DESIGN_STEPS.find((s) => s.key === lstep)?.hint}
                  </div>
                </div>
              </div>
              <div
                className="mb-5 rounded-xl border border-indigo-400/20 bg-indigo-500/[0.06] px-3.5 py-2.5 text-[11px] leading-relaxed text-indigo-100/80"
                data-testid="designer-step-action"
              >
                {stepAction()}
              </div>
              {selNote && (
                <div
                  className="mb-3 space-y-2 rounded-lg border border-amber-400/40 bg-amber-500/5 p-3"
                  data-testid="designer-note-panel"
                >
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
                    <TextT size={12} weight="bold" /> Text note
                  </div>
                  <textarea
                    data-testid="designer-note-text"
                    value={selNote.text || ""}
                    autoFocus
                    rows={3}
                    onChange={(e) => patchNote(selNote.id, { text: e.target.value })}
                    className="w-full resize-none rounded-md border border-zinc-800 bg-zinc-950 p-2 text-xs text-zinc-100 outline-none focus:border-amber-400"
                  />
                  <div className="flex gap-1" data-testid="designer-note-sizes">
                    {[
                      [2.2, "S"],
                      [3, "M"],
                      [4.5, "L"],
                    ].map(([v, label]) => (
                      <button
                        key={label}
                        type="button"
                        data-testid={`designer-note-size-${label}`}
                        onClick={() => patchNote(selNote.id, { size: v })}
                        className={`flex-1 rounded-md py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                          Number(selNote.size) === v
                            ? "bg-amber-400 text-zinc-950"
                            : "border border-zinc-800 text-zinc-400 hover:bg-zinc-800"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="designer-note-colors">
                    {["#fde68a", "#fafafa", "#f87171", "#67e8f9", "#86efac"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        data-testid={`designer-note-color-${c.slice(1)}`}
                        onClick={() => patchNote(selNote.id, { color: c })}
                        className={`h-6 w-6 rounded-full border-2 transition-transform duration-150 hover:scale-110 ${
                          (selNote.color || "#fde68a") === c
                            ? "border-white"
                            : "border-zinc-700"
                        }`}
                        style={{ background: c }}
                      />
                    ))}
                    <button
                      type="button"
                      data-testid="designer-note-delete"
                      onClick={() => deleteNote(selNote.id)}
                      className="ml-auto flex h-8 items-center gap-1 rounded-md border border-red-500/40 px-2 text-[10px] font-bold uppercase tracking-widest text-red-300 hover:bg-red-500/10"
                    >
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  </div>
                  <p className="text-[10px] text-zinc-500">Drag the note on the drawing to place it.</p>
                </div>
              )}
              {lstep === "dimensions" && (
                <div className="space-y-8">
                  <section data-testid="designer-quickstart-section">
                    <SectionTitle
                      icon={Shapes}
                      hint="One tap drops a common project — then tweak any piece."
                    >
                      Quick starts
                    </SectionTitle>
                    <div className="mt-3 space-y-2">
                      {STARTERS.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          data-testid={`designer-quickstart-${s.key}`}
                          onClick={() => addStarter(s)}
                          className="flex w-full items-center justify-between gap-2 rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-left transition-colors duration-150 hover:border-indigo-400 hover:bg-zinc-800"
                        >
                          <span className="flex items-center gap-2 text-[11px] font-bold text-zinc-100">
                            <Plus
                              size={12}
                              weight="bold"
                              className="shrink-0 text-indigo-300"
                            />
                            {s.label}
                          </span>
                          <span className="font-mono-data text-[9px] text-zinc-500">
                            {s.note}
                          </span>
                        </button>
                      ))}
                    </div>
                    {savedLayouts.length > 0 && (
                      <div
                        className="mt-2 space-y-2"
                        data-testid="designer-saved-layouts"
                      >
                        {savedLayouts.map((sl) => (
                          <div key={sl.id} className="flex items-center gap-1">
                            {renamingId === sl.id ? (
                              <div
                                className="flex flex-1 items-center gap-1"
                                data-testid={`designer-rename-layout-form-${sl.id}`}
                              >
                                <Input
                                  autoFocus
                                  data-testid={`designer-rename-layout-name-${sl.id}`}
                                  value={renameName}
                                  onChange={(e) => setRenameName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") confirmRename();
                                    if (e.key === "Escape") {
                                      setRenamingId(null);
                                      setRenameName("");
                                    }
                                  }}
                                  className="h-9 flex-1 border-zinc-800 bg-zinc-950 text-xs"
                                />
                                <button
                                  type="button"
                                  data-testid={`designer-rename-layout-confirm-${sl.id}`}
                                  onClick={confirmRename}
                                  disabled={!renameName.trim()}
                                  className="rounded-md bg-indigo-500 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  data-testid={`designer-rename-layout-cancel-${sl.id}`}
                                  onClick={() => {
                                    setRenamingId(null);
                                    setRenameName("");
                                  }}
                                  className="rounded-md border border-zinc-800 p-2 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
                                >
                                  <X size={13} weight="bold" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  data-testid={`designer-saved-layout-${sl.id}`}
                                  onClick={() => addSavedLayout(sl)}
                                  className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-indigo-400/30 bg-indigo-500/5 px-2.5 py-2 text-left transition-colors duration-150 hover:border-indigo-400 hover:bg-indigo-500/10"
                                >
                                  <span className="flex h-8 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 bg-zinc-950">
                                    <LayoutThumb
                                      pieces={sl.pieces}
                                      className="h-6 w-8"
                                    />
                                  </span>
                                  <span className="flex min-w-0 flex-1 flex-col">
                                    <span className="truncate text-[11px] font-bold text-zinc-100">
                                      {sl.name}
                                    </span>
                                    <span className="font-mono-data text-[9px] text-zinc-500">
                                      {(sl.pieces || []).length} pc
                                    </span>
                                  </span>
                                </button>
                                {onSaveLayout && (
                                  <button
                                    type="button"
                                    data-testid={`designer-duplicate-layout-${sl.id}`}
                                    onClick={() => duplicateLayout(sl)}
                                    title="Duplicate this layout"
                                    className="rounded-md border border-zinc-800 p-1.5 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-indigo-200"
                                  >
                                    <Copy size={11} weight="bold" />
                                  </button>
                                )}
                                {onRenameLayout && (
                                  <button
                                    type="button"
                                    data-testid={`designer-rename-layout-${sl.id}`}
                                    onClick={() => {
                                      setRenamingId(sl.id);
                                      setRenameName(sl.name || "");
                                    }}
                                    title="Rename this layout"
                                    className="rounded-md border border-zinc-800 p-1.5 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-indigo-200"
                                  >
                                    <PencilSimple size={11} weight="bold" />
                                  </button>
                                )}
                                {onDeleteLayout && (
                                  <button
                                    type="button"
                                    data-testid={`designer-delete-layout-${sl.id}`}
                                    onClick={() => onDeleteLayout(sl.id)}
                                    title="Remove this saved layout"
                                    className="rounded-md border border-zinc-800 p-1.5 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-red-400"
                                  >
                                    <Trash size={11} weight="bold" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {onSaveLayout &&
                      list.length > 0 &&
                      (savingLayout ? (
                        <div
                          className="mt-2 flex items-center gap-1"
                          data-testid="designer-save-layout-form"
                        >
                          <Input
                            autoFocus
                            data-testid="designer-save-layout-name"
                            value={layoutName}
                            onChange={(e) => setLayoutName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveCurrentLayout();
                              if (e.key === "Escape") {
                                setSavingLayout(false);
                                setLayoutName("");
                              }
                            }}
                            placeholder="Name this layout"
                            className="h-9 flex-1 border-zinc-800 bg-zinc-950 text-xs"
                          />
                          <button
                            type="button"
                            data-testid="designer-save-layout-confirm"
                            onClick={saveCurrentLayout}
                            disabled={!layoutName.trim()}
                            className="rounded-md bg-indigo-500 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            data-testid="designer-save-layout-cancel"
                            onClick={() => {
                              setSavingLayout(false);
                              setLayoutName("");
                            }}
                            className="rounded-md border border-zinc-800 p-2 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
                          >
                            <X size={13} weight="bold" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          data-testid="designer-save-layout-toggle"
                          onClick={() => setSavingLayout(true)}
                          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-zinc-700 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-400 hover:text-indigo-200"
                        >
                          <FloppyDisk size={12} weight="bold" /> Save this drawing
                        </button>
                      ))}
                  </section>

                  <section data-testid="designer-presets-section">
                    <SectionTitle
                      icon={Shapes}
                      hint="Drop a single shape on the sheet, then pull its sides to size."
                    >
                      Start from a layout
                    </SectionTitle>
                    <LayoutPresets
                      layouts={[...LAYOUTS, ...customLayouts]}
                      shapes={SHAPES}
                      onPick={addShape}
                      className="mt-4"
                      {...hiddenLayouts}
                      onDeleteCustom={onDeleteCustomLayout}
                    />
                    {selPiece && onSaveCustomLayout && (
                      customName === null ? (
                        <button
                          type="button"
                          data-testid="designer-save-custom-layout"
                          onClick={() => setCustomName(selPiece.label || "My shape")}
                          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-zinc-700 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-400 hover:text-indigo-200"
                        >
                          <Shapes size={12} weight="bold" /> Save selected shape as a tile
                        </button>
                      ) : (
                        <div className="mt-2 flex items-center gap-1" data-testid="designer-save-custom-layout-row">
                          <Input
                            autoFocus
                            data-testid="designer-custom-layout-name"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveCustomLayout();
                              if (e.key === "Escape") setCustomName(null);
                            }}
                            placeholder="Name this shape"
                            className="h-9 flex-1 border-zinc-800 bg-zinc-950 text-xs"
                          />
                          <button
                            type="button"
                            data-testid="designer-custom-layout-confirm"
                            onClick={saveCustomLayout}
                            disabled={!customName.trim()}
                            className="rounded-md bg-indigo-500 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            data-testid="designer-custom-layout-cancel"
                            onClick={() => setCustomName(null)}
                            className="rounded-md border border-zinc-800 p-2 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
                          >
                            <X size={13} weight="bold" />
                          </button>
                        </div>
                      )
                    )}
                    {selPiece?.circle && (
                      <div
                        className="mt-3 rounded-lg border border-indigo-500/30 bg-zinc-950 p-3"
                        data-testid="designer-circle-size"
                      >
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                          Circle diameter (inches)
                        </span>
                        <div className="mt-2 flex items-center gap-2">
                          <Input
                            type="number"
                            min={2}
                            step={0.5}
                            data-testid="designer-circle-diameter"
                            value={round2(selPiece.diameter || 36)}
                            onChange={(e) => {
                              const d = Math.max(2, Number(e.target.value) || 0);
                              replacePiece(
                                selPiece.id,
                                normalizePiece({ ...selPiece, circle: true, diameter: d }),
                              );
                            }}
                            className="h-9 w-28 border-zinc-800 bg-zinc-950 text-sm"
                          />
                          <span className="text-[11px] text-zinc-500">
                            ⌀ sets the size of {selPiece.label}
                          </span>
                        </div>
                      </div>
                    )}
                    {selPiece?.oval && (
                      <div
                        className="mt-3 rounded-lg border border-indigo-500/30 bg-zinc-950 p-3"
                        data-testid="designer-oval-size"
                      >
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                          Oval size (inches)
                        </span>
                        <div className="mt-2 flex items-center gap-2">
                          <label className="flex items-center gap-1 text-[11px] text-zinc-400">
                            L
                            <Input
                              type="number"
                              min={2}
                              step={0.5}
                              data-testid="designer-oval-length"
                              value={round2(selPiece.length_in || 48)}
                              onChange={(e) => {
                                const l = Math.max(2, Number(e.target.value) || 0);
                                replacePiece(
                                  selPiece.id,
                                  normalizePiece({ ...selPiece, oval: true, length_in: l }),
                                );
                              }}
                              className="h-9 w-20 border-zinc-800 bg-zinc-950 text-sm"
                            />
                          </label>
                          <span className="text-zinc-600">×</span>
                          <label className="flex items-center gap-1 text-[11px] text-zinc-400">
                            W
                            <Input
                              type="number"
                              min={2}
                              step={0.5}
                              data-testid="designer-oval-width"
                              value={round2(selPiece.width_in || 30)}
                              onChange={(e) => {
                                const w = Math.max(2, Number(e.target.value) || 0);
                                replacePiece(
                                  selPiece.id,
                                  normalizePiece({ ...selPiece, oval: true, width_in: w }),
                                );
                              }}
                              className="h-9 w-20 border-zinc-800 bg-zinc-950 text-sm"
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </section>

                  <section data-testid="designer-precision-section">
                    <SectionTitle
                      icon={SlidersHorizontal}
                      hint="Sides and moves round to this step."
                    >
                      Snap step
                    </SectionTitle>
                    <div
                      className="mt-3 flex gap-1"
                      data-testid="designer-snap-steps"
                    >
                      {SNAP_STEPS.map(([label, v]) => (
                        <button
                          key={label}
                          type="button"
                          data-testid={`designer-snap-${v}`}
                          onClick={() => setSnap(v)}
                          className={`flex-1 rounded-md py-2 text-[10px] font-bold transition-colors duration-150 ${
                            snap === v
                              ? "bg-indigo-500 text-white"
                              : "border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {lstep === "shape" && (
                <div className="space-y-6">
                  <GuideCard
                    icon={Polygon}
                    testId="designer-shape-guide"
                    title={selPiece ? `Shaping ${selPiece.label}` : "Pick a piece to shape"}
                    lines={
                      selPiece
                        ? [
                            "Click a side to add a bump-out, bump-in or curve.",
                            "Click a corner dot to clip, notch or radius it.",
                            "Tap a blue backsplash or waterfall to edit it here too.",
                            "Flip, split at a seam or merge from the piece panel.",
                          ]
                        : [
                            "Click a piece on the drawing first.",
                            "Then click its sides and corner dots to reshape it.",
                            "Tap a blue backsplash or waterfall to shape it too.",
                          ]
                    }
                  />
                  <section>
                    <SectionTitle icon={SlidersHorizontal}>Snap step</SectionTitle>
                    <div className="mt-3 flex gap-1" data-testid="designer-snap-steps">
                      {SNAP_STEPS.map(([label, v]) => (
                        <button
                          key={label}
                          type="button"
                          data-testid={`designer-snap-${v}`}
                          onClick={() => setSnap(v)}
                          className={`flex-1 rounded-md py-2 text-[10px] font-bold transition-colors duration-150 ${
                            snap === v
                              ? "bg-indigo-500 text-white"
                              : "border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {lstep === "edges" && (
                <div className="space-y-6">
                  <GuideCard
                    icon={LineSegments}
                    testId="designer-edges-guide"
                    title={selPiece ? `Edges on ${selPiece.label}` : "Pick a piece to finish"}
                    lines={
                      selPiece
                        ? [
                            "Click a side to set its profile, color and backsplash.",
                            "Mark a side as waterfall or mitre to drop a panel.",
                            "Copy one profile to every side from the side panel.",
                          ]
                        : [
                            "Click a piece on the drawing first.",
                            "Then click each side to choose how it's finished.",
                          ]
                    }
                  />
                  {(() => {
                    const used = [];
                    list.forEach((p) =>
                      (p.edges || []).forEach((e) => {
                        if (e.seam || p.panel_kind) return;
                        const name = e.profile || "Unfinished";
                        if (!used.some((u) => u.name === name))
                          used.push({
                            name,
                            style: styleFor(name),
                            count: 0,
                          });
                        used.find((u) => u.name === name).count += 1;
                      }),
                    );
                    if (!used.length) return null;
                    return (
                      <section data-testid="designer-edge-legend">
                        <SectionTitle icon={LineSegments}>In this drawing</SectionTitle>
                        <ul className="mt-3 space-y-1.5">
                          {used.map((u) => (
                            <li
                              key={u.name}
                              className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs"
                            >
                              <svg viewBox="0 0 24 14" className="h-4 w-7 shrink-0">
                                <path
                                  d={SHAPE_PATH[u.style?.shape || "eased"] || SHAPE_PATH.eased}
                                  fill="rgba(99,102,241,0.2)"
                                  stroke={u.style?.color || "#a5b4fc"}
                                  strokeWidth="1.2"
                                />
                              </svg>
                              <span className="min-w-0 flex-1 truncate text-zinc-200">{u.name}</span>
                              <span className="font-mono-data text-[10px] text-zinc-500">
                                {u.style?.abbr ? `${u.style.abbr} · ` : ""}
                                {u.count} side{u.count === 1 ? "" : "s"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    );
                  })()}
                </div>
              )}

              {lstep === "cutouts" && !selPiece && (
                <div
                  className="rounded-lg border border-dashed border-zinc-800 p-3 text-[11px] leading-relaxed text-zinc-500"
                  data-testid="designer-cutouts-hint"
                >
                  <CircleDashed
                    size={16}
                    weight="bold"
                    className="mb-1.5 text-indigo-300"
                  />
                  Click a piece on the drawing to add sinks, cooktops and holes
                  to it. Tap a blue backsplash or waterfall to cut into it too.
                </div>
              )}

              {selPiece && lstep === "cutouts" && (
                <>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    <CircleDashed
                      size={12}
                      weight="bold"
                      className="text-indigo-300"
                    />
                    Add a cut-out
                  </div>
                  <div
                    className="mt-2 space-y-3"
                    data-testid="designer-cutout-bar"
                  >
                    {selPiece.orientation === "vertical" && (
                      <div
                        className="rounded-lg border border-indigo-400/50 bg-indigo-500/10 p-2.5"
                        data-testid="designer-splash-outlet-quick"
                      >
                        <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-indigo-200">
                          <Lightning size={11} weight="fill" className="text-indigo-300" />
                          Outlet / switch on this{" "}
                          {selPiece.stand === "waterfall" ? "waterfall" : "backsplash"}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            data-testid="designer-quick-outlet"
                            title="Drops a single-gang outlet/switch, centred at the right height"
                            onClick={() => addCutout("Single gang")}
                            className="flex items-center justify-center gap-1.5 rounded-md bg-indigo-500 px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
                          >
                            <Plus size={12} weight="bold" /> Outlet / switch
                          </button>
                          <button
                            type="button"
                            data-testid="designer-quick-outlet-double"
                            title="Drops a double-gang outlet, centred at the right height"
                            onClick={() => addCutout("Double gang")}
                            className="flex items-center justify-center gap-1.5 rounded-md border border-indigo-400/50 bg-indigo-500/5 px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
                          >
                            <Plus size={12} weight="bold" /> Double
                          </button>
                        </div>
                        <button
                          type="button"
                          data-testid="designer-quick-outlet-row"
                          title="Drops a row of evenly-spaced outlets along the whole backsplash"
                          onClick={addOutletRow}
                          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-indigo-400/50 bg-indigo-500/5 px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
                        >
                          <Lightning size={12} weight="fill" /> Row of outlets
                        </button>
                      </div>
                    )}
                    {kits.length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {kits.map((kit) => (
                          <button
                            key={kit.id || kit.name}
                            type="button"
                            data-testid={`designer-add-kit-${(kit.id || kit.name || "kit")
                              .toString()
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => addKit(kit)}
                            className="flex w-full items-center gap-2 rounded-lg border border-indigo-400/50 bg-indigo-500/10 px-3 py-2.5 text-left transition-colors duration-150 hover:border-indigo-300 hover:bg-indigo-500/20"
                          >
                            <Plus
                              size={13}
                              weight="bold"
                              className="shrink-0 text-indigo-300"
                            />
                            <span className="flex min-w-0 flex-col">
                              <span className="text-[11px] font-bold text-indigo-100">
                                {kit.name || "Kit"}
                              </span>
                              <span className="truncate text-[9px] text-indigo-200/60">
                                {(kit.parts || [])
                                  .map((p) => p.kind)
                                  .join(" + ") || "Custom kit"}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {CUTOUT_GROUPS.map((grp) => {
                      const GIcon =
                        grp.key === "cooktop"
                          ? GridFour
                          : grp.key === "sinks"
                            ? Shapes
                            : CircleDashed;
                      return (
                        <div
                          key={grp.key}
                          data-testid={`designer-cutout-group-${grp.key}`}
                        >
                          <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                            <GIcon
                              size={11}
                              weight="bold"
                              className="text-indigo-300"
                            />
                            {grp.label}
                          </div>
                          <div
                            className={`grid gap-2 ${grp.items.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}
                          >
                            {grp.items.map((k) => (
                              <button
                                key={k}
                                type="button"
                                data-testid={`designer-add-cutout-${k.toLowerCase().replace(/ /g, "-")}`}
                                onClick={() =>
                                  k === "Custom Cut-out Square"
                                    ? setCustomCut({ name: k, w: 12, h: 12 })
                                    : addCutout(k)
                                }
                                className="flex flex-col items-start gap-0.5 rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-left transition-colors duration-150 hover:border-indigo-400 hover:bg-zinc-800"
                              >
                                <span className="flex items-center gap-1.5 text-[11px] font-bold text-zinc-100">
                                  <Plus
                                    size={11}
                                    weight="bold"
                                    className="shrink-0 text-indigo-300"
                                  />
                                  {k}
                                </span>
                                <span className="font-mono-data pl-[18px] text-[9px] text-zinc-500">
                                  {cutDims(k)[0]}" × {cutDims(k)[1]}"
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    <div data-testid="designer-cutout-group-custom">
                      <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                        <PencilRuler size={11} weight="bold" className="text-indigo-300" />
                        Custom cut-outs
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {CUSTOM_CUTOUT_PRESETS.map((k) => (
                          <button
                            key={k}
                            type="button"
                            data-testid={`designer-add-cutout-${k.toLowerCase()}`}
                            onClick={() => addCutout(k)}
                            className="flex flex-col items-start gap-0.5 rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-left transition-colors duration-150 hover:border-indigo-400 hover:bg-zinc-800"
                          >
                            <span className="flex items-center gap-1.5 text-[11px] font-bold text-zinc-100">
                              <Plus size={11} weight="bold" className="shrink-0 text-indigo-300" />
                              {k}
                            </span>
                            <span className="font-mono-data pl-[18px] text-[9px] text-zinc-500">
                              {cutDims(k)[0]}" × {cutDims(k)[1]}"
                            </span>
                          </button>
                        ))}
                      </div>
                      {customCut === null ? (
                        <button
                          type="button"
                          data-testid="designer-add-cutout-custom"
                          onClick={() => setCustomCut({ name: "", w: 24, h: 18 })}
                          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-zinc-700 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-400 hover:text-indigo-200"
                        >
                          <Plus size={12} weight="bold" /> Custom name…
                        </button>
                      ) : (
                        <div className="mt-2 space-y-2 rounded-lg border border-zinc-800 bg-zinc-950/70 p-2" data-testid="designer-custom-cutout-form">
                          <Input
                            autoFocus
                            data-testid="designer-custom-cutout-name"
                            value={customCut.name}
                            onChange={(e) => setCustomCut({ ...customCut, name: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") addCustomCutout();
                              if (e.key === "Escape") setCustomCut(null);
                            }}
                            placeholder="Cut-out name (e.g. Fireplace)"
                            className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                          />
                          <div className="flex items-center gap-2">
                            <MeasureInput
                              data-testid="designer-custom-cutout-w"
                              min={0.25}
                              fallback={24}
                              value={customCut.w}
                              onChange={(n) => setCustomCut({ ...customCut, w: n })}
                              className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                            />
                            <span className="text-xs text-zinc-500">×</span>
                            <MeasureInput
                              data-testid="designer-custom-cutout-h"
                              min={0.25}
                              fallback={18}
                              value={customCut.h}
                              onChange={(n) => setCustomCut({ ...customCut, h: n })}
                              className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              data-testid="designer-custom-cutout-confirm"
                              onClick={addCustomCutout}
                              disabled={!customCut.name.trim()}
                              className="flex-1 rounded-md bg-indigo-500 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
                            >
                              Add cut-out
                            </button>
                            <button
                              type="button"
                              data-testid="designer-custom-cutout-cancel"
                              onClick={() => setCustomCut(null)}
                              className="rounded-md border border-zinc-800 px-3 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
                            >
                              <X size={13} weight="bold" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {(selPiece.cutouts || []).length > 0 && (
                      <button
                        type="button"
                        data-testid="designer-clear-cutouts"
                        onClick={() => update(selPiece.id, { cutouts: [] })}
                        className="w-full rounded-lg border border-zinc-700 py-2.5 text-[11px] font-bold uppercase tracking-widest text-red-400 transition-colors duration-150 hover:bg-zinc-800"
                      >
                        Clear {selPiece.cutouts.length} cut-out
                        {selPiece.cutouts.length > 1 ? "s" : ""}
                      </button>
                    )}
                  </div>

                  <div className="mt-5 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    <span>Saved templates</span>
                    <button
                      type="button"
                      data-testid="designer-browse-templates"
                      onClick={() => setTplPicker({})}
                      className="flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-zinc-800"
                    >
                      <ClipboardText size={11} weight="bold" /> Browse
                    </button>
                  </div>
                  <div
                    className="mt-2 space-y-1"
                    data-testid="designer-cutout-templates"
                  >
                    {cutoutTemplates.length === 0 && (
                      <p className="text-[10px] text-zinc-600">
                        Select a cut-out and press "Save cut-out as template" to reuse it here.
                      </p>
                    )}
                    {cutoutTemplates.map((t) => (
                      <div key={t.id} className="flex items-center gap-1">
                        {renamingTplId === t.id ? (
                          <>
                            <Input
                              autoFocus
                              data-testid={`designer-rename-template-input-${t.id}`}
                              value={renameTplName}
                              onChange={(e) => setRenameTplName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") confirmTplRename();
                                if (e.key === "Escape") setRenamingTplId(null);
                              }}
                              className="h-9 flex-1 border-zinc-800 bg-zinc-950 text-xs"
                            />
                            <button
                              type="button"
                              data-testid={`designer-rename-template-confirm-${t.id}`}
                              onClick={confirmTplRename}
                              disabled={!renameTplName.trim()}
                              className="rounded-md bg-indigo-500 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              data-testid={`designer-rename-template-cancel-${t.id}`}
                              onClick={() => setRenamingTplId(null)}
                              className="rounded-md border border-zinc-800 p-2 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
                            >
                              <X size={13} weight="bold" />
                            </button>
                          </>
                        ) : (
                          <>
                        <button
                          type="button"
                          data-testid={`designer-apply-template-${t.id}`}
                          onClick={() => applyTemplate(t)}
                          className="min-w-0 flex-1 rounded-lg border border-zinc-700 bg-zinc-950 px-2.5 py-2.5 text-left text-[11px] font-bold text-zinc-200 transition-colors duration-150 hover:border-indigo-400 hover:bg-zinc-800"
                        >
                          <span className="block truncate">{t.name}</span>
                          <span className="block truncate font-mono-data text-[9px] font-normal text-zinc-500">
                            {formatInches(t.w)} x {formatInches(t.h)}
                            {Number(t.faucet_holes) > 0
                              ? ` · ${t.faucet_holes} hole${Number(t.faucet_holes) > 1 ? "s" : ""}`
                              : ""}
                          </span>
                        </button>
                        {onRenameCutoutTemplate && (
                          <button
                            type="button"
                            data-testid={`designer-rename-template-${t.id}`}
                            onClick={() => {
                              setRenamingTplId(t.id);
                              setRenameTplName(t.name || "");
                            }}
                            title="Rename this setup"
                            className="rounded-md border border-zinc-800 p-1.5 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-indigo-200"
                          >
                            <PencilSimple size={11} weight="bold" />
                          </button>
                        )}
                        {onDeleteCutoutTemplate && (
                          <button
                            type="button"
                            data-testid={`designer-delete-template-${t.id}`}
                            onClick={() => onDeleteCutoutTemplate(t.id)}
                            className="rounded-md border border-zinc-800 p-1.5 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-red-400"
                          >
                            <Trash size={11} weight="bold" />
                          </button>
                        )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {!free && (
              <div
                className="-mx-4 -mb-4 mt-6 flex items-center gap-2 border-t border-zinc-800 bg-zinc-900 px-4 py-3 lg:sticky lg:bottom-0"
                data-testid="designer-step-nav"
              >
                {prevStep && (
                  <button
                    type="button"
                    data-testid="designer-step-back"
                    onClick={() => setStep(prevStep.key)}
                    className="flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                  >
                    <ArrowLeft size={13} weight="bold" /> Back
                  </button>
                )}
                {nextStep ? (
                  <button
                    type="button"
                    data-testid="designer-step-next"
                    disabled={nextBlocked}
                    title={nextBlocked ? "Add a piece first" : ""}
                    onClick={() => !nextBlocked && setStep(nextStep.key)}
                    className="ml-auto flex items-center gap-1.5 rounded-lg bg-indigo-500 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-white shadow-[0_0_18px_-4px_rgba(99,102,241,0.8)] transition-colors duration-150 hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
                  >
                    Next: {nextStep.label}
                    <ArrowRight size={13} weight="bold" />
                  </button>
                ) : (
                  <span
                    className="ml-auto flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-emerald-200"
                    data-testid="designer-step-done"
                  >
                    <Check size={13} weight="bold" /> All steps done
                  </span>
                )}
              </div>
              )}
            </aside>
          )
        )}

        {!(step === "layout" && layoutBoard) && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex shrink-0">
              <div className="w-6 shrink-0 border-b border-r border-zinc-800 bg-zinc-900/60" />
              <div
                className="relative h-6 flex-1 overflow-hidden border-b border-zinc-800 bg-zinc-900/60"
                data-testid="designer-ruler-top"
              >
                {ticks.map((t) => (
                  <span
                    key={t}
                    className="absolute top-1 font-mono-data text-[8px] text-zinc-500"
                    style={{
                      left: `${(((t - view.x) / view.w) * 100).toFixed(3)}%`,
                    }}
                  >
                    {t}"
                  </span>
                ))}
              </div>
            </div>

            <div className="flex min-h-0 flex-1">
              <div
                className="relative w-6 shrink-0 overflow-hidden border-r border-zinc-800 bg-zinc-900/60"
                data-testid="designer-ruler-left"
              >
                {vTicks.map((t) => (
                  <span
                    key={t}
                    className="absolute left-0.5 font-mono-data text-[8px] text-zinc-500"
                    style={{
                      top: `${(((t - view.y) / view.h) * 100).toFixed(3)}%`,
                    }}
                  >
                    {t}"
                  </span>
                ))}
              </div>

              <div
                ref={viewRef}
                className="min-w-0 flex-1 overflow-hidden bg-zinc-950"
                onPointerDown={startPan}
                onPointerMove={onMove}
                onPointerUp={() => {
                  panRef.current = null;
                  setLegChip(null);
                  if (drag?.mode === "extrude" && drag.ext) {
                    const grown = list.find((p) => p.id === drag.pieceId);
                    if (grown && grown.points.length === drag.base.points.length + 2) {
                      const legEdge = drag.ext.axis === 1 ? drag.corner + 1 : drag.corner;
                      setTimeout(() => openLabel(grown, legEdge, { stopPropagation() {} }), 0);
                    }
                  }
                  setDrag(null);
                  setSeamSnap(null);
                  setCutoutGuide(null);
                  setSnapGuide(null);
                  if (draw?.kind === "box" && draw.pts.length && draw.cursor) {
                    const [ax, ay] = draw.pts[0];
                    const [bx, by] = draw.cursor;
                    const w = Math.abs(bx - ax);
                    const h = Math.abs(by - ay);
                    if (w >= 1 && h >= 1) {
                      const x0 = Math.min(ax, bx);
                      const y0 = Math.min(ay, by);
                      const made = finishPoly([
                        [x0, y0],
                        [x0 + w, y0],
                        [x0 + w, y0 + h],
                        [x0, y0 + h],
                      ]);
                      setDraw(made ? { kind: "box", pts: [], cursor: null } : null);
                    } else {
                      setDraw({ kind: "box", pts: [], cursor: null });
                    }
                  }
                }}
                onPointerLeave={() => {
                  panRef.current = null;
                  setDrag(null);
                  setSeamSnap(null);
                  setCutoutGuide(null);
                  setSnapGuide(null);
                  setHoverEdge(null);
                }}
              >
                <div
                  className="relative h-full w-full"
                  style={{
                    background: general?.canvas_bg || "#0e1523",
                  }}
                >
                  {draw?.kind === "line" && draw.pts.length >= 1 && dynPx && (
                    <div
                      data-testid="designer-dyn-input"
                      className="pointer-events-auto absolute z-40 flex items-center gap-1.5 rounded-md border border-sky-400/70 bg-zinc-900/95 px-2 py-1.5 text-[11px] text-zinc-200 shadow-xl backdrop-blur"
                      style={{ left: dynPx.x + 18, top: dynPx.y + 12 }}
                      onPointerDown={(ev) => ev.stopPropagation()}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <span className="whitespace-nowrap text-zinc-400">Specify next point @</span>
                      <input
                        data-testid="designer-dyn-length"
                        type="number"
                        step="any"
                        autoFocus
                        value={lineLen}
                        onChange={(ev) => setLineLen(ev.target.value === "" ? "" : Number(ev.target.value))}
                        onKeyDown={onDynKey}
                        className="h-7 w-16 rounded border border-zinc-700 bg-zinc-950 px-1.5 text-center text-zinc-100 outline-none focus:border-sky-400"
                      />
                      <span className="text-zinc-500">&lt;</span>
                      <input
                        data-testid="designer-dyn-angle"
                        type="number"
                        step="any"
                        value={lineAngle}
                        onChange={(ev) => setLineAngle(ev.target.value === "" ? "" : Number(ev.target.value))}
                        onKeyDown={onDynKey}
                        className="h-7 w-14 rounded border border-zinc-700 bg-zinc-950 px-1.5 text-center text-zinc-100 outline-none focus:border-sky-400"
                      />
                      <span className="text-zinc-500">°</span>
                    </div>
                  )}
                  {list.length === 0 && !draw && step === "dimensions" && (
                    <div
                      className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-6"
                      data-testid="designer-empty-gallery"
                    >
                      <div className="pointer-events-auto w-full max-w-3xl rounded-3xl border border-zinc-800 bg-zinc-900/90 p-7 shadow-2xl backdrop-blur">
                        <div className="flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-200">
                            <Shapes size={20} weight="bold" />
                          </span>
                          <div>
                            <div className="text-base font-bold tracking-tight text-zinc-50">
                              Start from a preset
                            </div>
                            <div className="text-xs text-zinc-500">
                              Pick a layout to drop on the sheet — you can resize every side afterwards, or draw your own from the panel.
                            </div>
                          </div>
                        </div>
                        <LayoutPresets
                          layouts={[...LAYOUTS, ...customLayouts]}
                          shapes={SHAPES}
                          onPick={addShape}
                          size="lg"
                          testPrefix="preset"
                          className="mt-6"
                          {...hiddenLayouts}
                          onDeleteCustom={onDeleteCustomLayout}
                        />
                        <div className="mt-6 flex flex-col items-start gap-3 border-t border-zinc-800 pt-5 sm:flex-row sm:items-center">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
                            {["Shape", "Size", "Edges & cut-outs", "Layout"].map(
                              (t, n) => (
                                <span key={t} className="flex items-center gap-2">
                                  {n > 0 && (
                                    <CaretRight
                                      size={11}
                                      weight="bold"
                                      className="text-zinc-700"
                                    />
                                  )}
                                  <span className="font-semibold text-zinc-300">
                                    {n + 1}. {t}
                                  </span>
                                </span>
                              ),
                            )}
                          </div>
                          <button
                            type="button"
                            data-testid="designer-empty-tour"
                            onClick={startTour}
                            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-indigo-400/40 bg-indigo-500/10 px-3 py-2 text-[11px] font-bold text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20 sm:ml-auto"
                          >
                            <Question size={13} weight="bold" /> Take the tour
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  <svg
                    ref={svgRef}
                    data-testid="designer-canvas"
                    viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
                    className={`h-full w-full touch-none ${draw ? "cursor-crosshair" : spaceDown ? "cursor-grab" : ""}`}
                    onDoubleClick={() => draw && finishDraw()}
                    onContextMenu={(ev) => {
                      if (!draw) return;
                      ev.preventDefault();
                      finishDraw();
                    }}
                    onClick={(ev) => {
                      if (draw) {
                        ev.stopPropagation();
                        if (draw.kind === "box") return;
                        if (draw.kind === "line") {
                          if (!draw.pts.length) {
                            const s = svgPoint(ev);
                            const start = [
                              round2(Math.max(0, Math.round(s[0] / snap) * snap)),
                              round2(Math.max(0, Math.round(s[1] / snap) * snap)),
                            ];
                            setDraw({ ...draw, pts: [start], cursor: null });
                          } else {
                            addLineSegment();
                          }
                          return;
                        }
                        const p = drawPoint(draw.pts, draw.kind, svgPoint(ev));
                        const first = draw.pts[0];
                        if (
                          draw.kind === "poly" &&
                          first &&
                          draw.pts.length >= 3 &&
                          first[0] === p[0] &&
                          first[1] === p[1]
                        ) {
                          finishDraw();
                          return;
                        }
                        const last = draw.pts[draw.pts.length - 1];
                        const same =
                          last && last[0] === p[0] && last[1] === p[1];
                        setDraw({
                          ...draw,
                          pts: same ? draw.pts : [...draw.pts, p],
                          cursor: p,
                        });
                        return;
                      }
                      const onBackground =
                        ev.target === ev.currentTarget ||
                        ev.target?.dataset?.bg === "1";
                      if (!onBackground) return;
                      setCtxMenu(null);
                      if (movedRef.current) {
                        movedRef.current = false;
                        return;
                      }
                      setSel(null);
                      setLabelEdit(null);
                    }}
                  >
                    <defs>
                      <pattern
                        id="dg-dots"
                        width="6"
                        height="6"
                        patternUnits="userSpaceOnUse"
                      >
                        <circle cx="0" cy="0" r="0.32" fill="rgba(96,165,250,0.22)" />
                      </pattern>
                      <pattern
                        id="dg-lines"
                        width="6"
                        height="6"
                        patternUnits="userSpaceOnUse"
                      >
                        <path d="M 6 0 L 0 0 0 6" fill="none" stroke="rgba(96,165,250,0.09)" strokeWidth="0.3" />
                      </pattern>
                      <pattern
                        id="dg-major"
                        width="24"
                        height="24"
                        patternUnits="userSpaceOnUse"
                      >
                        <path d="M 24 0 L 0 0 0 24" fill="none" stroke="rgba(59,130,246,0.16)" strokeWidth="0.4" />
                      </pattern>
                      <marker
                        id="dim-end"
                        markerUnits="userSpaceOnUse"
                        markerWidth="2"
                        markerHeight="2"
                        refX="1.9"
                        refY="1"
                        orient="auto"
                      >
                        <path d="M0,0 L2,1 L0,2 z" fill="#8b8b93" />
                      </marker>
                      <marker
                        id="dim-start"
                        markerUnits="userSpaceOnUse"
                        markerWidth="2"
                        markerHeight="2"
                        refX="0.1"
                        refY="1"
                        orient="auto"
                      >
                        <path d="M2,0 L0,1 L2,2 z" fill="#8b8b93" />
                      </marker>
                    </defs>
                    <rect
                      data-bg="1"
                      x={view.x}
                      y={view.y}
                      width={view.w}
                      height={view.h}
                      fill={gridStyle === "off" ? "transparent" : gridStyle === "dots" ? "url(#dg-dots)" : "url(#dg-lines)"}
                    />
                    {gridStyle === "lines" && (
                      <rect
                        x={view.x}
                        y={view.y}
                        width={view.w}
                        height={view.h}
                        fill="url(#dg-major)"
                        pointerEvents="none"
                        aria-hidden="true"
                      />
                    )}
                    {gridStyle !== "off" && (
                      <g data-testid="designer-canvas-crosshair" pointerEvents="none" aria-hidden="true">
                        <line x1={0} y1={view.y} x2={0} y2={view.y + view.h} stroke="rgba(59,130,246,0.28)" strokeWidth={0.5} />
                        <line x1={view.x} y1={0} x2={view.x + view.w} y2={0} stroke="rgba(59,130,246,0.28)" strokeWidth={0.5} />
                      </g>
                    )}
                    {traceUrl && (
                      <image
                        data-testid="designer-trace-image"
                        href={traceUrl}
                        x={Number(trace.x) || 0}
                        y={Number(trace.y) || 0}
                        width={Number(trace.w_in) || 120}
                        height={traceH}
                        opacity={trace.opacity ?? 0.55}
                        preserveAspectRatio="none"
                        className={
                          traceMove && !draw
                            ? "cursor-move"
                            : "pointer-events-none"
                        }
                        onPointerDown={traceMove && !draw ? startTraceDrag : undefined}
                      />
                    )}

                    {panelPieces.map((p) => {
                      const parent = p.parent;
                      const pcx = parent.length_in / 2;
                      const pcy = parent.width_in / 2;
                      const kind = p.panel_kind;
                      const edgeIdx = Number(p.panel_edge) || 0;
                      const parentSel = sel?.pieceId === parent.id && sel?.edge === edgeIdx;
                      const pick = (k) => (ev) => {
                        ev.stopPropagation();
                        // In the shape / cut-out steps, tapping a backsplash or
                        // waterfall detaches it into an editable piece automatically.
                        if (step === "shape" || step === "cutouts") {
                          detachPanel(parent, edgeIdx, kind);
                          return;
                        }
                        setSel({ pieceId: parent.id, edge: edgeIdx, panelEdge: k, panelKind: kind });
                      };
                      return (
                        <g
                          key={p.id}
                          data-testid={`designer-panel-${p.id}`}
                          className={draw ? "pointer-events-none" : undefined}
                          transform={`translate(${parent.x} ${parent.y}) rotate(${parent.rot} ${pcx} ${pcy})`}
                        >
                          <polygon
                            points={p.poly.map(([x, y]) => `${x},${y}`).join(" ")}
                            fill={parentSel && sel.panelKind === kind ? "rgba(96,165,250,0.22)" : "rgba(96,165,250,0.10)"}
                            stroke="#60a5fa"
                            strokeWidth="0.3"
                            className="cursor-pointer"
                            onClick={pick(0)}
                          />
                          {p.poly.map((a, s) => {
                            const b = p.poly[(s + 1) % 4];
                            const k = PANEL_GHOST_EDGE[s];
                            const prof = p.edges?.[k]?.profile;
                            const on = parentSel && sel.panelKind === kind && sel.panelEdge === k;
                            return (
                              <g key={`${p.id}-side-${k}`}>
                                <line
                                  data-testid={`designer-panel-edge-${p.id}-${k}`}
                                  x1={a[0]}
                                  y1={a[1]}
                                  x2={b[0]}
                                  y2={b[1]}
                                  stroke={on ? "#fbbf24" : styleFor(prof)?.color || "#93c5fd"}
                                  strokeWidth={on ? 0.8 : 0.45}
                                  className="cursor-pointer"
                                  onClick={pick(k)}
                                />
                                <line
                                  x1={a[0]}
                                  y1={a[1]}
                                  x2={b[0]}
                                  y2={b[1]}
                                  stroke="transparent"
                                  strokeWidth={Math.min(EDGE_HIT_W, 3)}
                                  style={{ pointerEvents: "stroke" }}
                                  className="cursor-pointer"
                                  onClick={pick(k)}
                                />
                              </g>
                            );
                          })}
                          <text
                            x={p.cx}
                            y={p.cy}
                            fill="#93c5fd"
                            fontSize="2"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            transform={`rotate(${p.textRot} ${p.cx} ${p.cy})`}
                            className="pointer-events-none select-none"
                          >
                            {`${p.label} | #${p.panel_no}`}
                          </text>
                        </g>
                      );
                    })}
                    {rejoinFly && (
                      <g
                        data-testid="designer-rejoin-fly"
                        className="pointer-events-none"
                      >
                        {rejoinFly.ghosts.map((g, gi) => (
                          <path
                            key={gi}
                            d={g.d}
                            fill="rgba(251,191,36,0.14)"
                            stroke="#fbbf24"
                            strokeWidth={0.5}
                            style={{
                              transform: rejoinFly.on
                                ? "translate(0px, 0px)"
                                : `translate(${g.tx}px, ${g.ty}px)`,
                              transition:
                                "transform 300ms cubic-bezier(.33,1,.68,1)",
                            }}
                          />
                        ))}
                      </g>
                    )}
                    {list.map((p) => {
                      if (rejoinFly?.hideIds?.has(p.id)) return null;
                      const selected = sel?.pieceId === p.id;
                      const cx = p.length_in / 2;
                      const cy = p.width_in / 2;
                      const isRound = !!(p.circle || p.oval);
                      return (
                        <g
                          key={p.id}
                          data-testid={`designer-piece-${p.id}`}
                          className={draw ? "pointer-events-none" : undefined}
                          onMouseEnter={() => setHoverPiece(p.id)}
                          onMouseLeave={() => setHoverPiece((h) => (h === p.id ? null : h))}
                          onContextMenu={(ev) => openCtx(ev, p.id)}
                          transform={`translate(${p.x} ${p.y}) rotate(${p.rot} ${cx} ${cy})`}
                        >
                          <path
                            d={piecePathD(p)}
                            fill={
                              p.panel_kind
                                ? "rgba(20,184,166,0.09)"
                                : selected
                                  ? "rgba(129,140,248,0.22)"
                                  : "rgba(99,102,241,0.10)"
                            }
                            stroke={
                              p.panel_kind
                                ? "#2dd4bf"
                                : selected
                                  ? "#a5b4fc"
                                  : "#6366f1"
                            }
                            strokeWidth={p.panel_kind ? lnW(0.45) : lnW(0.32)}
                            strokeLinejoin="round"
                            strokeDasharray={
                              p.panel_kind ? "1.6 1.1" : undefined
                            }
                            className="cursor-move"
                            onPointerDown={(e) => onDown(e, p.id)}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setSel({ pieceId: p.id, edge: null });
                            }}
                          />
                          {isRound && (
                            <g className="pointer-events-none select-none">
                              <line
                                x1={0}
                                y1={p.width_in / 2}
                                x2={p.length_in}
                                y2={p.width_in / 2}
                                stroke="#d4d4d8"
                                strokeWidth={0.18}
                              />
                              {(() => {
                                const dtxt = tag(
                                  round2(p.circle ? p.diameter || p.length_in : p.length_in),
                                );
                                const hw = dtxt.length * 1.7 + 2.4;
                                return (
                                  <g>
                                    <rect
                                      x={p.length_in / 2 - hw / 2}
                                      y={p.width_in / 2 - 2.2}
                                      width={hw}
                                      height={4.4}
                                      rx={1.3}
                                      fill="#0c0c0f"
                                      stroke="#52525b"
                                      strokeWidth={0.12}
                                    />
                                    <text
                                      x={p.length_in / 2}
                                      y={p.width_in / 2 + 1.35}
                                      fill="#f4f4f5"
                                      fontSize={3.2}
                                      textAnchor="middle"
                                    >
                                      {dtxt}
                                    </text>
                                  </g>
                                );
                              })()}
                              {p.oval && (
                                <>
                                  <line
                                    x1={p.length_in / 2}
                                    y1={0}
                                    x2={p.length_in / 2}
                                    y2={p.width_in}
                                    stroke="#d4d4d8"
                                    strokeWidth={0.18}
                                  />
                                  {(() => {
                                    const wtxt = tag(round2(p.width_in));
                                    const ww = wtxt.length * 1.7 + 2.4;
                                    return (
                                      <g>
                                        <rect
                                          x={p.length_in / 2 - ww / 2}
                                          y={p.width_in * 0.75 - 2.2}
                                          width={ww}
                                          height={4.4}
                                          rx={1.3}
                                          fill="#0c0c0f"
                                          stroke="#52525b"
                                          strokeWidth={0.12}
                                        />
                                        <text
                                          x={p.length_in / 2}
                                          y={p.width_in * 0.75 + 1.35}
                                          fill="#f4f4f5"
                                          fontSize={3.2}
                                          textAnchor="middle"
                                        >
                                          {wtxt}
                                        </text>
                                      </g>
                                    );
                                  })()}
                                </>
                              )}
                            </g>
                          )}
                          {isRound &&
                            selected &&
                            general?.enable_resize !== false && (
                              <>
                                <rect
                                  data-testid={`designer-round-handle-x-${p.id}`}
                                  x={p.length_in - 1.1}
                                  y={p.width_in / 2 - 1.1}
                                  width={2.2}
                                  height={2.2}
                                  rx={0.4}
                                  fill="#f472b6"
                                  stroke="#09090b"
                                  strokeWidth={0.25}
                                  className="cursor-ew-resize"
                                  style={{ pointerEvents: "all" }}
                                  onPointerDown={(ev) => onRoundResizeDown(ev, p, "x")}
                                />
                                {p.oval && (
                                  <rect
                                    data-testid={`designer-round-handle-y-${p.id}`}
                                    x={p.length_in / 2 - 1.1}
                                    y={p.width_in - 1.1}
                                    width={2.2}
                                    height={2.2}
                                    rx={0.4}
                                    fill="#f472b6"
                                    stroke="#09090b"
                                    strokeWidth={0.25}
                                    className="cursor-ns-resize"
                                    style={{ pointerEvents: "all" }}
                                    onPointerDown={(ev) => onRoundResizeDown(ev, p, "y")}
                                  />
                                )}
                              </>
                            )}
                          {!isRound && p.points.map((a, i) => {
                            const b = p.points[(i + 1) % p.points.length];
                            const e = p.edges[i] || {};
                            const isSel = selected && sel.edge === i;
                            const isHover =
                              step === "edges" &&
                              hoverEdge &&
                              hoverEdge.pieceId === p.id &&
                              hoverEdge.edge === i;
                            const mx = (a[0] + b[0]) / 2;
                            const my = (a[1] + b[1]) / 2;
                            const len = Math.round(edgeLen(p.points, i));
                            return (
                              <g key={`${p.id}-edge-${i}`}>
                                {e.miter && (
                                  <line
                                    data-testid={`designer-miter-${p.id}-${i}`}
                                    {...miterLine(p.points, i)}
                                    stroke="#22d3ee"
                                    strokeWidth="0.5"
                                  />
                                )}
                                {(e.seam
                                  ? segmentMinusBoxes(a, b, cutoutBoxes(p, list))
                                  : [[a, b]]
                                ).map(([sa, sb], si) => (
                                <path
                                  key={si}
                                  data-testid={si === 0 ? `designer-edge-${p.id}-${i}` : undefined}
                                  d={
                                    isArcEdge(p, i)
                                      ? edgePathD(p, i)
                                      : `M${sa[0]},${sa[1]} L${sb[0]},${sb[1]}`
                                  }
                                  fill="none"
                                  strokeLinecap="round"
                                  stroke={
                                    isSel
                                      ? "#fbbf24"
                                      : isHover
                                        ? "#fcd34d"
                                        : p.panel_kind
                                          ? "#2dd4bf"
                                          : e.color
                                            ? e.color
                                            : e.seam
                                              ? SEAM
                                              : styleFor(e.profile)?.color ||
                                                "#a5b4fc"
                                  }
                                  strokeWidth={
                                    isSel
                                      ? lnW(0.8)
                                      : isHover
                                        ? lnW(0.7)
                                        : p.panel_kind
                                          ? lnW(0.35)
                                          : e.seam
                                            ? lnW(0.45)
                                            : lnW(0.32)
                                  }
                                  strokeDasharray={
                                    p.panel_kind ? "1.6 1.1" : undefined
                                  }
                                  className={
                                    step === "dimensions" && general?.enable_resize !== false
                                      ? Math.abs(a[0] - b[0]) < Math.abs(a[1] - b[1])
                                        ? "cursor-ew-resize"
                                        : "cursor-ns-resize"
                                      : "cursor-pointer"
                                  }
                                  style={{ pointerEvents: "stroke" }}
                                  onPointerDown={(ev) => {
                                    if (step === "dimensions" && general?.enable_resize !== false)
                                      onSideDown(ev, p, i);
                                  }}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    if (movedRef.current) return;
                                    setSel({ pieceId: p.id, edge: i });
                                  }}
                                />
                                ))}
                                {(step === "edges" ||
                                  (step === "dimensions" && general?.enable_resize !== false)) && (
                                  <path
                                    data-testid={`designer-edge-hit-${p.id}-${i}`}
                                    d={edgePathD(p, i)}
                                    fill="none"
                                    stroke="transparent"
                                    strokeWidth={step === "edges" ? EDGE_HIT_W : 5}
                                    strokeLinecap="butt"
                                    style={{ pointerEvents: "stroke" }}
                                    className={
                                      step === "edges"
                                        ? "cursor-pointer"
                                        : Math.abs(a[0] - b[0]) < Math.abs(a[1] - b[1])
                                          ? "cursor-ew-resize"
                                          : "cursor-ns-resize"
                                    }
                                    onPointerDown={(ev) => {
                                      if (step === "dimensions") onSideDown(ev, p, i);
                                    }}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      if (movedRef.current) return;
                                      setSel({ pieceId: p.id, edge: i });
                                    }}
                                  />
                                )}
                                {e.appliance &&
                                  (() => {
                                    const fp = applianceFootprintGeom(
                                      p.points,
                                      i,
                                      e,
                                    );
                                    if (!fp) return null;
                                    const col =
                                      APP_COLOR[e.appliance] || "#22d3ee";
                                    return (
                                      <g
                                        data-testid={`designer-appliance-${p.id}-${i}`}
                                        className="pointer-events-none"
                                      >
                                        <polygon
                                          points={fp.poly}
                                          fill={`${col}22`}
                                          stroke={col}
                                          strokeWidth="0.4"
                                          strokeDasharray="1.6 1.1"
                                        />
                                        <text
                                          x={fp.cx}
                                          y={fp.cy}
                                          fill={col}
                                          fontSize="3.2"
                                          fontWeight="700"
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                        >
                                          {APP_LABEL[e.appliance]}
                                        </text>
                                      </g>
                                    );
                                  })()}
                                {!p.panel_kind &&
                                  (Number(e.backsplash_height_in) > 0 ||
                                    Number(e.drop_in) > 0) &&
                                  (() => {
                                    const [nx, ny] = outwardNormal(p.points, i);
                                    const splash =
                                      Number(e.backsplash_height_in) > 0;
                                    const height = splash
                                      ? Number(e.backsplash_height_in)
                                      : Number(e.drop_in);
                                    const abbr = splash
                                      ? "BS"
                                      : e.miter
                                        ? "MT"
                                        : "WF";
                                    return (
                                      <text
                                        data-testid={`designer-panel-tag-${p.id}-${i}`}
                                        x={mx - nx * 3.4}
                                        y={my - ny * 3.4 + 0.9}
                                        fill="#5eead4"
                                        fontSize="2.4"
                                        textAnchor="middle"
                                        className="pointer-events-none select-none"
                                      >
                                        {abbr}-{formatInches(height)}
                                      </text>
                                    );
                                  })()}
                                {dimVisible(p, i, selected) &&
                                  !arcHandleHidden(p).labelHide.has(i) &&
                                  (() => {
                                    const g = dimGeom(p.points, i, 4.5);
                                    const exact = edgeLen(p.points, i);
                                    const label = `${SIDE_LETTER(i)}: ${tag(exact)}`;
                                    const focus = selected || hoverPiece === p.id;
                                    const minor = isShortSide(p, i) || (step !== "dimensions" && !focus);
                                    // edge abbreviations only matter once we're past sizing
                                    const extra = p.panel_kind
                                      ? ""
                                      : [
                                          step !== "dimensions" || isSel
                                            ? styleFor(e.profile)?.alias ||
                                              styleFor(e.profile)?.abbr ||
                                              ""
                                            : "",
                                        ]
                                          .filter(Boolean)
                                          .join(" ");
                                    const text = extra
                                      ? `${label} · ${extra}`
                                      : label;
                                    const fs = minor ? 1.45 : 1.8;
                                    const w = text.length * (minor ? 0.74 : 0.9) + 2;
                                    // keep side labels clear of the piece body
                                    const vertical =
                                      Math.abs(b[1] - a[1]) >
                                      Math.abs(b[0] - a[0]);
                                    const [nx, ny] = outwardNormal(p.points, i);
                                    const push = vertical ? w / 2 - 1 : 0;
                                    const lx = g.om[0] + nx * push;
                                    const ly = g.om[1] + ny * push;
                                    return (
                                      <g
                                        data-testid={`designer-dim-${p.id}-${i}`}
                                        data-minor={minor ? "1" : undefined}
                                        opacity={minor && !isSel ? 0.55 : 1}
                                        style={{ transition: "opacity 0.15s ease-out" }}
                                      >
                                        <line
                                          x1={g.a[0]}
                                          y1={g.a[1]}
                                          x2={g.oa[0]}
                                          y2={g.oa[1]}
                                          stroke="#52525b"
                                          strokeWidth={dimLineW}
                                        />
                                        <line
                                          x1={g.b[0]}
                                          y1={g.b[1]}
                                          x2={g.ob[0]}
                                          y2={g.ob[1]}
                                          stroke="#52525b"
                                          strokeWidth={dimLineW}
                                        />
                                        <line
                                          x1={g.oa[0]}
                                          y1={g.oa[1]}
                                          x2={g.ob[0]}
                                          y2={g.ob[1]}
                                          stroke="#8b8b93"
                                          strokeWidth={dimLineW}
                                          markerStart="url(#dim-start)"
                                          markerEnd="url(#dim-end)"
                                        />
                                        <g
                                          className="cursor-text"
                                          onClick={(ev) => openLabel(p, i, ev)}
                                        >
                                          <rect
                                            x={lx - w / 2}
                                            y={ly - (minor ? 1.2 : 1.5)}
                                            width={w}
                                            height={minor ? 2.4 : 3}
                                            rx={1.3}
                                            fill="#0c0c0f"
                                            stroke={
                                              isSel ? "#fbbf24" : minor ? "#3f3f46" : "#71717a"
                                            }
                                            strokeWidth="0.12"
                                          />
                                          <text
                                            x={lx}
                                            y={ly + fs * 0.36}
                                            fill={
                                              e.seam ? SEAM : minor ? "#a1a1aa" : "#e4e4e7"
                                            }
                                            fontSize={fs}
                                            textAnchor="middle"
                                            className="select-none"
                                          >
                                            {text}
                                          </text>
                                        </g>
                                      </g>
                                    );
                                  })()}
                              </g>
                            );
                          })}

                          {(step === "dimensions" || step === "shape") &&
                            (() => {
                              const cap = uCapOf(p.points);
                              if (!cap) return null;
                              const mA = capMid(p.points, cap.a);
                              const mE = capMid(p.points, cap.b);
                              const { gap } = uCapGap(p.points, cap);
                              const mid = [(mA[0] + mE[0]) / 2, (mA[1] + mE[1]) / 2];
                              const gl = tag(gap);
                              const gw = gl.length * 0.95 + 3;
                              return (
                                <g data-testid={`designer-ucap-gap-${p.id}`}>
                                  <line
                                    x1={mA[0]}
                                    y1={mA[1]}
                                    x2={mE[0]}
                                    y2={mE[1]}
                                    stroke="#8b8b93"
                                    strokeWidth="0.12"
                                    markerStart="url(#dim-start)"
                                    markerEnd="url(#dim-end)"
                                  />
                                  <g className="cursor-text" onClick={(ev) => openUCapHeight(p, ev)}>
                                    <rect
                                      x={mid[0] - gw / 2}
                                      y={mid[1] - 1.5}
                                      width={gw}
                                      height={3}
                                      rx={1.3}
                                      fill="#0c0c0f"
                                      stroke={sel?.pieceId === p.id ? "#fbbf24" : "#71717a"}
                                      strokeWidth="0.12"
                                    />
                                    <text
                                      x={mid[0]}
                                      y={mid[1] + 0.65}
                                      fill="#e4e4e7"
                                      fontSize="1.8"
                                      textAnchor="middle"
                                      className="select-none"
                                    >
                                      {gl}
                                    </text>
                                  </g>
                                </g>
                              );
                            })()}

                          {(p.cutouts || []).map((c) => (
                            <g
                              key={c.id}
                              data-testid={`designer-cutout-${c.id}`}
                              onPointerDown={(ev) =>
                                onCutoutDown(ev, p.id, c.id)
                              }
                              onContextMenu={(ev) => openCutCtx(ev, p.id, c.id)}
                              onClick={(ev) => {
                                if (step !== "cutouts") return;
                                ev.stopPropagation();
                                setSel({
                                  pieceId: p.id,
                                  edge: null,
                                  cutoutId: c.id,
                                });
                              }}
                              className={step === "cutouts" ? "cursor-move" : "cursor-context-menu"}
                            >
                              {(() => {
                                const cl = cutClearance(p, c, seamsFor.pieceId === p.id ? seamsFor.items : []);
                                const tight = clearanceMin > 0 && cl.gap < clearanceMin;
                                return (
                                  <>
                                    <CutoutShape
                                      c={c}
                                      piece={null}
                                      fill={tight ? "rgba(127,29,29,0.45)" : cutoutColors?.[c.kind] ? `${cutoutColors[c.kind]}22` : "rgba(9,9,11,0.55)"}
                                      stroke={tight ? "#f87171" : sel?.cutoutId === c.id ? "#fbbf24" : cutoutColors?.[c.kind] || "#fafafa"}
                                      strokeWidth={sel?.cutoutId === c.id ? 0.34 : 0.2}
                                      fontSize={1.8}
                                    />
                                    {tight && (
                                      <text
                                        x={c.x + c.w / 2}
                                        y={c.y - 0.8}
                                        textAnchor="middle"
                                        fontSize="1.6"
                                        fontWeight="700"
                                        fill="#fca5a5"
                                        className="pointer-events-none select-none"
                                        data-testid={`designer-cutout-clearance-${c.id}`}
                                      >
                                        {formatInches(cl.gap)} to {cl.what} · keep {formatInches(clearanceMin)}
                                      </text>
                                    )}
                                  </>
                                );
                              })()}
                              {c.ai_unconfirmed && (
                                <g
                                  data-testid={`designer-cutout-confirm-${c.id}`}
                                  className="cursor-pointer"
                                  onPointerDown={(ev) => ev.stopPropagation()}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setSel({
                                      pieceId: p.id,
                                      edge: null,
                                      cutoutId: c.id,
                                    });
                                    patchCutout(p.id, c.id, {
                                      ai_unconfirmed: false,
                                    });
                                    toast.success("Cutout size confirmed");
                                  }}
                                >
                                  <rect
                                    x={c.x + c.w / 2 - 7}
                                    y={c.y + c.h + 0.6}
                                    width="14"
                                    height="3.8"
                                    rx="1"
                                    fill="#f59e0b"
                                    stroke="#09090b"
                                    strokeWidth="0.2"
                                  />
                                  <text
                                    x={c.x + c.w / 2}
                                    y={c.y + c.h + 3.1}
                                    fill="#09090b"
                                    fontSize="2.1"
                                    fontWeight="700"
                                    textAnchor="middle"
                                    className="select-none"
                                  >
                                    check size
                                  </text>
                                </g>
                              )}
                              {sel?.cutoutId === c.id && step === "cutouts" && (
                                <g
                                  data-testid={`designer-cutout-rotate-${c.id}`}
                                  className="cursor-pointer"
                                  onPointerDown={(ev) => ev.stopPropagation()}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    rotateCutout(p.id, c.id);
                                  }}
                                >
                                  <title>Rotate 90°</title>
                                  <svg
                                    x={c.x + c.w - 2.4}
                                    y={c.y - 2.4}
                                    width="4.8"
                                    height="4.8"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle cx="12" cy="12" r="12" fill="#fbbf24" stroke="#09090b" strokeWidth="1" />
                                    <path d="M16.9 8.4a6 6 0 1 0 1.3 4.2" fill="none" stroke="#09090b" strokeWidth="2.1" strokeLinecap="round" />
                                    <path d="M18.6 4.2 L18 9.4 L13 8 Z" fill="#09090b" />
                                  </svg>
                                </g>
                              )}
                            </g>
                          ))}
                          {cutoutGuide?.pieceId === p.id && (
                            <g className="pointer-events-none">
                              {cutoutGuide.x != null && (
                                <line
                                  x1={cutoutGuide.x}
                                  y1={-2}
                                  x2={cutoutGuide.x}
                                  y2={p.width_in + 2}
                                  stroke="#34d399"
                                  strokeWidth="0.35"
                                  strokeDasharray="2 1.5"
                                />
                              )}
                              {cutoutGuide.y != null && (
                                <line
                                  x1={-2}
                                  y1={cutoutGuide.y}
                                  x2={p.length_in + 2}
                                  y2={cutoutGuide.y}
                                  stroke="#34d399"
                                  strokeWidth="0.35"
                                  strokeDasharray="2 1.5"
                                />
                              )}
                            </g>
                          )}
                          {p.panel_kind ? (
                            <text
                              x={(p.length_in || 0) / 2}
                              y={(p.width_in || 0) / 2 + 1}
                              fill="#e4e4e7"
                              fontSize="2.6"
                              textAnchor="middle"
                              className="pointer-events-none select-none"
                              data-testid={`designer-panel-note-${p.id}`}
                            >
                              {p.label}
                              {p.panel_no ? ` | #${p.panel_no}` : ""}
                            </text>
                          ) : (
                            <text
                              x="2"
                              y="4.2"
                              fill="#e4e4e7"
                              fontSize="2.2"
                              className="pointer-events-none select-none"
                            >
                              {p.label}
                              {uprightTag(p)}
                            </text>
                          )}
                          {step === "dimensions" && selected && !p.panel_kind && !isRound && (
                            <g data-testid={`designer-right-angles-${p.id}`}>
                              {p.points.map((v, i) => {
                                const pts = p.points;
                                const n = pts.length;
                                const a = pts[(i - 1 + n) % n];
                                const b = pts[(i + 1) % n];
                                const ux = a[0] - v[0], uy = a[1] - v[1];
                                const wx = b[0] - v[0], wy = b[1] - v[1];
                                const lu = Math.hypot(ux, uy) || 1, lw = Math.hypot(wx, wy) || 1;
                                if (Math.abs((ux * wx + uy * wy) / (lu * lw)) > 0.02) return null;
                                let bx = ux / lu + wx / lw, by = uy / lu + wy / lw;
                                const bl = Math.hypot(bx, by) || 1;
                                bx /= bl; by /= bl;
                                if (!insidePoly([v[0] + bx * 0.5, v[1] + by * 0.5], pts)) return null;
                                const cx = v[0] + bx * 3.4, cy = v[1] + by * 3.4;
                                return (
                                  <g key={`ra-${i}`} data-testid={`designer-right-angle-${p.id}-${i}`} className="pointer-events-none select-none">
                                    <circle cx={cx} cy={cy} r={2.1} fill="#e4e4e7" fillOpacity="0.92" stroke="#52525b" strokeWidth="0.15" />
                                    <text x={cx} y={cy + 0.05} fill="#3f3f46" fontSize="1.5" fontWeight="700" textAnchor="middle" dominantBaseline="middle">
                                      90°
                                    </text>
                                  </g>
                                );
                              })}
                            </g>
                          )}
                          {step === "dimensions" &&
                            general?.enable_resize !== false &&
                            selected &&
                            !isRound &&
                            (() => {
                              const ah = arcHandleHidden(p);
                              return p.points.map((v, i) => {
                                if (ah.hide.has(i)) return null;
                                const bumpId = ah.apex.get(i);
                                if (bumpId)
                                  return (
                                    <circle
                                      key={`apex-${i}`}
                                      data-testid={`designer-apex-${p.id}-${i}`}
                                      cx={v[0]}
                                      cy={v[1]}
                                      r={1.3}
                                      fill={
                                        drag?.type === "bump-depth" && drag?.pieceId === p.id
                                          ? "#fbbf24"
                                          : "#818cf8"
                                      }
                                      stroke="#e0e7ff"
                                      strokeWidth="0.35"
                                      className="cursor-ns-resize"
                                      onPointerDown={(ev) => onBumpDepthDown(ev, p, bumpId)}
                                    >
                                      <title>Drag to change how far the curve sticks out</title>
                                    </circle>
                                  );
                                return (
                                  <circle
                                    key={`vertex-${i}`}
                                    data-testid={`designer-vertex-${p.id}-${i}`}
                                    cx={v[0]}
                                    cy={v[1]}
                                    r={0.85}
                                    fill={
                                      drag?.corner === i && drag?.pieceId === p.id
                                        ? "#fbbf24"
                                        : "#fafafa"
                                    }
                                    stroke="#2563eb"
                                    strokeWidth="0.2"
                                    className="cursor-move"
                                    onPointerDown={(ev) => onCornerDown(ev, p, i)}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      if (movedRef.current) return;
                                      setCtxMenu(null);
                                      setLabelEdit(null);
                                      setSel({ pieceId: p.id, edge: null, vertex: i });
                                      setCornerMenu({ pieceId: p.id, vertex: i, ...ctxPos(ev) });
                                    }}
                                  />
                                );
                              });
                            })()}
                          {step === "shape" &&
                            !isRound &&
                            (() => {
                              const ah = arcHandleHidden(p);
                              return p.points.map((v, i) => {
                                if (ah.hide.has(i)) return null;
                                const bumpId = ah.apex.get(i);
                                if (bumpId)
                                  return (
                                    <circle
                                      key={`corner-${i}`}
                                      data-testid={`designer-apex-${p.id}-${i}`}
                                      cx={v[0]}
                                      cy={v[1]}
                                      r={1.3}
                                      fill={
                                        drag?.type === "bump-depth" && drag?.pieceId === p.id
                                          ? "#fbbf24"
                                          : "#818cf8"
                                      }
                                      stroke="#e0e7ff"
                                      strokeWidth="0.35"
                                      className="cursor-ns-resize"
                                      onPointerDown={(ev) => onBumpDepthDown(ev, p, bumpId)}
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        if (movedRef.current) return;
                                        setSel({ pieceId: p.id, edge: bumpTopEdge(p, bumpId) });
                                      }}
                                    >
                                      <title>Drag to change the curve depth</title>
                                    </circle>
                                  );
                                return (
                              <circle
                                key={`corner-${i}`}
                                data-testid={`designer-corner-${p.id}-${i}`}
                                cx={v[0]}
                                cy={v[1]}
                                r={1}
                                fill="#a5b4fc"
                                stroke="#09090b"
                                strokeWidth="0.2"
                                className="cursor-pointer"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setSel({
                                    pieceId: p.id,
                                    edge: null,
                                    vertex: i,
                                  });
                                  setCtxMenu(null);
                                  setCornerMenu({
                                    pieceId: p.id,
                                    vertex: i,
                                    ...ctxPos(ev),
                                  });
                                }}
                              />
                                );
                              });
                            })()}
                          {step === "shape" &&
                            !isRound &&
                            (() => {
                              const pts = p.points;
                              const n = pts.length;
                              const feats = cornerFeatures(p);
                              const skip = new Set();
                              feats.forEach((f) =>
                                f.edges.forEach((k) => {
                                  skip.add(k);
                                  skip.add((k + 1) % n);
                                }),
                              );
                              (p.edges || []).forEach((e, k) => {
                                if (e.bump) {
                                  skip.add(k);
                                  skip.add((k + 1) % n);
                                }
                              });
                              return (
                                <g data-testid={`designer-corner-tags-${p.id}`}>
                                  {pts.map((v, i) => {
                                    if (skip.has(i)) return null;
                                    const a = pts[(i - 1 + n) % n];
                                    const b = pts[(i + 1) % n];
                                    const l1 = Math.hypot(a[0] - v[0], a[1] - v[1]) || 1;
                                    const l2 = Math.hypot(b[0] - v[0], b[1] - v[1]) || 1;
                                    let bx = (a[0] - v[0]) / l1 + (b[0] - v[0]) / l2;
                                    let by = (a[1] - v[1]) / l1 + (b[1] - v[1]) / l2;
                                    const bl = Math.hypot(bx, by);
                                    if (bl < 0.05) return null;
                                    bx /= bl;
                                    by /= bl;
                                    if (!insidePoly([v[0] + bx * 0.5, v[1] + by * 0.5], pts)) {
                                      bx = -bx;
                                      by = -by;
                                    }
                                    return (
                                      <text
                                        key={`std-${i}`}
                                        data-testid={`designer-corner-std-${p.id}-${i}`}
                                        x={v[0] + bx * 4.2}
                                        y={v[1] + by * 4.2}
                                        fill="#a1a1aa"
                                        fontSize="2.1"
                                        fontWeight="600"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        className="pointer-events-none select-none"
                                      >
                                        Std
                                      </text>
                                    );
                                  })}
                                  {feats.map((f) => {
                                    const ring = [
                                      ...f.edges.map((k) => pts[k]),
                                      pts[(f.edges[f.edges.length - 1] + 1) % n],
                                    ];
                                    const midEdge = f.edges[Math.floor(f.edges.length / 2)];
                                    const [nx, ny] = outwardNormal(pts, midEdge);
                                    const mv = ring[Math.floor(ring.length / 2)];
                                    const on = sel?.corner === f.id && selected;
                                    const label = `${CORNER_LETTER[f.kind] || ""}${round2(
                                      Number(f.record.size ?? f.size) || 0,
                                    )}"`;
                                    const bw = label.length * 1.5 + 2.4;
                                    const bxm = mv[0] + nx * 4.5;
                                    const bym = mv[1] + ny * 4.5;
                                    return (
                                      <g
                                        key={f.id}
                                        data-testid={`designer-corner-feature-${p.id}-${f.id}`}
                                        className="cursor-pointer"
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          setCtxMenu(null);
                                          setCornerMenu(null);
                                          setSel({ pieceId: p.id, edge: null, corner: f.id });
                                        }}
                                      >
                                        <polyline
                                          points={ring.map(([x, y]) => `${x},${y}`).join(" ")}
                                          fill="none"
                                          stroke={on ? "#22d3ee" : "#60a5fa"}
                                          strokeWidth={on ? 1.5 : 1.1}
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <rect
                                          x={bxm - bw / 2}
                                          y={bym - 1.9}
                                          width={bw}
                                          height={3.8}
                                          rx="0.8"
                                          fill={on ? "#0891b2" : "#3b82f6"}
                                          stroke="#09090b"
                                          strokeWidth="0.2"
                                        />
                                        <text
                                          x={bxm}
                                          y={bym}
                                          fill="#fff"
                                          fontSize="2.4"
                                          fontWeight="700"
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                          className="select-none"
                                        >
                                          {label}
                                        </text>
                                      </g>
                                    );
                                  })}
                                </g>
                              );
                            })()}
                          {(p.seam_suggestions || []).map((sm, si) => {
                            const axis = sm.dir === "h" ? 1 : 0;
                            const span = axis === 0 ? p.length_in : p.width_in;
                            const at = Math.min(
                              Math.max(Number(sm.at) || 0, 0),
                              span,
                            );
                            const spans = seamSpans(p.points, axis, at);
                            const cuts = spans.length
                              ? spans
                              : [[0, axis === 0 ? p.width_in : p.length_in]];
                            const widest = cuts.reduce((m, s) =>
                              s[1] - s[0] > m[1] - m[0] ? s : m,
                            );
                            const mid = (widest[0] + widest[1]) / 2;
                            const hx = axis === 0 ? at : mid;
                            const hy = axis === 0 ? mid : at;
                            return (
                              <g
                                key={`sug-${sm.id || si}`}
                                data-testid={`designer-seam-suggestion-${p.id}-${si}`}
                              >
                                {cuts.map(([lo, hi], ci) => (
                                  <line
                                    key={ci}
                                    x1={axis === 0 ? at : lo}
                                    y1={axis === 0 ? lo : at}
                                    x2={axis === 0 ? at : hi}
                                    y2={axis === 0 ? hi : at}
                                    stroke={SEAM}
                                    strokeWidth="0.55"
                                    strokeDasharray="1.6 1.6"
                                    opacity="0.9"
                                    className="pointer-events-none"
                                  />
                                ))}
                                <g
                                  data-testid={`designer-seam-cut-${p.id}-${si}`}
                                  className="cursor-pointer"
                                  onPointerDown={(ev) => ev.stopPropagation()}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    cutSuggestion(p, sm);
                                  }}
                                >
                                  <rect
                                    x={hx - 7}
                                    y={hy - 2}
                                    width="14"
                                    height="4"
                                    rx="1"
                                    fill={SEAM}
                                    stroke="#09090b"
                                    strokeWidth="0.25"
                                  />
                                  <text
                                    x={hx}
                                    y={hy + 1.4}
                                    fill="#09090b"
                                    fontSize="2.4"
                                    fontWeight="700"
                                    textAnchor="middle"
                                    className="select-none"
                                  >
                                    Cut here
                                  </text>
                                </g>
                              </g>
                            );
                          })}
                          {selected && (
                            <>
                              {step === "shape" &&
                                showSeamGuide &&
                                seams.map((sm, si) => {
                                  const axis = sm.dir === "v" ? 0 : 1;
                                  const span =
                                    axis === 0 ? p.length_in : p.width_in;
                                  const at = Math.min(
                                    Math.max(Number(sm.at) || 0, 0),
                                    span,
                                  );
                                  // clip the seam to the outline so it matches the counter depth
                                  const spans = seamSpans(p.points, axis, at);
                                  const cuts = spans.length
                                    ? spans
                                    : [
                                        [
                                          0,
                                          axis === 0 ? p.width_in : p.length_in,
                                        ],
                                      ];
                                  const widest = cuts.reduce((m, s) =>
                                    s[1] - s[0] > m[1] - m[0] ? s : m,
                                  );
                                  const mid = (widest[0] + widest[1]) / 2;
                                  const on = activeSeam === sm.id;
                                  // seam through a cutout but not on its centre → warn before templating
                                  const offCentre = (p.cutouts || []).find((c) => {
                                    const lo = axis === 0 ? c.x : c.y;
                                    const hi = axis === 0 ? c.x + c.w : c.y + c.h;
                                    return at > lo && at < hi && Math.abs(at - (lo + hi) / 2) > 0.5;
                                  });
                                  return (
                                    <g
                                      key={sm.id}
                                      data-testid={`designer-seam-guide-${p.id}-${si}`}
                                    >
                                      {cuts.flatMap(([lo, hi]) =>
                                        segmentMinusBoxes(
                                          axis === 0 ? [at, lo] : [lo, at],
                                          axis === 0 ? [at, hi] : [hi, at],
                                          cutoutBoxes(p, list),
                                        ),
                                      ).map(([sa, sb], ci) => {
                                        const [x1, y1] = sa;
                                        const [x2, y2] = sb;
                                        return (
                                          <g key={ci}>
                                            <line
                                              x1={x1}
                                              y1={y1}
                                              x2={x2}
                                              y2={y2}
                                              stroke="transparent"
                                              strokeWidth="5"
                                              strokeLinecap="round"
                                              className="cursor-grab"
                                              style={{ pointerEvents: "stroke" }}
                                              onPointerDown={(ev) =>
                                                onSeamDown(ev, p.id, sm.id)
                                              }
                                            />
                                            <line
                                              x1={x1}
                                              y1={y1}
                                              x2={x2}
                                              y2={y2}
                                              stroke={on ? "#7dff97" : SEAM}
                                              strokeWidth={on ? "0.7" : "0.45"}
                                              className="pointer-events-none"
                                            />
                                          </g>
                                        );
                                      })}
                                      <circle
                                        cx={axis === 0 ? at : mid}
                                        cy={axis === 0 ? mid : at}
                                        r={on ? 5 : 4.5}
                                        fill="transparent"
                                        className="cursor-grab"
                                        style={{ pointerEvents: "all" }}
                                        onPointerDown={(ev) =>
                                          onSeamDown(ev, p.id, sm.id)
                                        }
                                      />
                                      <circle
                                        data-testid={`designer-seam-handle-${p.id}-${si}`}
                                        cx={axis === 0 ? at : mid}
                                        cy={axis === 0 ? mid : at}
                                        r={on ? 2.6 : 2.2}
                                        fill={SEAM}
                                        stroke={on ? "#fef3c7" : "#09090b"}
                                        strokeWidth="0.35"
                                        className="cursor-grab"
                                        style={{ pointerEvents: "all" }}
                                        onPointerDown={(ev) =>
                                          onSeamDown(ev, p.id, sm.id)
                                        }
                                      />
                                      <text
                                        x={axis === 0 ? at + 2 : mid + 3}
                                        y={axis === 0 ? mid - 3 : at - 2}
                                        fill={on ? "#dcffe4" : SEAM}
                                        fontSize="2.2"
                                        fontWeight={on ? "700" : "400"}
                                        className="pointer-events-none select-none"
                                      >
                                        {`Seam ${si + 1} · ${formatInches(at)} | ${formatInches(Math.max(0, span - at))}`}
                                      </text>
                                      {offCentre && (() => {
                                        const cc = axis === 0
                                          ? offCentre.x + offCentre.w / 2
                                          : offCentre.y + offCentre.h / 2;
                                        const tx = axis === 0 ? at + 2 : mid + 3;
                                        const ty = axis === 0 ? mid - 5 : at - 4;
                                        return (
                                          <>
                                            <text
                                              data-testid={`designer-seam-warning-${p.id}-${si}`}
                                              x={tx}
                                              y={ty}
                                              fill="#f87171"
                                              fontSize="1.9"
                                              fontWeight="700"
                                              className="pointer-events-none select-none"
                                            >
                                              {`⚠ Seam off-centre through ${offCentre.kind || "cutout"} by ${formatInches(Math.abs(at - cc))}`}
                                            </text>
                                            <g
                                              data-testid={`designer-seam-centre-btn-${p.id}-${si}`}
                                              className="cursor-pointer"
                                              style={{ pointerEvents: "all" }}
                                              onPointerDown={(ev) => ev.stopPropagation()}
                                              onClick={(ev) => {
                                                ev.stopPropagation();
                                                editSeams((items) =>
                                                  items.map((s) => (s.id === sm.id ? { ...s, at: round2(cc) } : s)),
                                                );
                                                toast.success("Seam centred on sink");
                                              }}
                                            >
                                              <rect x={tx} y={ty + 1} width="15" height="3.4" rx="0.8" fill="#f87171" />
                                              <text
                                                x={tx + 7.5}
                                                y={ty + 3.3}
                                                fill="#09090b"
                                                fontSize="1.7"
                                                fontWeight="700"
                                                textAnchor="middle"
                                                className="select-none"
                                              >
                                                Centre on sink
                                              </text>
                                            </g>
                                          </>
                                        );
                                      })()}
                                      {on && (
                                        <text
                                          x={axis === 0 ? at + 2 : mid + 3}
                                          y={axis === 0 ? mid : at + 0.6}
                                          fill={seamSnap ? "#34d399" : "#a1a1aa"}
                                          fontSize="1.9"
                                          className="pointer-events-none select-none"
                                        >
                                          {seamSnap
                                            ? `Snapped · ${seamSnap}`
                                            : `Slab ${formatInches(slabL)}`}
                                        </text>
                                      )}
                                    </g>
                                  );
                                })}
                              {step === "shape" &&
                                p.points.map((v, i) => (
                                  <circle
                                    key={`selcorner-${i}`}
                                    cx={v[0]}
                                    cy={v[1]}
                                    r={sel.vertex === i ? 1.9 : 0}
                                    fill="#fbbf24"
                                    stroke="#09090b"
                                    strokeWidth="0.25"
                                    className="pointer-events-none"
                                  />
                                ))}
                              {step !== "shape" && (
                                <>
                                  <line
                                    x1={p.length_in}
                                    y1={cy}
                                    x2={p.length_in + 5}
                                    y2={cy}
                                    stroke="#a5b4fc"
                                    strokeWidth="0.35"
                                  />
                                  <circle
                                    data-testid={`designer-rotate-handle-${p.id}`}
                                    cx={p.length_in + 6}
                                    cy={cy}
                                    r={2}
                                    fill="#4f46e5"
                                    stroke="#a5b4fc"
                                    strokeWidth="0.35"
                                    className="cursor-grab"
                                    onPointerDown={(ev) => onRotDown(ev, p.id)}
                                  />
                                </>
                              )}
                            </>
                          )}
                        </g>
                      );
                    })}

                    {walls.map((path, wi) => (
                      <g
                        key={`wall-${wi}`}
                        data-testid={`designer-wall-${wi}`}
                        className="pointer-events-none"
                      >
                        <polyline
                          points={path.map(([x, y]) => `${x},${y}`).join(" ")}
                          fill="none"
                          stroke="#3f3f46"
                          strokeWidth="2.6"
                          strokeLinecap="square"
                        />
                        <polyline
                          points={path.map(([x, y]) => `${x},${y}`).join(" ")}
                          fill="none"
                          stroke="#fbbf24"
                          strokeWidth={lnW(0.5)}
                          strokeDasharray="4 2"
                        />
                      </g>
                    ))}

                    {(notes || []).map((n) => {
                      const lines = String(n.text || "").split("\n");
                      const fs = Number(n.size) || 3;
                      const on = sel?.noteId === n.id;
                      const wpx =
                        Math.max(...lines.map((l) => l.length), 1) * fs * 0.58 + 2;
                      const hpx = lines.length * fs * 1.25 + 1.5;
                      return (
                        <g
                          key={n.id}
                          data-testid={`designer-note-${n.id}`}
                          transform={`translate(${n.x} ${n.y})`}
                          className="cursor-move"
                          onPointerDown={(ev) => startNoteDrag(ev, n)}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setSel({ noteId: n.id });
                          }}
                        >
                          <rect
                            x={-1}
                            y={-fs}
                            width={wpx}
                            height={hpx}
                            rx={0.6}
                            fill={on ? "rgba(251,191,36,0.08)" : "transparent"}
                            stroke={on ? "#fbbf24" : "transparent"}
                            strokeWidth="0.2"
                            strokeDasharray="1 0.8"
                          />
                          {lines.map((l, k) => (
                            <text
                              key={k}
                              x={0}
                              y={k * fs * 1.25}
                              fill={n.color || "#fde68a"}
                              fontSize={fs}
                              fontWeight="600"
                              className="select-none"
                            >
                              {l}
                            </text>
                          ))}
                        </g>
                      );
                    })}

                    {draw?.kind === "box" &&
                      draw.pts.length === 1 &&
                      draw.cursor &&
                      (() => {
                        const [ax, ay] = draw.pts[0];
                        const [bx, by] = draw.cursor;
                        const x0 = Math.min(ax, bx);
                        const y0 = Math.min(ay, by);
                        const w = Math.abs(bx - ax);
                        const h = Math.abs(by - ay);
                        return (
                          <g data-testid="designer-box-preview" className="pointer-events-none">
                            <rect
                              x={x0}
                              y={y0}
                              width={w}
                              height={h}
                              fill="rgba(56,189,248,0.28)"
                              stroke="#38bdf8"
                              strokeWidth="0.6"
                              strokeDasharray="2 1.5"
                            />
                            {w >= 4 && (
                              <text x={x0 + w / 2} y={y0 - 1.5} fill="#38bdf8" fontSize="3.2" fontWeight="bold" textAnchor="middle">
                                {`${Math.round(w * 10) / 10}"`}
                              </text>
                            )}
                            {h >= 4 && (
                              <text x={x0 + w + 1.5} y={y0 + h / 2 + 1.2} fill="#38bdf8" fontSize="3.2" fontWeight="bold">
                                {`${Math.round(h * 10) / 10}"`}
                              </text>
                            )}
                          </g>
                        );
                      })()}
                    {draw &&
                      draw.kind !== "box" &&
                      (() => {
                        const isLine = draw.kind === "line";
                        let pts = draw.cursor
                          ? [...draw.pts, draw.cursor]
                          : draw.pts;
                        if (isLine && draw.pts.length) {
                          const last = draw.pts[draw.pts.length - 1];
                          pts = [
                            ...draw.pts,
                            polarNext(last, lineLen, lineAngle, lineSnap),
                          ];
                        }
                        if (!pts.length) return null;
                        const wall = draw.kind === "wall";
                        const free = draw.kind === "poly";
                        const freeLike = free || isLine;
                        const w = Math.max(6, Number(runWidth) || 25.5);
                        const poly = freeLike
                          ? pts.length > 2
                            ? pts
                            : null
                          : !wall && pts.length > 1
                            ? runPolygon(pts, w)
                            : null;
                        const line = pts.map(([x, y]) => `${x},${y}`).join(" ");
                        const closeHint =
                          (free &&
                            draw.cursor &&
                            draw.pts.length >= 3 &&
                            nearPt(draw.cursor, draw.pts[0])) ||
                          (isLine &&
                            draw.pts.length >= 3 &&
                            pts.length > draw.pts.length &&
                            nearPt(pts[pts.length - 1], draw.pts[0]));
                        const stroke = wall
                          ? "#fbbf24"
                          : isLine
                            ? "#f0abfc"
                            : free
                              ? "#6ee7b7"
                              : "#a5b4fc";
                        const areaSqft =
                          freeLike && pts.length > 2
                            ? Math.round((polyArea(pts) / 144) * 100) / 100
                            : null;
                        const cx =
                          pts.reduce((s, p) => s + p[0], 0) / pts.length;
                        const cy =
                          pts.reduce((s, p) => s + p[1], 0) / pts.length;
                        return (
                          <g
                            data-testid="designer-draw-layer"
                            className="pointer-events-none"
                          >
                            {poly && (
                              <polygon
                                points={poly
                                  .map(([x, y]) => `${x},${y}`)
                                  .join(" ")}
                                fill={
                                  isLine
                                    ? "rgba(240,171,252,0.12)"
                                    : free
                                      ? "rgba(52,211,153,0.16)"
                                      : "rgba(147,197,253,0.55)"
                                }
                                stroke={
                                  isLine
                                    ? "#e879f9"
                                    : free
                                      ? "#34d399"
                                      : "#60a5fa"
                                }
                                strokeWidth="0.4"
                                strokeDasharray="2 1.5"
                              />
                            )}
                            <polyline
                              points={line}
                              fill="none"
                              stroke={stroke}
                              strokeWidth={wall ? 2.2 : isLine ? 0.8 : 0.6}
                              strokeLinecap="square"
                            />
                            {pts.map(([x, y], i) => (
                              <circle
                                key={i}
                                cx={x}
                                cy={y}
                                r={closeHint && i === 0 ? 2.2 : 1}
                                fill={stroke}
                                opacity={closeHint && i === 0 ? 0.6 : 1}
                              />
                            ))}
                            {areaSqft != null && (
                              <text
                                data-testid="designer-draw-area"
                                x={cx}
                                y={cy}
                                fill="#f5d0fe"
                                stroke="#09090b"
                                strokeWidth="0.9"
                                paintOrder="stroke"
                                fontSize="4.5"
                                fontWeight="bold"
                                textAnchor="middle"
                                className="select-none"
                              >
                                {areaSqft} sq ft
                              </text>
                            )}
                            {!freeLike &&
                              !wall &&
                              pts.slice(1, -1).map(([x, y], i) => {
                                const [ax, ay] = pts[i];
                                const [bx, by] = pts[i + 2];
                                const a1 = Math.atan2(ay - y, ax - x);
                                const a2 = Math.atan2(by - y, bx - x);
                                let deg = Math.abs(((a2 - a1) * 180) / Math.PI);
                                if (deg > 180) deg = 360 - deg;
                                deg = Math.round(deg * 2) / 2;
                                return (
                                  <g key={`ang-${i}`} data-testid={`designer-run-corner-${i}`}>
                                    <circle cx={x} cy={y} r="3.6" fill="#f59e0b" stroke="#09090b" strokeWidth="0.5" />
                                    <text
                                      x={x}
                                      y={y + 1.3}
                                      fill="#09090b"
                                      fontSize="3"
                                      fontWeight="bold"
                                      textAnchor="middle"
                                      className="select-none"
                                    >
                                      {deg}°
                                    </text>
                                  </g>
                                );
                              })}
                            {pts.slice(1).map(([x, y], i) => {
                              const [ax, ay] = pts[i];
                              const len =
                                Math.round(Math.hypot(x - ax, y - ay) * 10) /
                                10;
                              if (len < 5) return null;
                              const vertical =
                                Math.abs(x - ax) < Math.abs(y - ay);
                              const ang = freeLike
                                ? Math.round(
                                    (Math.atan2(-(y - ay), x - ax) * 180) /
                                      Math.PI,
                                  )
                                : null;
                              return (
                                <text
                                  key={`len-${i}`}
                                  x={(ax + x) / 2 + (vertical ? 2.5 : 0)}
                                  y={(ay + y) / 2 - (vertical ? 0 : 2)}
                                  fill={
                                    isLine
                                      ? "#f5d0fe"
                                      : free
                                        ? "#a7f3d0"
                                        : "#c7d2fe"
                                  }
                                  stroke="#09090b"
                                  strokeWidth="0.7"
                                  paintOrder="stroke"
                                  fontSize="3"
                                  fontWeight="bold"
                                  textAnchor={vertical ? "start" : "middle"}
                                  dominantBaseline={
                                    vertical ? "middle" : "auto"
                                  }
                                  className="select-none"
                                >
                                  {ang !== null && ((ang % 90) + 90) % 90 !== 0
                                    ? `${tag(Math.hypot(x - ax, y - ay))} · ${((ang % 360) + 360) % 360}°`
                                    : tag(Math.hypot(x - ax, y - ay))}
                                </text>
                              );
                            })}
                          </g>
                        );
                      })()}
                    {snapGuide && drag && (
                      <g className="pointer-events-none" data-testid="designer-snap-guide">
                        <line
                          x1={snapGuide.x ?? snapGuide.from}
                          y1={snapGuide.y ?? snapGuide.from}
                          x2={snapGuide.x ?? snapGuide.to}
                          y2={snapGuide.y ?? snapGuide.to}
                          stroke="#34d399"
                          strokeWidth={view.w / 900}
                          strokeDasharray={`${view.w / 120} ${view.w / 200}`}
                        />
                        <g transform={`translate(${snapGuide.bx} ${snapGuide.by})`}>
                          <rect x={-view.w / 60} y={-view.w / 140} width={view.w / 30} height={view.w / 70} rx={view.w / 140} fill="#064e3b" stroke="#34d399" strokeWidth={view.w / 1600} />
                          <text textAnchor="middle" dominantBaseline="central" fill="#a7f3d0" fontSize={view.w / 130} fontWeight="700" letterSpacing={view.w / 2000}>
                            FLUSH
                          </text>
                        </g>
                      </g>
                    )}
                  </svg>
                  {legChip && (
                    <div
                      data-testid="designer-leg-chip"
                      className="pointer-events-none absolute z-30 -translate-y-full rounded-full border border-amber-400/60 bg-zinc-950/90 px-2.5 py-1 font-mono-data text-xs font-bold text-amber-300 shadow-lg backdrop-blur"
                      style={{ left: legChip.x + 14, top: legChip.y - 10 }}
                    >
                      Leg {tag(legChip.L)}
                    </div>
                  )}
                  {labelEdit && (
                    <div
                      data-testid="designer-dim-editor"
                      data-designer-popout="label"
                      className="absolute z-30 w-56 -translate-x-1/2 rounded-xl border border-zinc-700 bg-zinc-900/95 p-3 shadow-2xl backdrop-blur"
                      style={{
                        left: `${labelEdit.left}%`,
                        top: `${labelEdit.top}%`,
                      }}
                      onPointerDown={(ev) => ev.stopPropagation()}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <div
                        className="-m-1 flex cursor-move touch-none items-center justify-between rounded-lg p-1 hover:bg-zinc-800/60"
                        onPointerDown={(ev) => startPopoutDrag(ev, "label")}
                        title="Drag to move"
                      >
                        <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-200">
                          Edit {labelEdit.ucapHeight ? "height (back \u2194 inner)" : labelEdit.depth ? (uShapeOf(selPiece?.points) ? "middle depth" : "depth (middle piece)") : `dimension ${SIDE_LETTER(labelEdit.edge)}`}
                        </span>
                        <button
                          type="button"
                          data-testid="designer-dim-editor-close"
                          onClick={() => setLabelEdit(null)}
                          className="rounded-md p-1 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </div>

                      <div className="mt-2 space-y-2">
                        <div className="space-y-1">
                          <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                            Length (inches)
                          </span>
                          <MeasureInput
                            autoFocus
                            data-testid="designer-inline-length"
                            min={0.25}
                            value={labelEdit.value}
                            onChange={(n) => {
                              setLabelEdit({ ...labelEdit, value: n });
                              const pc = list.find((p) => p.id === labelEdit.pieceId);
                              if (!pc || !(Number(n) > 0)) return;
                              if (labelEdit.ucapHeight) {
                                setUCapHeight(pc, Number(n));
                                return;
                              }
                              if (labelEdit.depth) {
                                applyDepth(pc, labelEdit.depth, Number(n));
                                return;
                              }
                              if (Math.abs(edgeLen(pc.points, labelEdit.edge) - Number(n)) >= 0.02)
                                setEdgeLengthOn(pc, labelEdit.edge, Number(n));
                            }}
                            onKeyDown={(ev) => {
                              if (ev.key === "Enter") commitLabel();
                              if (ev.key === "Escape") setLabelEdit(null);
                            }}
                            onBlur={() => commitLabel(false)}
                            className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                          />
                        </div>

                        <div className="space-y-1">
                          <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                            Edge type
                          </span>
                          <Select
                            value={
                              selEdge?.appliance
                                ? selEdge.appliance
                                : selEdge?.seam
                                  ? "seam"
                                  : selDrop === "waterfall"
                                    ? "waterfall"
                                    : selDrop === "mitre"
                                      ? "mitre"
                                      : "finished"
                            }
                            onValueChange={setEdgeType}
                          >
                            <SelectTrigger
                              data-testid="designer-dim-edge-type"
                              className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="finished">Finished</SelectItem>
                              <SelectItem value="seam">Seam</SelectItem>
                              <SelectItem value="waterfall">
                                Waterfall
                              </SelectItem>
                              <SelectItem value="mitre">Mitre</SelectItem>
                              <SelectItem value="fridge">Fridge</SelectItem>
                              <SelectItem value="range">Stove</SelectItem>
                              <SelectItem value="dishwasher">
                                Dishwasher
                              </SelectItem>
                              <SelectItem value="hood">Range hood</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1" data-testid="designer-dim-quick">
                          <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                            Quick shape
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {[
                              ["bump_out", "Bump-Out", () => { setLabelEdit(null); addBumpOnEdge("bump_out"); }],
                              ["bump_in", "Bump-In", () => { setLabelEdit(null); addBumpOnEdge("bump_in"); }],
                              ["full_radius", "Full Radius", () => {
                                const pc = list.find((p) => p.id === labelEdit.pieceId);
                                if (!pc) return;
                                const len = edgeLen(pc.points, labelEdit.edge);
                                const next = edgeShape(pc, labelEdit.edge, "full_curve", len, Math.min(6, len / 4));
                                if (next) replacePiece(pc.id, next);
                                setLabelEdit(null);
                              }],
                              ["splash4", '4" Splash', () => setEdge({ backsplash_height_in: 4 })],
                              ["splash12", '12" Splash', () => setEdge({ backsplash_height_in: 12 })],
                            ].map(([k, label, fn]) => (
                              <button
                                key={k}
                                type="button"
                                data-testid={`designer-dim-quick-${k}`}
                                onClick={fn}
                                className="rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] font-semibold text-zinc-200 transition-colors duration-150 hover:border-indigo-400 hover:bg-indigo-500/15 hover:text-white"
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-1">
                          <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                            Backsplash height (inches)
                          </span>
                          <MeasureInput
                            data-testid="designer-dim-splash"
                            min={0}
                            value={selEdge?.backsplash_height_in ?? 0}
                            onChange={(n) =>
                              setEdge({
                                backsplash_height_in: n,
                              })
                            }
                            className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                          />
                          <span
                            className="font-mono-data block text-[10px] text-emerald-300"
                            data-testid="designer-dim-splash-sqft"
                          >
                            {round2(
                              ((Number(selEdge?.backsplash_height_in) || 0) *
                                (selEdge?.length_in || 0)) /
                                144,
                            )}{" "}
                            ft² splash on this side
                          </span>
                        </div>

                        <div className="space-y-1">
                          <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                            Profile
                          </span>
                          <Select
                            value={selEdge?.profile || "Eased"}
                            onValueChange={setEdgeProfile}
                          >
                            <SelectTrigger
                              data-testid="designer-dim-profile"
                              className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {edgeProfiles.map((prof) => (
                                <SelectItem key={prof} value={prof}>
                                  {prof}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {selDrop && (
                          <div className="space-y-1">
                            <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                              Depth (inches)
                            </span>
                            <MeasureInput
                              data-testid="designer-dim-depth"
                              min={0}
                              value={selEdge?.drop_in ?? 0}
                              onChange={(n) =>
                                setEdge({
                                  drop_in: n,
                                })
                              }
                              className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                            />
                            <span
                              className="font-mono-data block text-[10px] text-cyan-300"
                              data-testid="designer-dim-depth-sqft"
                            >
                              {round2(
                                ((Number(selEdge?.drop_in) || 0) *
                                  (selEdge?.length_in || 0)) /
                                  144,
                              )}{" "}
                              ft²{" "}
                              {selDrop === "waterfall" ? "waterfall" : "mitre"}{" "}
                              panel
                            </span>
                          </div>
                        )}

                        <Button
                          data-testid="designer-dim-editor-done"
                          onClick={() => commitLabel()}
                          className="h-9 w-full bg-indigo-500 text-[10px] font-bold uppercase tracking-widest hover:bg-indigo-400"
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  )}
                  {draw?.kind === "run" &&
                    draw.pts.length >= 1 &&
                    (() => {
                      const last = draw.pts[draw.pts.length - 1];
                      const left = Math.min(88, Math.max(12, ((last[0] - view.x) / view.w) * 100));
                      const top = Math.min(92, Math.max(14, ((last[1] - view.y) / view.h) * 100 - 4));
                      return (
                        <div
                          data-testid="designer-run-angle"
                          className="absolute z-30 -translate-x-1/2 -translate-y-full rounded-xl border border-zinc-700 bg-zinc-900/95 p-2 shadow-2xl backdrop-blur"
                          style={{ left: `${left}%`, top: `${top}%` }}
                          onPointerDown={(ev) => ev.stopPropagation()}
                          onClick={(ev) => ev.stopPropagation()}
                          onContextMenu={(ev) => ev.preventDefault()}
                        >
                          <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-300">
                            Select angle
                          </span>
                          <div className="mt-1.5">
                            <AnglePicker
                              value={runAngle}
                              onPick={(a) => setRunAngle(a)}
                              idBase="designer-run-angle"
                            />
                          </div>
                          <span className="mt-1 block text-[9px] text-zinc-500">
                            Next click bends the run by this much · double-click or right-click to finish
                          </span>
                        </div>
                      );
                    })()}
                  {cornerMenu &&
                    (() => {
                      const cp = list.find((p) => p.id === cornerMenu.pieceId);
                      if (!cp || !cp.points?.[cornerMenu.vertex]) return null;
                      const run = runOf(cp.points);
                      const bendC = run ? runCornerOf(run, cornerMenu.vertex) : -1;
                      const bendNow = bendC > 0 ? round2(Math.abs(runTurnDeg(run.path, bendC))) : null;
                      const apply = (mode) => {
                        const size = Number(cornerSize) || 1;
                        if (
                          mode === "clip" &&
                          (Number(cornerAngle) >= 90 || Number(cornerAngle) <= 0)
                        ) {
                          setCornerMenu(null);
                          return;
                        }
                        cutCorner(
                          cp,
                          cornerMenu.vertex,
                          size,
                          mode,
                          mode === "clip" ? { angle: Number(cornerAngle) } : {},
                        );
                      };
                      const options = [
                        ["radius", "Radius", "Rounds the corner"],
                        [
                          "clip",
                          "Clip corner",
                          Number(cornerAngle) >= 90
                            ? "Keeps the corner square"
                            : `Cuts a ${cornerAngle}° slanted flat`,
                        ],
                        ["notch", "Notch", "Cuts a square out"],
                      ];
                      return (
                        <div
                          data-testid="designer-corner-menu"
                          data-designer-popout="corner"
                          className="absolute z-30 w-56 -translate-x-1/2 rounded-xl border border-zinc-700 bg-zinc-900/95 p-3 shadow-2xl backdrop-blur"
                          style={{
                            left: `${cornerMenu.left}%`,
                            top: `${cornerMenu.top}%`,
                          }}
                          onPointerDown={(ev) => ev.stopPropagation()}
                          onClick={(ev) => ev.stopPropagation()}
                          onContextMenu={(ev) => ev.preventDefault()}
                        >
                          <div
                            className="-m-1 flex cursor-move touch-none items-center justify-between rounded-lg p-1 hover:bg-zinc-800/60"
                            onPointerDown={(ev) => startPopoutDrag(ev, "corner")}
                            title="Drag to move"
                          >
                            <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-200">
                              Corner {cornerMenu.vertex + 1}
                            </span>
                            <button
                              type="button"
                              data-testid="designer-corner-menu-close"
                              onClick={() => setCornerMenu(null)}
                              className="rounded-md p-1 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
                            >
                              <X size={12} weight="bold" />
                            </button>
                          </div>

                          {bendC > 0 && (
                            <div
                              className="mt-2 space-y-1.5 rounded-lg border border-indigo-400/30 bg-indigo-500/5 p-2"
                              data-testid="designer-corner-bend"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-200">
                                  Select angle
                                </span>
                                <span
                                  className="font-mono-data text-[10px] text-indigo-200"
                                  data-testid="designer-corner-bend-now"
                                >
                                  {bendNow}°
                                </span>
                              </div>
                              <AnglePicker
                                value={bendNow}
                                onPick={(a) => bendRunCorner(cp, bendC, a)}
                                idBase="designer-corner-bend"
                              />
                              <span className="block text-[9px] text-zinc-500">
                                Swings the shorter leg · 90° is a square L
                              </span>
                            </div>
                          )}

                          <div className="mt-2 space-y-1">
                            <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                              Size (inches)
                            </span>
                            <MeasureInput
                              autoFocus
                              data-testid="designer-corner-menu-size"
                              min={0.25}
                              value={cornerSize}
                              onChange={(n) => setCornerSize(n)}
                              className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                            />
                          </div>

                          <div className="mt-2 space-y-1">
                            <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                              Chamfer angle
                            </span>
                            <div
                              className="grid grid-cols-4 gap-1"
                              data-testid="designer-corner-menu-angle"
                            >
                              {[90, 67.5, 60, 45, 30, 22.5, 15, 10].map((a) => (
                                <button
                                  key={a}
                                  type="button"
                                  data-testid={`designer-corner-menu-angle-${a}`}
                                  onClick={() => setCornerAngle(a)}
                                  className={`rounded-md py-1.5 text-[10px] font-bold transition-colors duration-150 ${
                                    Number(cornerAngle) === a
                                      ? "bg-indigo-500 text-white"
                                      : "border border-zinc-800 text-zinc-400 hover:bg-zinc-800"
                                  }`}
                                >
                                  {a}°
                                </button>
                              ))}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-zinc-500">Custom</span>
                              <Input
                                data-testid="designer-corner-menu-angle-custom"
                                type="number"
                                min={0}
                                max={90}
                                step={0.5}
                                value={cornerAngle}
                                onChange={(e) => setCornerAngle(e.target.value)}
                                className="h-8 w-20 border-zinc-800 bg-zinc-950 text-xs"
                              />
                              <span className="text-[10px] text-zinc-500">° (0 or 90 keeps it square)</span>
                            </div>
                          </div>

                          <div className="mt-2 space-y-1.5">
                            {options.map(([mode, label, hint]) => (
                              <button
                                key={mode}
                                type="button"
                                data-testid={`designer-corner-menu-${mode === "clip" ? "chamfer" : mode}`}
                                onClick={() => apply(mode)}
                                className="flex w-full items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-2.5 py-2 text-left transition-colors duration-150 hover:border-indigo-400/40 hover:bg-indigo-500/10"
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  className="h-6 w-6 shrink-0"
                                >
                                  <path
                                    d={CORNER_ICON_PATH[mode] || CORNER_ICON_PATH.none}
                                    fill="rgba(99,102,241,0.12)"
                                    stroke="#a5b4fc"
                                    strokeWidth="1.4"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <span className="min-w-0">
                                  <span className="block text-[11px] font-bold uppercase tracking-widest text-zinc-100">
                                    {label}
                                  </span>
                                  <span className="block text-[10px] text-zinc-500">
                                    {hint}
                                  </span>
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  {selPiece && !draw && !ctxMenu && !cornerMenu &&
                    (() => {
                      const px = ((selPiece.x - view.x) / view.w) * 100;
                      const py = ((selPiece.y - view.y) / view.h) * 100;
                      return (
                        <div
                          data-testid="designer-float-toolbar"
                          className="absolute z-20 flex items-center gap-0.5 rounded-xl border border-zinc-700 bg-zinc-900/95 p-1 shadow-2xl backdrop-blur"
                          style={{
                            left: `${Math.min(80, Math.max(1, px))}%`,
                            top: `max(4px, calc(${Math.min(94, Math.max(0, py))}% - 46px))`,
                          }}
                          onPointerDown={(ev) => ev.stopPropagation()}
                        >
                          {TOOLS.map(([key, Icon, label]) => (
                            <button
                              key={key}
                              type="button"
                              data-testid={`designer-float-${key}`}
                              title={label}
                              onClick={() => setTool(key)}
                              className={`flex h-8 items-center gap-1 rounded-lg px-2 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                                step === key
                                  ? "bg-indigo-500 text-white"
                                  : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                              }`}
                            >
                              <Icon size={13} weight="bold" />
                              <span className="hidden xl:inline">{label}</span>
                            </button>
                          ))}
                          <span className="mx-0.5 h-5 w-px bg-zinc-700" />
                          <button
                            type="button"
                            data-testid="designer-float-duplicate"
                            title="Duplicate piece"
                            onClick={() => duplicatePiece(selPiece)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 hover:text-white"
                          >
                            <Copy size={13} weight="bold" />
                          </button>
                          <button
                            type="button"
                            data-testid="designer-float-flip"
                            title="Flip horizontally"
                            onClick={() => replacePiece(selPiece.id, flipPiece(selPiece, "h"))}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 hover:text-white"
                          >
                            <FlipHorizontal size={13} weight="bold" />
                          </button>
                          <button
                            type="button"
                            data-testid="designer-float-delete"
                            title="Delete piece"
                            onClick={() => {
                              push(list.filter((p) => p.id !== selPiece.id));
                              setSel(null);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-red-400 transition-colors duration-150 hover:bg-red-500/10"
                          >
                            <Trash size={13} weight="bold" />
                          </button>
                        </div>
                      );
                    })()}
                  {ctxMenu &&
                    (() => {
                      const cp = list.find((p) => p.id === ctxMenu.pieceId);
                      if (!cp) return null;
                      const cut = ctxMenu.cutId
                        ? (cp.cutouts || []).find((c) => c.id === ctxMenu.cutId)
                        : null;
                      if (ctxMenu.cutId && !cut) return null;
                      const pos = {
                        left: `${ctxMenu.left}%`,
                        top: `${ctxMenu.top}%`,
                      };
                      if (cut) {
                        const spinCut = (deg) =>
                          patchCutout(cp.id, cut.id, {
                            rot:
                              ((((Number(cut.rot) || 0) + deg) % 360) + 360) %
                              360,
                          });
                        return (
                          <div
                            data-testid="designer-cutout-menu"
                            className="absolute z-20 flex max-w-[94%] -translate-x-1/2 flex-wrap items-center justify-center gap-1.5 rounded-xl border border-zinc-700 bg-zinc-900/95 p-1.5 shadow-2xl backdrop-blur"
                            style={pos}
                            onContextMenu={(ev) => ev.preventDefault()}
                          >
                            <div className="rounded-lg bg-zinc-800 px-2.5 py-1 text-center">
                              <span className="block text-[8px] font-bold uppercase tracking-widest text-zinc-500">
                                Size
                              </span>
                              <span
                                className="block font-mono-data text-[11px] font-bold text-zinc-100"
                                data-testid="designer-cutout-menu-size"
                              >
                                {formatInches(cut.w)} x {formatInches(cut.h)}
                              </span>
                            </div>
                            <button
                              type="button"
                              data-testid="designer-cutout-menu-rotate-left"
                              onClick={() => spinCut(-ctxAngle)}
                              className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                            >
                              <ArrowCounterClockwise size={14} weight="bold" />
                            </button>
                            <Select
                              value={String(ctxAngle)}
                              onValueChange={(v) => setCtxAngle(Number(v))}
                            >
                              <SelectTrigger
                                data-testid="designer-cutout-menu-angle"
                                className="h-9 w-24 border-zinc-700 bg-zinc-950 text-xs"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[1, 5, 15, 22.5, 45, 90].map((a) => (
                                  <SelectItem key={a} value={String(a)}>
                                    {a}°
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <button
                              type="button"
                              data-testid="designer-cutout-menu-rotate-right"
                              onClick={() => spinCut(ctxAngle)}
                              className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                            >
                              <ArrowClockwise size={14} weight="bold" />
                            </button>
                            <Select
                              value={cut.kind}
                              onValueChange={(v) =>
                                patchCutout(cp.id, cut.id, kindPatch(v))
                              }
                            >
                              <SelectTrigger
                                data-testid="designer-cutout-menu-kind"
                                className="h-9 w-40 border-zinc-700 bg-zinc-950 text-xs"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {withShapeCutouts(cutoutKinds).map((k) => (
                                  <SelectItem key={k} value={k}>
                                    {k}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <button
                              type="button"
                              data-testid="designer-cutout-menu-center"
                              onClick={() =>
                                patchCutout(cp.id, cut.id, {
                                  x: round2(cp.length_in / 2 - cut.w / 2),
                                  y: round2(cp.width_in / 2 - cut.h / 2),
                                })
                              }
                              className="rounded-lg border border-zinc-700 px-2 py-2 text-[9px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                            >
                              Centre
                            </button>
                            <button
                              type="button"
                              data-testid="designer-cutout-menu-duplicate"
                              onClick={() => {
                                update(cp.id, {
                                  cutouts: [
                                    ...(cp.cutouts || []),
                                    {
                                      ...cut,
                                      id: uid(),
                                      x: round2(cut.x + 4),
                                      y: round2(cut.y + 4),
                                    },
                                  ],
                                });
                                setCtxMenu(null);
                              }}
                              className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                            >
                              <Copy size={14} weight="bold" />
                            </button>
                            <button
                              type="button"
                              data-testid="designer-cutout-menu-delete"
                              onClick={() => {
                                update(cp.id, {
                                  cutouts: (cp.cutouts || []).filter(
                                    (x) => x.id !== cut.id,
                                  ),
                                });
                                setSel({ pieceId: cp.id, edge: null });
                                setCtxMenu(null);
                                toast.success(`${cut.kind || "Cut-out"} removed`);
                              }}
                              className="flex items-center gap-1 rounded-lg border border-red-500/40 px-2 py-2 text-[9px] font-bold uppercase tracking-widest text-red-400 transition-colors duration-150 hover:bg-red-500/10"
                            >
                              <Trash size={14} weight="bold" /> Remove
                            </button>
                          </div>
                        );
                      }
                      const spin = (deg) =>
                        update(cp.id, {
                          rot: (((cp.rot + deg) % 360) + 360) % 360,
                        });
                      return (
                        <div
                          data-testid="designer-piece-menu"
                          className="absolute z-20 flex max-w-[94%] -translate-x-1/2 flex-wrap items-center justify-center gap-1.5 rounded-xl border border-zinc-700 bg-zinc-900/95 p-1.5 shadow-2xl backdrop-blur"
                          style={pos}
                          onContextMenu={(ev) => ev.preventDefault()}
                        >
                          <div className="rounded-lg bg-zinc-800 px-2.5 py-1 text-center">
                            <span className="block text-[8px] font-bold uppercase tracking-widest text-zinc-500">
                              Area
                            </span>
                            <span
                              className="block font-mono-data text-[11px] font-bold text-zinc-100"
                              data-testid="designer-menu-sqft"
                            >
                              {(polyArea(cp.points) / 144).toFixed(2)} SF
                            </span>
                          </div>
                          <button
                            type="button"
                            data-testid="designer-menu-rotate-left"
                            onClick={() => spin(-ctxAngle)}
                            className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                          >
                            <ArrowCounterClockwise size={14} weight="bold" />
                          </button>
                          <Select
                            value={String(ctxAngle)}
                            onValueChange={(v) => setCtxAngle(Number(v))}
                          >
                            <SelectTrigger
                              data-testid="designer-menu-angle"
                              className="h-9 w-24 border-zinc-700 bg-zinc-950 text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 5, 15, 22.5, 45, 90].map((a) => (
                                <SelectItem key={a} value={String(a)}>
                                  {a}°
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            data-testid="designer-menu-rotate-right"
                            onClick={() => spin(ctxAngle)}
                            className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                          >
                            <ArrowClockwise size={14} weight="bold" />
                          </button>
                          {/* nudge pad: move small pieces precisely without grabbing them */}
                          <div
                            className="flex items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-950 px-1.5 py-1"
                            data-testid="designer-menu-move"
                            title="Move this piece by the step (hold Shift for 1/8 inch)"
                          >
                            <button
                              type="button"
                              data-testid="designer-menu-move-grip"
                              title="Drag here to move the piece"
                              onPointerDown={(ev) => {
                                ev.preventDefault();
                                ev.stopPropagation();
                                const rect = svgRef.current?.getBoundingClientRect();
                                if (!rect) return;
                                const pxPerIn = rect.width / view.w;
                                const start = { cx: ev.clientX, cy: ev.clientY, x: cp.x, y: cp.y, id: cp.id };
                                snapshot();
                                const move = (e2) => {
                                  const dx = (e2.clientX - start.cx) / pxPerIn;
                                  const dy = (e2.clientY - start.cy) / pxPerIn;
                                  const st = e2.shiftKey ? 0.125 : ctxStep;
                                  update(
                                    start.id,
                                    { x: round2(start.x + Math.round(dx / st) * st), y: round2(start.y + Math.round(dy / st) * st) },
                                    { silent: true },
                                  );
                                };
                                const up = () => {
                                  window.removeEventListener("pointermove", move);
                                  window.removeEventListener("pointerup", up);
                                  sync();
                                };
                                window.addEventListener("pointermove", move);
                                window.addEventListener("pointerup", up);
                              }}
                              className="flex cursor-grab items-center gap-1 rounded px-1 py-0.5 text-[8px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:bg-zinc-800 hover:text-amber-200 active:cursor-grabbing"
                            >
                              <ArrowsOutCardinal size={12} weight="bold" /> Drag
                            </button>
                            {[
                              ["left", ArrowLeft, -1, 0],
                              ["up", ArrowUp, 0, -1],
                              ["down", ArrowDown, 0, 1],
                              ["right", ArrowRight, 1, 0],
                            ].map(([k, Icon, dx, dy]) => (
                              <button
                                key={k}
                                type="button"
                                data-testid={`designer-menu-move-${k}`}
                                onClick={(ev) => {
                                  const step = ev.shiftKey ? 0.125 : ctxStep;
                                  update(cp.id, { x: round2(cp.x + dx * step), y: round2(cp.y + dy * step) });
                                }}
                                className="rounded p-1 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 hover:text-white"
                              >
                                <Icon size={12} weight="bold" />
                              </button>
                            ))}
                            <Select value={String(ctxStep)} onValueChange={(v) => setCtxStep(Number(v))}>
                              <SelectTrigger
                                data-testid="designer-menu-move-step"
                                className="h-7 w-16 border-zinc-700 bg-zinc-950 px-1.5 text-[10px]"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[0.125, 0.25, 0.5, 1, 6, 12].map((v) => (
                                  <SelectItem key={v} value={String(v)}>
                                    {formatInches(v)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {areas.length > 1 && (
                            <Select
                              value={currentAreaId || ""}
                              onValueChange={(v) => {
                                onMovePiece?.(cp.id, v);
                                setCtxMenu(null);
                              }}
                            >
                              <SelectTrigger
                                data-testid="designer-menu-area"
                                className="h-9 w-36 border-zinc-700 bg-zinc-950 text-xs"
                              >
                                <SelectValue placeholder="Area" />
                              </SelectTrigger>
                              <SelectContent>
                                {areas.map((a) => (
                                  <SelectItem key={a.id} value={a.id}>
                                    {a.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <Select
                            value=""
                            onValueChange={(k) => {
                              addCutout(k, undefined, { piece: cp, at: ctxMenu.at });
                              setStep("cutouts");
                              setCtxMenu(null);
                            }}
                          >
                            <SelectTrigger
                              data-testid="designer-menu-add-cutout"
                              title="Add a sink, cooktop or hole right here"
                              className="h-9 w-36 border-zinc-700 bg-zinc-950 text-xs"
                            >
                              <SelectValue placeholder="+ Cut-out here" />
                            </SelectTrigger>
                            <SelectContent>
                              {withShapeCutouts(cutoutKinds).map((k) => (
                                <SelectItem key={k} value={k} data-testid={`designer-menu-add-cutout-${k.replace(/\s+/g, "-").toLowerCase()}`}>
                                  {k}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            data-testid="designer-menu-split-seam"
                            title="Seam this piece — set the seams in the side panel"
                            onClick={() => {
                              setSel({ pieceId: cp.id, edge: null });
                              setStep("shape");
                              setShowSeamGuide(true);
                              if (
                                seamsFor.pieceId !== cp.id ||
                                !seamsFor.items.length
                              )
                                addSeam(cp, "v");
                              setCtxMenu(null);
                            }}
                            className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
                          >
                            <Scissors size={14} weight="bold" />
                          </button>
                          <button
                            type="button"
                            data-testid="designer-menu-duplicate"
                            onClick={() => {
                              duplicatePiece(cp);
                              setCtxMenu(null);
                            }}
                            className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                          >
                            <Copy size={14} weight="bold" />
                          </button>
                          <button
                            type="button"
                            data-testid="designer-menu-delete"
                            onClick={() => {
                              push(list.filter((p) => p.id !== cp.id));
                              setSel(null);
                              setCtxMenu(null);
                            }}
                            className="rounded-lg border border-zinc-700 p-2 text-red-400 transition-colors duration-150 hover:bg-red-500/10"
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      );
                    })()}
                </div>
              </div>
            </div>

            <div
              className="flex items-center gap-2 border-t border-zinc-800 bg-zinc-900 px-3 py-2"
              data-testid="designer-zoom-bar"
            >
              <button
                type="button"
                data-testid="designer-zoom-out"
                onClick={() =>
                  setZoom((z) =>
                    Math.max(0.2, Math.round((z - 0.25) * 100) / 100),
                  )
                }
                className="rounded-md border border-zinc-700 p-1.5 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                <Minus size={13} weight="bold" />
              </button>
              <span
                className="font-mono-data w-12 text-center text-[11px] text-zinc-300"
                data-testid="designer-zoom-value"
              >
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                data-testid="designer-zoom-in"
                onClick={() =>
                  setZoom((z) =>
                    Math.min(4, Math.round((z + 0.25) * 100) / 100),
                  )
                }
                className="rounded-md border border-zinc-700 p-1.5 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                <PlusIcon size={13} weight="bold" />
              </button>
              <button
                type="button"
                data-testid="designer-zoom-fit"
                onClick={fitView}
                className="rounded-md border border-zinc-700 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                <ArrowsOut size={12} weight="bold" className="mr-1 inline" />{" "}
                Fit page
              </button>
              <button
                type="button"
                data-testid="designer-zoom-fit-content"
                onClick={fitToContent}
                title="Zoom to fit your drawing and recenter"
                className="rounded-md border border-zinc-700 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                <ArrowsIn size={12} weight="bold" className="mr-1 inline" />{" "}
                Fit to screen
              </button>
              <button
                type="button"
                data-testid="designer-zoom-100"
                onClick={() => setZoom(1)}
                className="rounded-md border border-zinc-700 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                100%
              </button>
              <span className="ml-auto hidden font-mono-data text-[10px] uppercase tracking-widest text-zinc-600 md:block">
                Space or middle-drag pans
              </span>
              <span className="font-mono-data ml-auto text-[10px] uppercase tracking-widest text-zinc-500 md:ml-3">
                Snap {snap}" · Sheet {Math.round(bounds.w)}" x{" "}
                {Math.round(bounds.h)}"
              </span>
            </div>
          </div>
        )}

        {step === "layout" && layoutBoard ? null : selPiece && !focus && showPanel ? (
          <aside
            className="shrink-0 border-t border-zinc-800 bg-zinc-900 p-5 lg:h-full lg:w-80 lg:overflow-y-auto lg:border-l lg:border-t-0"
            data-testid="designer-panel"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Input
                data-testid="designer-piece-label"
                value={selPiece.label}
                onChange={(e) => update(selPiece.id, { label: e.target.value })}
                className="h-10 w-44 border-zinc-800 bg-zinc-950 font-semibold"
              />
              <span className="font-mono-data text-xs text-zinc-500">
                {(polyArea(selPiece.points) / 144).toFixed(2)} ft² ·{" "}
                {formatInches(selPiece.length_in)} x {formatInches(selPiece.width_in)}
              </span>
              {areas.length > 1 && (
                <Select
                  value={currentAreaId || ""}
                  onValueChange={(v) => onMovePiece?.(selPiece.id, v)}
                >
                  <SelectTrigger
                    data-testid="designer-piece-area"
                    className="h-10 w-40 border-zinc-800 bg-zinc-950 text-xs"
                  >
                    <SelectValue placeholder="Area" />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {free && (
              <div className="mt-4" data-testid="designer-tools-wrap">
                <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Tool
                </span>
                <div
                  className="grid grid-cols-4 gap-1.5 rounded-xl border border-zinc-800 bg-zinc-950/60 p-1.5"
                  data-testid="designer-tools"
                >
                  {TOOLS.map(([key, Icon, label]) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`designer-tool-${key}`}
                      data-active={step === key ? "1" : "0"}
                      onClick={() => setTool(key)}
                      className={`flex h-12 flex-col items-center justify-center gap-1 rounded-lg text-[9px] font-bold uppercase tracking-widest transition-all duration-150 ${
                        step === key
                          ? "bg-indigo-500 text-white shadow-[0_0_16px_-6px_rgba(99,102,241,0.9)]"
                          : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                      }`}
                    >
                      <Icon size={16} weight="bold" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {free && onReview && (
              <button
                type="button"
                data-testid="designer-tool-review"
                onClick={onReview}
                className="mt-2 flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-emerald-400/40 bg-emerald-500/10 text-[10px] font-bold uppercase tracking-widest text-emerald-200 transition-colors duration-150 hover:bg-emerald-500/20"
              >
                <ClipboardText size={13} weight="bold" /> Review quote <ArrowRight size={12} weight="bold" />
              </button>
            )}

            {step === "cutouts" && cutoutPanel && (
              <div
                className="mt-4 rounded-xl border border-amber-400/30 bg-zinc-950/60 p-4"
                data-testid="designer-cutout-right"
              >
                {(() => {
                  const cc = (selPiece.cutouts || []).find((x) => x.id === sel?.cutoutId);
                  if (!cc) return null;
                  const seamItems = seamsFor.pieceId === selPiece.id ? seamsFor.items : [];
                  const cl = cutClearance(selPiece, cc, seamItems);
                  return clearanceMin > 0 && cl.gap < clearanceMin ? (
                    <div
                      data-testid="designer-cutout-clearance-warning"
                      className="mb-3 rounded-lg border border-red-500/40 bg-red-950/40 px-3 py-2 text-[11px] font-semibold text-red-200"
                    >
                      Only {formatInches(cl.gap)} from the nearest {cl.what} — keep at least {formatInches(clearanceMin)} of stone around a cut-out so it doesn't crack.
                      <button
                        type="button"
                        data-testid="designer-cutout-nudge-clear"
                        onClick={() => {
                          const spot = nudgeClear(selPiece, cc, seamItems);
                          if (!spot) {
                            toast.error("No spot on this piece clears that margin — shrink the cut-out or move the seam");
                            return;
                          }
                          snapshot();
                          patchCutout(selPiece.id, cc.id, { x: spot.x, y: spot.y });
                          toast.success(`Moved to the nearest spot with ${formatInches(clearanceMin)} clear`);
                        }}
                        className="mt-2 flex h-8 items-center gap-1.5 rounded-lg border border-red-400/50 bg-red-500/15 px-2.5 text-[10px] font-bold uppercase tracking-widest text-red-100 transition-colors duration-150 hover:bg-red-500/30"
                      >
                        Nudge to {formatInches(clearanceMin)}
                      </button>
                    </div>
                  ) : null;
                })()}
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
                    <CircleDashed size={12} weight="bold" /> Selected cut-out
                  </span>
                  <button
                    type="button"
                    data-testid="designer-cutout-done"
                    onClick={() => setSel({ pieceId: selPiece.id, edge: null })}
                    className="rounded-full border border-zinc-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                  >
                    Done
                  </button>
                </div>
                {cutoutPanel}
              </div>
            )}

            {step === "dimensions" && (
            <div
              className="mt-5 flex flex-wrap items-center gap-2"
              data-testid="designer-orientation"
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Sits
              </span>
              <div className="flex overflow-hidden rounded-md border border-zinc-700">
                {[
                  ["horizontal", "Flat · countertop"],
                  ["vertical", "Upright · panel"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`designer-orientation-${k}`}
                    onClick={() =>
                      update(selPiece.id, {
                        orientation: k,
                        stand: selPiece.stand || "backsplash",
                      })
                    }
                    className={`h-9 px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      (selPiece.orientation === "vertical") === (k === "vertical")
                        ? "bg-indigo-500 text-white"
                        : "bg-zinc-950 text-zinc-400 hover:bg-zinc-900"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {selPiece.orientation === "vertical" && (
                <div className="flex overflow-hidden rounded-md border border-zinc-700">
                  {[
                    ["backsplash", "Backsplash"],
                    ["waterfall", "Waterfall"],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`designer-stand-${k}`}
                      title={
                        k === "waterfall"
                          ? "Hangs from the counter top down to the floor"
                          : "Rises off the counter top against the wall"
                      }
                      onClick={() => {
                        update(selPiece.id, { stand: k });
                        if (selPiece.attach?.piece_id) {
                          const par = list.find(
                            (pp) => pp.id === selPiece.attach.piece_id,
                          );
                          if (par)
                            attachPanelToEdge(
                              { ...selPiece, stand: k },
                              par,
                              selPiece.attach.edge,
                            );
                        }
                      }}
                      className={`h-9 px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                        (selPiece.stand || "backsplash") === k
                          ? "bg-indigo-500 text-white"
                          : "bg-zinc-950 text-zinc-400 hover:bg-zinc-900"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {selPiece.orientation === "vertical" && (
                <div
                  className="flex w-full flex-wrap items-center gap-2"
                  data-testid="designer-attach"
                >
                  <button
                    type="button"
                    data-testid="designer-attach-toggle"
                    onClick={() => setAttachPick((v) => !v)}
                    className="h-9 rounded-md border border-zinc-700 bg-zinc-950 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-900"
                  >
                    {attachPick
                      ? "Cancel"
                      : selPiece.attach?.piece_id
                        ? "Re-snap to edge"
                        : "Snap to edge"}
                  </button>
                  {selPiece.attach?.piece_id ? (
                    <span
                      data-testid="designer-attach-status"
                      className="text-[11px] font-semibold text-emerald-400"
                    >
                      Snapped to{" "}
                      {list.find((p) => p.id === selPiece.attach.piece_id)
                        ?.label || "the counter"}{" "}
                      · side {SIDE_LETTER(selPiece.attach.edge)}
                    </span>
                  ) : (
                    <span
                      data-testid="designer-attach-status"
                      className="text-[11px] text-zinc-500"
                    >
                      Not snapped to a counter yet
                    </span>
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      {selPiece.stand === "waterfall" ? "Drop" : "Height"}
                    </span>
                    <input
                      data-testid="designer-panel-height"
                      type="number"
                      min="1"
                      value={Math.round(Number(selPiece.width_in) || 0)}
                      onChange={(e) => setPanelHeight(selPiece, e.target.value)}
                      className="h-9 w-16 rounded-md border border-zinc-700 bg-zinc-950 px-2 text-center text-sm text-white"
                    />
                    <span className="text-[11px] text-zinc-500">in</span>
                    <button
                      type="button"
                      data-testid="designer-panel-height-4"
                      onClick={() => setPanelHeight(selPiece, 4)}
                      className="h-9 rounded-md border border-zinc-700 px-2 text-[10px] font-bold text-zinc-300 hover:bg-zinc-900"
                    >
                      4&quot;
                    </button>
                    <button
                      type="button"
                      data-testid="designer-panel-height-full"
                      onClick={() =>
                        setPanelHeight(
                          selPiece,
                          selPiece.stand === "waterfall" ? 34.5 : 18,
                        )
                      }
                      className="h-9 rounded-md border border-zinc-700 px-2 text-[10px] font-bold text-zinc-300 hover:bg-zinc-900"
                    >
                      Full
                    </button>
                  </div>
                  {attachPick && (
                    <div
                      className="w-full space-y-1.5 rounded-lg border border-zinc-800 bg-zinc-950 p-2"
                      data-testid="designer-attach-list"
                    >
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                        Pick the counter line to snap this panel to
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {list
                          .filter(
                            (p) =>
                              p.orientation !== "vertical" &&
                              p.id !== selPiece.id,
                          )
                          .flatMap((p) =>
                            (p.points || []).map((_, i) => (
                              <button
                                key={`${p.id}-${i}`}
                                type="button"
                                data-testid={`designer-attach-edge-${p.id}-${i}`}
                                onClick={() => {
                                  attachPanelToEdge(selPiece, p, i);
                                  setAttachPick(false);
                                }}
                                className="rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[10px] font-semibold text-zinc-200 transition-colors duration-150 hover:border-indigo-500 hover:text-white"
                              >
                                {p.label} · {SIDE_LETTER(i)} (
                                {Math.round(edgeLen(p.points, i))}&quot;)
                              </button>
                            )),
                          )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            <div className="mt-5 flex flex-wrap items-end gap-2">
              {step === "shape" && (
                <>
                  <Button
                    data-testid="designer-seam-guide-toggle"
                    variant="outline"
                    title="Mark where this top seams — add as many seams as you need"
                    onClick={() => {
                      const on = !showSeamGuide;
                      setShowSeamGuide(on);
                      if (on && seams.length === 0) addSeam(selPiece, "v");
                    }}
                    className={`h-10 border-zinc-700 bg-transparent text-xs hover:bg-zinc-800 ${
                      showSeamGuide ? "border-amber-500/50 text-amber-200" : ""
                    }`}
                  >
                    <Scissors size={14} weight="bold" />
                  </Button>
                  {showSeamGuide && (
                    <div
                      className="flex w-full flex-col gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2"
                      data-testid="designer-seam-panel"
                    >
                      {seams.length === 0 && (
                        <span
                          className="text-[11px] text-amber-200/70"
                          data-testid="designer-seam-empty"
                        >
                          No seams on this piece yet — add one to mark where the
                          tops join.
                        </span>
                      )}
                      {seams.map((sm, si) => (
                        <div
                          key={sm.id}
                          data-testid={`designer-seam-row-${si}`}
                          onClick={() => setActiveSeam(sm.id)}
                          className={`flex flex-wrap items-center gap-2 rounded-md px-1 py-1 transition-colors duration-150 ${
                            activeSeam === sm.id ? "bg-amber-500/10" : ""
                          }`}
                        >
                          <span
                            data-testid={`designer-seam-name-${si}`}
                            className="text-[10px] font-bold uppercase tracking-widest text-amber-200"
                          >
                            Seam {si + 1}
                          </span>
                          <div className="flex overflow-hidden rounded-md border border-amber-500/40">
                            {[
                              ["v", "Down"],
                              ["h", "Across"],
                            ].map(([d, label]) => (
                              <button
                                key={d}
                                type="button"
                                data-testid={`designer-seam-dir-${d}-${si}`}
                                onClick={() =>
                                  editSeams((items) =>
                                    items.map((s) =>
                                      s.id === sm.id
                                        ? {
                                            ...s,
                                            dir: d,
                                            at: round2(
                                              Math.min(
                                                Number(s.at) || 0,
                                                (d === "v"
                                                  ? selPiece.length_in
                                                  : selPiece.width_in) - 1,
                                              ),
                                            ),
                                          }
                                        : s,
                                    ),
                                  )
                                }
                                className={`h-9 px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                                  sm.dir === d
                                    ? "bg-amber-500 text-zinc-950"
                                    : "bg-zinc-950 text-amber-200 hover:bg-zinc-900"
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <MeasureInput
                            data-testid={`designer-seam-at-${si}`}
                            min={0}
                            value={sm.at}
                            onChange={(n) =>
                              editSeams((items) =>
                                items.map((s) =>
                                  s.id === sm.id
                                    ? { ...s, at: n }
                                    : s,
                                ),
                              )
                            }
                            className="font-mono-data h-9 w-24 border-amber-500/30 bg-zinc-950 text-xs"
                          />
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                            in from {sm.dir === "v" ? "the left" : "the back"}
                          </span>
                          <Button
                            data-testid={`designer-seam-delete-${si}`}
                            variant="outline"
                            title="Take this seam off"
                            onClick={() => {
                              editSeams((items) =>
                                items.filter((s) => s.id !== sm.id),
                              );
                              if (activeSeam === sm.id) setActiveSeam(null);
                            }}
                            className="ml-auto h-9 border-zinc-700 bg-transparent px-2 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash size={13} weight="bold" />
                          </Button>
                        </div>
                      ))}
                      {seamCharge > 0 && seams.length > 0 && (
                        <div
                          data-testid="designer-seam-cost"
                          className="flex items-center justify-between rounded-md border border-amber-500/20 bg-amber-500/5 px-2 py-1.5 text-[10px] text-amber-200/90"
                        >
                          <span className="uppercase tracking-widest">
                            Seam labour · {money(seamCharge)} each
                          </span>
                          <span className="font-mono-data font-bold text-amber-100">
                            {seams.length} × = {money(seamCharge * seams.length)}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          data-testid="designer-seam-add"
                          variant="outline"
                          onClick={() => addSeam(selPiece, "v")}
                          className="h-9 border-amber-500/40 bg-transparent text-[10px] font-bold uppercase tracking-widest text-amber-200 hover:bg-amber-500/10"
                        >
                          <Plus size={13} weight="bold" className="mr-1.5" />
                          Add seam
                        </Button>
                        <Button
                          data-testid="designer-seam-autonest"
                          variant="outline"
                          onClick={() => autoNestSeams(selPiece)}
                          title="Suggest seam positions that split this piece to fit standard slabs"
                          className="h-9 border-indigo-400/40 bg-transparent text-[10px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/10"
                        >
                          <SquaresFour
                            size={13}
                            weight="bold"
                            className="mr-1.5"
                          />
                          Auto-nest
                        </Button>
                        <Button
                          data-testid="designer-seam-split"
                          disabled={!seams.length}
                          onClick={() => splitAtSeams(selPiece)}
                          className="h-9 bg-amber-500 text-[10px] font-bold uppercase tracking-widest text-zinc-950 hover:bg-amber-400 disabled:opacity-40"
                        >
                          <Scissors
                            size={13}
                            weight="bold"
                            className="mr-1.5"
                          />
                          {seams.length > 1
                            ? `Cut ${seams.length} seams`
                            : "Cut seam"}
                        </Button>
                        <Button
                          data-testid="designer-seam-remove"
                          variant="outline"
                          title="Join this piece back to the one next to it"
                          onClick={() => joinSeam(selPiece)}
                          className="h-9 border-zinc-700 bg-transparent text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                        >
                          Join back
                        </Button>
                        <Button
                          data-testid="designer-seam-rejoin-all"
                          variant="outline"
                          title="Rejoin every split run on the drawing back into whole pieces"
                          disabled={
                            !list.some((p) =>
                              (p.edges || []).some(
                                (e) => e?.seam && e?.seam_id,
                              ),
                            )
                          }
                          onClick={rejoinAll}
                          className="h-9 border-indigo-500/60 bg-transparent text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-40"
                        >
                          Rejoin all
                        </Button>
                        <Button
                          data-testid="designer-seam-hide"
                          variant="outline"
                          onClick={() => setShowSeamGuide(false)}
                          className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                        >
                          Hide guide
                        </Button>
                      </div>
                    </div>
                  )}
                  <Button
                    data-testid="designer-flip-h"
                    variant="outline"
                    onClick={() =>
                      replacePiece(selPiece.id, flipPiece(selPiece, "h"))
                    }
                    className="h-10 border-zinc-700 bg-transparent text-xs hover:bg-zinc-800"
                  >
                    <FlipHorizontal size={14} weight="bold" />
                  </Button>
                  <Button
                    data-testid="designer-flip-v"
                    variant="outline"
                    onClick={() =>
                      replacePiece(selPiece.id, flipPiece(selPiece, "v"))
                    }
                    className="h-10 border-zinc-700 bg-transparent text-xs hover:bg-zinc-800"
                  >
                    <FlipVertical size={14} weight="bold" />
                  </Button>
                </>
              )}
            </div>

            {step === "shape" && (lShapeOf(selPiece.points) || uShapeOf(selPiece.points)) && (
              <div
                className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3"
                data-testid="designer-lshape-strip"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                    {uShapeOf(selPiece.points) ? "U-shape" : "L-shape"} · strip depth
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={lStripLock}
                    data-testid="designer-lshape-lock"
                    onClick={() => setLStripLock((v) => !v)}
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      lStripLock
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        : "border-zinc-700 text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    {lStripLock ? "Locked" : "Unlocked"}
                  </button>
                </div>
                <div className="mt-2 flex items-end gap-3">
                  <label className="space-y-1">
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                      Strip width (in)
                    </span>
                    <MeasureInput
                      data-testid="designer-lshape-strip-input"
                      min={1}
                      fallback={7}
                      value={round2(stripWidthOf(selPiece.points))}
                      onChange={(n) => setStrip(selPiece, n)}
                      className="h-10 w-24 border-zinc-800 bg-zinc-950"
                    />
                  </label>
                  <p className="flex-1 text-[10px] leading-snug text-zinc-500">
                    {lStripLock
                      ? uShapeOf(selPiece.points)
                        ? "Both returns and the base stay at this depth while you edit the overall size."
                        : "Each leg keeps its own depth (B & E) while you edit the overall width and length — type a leg to change just that leg."
                      : "Unlocked — each side edits on its own; the wall edges stay put and corners stay 90°."}
                  </p>
                </div>
                {(() => {
                  const pts = selPiece.points;
                  const L = lShapeOf(pts);
                  if (L) {
                    const kinds = pts.map((_, k) => lSideKind(pts, L, k));
                    const cwEdge = kinds.indexOf("cw");
                    const chEdge = kinds.indexOf("ch");
                    const legs = [
                      { label: "Left leg E", key: "e", edge: cwEdge, val: round2(L.W - L.nw) },
                      { label: "Top leg B", key: "b", edge: chEdge, val: round2(L.H - L.nh) },
                    ].filter((x) => x.edge >= 0);
                    return (
                      <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="designer-leg-depths">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Leg depths (in)
                        </span>
                        <div className="mt-2 flex flex-wrap gap-3">
                          {legs.map((leg) => (
                            <label key={leg.key} className="space-y-1">
                              <span className="block text-[10px] text-zinc-400">{leg.label}</span>
                              <MeasureInput
                                data-testid={`designer-leg-depth-${leg.key}`}
                                min={1}
                                fallback={25.5}
                                value={leg.val}
                                onChange={(n) => setEdgeLengthOn(selPiece, leg.edge, Number(n))}
                                className="h-10 w-24 border-zinc-800 bg-zinc-950"
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  const U = uShapeOf(pts);
                  if (U) {
                    const legs = [
                      { label: "Left return", key: "rwA", val: round2(U.rwA), set: (n) => setUParam(selPiece, { rwA: Number(n) }, "Left return") },
                      { label: "Right return", key: "rwB", val: round2(U.rwB), set: (n) => setUParam(selPiece, { rwB: Number(n) }, "Right return") },
                      { label: "Base depth", key: "d", val: round2(U.d), set: (n) => setUBaseDepth(selPiece, Number(n)) },
                    ];
                    return (
                      <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="designer-leg-depths">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                          Return & base depths (in)
                        </span>
                        <div className="mt-2 flex flex-wrap gap-3">
                          {legs.map((leg) => (
                            <label key={leg.key} className="space-y-1">
                              <span className="block text-[10px] text-zinc-400">{leg.label}</span>
                              <MeasureInput
                                data-testid={`designer-leg-depth-${leg.key}`}
                                min={1}
                                fallback={25.5}
                                value={leg.val}
                                onChange={leg.set}
                                className="h-10 w-24 border-zinc-800 bg-zinc-950"
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {(step === "shape" || step === "dimensions") &&
              (() => {
                const U = uShapeOf(selPiece.points);
                if (!U) return null;
                const kinds = selPiece.points.map((_, k) => uSideKind(selPiece.points, U, k));
                const outer = SIDE_LETTER(kinds.indexOf("W"));
                const inner = SIDE_LETTER(kinds.indexOf("span"));
                const gA = SIDE_LETTER(kinds.indexOf("rwA"));
                const gB = SIDE_LETTER(kinds.indexOf("rwB"));
                const field = (id, label, value, onChange) => (
                  <label className="space-y-1">
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                      {label}
                    </span>
                    <MeasureInput
                      data-testid={id}
                      min={1}
                      fallback={25.5}
                      value={round2(value)}
                      onChange={onChange}
                      className="h-10 w-24 border-zinc-800 bg-zinc-950"
                    />
                  </label>
                );
                return (
                  <div
                    className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3"
                    data-testid="designer-ushape-base"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      U-shape · middle depth ({outer} to {inner}), returns & leg lengths
                    </span>
                    <div className="mt-2 flex flex-wrap items-end gap-3">
                      {field("designer-ushape-base-input", `Middle (${outer}–${inner})`, U.d, (n) => setUBaseDepth(selPiece, n))}
                      {field("designer-ushape-rwa-input", `Return ${gA}`, U.rwA, (n) => setUParam(selPiece, { rwA: Number(n) || 0 }, `Return ${gA}`))}
                      {field("designer-ushape-rwb-input", `Return ${gB}`, U.rwB, (n) => setUParam(selPiece, { rwB: Number(n) || 0 }, `Return ${gB}`))}
                      {field("designer-ushape-ha-input", `Length ${SIDE_LETTER(kinds.indexOf("hA"))}`, U.hA, (n) => setUParam(selPiece, { hA: Number(n) || 0 }, `Length ${SIDE_LETTER(kinds.indexOf("hA"))}`))}
                      {field("designer-ushape-hb-input", `Length ${SIDE_LETTER(kinds.indexOf("hB"))}`, U.hB, (n) => setUParam(selPiece, { hB: Number(n) || 0 }, `Length ${SIDE_LETTER(kinds.indexOf("hB"))}`))}
                    </div>
                    <p className="mt-2 text-[10px] leading-snug text-zinc-500">
                      Each field moves just its own side — uneven returns and one shorter leg are fine. Use Swing leg below to angle a return.
                    </p>
                  </div>
                );
              })()}

            {(step === "shape" || step === "dimensions") &&
              (() => {
                const cap = uCapOf(selPiece.points);
                if (!cap) return null;
                const pts = selPiece.points;
                const outerL = SIDE_LETTER(cap.a);
                const innerL = SIDE_LETTER(cap.b);
                const { gap } = uCapGap(pts, cap);
                const field = (id, label, value, onChange) => (
                  <label className="space-y-1">
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                      {label}
                    </span>
                    <MeasureInput
                      data-testid={id}
                      min={1}
                      fallback={25.5}
                      value={round2(value)}
                      onChange={onChange}
                      className="h-10 w-24 border-zinc-800 bg-zinc-950"
                    />
                  </label>
                );
                return (
                  <div
                    className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3"
                    data-testid="designer-ucap-base"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      Angled U · runs {outerL} / {innerL} & height
                    </span>
                    <div className="mt-2 flex flex-wrap items-end gap-3">
                      {field(`designer-ucap-outer-input`, `Length ${outerL}`, edgeLen(pts, cap.a), (n) =>
                        setEdgeLengthOn(selPiece, cap.a, Number(n) || 0),
                      )}
                      {field(`designer-ucap-inner-input`, `Length ${innerL}`, edgeLen(pts, cap.b), (n) =>
                        setEdgeLengthOn(selPiece, cap.b, Number(n) || 0),
                      )}
                      {field(`designer-ucap-height-input`, `Height ${outerL}–${innerL}`, gap, (n) =>
                        setUCapHeight(selPiece, Number(n) || 0),
                      )}
                    </div>
                    {(() => {
                      const legs = uCapLegs(pts, cap);
                      if (!legs.length) return null;
                      return (
                        <div
                          className="mt-3 border-t border-zinc-800 pt-3"
                          data-testid="designer-ucap-legs"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                              Angled legs — length & splay angle
                            </span>
                            <button
                              type="button"
                              data-testid="designer-ucap-mirror-toggle"
                              aria-pressed={ucapMirror}
                              onClick={() => setUcapMirror((v) => !v)}
                              className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${ucapMirror ? "border-indigo-400 bg-indigo-500 text-white" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}
                            >
                              Mirror {ucapMirror ? "on" : "off"}
                            </button>
                          </div>
                          <div className="mt-2 space-y-2">
                            {legs.map((li) => {
                              const LL = SIDE_LETTER(li);
                              const ang = Math.round(uCapLegAngle(pts, cap, li));
                              return (
                                <div key={li} className="flex flex-wrap items-end gap-3">
                                  {field(`designer-ucap-leg-${LL}-len`, `Length ${LL}`, edgeLen(pts, li), (nv) =>
                                    setUCapLegLength(selPiece, li, Number(nv) || 0),
                                  )}
                                  <label className="space-y-1">
                                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                                      Angle {LL} (°)
                                    </span>
                                    <input
                                      key={`${li}-${ang}`}
                                      type="number"
                                      min={5}
                                      max={175}
                                      step={1}
                                      data-testid={`designer-ucap-leg-${LL}-angle`}
                                      defaultValue={ang}
                                      onBlur={(ev) => setUCapLegAngle(selPiece, li, Number(ev.target.value) || 0)}
                                      onKeyDown={(ev) => {
                                        if (ev.key === "Enter") setUCapLegAngle(selPiece, li, Number(ev.target.value) || 0);
                                      }}
                                      className="h-10 w-24 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 outline-none focus:border-indigo-400"
                                    />
                                  </label>
                                  <div className="flex gap-1" data-testid={`designer-ucap-leg-${LL}-presets`}>
                                    {[45, 90, 135].map((pa) => (
                                      <button
                                        key={pa}
                                        type="button"
                                        data-testid={`designer-ucap-leg-${LL}-preset-${pa}`}
                                        onClick={() => setUCapLegAngle(selPiece, li, pa)}
                                        className="h-10 rounded-md border border-zinc-700 px-2 text-[11px] font-semibold text-zinc-300 transition-colors duration-150 hover:border-indigo-400 hover:bg-indigo-500/15 hover:text-white"
                                      >
                                        {pa}°
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    <p className="mt-2 text-[10px] leading-snug text-zinc-500">
                      Changing {innerL} grows {outerL} by the same amount, centred — the legs keep their
                      length and angle. Height sets the straight gap from {outerL} down to {innerL}. Each leg's
                      length slides along its own line; its angle hinges at the {outerL}/{innerL} corner.
                    </p>
                  </div>
                );
              })()}

            {(step === "shape" || step === "dimensions") &&
              (() => {
                const run = runOf(selPiece.points);
                if (!run || run.path.length < 3) return null;
                const corners = run.path.slice(1, -1).map((_, i) => i + 1);
                return (
                  <div
                    className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3"
                    data-testid="designer-run-bend"
                  >
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      <BendIcon
                        deg={Math.abs(runTurnDeg(run.path, corners[0]))}
                        className="h-3.5 w-3.5 text-indigo-300"
                      />
                      Swing leg (bend run)
                    </div>
                    {corners.map((c) => {
                      const cur = round2(Math.abs(runTurnDeg(run.path, c)));
                      const idBase = corners.length > 1 ? `designer-run-bend-c${c}` : "designer-run-bend";
                      return (
                        <div key={c} className="mt-2 space-y-2">
                          <span
                            className="block text-[10px] uppercase tracking-widest text-zinc-500"
                            data-testid={`${idBase}-now`}
                          >
                            {corners.length > 1 ? `Corner ${c} · ` : ""}now {cur}° off straight
                          </span>
                          <AnglePicker value={cur} onPick={(a) => bendRunCorner(selPiece, c, a)} idBase={idBase} />
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-zinc-500">Custom</span>
                            <Input
                              key={`${c}-${cur}`}
                              data-testid={`${idBase}-custom`}
                              type="number"
                              min={5}
                              max={170}
                              step={0.5}
                              defaultValue={cur}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  bendRunCorner(selPiece, c, e.currentTarget.value);
                                }
                              }}
                              onBlur={(e) => {
                                if (Math.abs(Number(e.target.value) - cur) > 0.01)
                                  bendRunCorner(selPiece, c, e.target.value);
                              }}
                              className="h-8 w-20 border-zinc-800 bg-zinc-950 text-xs"
                            />
                            <span className="text-[10px] text-zinc-500">°</span>
                          </div>
                        </div>
                      );
                    })}
                    <p className="mt-2 text-[10px] leading-snug text-zinc-500">
                      Swings a whole leg around the corner (90° = square L) — lengths,
                      depth, edges and seams stay. To cut an angled/diagonal corner
                      instead, click that corner and use <b>Clip corner</b>.
                    </p>
                  </div>
                );
              })()}

            {selCorner ? (
              <CornerParams
                feature={selCorner}
                services={services}
                cutoutKinds={cutoutKinds}
                onChange={updateCorner}
                onRemove={deleteCorner}
                onBack={() => setSel({ pieceId: selPiece.id, edge: null })}
              />
            ) : sel.edge != null && sel.panelEdge != null && selEdge ? (
              (() => {
                const kind = sel.panelKind || "backsplash";
                const k = sel.panelEdge;
                const word = kind === "backsplash" ? "Backsplash" : kind === "mitre" ? "Mitre panel" : "Waterfall";
                const height = kind === "backsplash" ? selEdge.backsplash_height_in : selEdge.drop_in;
                const current = selEdge.panel_edges?.[kind]?.[k] || selEdge.profile || "Eased";
                const cards = (edgeStyles || []).filter((s) => s.visible !== false);
                const list = cards.length ? cards : (edgeProfiles || []).map((name) => ({ name, shape: "eased" }));
                const setSide = (name) =>
                  setEdge({
                    panel_edges: {
                      ...(selEdge.panel_edges || {}),
                      [kind]: { ...((selEdge.panel_edges || {})[kind] || {}), [k]: name },
                    },
                  });
                return (
                  <div className="mt-5 rounded-lg border border-zinc-800 bg-zinc-950" data-testid="designer-panel-side">
                    <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2.5">
                      <span className="text-xs font-bold uppercase tracking-widest text-zinc-200">
                        {word} · {formatInches(height)} · side {EDGE_LETTER(sel.edge)}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-testid="designer-panel-detach"
                          title={`Detach this ${word.toLowerCase()} into a standalone piece you can shape, seam and cut`}
                          onClick={() => detachPanel(selPiece, sel.edge, kind)}
                          className="rounded-md border border-indigo-400/50 bg-indigo-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
                        >
                          Detach &amp; edit
                        </button>
                        <button
                          type="button"
                          data-testid="designer-panel-side-close"
                          onClick={() => setSel({ pieceId: selPiece.id, edge: sel.edge })}
                          className="rounded-md p-1 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
                        >
                          <X size={13} weight="bold" />
                        </button>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="flex flex-wrap gap-1.5" data-testid="designer-panel-side-tabs">
                        {PANEL_SIDE_NAME.map((nm, idx) => (
                          <button
                            key={nm}
                            type="button"
                            data-testid={`designer-panel-side-tab-${idx}`}
                            onClick={() => setSel({ ...sel, panelEdge: idx })}
                            className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                              idx === k
                                ? "border-amber-400 bg-amber-500/15 text-amber-200"
                                : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                            }`}
                          >
                            {nm}
                          </button>
                        ))}
                      </div>
                      <span className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                        Edge on this side · now {current}
                      </span>
                      <div className="mt-2 grid max-h-[22rem] grid-cols-2 gap-2 overflow-y-auto pr-1" data-testid="designer-panel-profile-grid">
                        {list.slice(0, 40).map((s) => {
                          const on = s.name === current;
                          return (
                            <button
                              key={s.id || s.name}
                              type="button"
                              data-testid={`designer-panel-profile-${s.name}`}
                              onClick={() => setSide(s.name)}
                              className={`rounded-lg border p-2 text-left transition-colors duration-150 ${
                                on ? "border-indigo-400 bg-indigo-500/15" : "border-zinc-800 bg-zinc-900 hover:border-zinc-600"
                              }`}
                            >
                              <EdgeShape shape={s.shape} color={s.color || "#a5b4fc"} />
                              <span className="mt-1 block text-[11px] font-semibold leading-tight text-zinc-200">{s.name}</span>
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          data-testid="designer-panel-side-all"
                          onClick={() =>
                            setEdge({
                              panel_edges: {
                                ...(selEdge.panel_edges || {}),
                                [kind]: { 0: current, 1: current, 2: current, 3: current },
                              },
                            })
                          }
                          className="rounded-md border border-zinc-700 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                        >
                          Same on all 4 sides
                        </button>
                        <button
                          type="button"
                          data-testid="designer-panel-side-reset"
                          onClick={() => {
                            const rest = { ...(selEdge.panel_edges || {}) };
                            delete rest[kind];
                            setEdge({ panel_edges: rest });
                          }}
                          className="rounded-md border border-zinc-700 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                        >
                          Match counter edge
                        </button>
                      </div>
                      <p className="mt-2 text-[10px] leading-snug text-zinc-500">
                        Click any side of the {word.toLowerCase()} on the sheet to color it. The top of a backsplash or the face of a waterfall is the one usually polished.
                      </p>
                    </div>
                  </div>
                );
              })()
            ) : sel.edge != null ? (
              <>
                <div
                  className="mt-5 grid gap-3"
                  data-testid="designer-edge-panel"
                >
                  {step !== "edges" && step !== "cutouts" && (
                  <div className="space-y-1">
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                      Side {sel.edge + 1} length (in)
                    </span>
                    <MeasureInput
                      data-testid="designer-edge-length"
                      min={0.25}
                      value={round2(edgeLen(selPiece.points, sel.edge))}
                      onChange={(n) => setEdgeLength(n)}
                      className="h-10 border-zinc-800 bg-zinc-950"
                    />
                  </div>
                  )}
                  {step === "edges" ? (
                    <EdgePanel
                      styles={edgeStyles}
                      profiles={edgeProfiles}
                      edge={selPiece.edges?.[sel.edge]}
                      edgeIndex={sel.edge}
                      edgeKind={
                        selEdge?.appliance
                          ? selEdge.appliance
                          : selEdge?.seam
                            ? "seam"
                            : selDrop === "waterfall"
                              ? "waterfall"
                              : selDrop === "mitre"
                                ? "mitre"
                                : "finished"
                      }
                      onProfile={setEdgeProfile}
                      onEdge={setEdge}
                      onEdgeType={setEdgeType}
                      onCopyToAll={(name) =>
                        update(selPiece.id, {
                          edges: selPiece.edges.map((e) => ({
                            ...e,
                            profile: name,
                          })),
                        })
                      }
                      onReset={() =>
                        setEdge({
                          profile:
                            (edgeStyles || []).find((s) => s.default)?.name ||
                            edgeProfiles[0] ||
                            "Eased",
                          seam: false,
                          miter: false,
                          drop_in: 0,
                          backsplash_height_in: 0,
                        })
                      }
                      onClose={() =>
                        setSel({ pieceId: selPiece.id, edge: null })
                      }
                    />
                  ) : null}
                  {step === "edges" &&
                    (Number(selEdge?.backsplash_height_in) > 0 ||
                      Number(selEdge?.drop_in) > 0) && (
                      <div
                        className="mt-3 flex flex-wrap gap-2"
                        data-testid="designer-edge-detach-row"
                      >
                        {Number(selEdge?.backsplash_height_in) > 0 && (
                          <button
                            type="button"
                            data-testid="designer-edge-detach-backsplash"
                            title="Turn this backsplash into a standalone piece you can shape, seam and cut"
                            onClick={() =>
                              detachPanel(selPiece, sel.edge, "backsplash")
                            }
                            className="rounded-md border border-indigo-400/50 bg-indigo-500/10 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
                          >
                            Detach backsplash to edit
                          </button>
                        )}
                        {Number(selEdge?.drop_in) > 0 && (
                          <button
                            type="button"
                            data-testid="designer-edge-detach-drop"
                            title="Turn this panel into a standalone piece you can shape, seam and cut"
                            onClick={() =>
                              detachPanel(
                                selPiece,
                                sel.edge,
                                selEdge?.miter ? "mitre" : "waterfall",
                              )
                            }
                            className="rounded-md border border-indigo-400/50 bg-indigo-500/10 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
                          >
                            Detach {selEdge?.miter ? "mitre" : "waterfall"} to edit
                          </button>
                        )}
                      </div>
                    )}
                </div>
                {step === "shape" && selBump && pickerFor !== selBump.id && (
                  <BumpParams
                    bump={selBump}
                    services={services}
                    sideLength={round2(selPiece.edges?.[sel.edge]?.length_in || 0)}
                    onChange={updateBump}
                    onDuplicate={duplicateBump}
                    onRemove={deleteBump}
                    onSavePreset={saveBumpPreset}
                    onBack={() => setPickerFor(selBump.id)}
                  />
                )}
                {step !== "shape" && free && (
                  <button
                    type="button"
                    data-testid="designer-edge-open-shape"
                    onClick={() => setStep("shape")}
                    className="mt-3 flex h-9 w-full items-center justify-center gap-2 rounded-md border border-indigo-400/50 bg-indigo-500/10 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
                  >
                    <Polygon size={13} weight="bold" /> Curves &amp; bumps on this side
                  </button>
                )}
                {step === "shape" && (!selBump || pickerFor === selBump.id) && (
                  <div className="mt-3 w-full" data-testid="designer-bump-row">
                    <div className="text-sm font-bold text-zinc-100">Curves &amp; Bumps</div>
                    <div className="mt-1 text-[10px] text-zinc-500">side {sel.edge + 1} · {tag(selPiece.edges?.[sel.edge]?.length_in || 0)}</div>
                    {bumpPresets.length > 0 && (
                      <div className="mt-3" data-testid="designer-curve-gallery-presets">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                          Presets
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {bumpPresets.map((p) => (
                            <div key={p.id} className="relative">
                              <button
                                type="button"
                                data-testid={`designer-preset-${p.id}`}
                                onClick={() => applyBumpPreset(p)}
                                className="group w-full rounded-lg border border-amber-400/40 bg-zinc-950 p-2 text-left transition-colors duration-150 hover:border-amber-300 hover:bg-zinc-900"
                              >
                                <svg viewBox="0 0 60 40" className="h-12 w-full" preserveAspectRatio="xMidYMid meet">
                                  <path
                                    d={CURVE_OPTIONS.find((o) => o.kind === p.kind)?.d}
                                    fill="none"
                                    stroke="#fbbf24"
                                    strokeWidth="1.6"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <div className="mt-1 truncate text-xs font-semibold text-zinc-200 group-hover:text-white">
                                  {p.label}
                                </div>
                                {p.service_name && (
                                  <div className="truncate text-[10px] text-zinc-500">{p.service_name}</div>
                                )}
                              </button>
                              <button
                                type="button"
                                data-testid={`designer-preset-delete-${p.id}`}
                                title="Remove preset"
                                onClick={() => savePresets(bumpPresets.filter((x) => x.id !== p.id))}
                                className="absolute right-1 top-1 rounded p-0.5 text-zinc-500 hover:bg-zinc-800 hover:text-red-300"
                              >
                                <X size={11} weight="bold" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {[
                      ["used", "Used", CURVE_OPTIONS.filter((o) => usedBumpKinds.includes(o.kind))],
                      ["general", "General", CURVE_OPTIONS],
                    ]
                      .filter(([, , opts]) => opts.length)
                      .map(([key, title, opts]) => (
                        <div key={key} className="mt-3" data-testid={`designer-curve-gallery-${key}`}>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                            {title}
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {opts.map((o) => {
                              const active = selBump ? o.kind === selBump.kind : o.kind === "none";
                              return (
                                <button
                                  key={o.kind}
                                  type="button"
                                  data-testid={`designer-shape-${o.kind}${key === "used" ? "-used" : ""}`}
                                  onClick={() => {
                                    if (BUMP_KINDS.includes(o.kind)) {
                                      if (selBump && o.kind === selBump.kind) {
                                        setPickerFor(null);
                                        return;
                                      }
                                      addBumpOnEdge(o.kind);
                                      return;
                                    }
                                    if (selBump) {
                                      deleteBump();
                                      return;
                                    }
                                    const next = edgeShape(selPiece, sel.edge, "none", 1, 1, "");
                                    if (next) replacePiece(selPiece.id, next);
                                  }}
                                  className={`group rounded-lg border p-2 text-left transition-colors duration-150 ${
                                    active
                                      ? "border-indigo-400 bg-indigo-500/15"
                                      : "border-zinc-700 bg-zinc-950 hover:border-indigo-400 hover:bg-zinc-900"
                                  }`}
                                >
                                  <svg viewBox="0 0 60 40" className="h-12 w-full" preserveAspectRatio="xMidYMid meet">
                                    <path
                                      d={o.d}
                                      fill={active ? "rgba(99,102,241,0.25)" : "none"}
                                      stroke={active ? "#818cf8" : "#a1a1aa"}
                                      strokeWidth="1.6"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  <div className="mt-1 text-xs font-semibold text-zinc-200 group-hover:text-white">
                                    {o.label}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </>
            ) : sel.vertex != null ? (
              <div
                className="mt-3 space-y-2"
                data-testid="designer-corner-row"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                    Corner {sel.vertex + 1}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Size (in)
                    </span>
                    <MeasureInput
                      data-testid="designer-corner-size"
                      min={0.25}
                      value={cornerSize}
                      onChange={(n) => setCornerSize(n)}
                      className="h-9 w-20 border-zinc-800 bg-zinc-950 text-xs"
                    />
                  </div>
                </div>
                <div
                  className="grid grid-cols-4 gap-1.5"
                  data-testid="designer-corner-angle"
                >
                  {[90, 67.5, 45, 22.5].map((a) => (
                    <button
                      key={a}
                      type="button"
                      data-testid={`designer-corner-angle-${a}`}
                      onClick={() => setCornerAngle(a)}
                      className={`rounded-md py-1.5 text-[10px] font-bold transition-colors duration-150 ${
                        Number(cornerAngle) === a
                          ? "bg-indigo-500 text-white"
                          : "border border-zinc-800 text-zinc-400 hover:bg-zinc-800"
                      }`}
                    >
                      {a}°
                    </button>
                  ))}
                </div>
                <div
                  className="flex items-center gap-2"
                  data-testid="designer-corner-angle-custom-row"
                >
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Custom angle
                  </span>
                  <Input
                    data-testid="designer-corner-angle-custom"
                    type="number"
                    min={1}
                    max={89}
                    step={0.5}
                    value={cornerAngle}
                    onChange={(e) =>
                      setCornerAngle(Number(e.target.value) || 45)
                    }
                    className="h-8 w-20 border-zinc-800 bg-zinc-950 text-xs"
                  />
                  <span className="text-[10px] text-zinc-500">
                    ° · used by Clip corner
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ["radius", "Radius"],
                    ["clip", "Clip corner"],
                    ["notch", "Notch"],
                  ].map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      data-testid={`designer-${mode === "clip" ? "chamfer" : mode}-corner`}
                      onClick={() => {
                        if (mode === "clip" && Number(cornerAngle) >= 90) return;
                        cutCorner(
                          selPiece,
                          sel.vertex,
                          Number(cornerSize) || 1,
                          mode,
                          mode === "clip" ? { angle: Number(cornerAngle) } : {},
                        );
                      }}
                      className="group flex flex-col items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-950 p-2 transition-colors duration-150 hover:border-indigo-400 hover:bg-indigo-500/10"
                    >
                      <svg viewBox="0 0 24 24" className="h-9 w-9">
                        <path
                          d={CORNER_ICON_PATH[mode]}
                          fill="rgba(99,102,241,0.12)"
                          stroke="#a5b4fc"
                          strokeWidth="1.4"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-200 group-hover:text-white">
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500">
                  Radius rounds the corner, Clip cuts a{" "}
                  {Number(cornerAngle) >= 90 ? "square (90°)" : `${cornerAngle}°`}{" "}
                  flat, notch cuts a square out. Pick the angle above first.
                </p>
              </div>
            ) : sel.cutoutId != null ? (
              <div className="mt-4 space-y-2" data-testid="designer-cutout-list">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Cut-outs on this piece · pick one to edit above
                </div>
                {(selPiece.cutouts || []).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`designer-cutout-row-${c.id}`}
                    onClick={() => setSel({ pieceId: selPiece.id, edge: null, cutoutId: c.id })}
                    className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-xs transition-colors duration-150 ${
                      sel.cutoutId === c.id
                        ? "border-amber-400/60 bg-amber-500/10 text-amber-100"
                        : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                    }`}
                  >
                    <span className="font-semibold">{c.name || c.kind}</span>
                    <span className="font-mono-data text-[10px] text-zinc-500">
                      {c.w}" × {c.h}"{Number(c.radius) > 0 ? ` · R${c.radius}"` : ""}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-[11px] leading-relaxed text-zinc-500">
                {step === "dimensions"
                  ? "Drag a side handle to resize, a corner dot to resize two sides at once, or click a side to type its length. Drag the handle above the piece to spin it."
                  : step === "shape"
                    ? "Click a side for bumps and curves, or a corner dot to clip, notch or radius it."
                    : step === "edges"
                      ? "Click a side to choose its profile, backsplash or waterfall."
                      : "Cut-outs live in the left panel — pick one to add it, then click it on the drawing to edit size, position and corner radius."}
              </p>
            )}
            {lstep === "dimensions" && (
              <div className="mt-6 border-t border-zinc-800 pt-4" data-testid="designer-panel-draw">
                {drawSection}
              </div>
            )}
          </aside>
        ) : !focus && lstep === "dimensions" && showPanel ? (
          <aside
            className="shrink-0 border-t border-zinc-800 bg-zinc-900 p-5 lg:h-full lg:w-80 lg:overflow-y-auto lg:border-l lg:border-t-0"
            data-testid="designer-draw-panel"
          >
            {drawSection}
            {list.length > 0 && !free && (
              <p
                className="mt-6 text-[11px] leading-relaxed text-zinc-500"
                data-testid="designer-idle-hint"
              >
                Click a piece to size it. Seam sides are dashed amber and are not
                charged as finished edge. Right-click a piece for quick tools; hold
                space or the middle mouse button to pan.
              </p>
            )}
          </aside>
        ) : null}
      </div>
      {tourOpen && <DesignerTour steps={TOUR_STEPS} onClose={closeTour} />}
      <CutoutTemplatePicker
        open={!!tplPicker}
        onClose={() => setTplPicker(null)}
        templates={cutoutTemplates}
        onUse={useTemplateFromPicker}
        onDelete={onDeleteCutoutTemplate}
        onRename={onRenameCutoutTemplate}
        onUploadPhoto={onUploadTrace}
        onSetPhoto={onSetCutoutTemplatePhoto}
      />
    </div>
  );
};

export const CountertopDesigner = (props) => (
  <StudioErrorBoundary>
    <CountertopDesignerInner {...props} />
  </StudioErrorBoundary>
);
